function isPlainObject(value) {
  return Object.prototype.toString.call(value) === "[object Object]";
}

function deepMerge(base, override) {
  if (Array.isArray(base) || Array.isArray(override)) {
    return Array.isArray(override) ? [...override] : Array.isArray(base) ? [...base] : override;
  }

  if (!isPlainObject(base) || !isPlainObject(override)) {
    return override === undefined ? base : override;
  }

  const result = { ...base };

  Object.keys(override).forEach((key) => {
    const baseValue = base[key];
    const overrideValue = override[key];

    if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
      result[key] = deepMerge(baseValue, overrideValue);
      return;
    }

    if (Array.isArray(overrideValue)) {
      result[key] = [...overrideValue];
      return;
    }

    result[key] = overrideValue;
  });

  return result;
}

const en = {
  common: {
    login: "Login",
    register: "Register",
    logout: "Logout",
    dashboard: "Dashboard",
    studio: "Studio",
    library: "Library",
    billing: "Billing",
    home: "Home",
    features: "Features",
    pricing: "Pricing",
    about: "About",
    blog: "Blog",
    settings: "Account Settings",
    waitlistAdmin: "Waitlist Admin",
    fullscreenEnter: "Enter Fullscreen",
    fullscreenExit: "Exit Fullscreen",
    light: "Light",
    dark: "Dark",
    language: "Language",
    theme: "Theme",
    current: "Current",
    tools: "Tools",
    unlockAllStudioTools: "Unlock all Studio tools",
    readGuide: "Read Guide",
    loading: "Loading",
    copied: "Copied",
    copyCode: "Copy Code",
    copyLink: "Copy Link"
  },

  languages: {
    en: "English",
    es: "Spanish",
    fr: "French",
    pt: "Portuguese"
  },

hookAngleTool: {
  hero: {
    badge: "Angle strategy tool",
    title: "Hook Angles",
    description: {
      before: "Use this tool to generate",
      strong: "multiple strategic hook angles",
      after:
        "from one topic so you can approach the same idea in fresher, more effective ways for different audiences and goals."
    }
  },
  fields: {
    topic: {
      label: "Topic",
      hint: "The main idea you want Hookora to build hook angles around.",
      placeholder: "What topic do you want hook angles for?"
    },
    platform: {
      label: "Platform",
      hint: "Choose the platform where the hook will be used."
    },
    audience: {
      label: "Audience",
      hint:
        "Who the hook should speak to. Example: creators, founders, students, business owners.",
      placeholder: "Audience"
    },
    goal: {
      label: "Goal",
      hint:
        "What the hook should help achieve. Example: clicks, retention, curiosity, comments, conversions.",
      placeholder: "Goal"
    },
    offer: {
      label: "Offer",
      hint:
        "The product, service, value, or transformation behind the content.",
      placeholder: "Offer or message"
    },
    painPoint: {
      label: "Pain Point",
      hint: "The problem, frustration, or challenge your audience faces.",
      placeholder: "Pain point"
    },
    desiredOutcome: {
      label: "Desired Outcome",
      hint: "The result your audience wants after solving the problem.",
      placeholder: "Desired outcome"
    },
    brandVoice: {
      label: "Brand Voice",
      hint:
        "How the hook should sound. Example: bold, witty, premium, direct, expert.",
      placeholder: "Brand voice"
    },
    avoidPhrases: {
      label: "Avoid Phrases",
      hint: "Words or patterns you do not want repeated in the angles.",
      placeholder: "Avoid phrases"
    },
    extraContext: {
      label: "Extra Context",
      hint:
        "Add product notes, campaign context, audience details, proof, or any extra direction.",
      placeholder: "Extra context, proof, or campaign notes"
    }
  },
  contextTitle: "Hook Angle Context",
  errors: {
    missingTopic: "Please enter a topic first.",
    generateFailed: "Failed to generate hook angles."
  },
  actions: {
    generate: "Generate Hook Angles",
    generating: "Generating...",
    copy: "Copy",
    copied: "Copied"
  },
  results: {
    intro: {
      before: "These are",
      strong: "strategic hook angle directions",
      after:
        "you can use to approach the same topic from different perspectives."
    },
    score: "Score",
    angleType: "Angle Type",
    bestUseCase: "Best Use Case",
    sampleHook: "Sample Hook"
  }
},  

  hookTool: {
  heroBadge: "Core hook generator",
  title: "Viral Hook Generator",
  description:
    "Generate stronger hooks for your content using different hook styles, audience context, and platform direction.",

  contextSectionTitle: "Hook context",

  fields: {
    topic: "Topic",
    hookStyle: "Hook style",
    platform: "Platform",
    audience: "Audience",
    goal: "Goal",
    offer: "Offer",
    painPoint: "Pain point",
    desiredOutcome: "Desired outcome",
    angle: "Angle",
    ctaDirection: "CTA direction",
    keywords: "Keywords",
    brandVoice: "Brand voice",
    avoidPhrases: "Avoid phrases",
    extraContext: "Extra context"
  },

  placeholders: {
    topic: "What topic do you want hooks for?",
    audience: "Who is this for?",
    goal: "What is the goal?",
    offer: "What are you offering?",
    painPoint: "What problem are they facing?",
    desiredOutcome: "What outcome do they want?",
    angle: "What angle should the hook take?",
    ctaDirection: "What action should people take next?",
    keywords: "Keywords to include",
    brandVoice: "How should the hooks sound?",
    avoidPhrases: "Words or phrases to avoid",
    extraContext: "Any extra context to improve the hooks"
  },

  templates: {
    curiosity: {
      label: "Curiosity",
      description: "Create intrigue and make people want to know more."
    },
    authority: {
      label: "Authority",
      description: "Lead with confidence, expertise, or a strong point of view."
    },
    problem: {
      label: "Problem",
      description: "Call out a pain point or frustration immediately."
    },
    benefit: {
      label: "Benefit",
      description: "Highlight the outcome, value, or transformation quickly."
    },
    controversy: {
      label: "Controversy",
      description: "Challenge assumptions with a bold or surprising angle."
    },
    story: {
      label: "Story",
      description: "Open with a story-like line that feels human and engaging."
    }
  },

  actions: {
  generate: "Generate Hooks",
  generating: "Generating...",
  clearResults: "Clear Results",
  resetForm: "Reset Form",
  copy: "Copy",
  copyHook: "Copy Hook",
  copied: "Copied"
},

messages: {
  readyToUse:
    "These are <strong>ready-to-use hooks</strong>. Pick the strongest one and use it directly as your opening line, title opener, video intro, caption opener, or first carousel slide.",

  score: "Score",

  topicRequired: "Please enter a topic first.",

  generateFailed: "Failed to generate hooks."
},

errors: {
  missingTopic: "Please enter a topic first.",
  generationFailed: "Failed to generate hooks."
}
},

libraryPage: {
  title: "Library",
  subtitle: "Browse saved hooks and saved Studio outputs from your content workflow.",

  currentPlanLabel: "Your current plan:",

  planLabels: {
    free: "Free",
    creator: "✔ Creator",
    pro: "✔ Pro"
  },

  planBox: {
    line1: "Hooks and Studio assets are both stored here now.",
    line2: {
      before: "CSV export and bulk actions are available on",
      creator: "Creator",
      middle: "and",
      pro: "Pro"
    },
    line3: {
      before: "Advanced library intelligence and future workspaces are planned for",
      pro: "Pro"
    }
  },

  badges: {
    creatorPlus: "CREATOR+",
    proInsights: "PRO INSIGHTS",
    locked: "Locked"
  },

  tools: {
    hooks: {
      label: "Viral Hook Generator",
      badge: "HOOKS"
    },
    youtubeTitleOptimizer: {
      label: "YouTube Title Optimizer",
      badge: "TITLE"
    },
    captionGenerator: {
      label: "Caption Generator",
      badge: "CAPTION"
    },
    hashtagGenerator: {
      label: "Hashtag Generator",
      badge: "HASHTAG"
    },
    descriptionRewriter: {
      label: "Description Rewriter",
      badge: "DESCRIPTION"
    },
    shortScriptGenerator: {
      label: "Short Script Generator",
      badge: "SCRIPT"
    },
    thumbnailTextAnalyzer: {
      label: "Thumbnail Text Analyzer",
      badge: "THUMBNAIL"
    },
    hookAngles: {
      label: "Hook Angles",
      badge: "ANGLES"
    },
    ctaGenerator: {
      label: "CTA Generator",
      badge: "CTA"
    },
    humanizer: {
      label: "AI Humanizer",
      badge: "HUMANIZER"
    },
    other: {
      label: "Other",
      badge: "OTHER"
    }
  },

  stats: {
    savedHooks: "Saved Hooks",
    savedStudioAssets: "Saved Studio Assets",
    favoriteHooks: "Favorite Hooks",
    averageHookScore: "Average Hook Score"
  },

  toolBreakdown: {
    title: "Studio Tool Breakdown",
    savedItemsCount: "{{count}} saved studio item",
    savedItemsCount_plural: "{{count}} saved studio items"
  },

  filters: {
    search: "Search",
    searchPlaceholder: "Search topic, content, or tool...",
    platform: "Platform",
    allPlatforms: "All",
    studioTool: "Studio Tool",
    allStudioTools: "All Studio Tools",
    sortBy: "Sort By",
    favoritesOnly: "Favorites only"
  },

  sort: {
    newest: "Newest",
    oldest: "Oldest",
    highestScore: "Highest Score",
    lowestScore: "Lowest Score",
    topic: "Topic"
  },

  actions: {
    upgradeToCreator: "Upgrade to Creator",
    unselectVisibleHooks: "Unselect Visible Hooks",
    selectVisibleHooks: "Select Visible Hooks",
    clearSelection: "Clear Selection",
    copiedAll: "Copied All!",
    copyVisibleHooks: "Copy Visible Hooks",
    exportHooksTxt: "Export Hooks TXT",
    exportHooksCsv: "Export Hooks CSV",
    favoriteSelected: "Favorite Selected",
    unfavoriteSelected: "Unfavorite Selected",
    deleteSelected: "Delete Selected",
    copied: "Copied!",
    copy: "Copy",
    copyHook: "Copy Hook",
    regenerate: "Regenerate",
    favorite: "Favorite",
    unfavorite: "Unfavorite",
    delete: "Delete",
    rename: "Rename",
    deleteFolder: "Delete Folder",
    save: "Save",
    saving: "Saving...",
    cancel: "Cancel",
    deleting: "Deleting..."
  },

  states: {
    loading: "Loading hook library...",
    working: "Working...",
    noFoldersMatch: "No folders match your current filters."
  },

  labels: {
    untitled: "Untitled",
    score: "Score",
    selectedHooksCount: "{{count}} hooks selected",
    toolType: "Tool type",
    noDate: "No date",
    favorite: "Favorite"
  },

  export: {
    topic: "Topic",
    platform: "Platform",
    score: "Score",
    favorite: "Favorite",
    created: "Created",
    hook: "Hook",
    yes: "Yes",
    no: "No"
  },

  features: {
    bulkSelection: "Bulk selection",
    csvExport: "CSV export",
    bulkDelete: "Bulk delete",
    bulkFavorite: "Bulk favorite",
    bulkUnfavorite: "Bulk unfavorite"
  },

  notices: {
    freeLock: {
      before: "Bulk actions and CSV export are locked on Free.",
      upgradeLink: "Upgrade to Creator"
    },
    proPreview: {
      before: "Pro preview: future library intelligence, saved presets, and workspaces will be available on",
      pro: "Pro"
    }
  },

  folders: {
    title: "Topic Folders",
    folderNamePlaceholder: "Enter folder name",
    itemsCount: "{{count}} item",
    itemsCount_plural: "{{count}} items",
    hooksCount: "{{count}} hook",
    hooksCount_plural: "{{count}} hooks",
    assetsCount: "{{count}} asset",
    assetsCount_plural: "{{count}} assets"
  },

  sections: {
    studioAssets: "Studio Assets",
    hooks: "Hooks"
  },

  confirmations: {
    deleteHook: "Are you sure you want to delete this hook?",
    deleteAsset: "Are you sure you want to delete this saved item?",
    deleteSelectedHooks: "Are you sure you want to delete {{count}} selected hook(s)?",
    deleteFolder: "Are you sure you want to delete this folder and all its contents?\n\nFolder: {{folderName}}\nItems: {{itemCount}}"
  },

  messages: {
    notLoggedIn: "You are not logged in. Please log in first.",
    loadFailed: "Failed to load hook library.",
    upgradeRequired: "{{featureName}} is available on Creator and Pro plans. Upgrade to unlock this feature.",
    favoriteUpdateFailed: "Failed to update favorite status.",
    deleteHookFailed: "Failed to delete hook.",
    deleteAssetFailed: "Failed to delete saved item.",
    deleteFolderFailed: "Failed to delete folder.",
    regenerateFailed: "Failed to regenerate hook.",
    folderNameRequired: "Folder name cannot be empty.",
    renameFolderFailed: "Failed to rename folder.",
    copyVisibleFailed: "Failed to copy visible hooks.",
    csvExportFailed: "CSV export failed.",
    bulkDeleteFailed: "Failed to delete selected hooks.",
    bulkUpdateFailed: "Failed to update selected hooks."
  }
},

titleTool: {
  hero: {
    badge: "Click-focused tool",
    title: "Titles",
    description:
      "Use this tool to generate clickable title options for videos and content pieces. Add search intent, angle, and context so Hookora can return stronger title variations that feel sharper, clearer, and more likely to earn attention.",
  },
  fields: {
    topic: {
      label: "Topic",
      hint:
        "The main subject of the video or content piece. The clearer your topic, the better the title variations.",
      placeholder: "What video or topic are these titles for?",
    },
    audience: {
      label: "Audience",
      hint:
        "Who the content is for. Example: students, creators, ecommerce founders, Nigerian freelancers.",
      placeholder: "Audience",
    },
    goal: {
      label: "Goal",
      hint:
        "What the title should optimize for. Example: clicks, curiosity, search traffic, authority, conversions.",
      placeholder: "Goal",
    },
    angle: {
      label: "Angle",
      hint:
        "The framing of the title. Example: mistake-led, beginner-friendly, contrarian, results-focused.",
      placeholder: "Angle",
    },
    keywords: {
      label: "Keywords",
      hint:
        "Important search terms to weave into the titles naturally. Separate them with commas.",
      placeholder: "Keywords (comma separated)",
    },
    brandVoice: {
      label: "Brand Voice",
      hint:
        "How the title should feel. Example: sharp, premium, direct, playful, bold.",
      placeholder: "Brand voice",
    },
    avoidPhrases: {
      label: "Avoid Phrases",
      hint:
        "Words or repeated patterns you do not want the title generator to use.",
      placeholder: "Avoid phrases",
    },
    extraContext: {
      label: "Extra Context",
      hint:
        "Add proof points, specific results, target emotion, or extra positioning notes to improve title quality.",
      placeholder:
        "Extra context, unique proof, result, example, or positioning note",
    },
  },
  contextSectionTitle: "Title Context",
  actions: {
    generating: "Generating...",
    generate: "Generate Titles",
    clearResults: "Clear Results",
    resetForm: "Reset Form",
    copied: "Copied",
    copy: "Copy",
  },
  resultsIntro:
    "These are ready-to-test title options. Pick the strongest one based on click appeal, clarity, and fit for your audience.",
  labels: {
    score: "Score",
  },
  fallbackUntitledTopic: "Untitled Topic",
  errors: {
    topicRequired: "Please enter a topic first.",
    generateFailed: "Failed to generate titles.",
  },
},

captionTool: {
  hero: {
    badge: "Social writing tool",
    title: "Captions",
    description:
      "Use this tool to generate ready-to-post captions for social content. Add platform, tone, CTA direction, and extra context so Hookora can create stronger, more relevant caption options for your post or video."
  },

  fields: {
    topic: {
      label: "Topic",
      hint:
        "The main subject of the caption. Be clear about what the post or video is about.",
      placeholder: "What is the caption about?"
    },
    platform: {
      label: "Platform",
      hint:
        "Choose where the caption will be posted. Platform affects tone, pacing, and CTA style."
    },
    tone: {
      label: "Tone",
      hint:
        "How the caption should sound. Example: engaging, bold, playful, educational, premium, direct.",
      placeholder: "Tone"
    },
    audience: {
      label: "Audience",
      hint:
        "Who the caption is for. Example: skincare brands, creators, startup founders, Nigerian students.",
      placeholder: "Audience"
    },
    goal: {
      label: "Goal",
      hint:
        "What you want the caption to achieve. Example: more saves, comments, clicks, signups, or shares.",
      placeholder: "Goal"
    },
    offer: {
      label: "Offer",
      hint:
        "The product, service, value proposition, freebie, or message behind the content.",
      placeholder: "Offer or product"
    },
    painPoint: {
      label: "Pain Point",
      hint: "The problem or frustration your audience is dealing with.",
      placeholder: "Pain point"
    },
    desiredOutcome: {
      label: "Desired Outcome",
      hint:
        "The result your audience wants. Example: more engagement, more leads, stronger content, better sales.",
      placeholder: "Desired outcome"
    },
    angle: {
      label: "Angle",
      hint:
        "The perspective or framing. Example: beginner-friendly, urgent, story-led, myth-busting, premium.",
      placeholder: "Angle"
    },
    callToAction: {
      label: "CTA",
      hint:
        "The action you want people to take. Example: comment, share, save, follow, click the link.",
      placeholder: "CTA"
    },
    keywords: {
      label: "Keywords",
      hint:
        "Important search or topic words to include naturally. Separate them with commas.",
      placeholder: "Keywords"
    },
    brandVoice: {
      label: "Brand Voice",
      hint:
        "How the caption should feel. Example: direct, witty, expert, playful, premium.",
      placeholder: "Brand voice"
    },
    avoidPhrases: {
      label: "Avoid Phrases",
      hint:
        "Repeated words, clichés, or phrases you do not want in the caption.",
      placeholder: "Avoid phrases"
    },
    extraContext: {
      label: "Extra Context",
      hint:
        "Add examples, product details, campaign notes, proof, or extra background to improve the output.",
      placeholder:
        "Extra context, campaign note, proof point, or creator angle"
    }
  },

  contextSectionTitle: "Caption Context",

  actions: {
    generating: "Generating...",
    generate: "Generate Captions",
    clearResults: "Clear Results",
    resetForm: "Reset Form",
    copied: "Copied",
    copy: "Copy"
  },

  resultsIntro:
    "These are ready-to-post caption options. Pick the one that best matches your platform, tone, and conversion goal.",

  labels: {
    score: "Score"
  },

  errors: {
    topicRequired: "Please enter a topic first.",
    generateFailed: "Failed to generate captions."
  },

  fallbackUntitledTopic: "Untitled Topic"
},


thumbnailTool: {
  hero: {
    badge: "CTR polish tool",
    title: "Thumbnail Text",
    description:
      "Use this tool to analyze current thumbnail text and generate stronger alternatives. Add topic and positioning context so Hookora can suggest tighter, more clickable thumbnail words.",
  },
  fields: {
    topic: {
      label: "Topic",
      hint:
        "Optional topic context for the thumbnail analyzer. This helps the suggestions feel more relevant to the content.",
      placeholder: "Optional topic for the thumbnail analysis",
    },
    thumbnailText: {
      label: "Thumbnail Text",
      hint:
        "The exact thumbnail words you want analyzed. Keep it short and punchy for best results.",
      placeholder: "Enter current thumbnail text",
    },
    audience: {
      label: "Audience",
      hint:
        "Who the thumbnail is trying to attract. Example: creators, students, founders, fitness lovers.",
      placeholder: "Audience",
    },
    angle: {
      label: "Angle",
      hint:
        "The positioning or emotional framing. Example: shocking, premium, direct, urgent, beginner-friendly.",
      placeholder: "Angle",
    },
    desiredOutcome: {
      label: "Desired Outcome",
      hint:
        "What result the thumbnail should help drive. Example: clicks, curiosity, watch time, conversions.",
      placeholder: "Desired outcome",
    },
    avoidPhrases: {
      label: "Avoid Phrases",
      hint:
        "Words or expressions you do not want the analyzer to repeat in its suggestions.",
      placeholder: "Avoid phrases",
    },
    extraContext: {
      label: "Extra Context",
      hint:
        "Add emotional target, positioning notes, competitor style, or extra context to improve the suggestions.",
      placeholder: "Extra context, emotional target, or positioning note",
    },
  },
  analysisContext: {
    title: "Analysis Context",
  },
  actions: {
    analyzing: "Analyzing...",
    analyze: "Analyze Thumbnail Text",
    clearResults: "Clear Results",
    resetForm: "Reset Form",
    copied: "Copied",
    copy: "Copy",
  },
  analysis: {
    title: "Current Text Analysis",
  },
  labels: {
    score: "Score",
    words: "Words",
    characters: "Characters",
    untitledTopic: "Untitled Topic",
  },
  suggestionsIntro:
    "These are suggested thumbnail text alternatives. Pick the line that feels clearest, punchiest, and most clickable for your audience.",
  errors: {
    enterTextOrTopic: "Enter thumbnail text or a topic first.",
    analyzeFailed: "Failed to analyze thumbnail text.",
  },
},




studioTools: {

  trendToContentTool: {
  badge: "Trend-to-content tool",
  title: "Trend To Content",
  description:
    "Turn trends, conversations, or market shifts into usable content directions, ideas, hooks, captions, and angles for your brand.",
  fields: {
    topic: {
      label: "Topic",
      placeholder: "Optional topic to guide the output",
      hint:
        "Add your main topic if you want the trend ideas to stay tied to a specific subject or niche.",
    },
    platform: {
      label: "Platform",
      hint:
        "Choose the platform where you want to turn this trend into content.",
    },
    outputType: {
      label: "Output Type",
      hint:
        "Select the kind of output you want Hookora to generate from the trend.",
    },
    goal: {
      label: "Goal",
      placeholder: "What should this content help you achieve?",
      hint:
        "Example: reach, engagement, authority, awareness, clicks, or conversions.",
    },
    audience: {
      label: "Audience",
      placeholder: "Who is this content for?",
      hint:
        "Describe the audience you want the generated content to speak to.",
    },
    trend: {
      label: "Trend",
      placeholder: "Describe the trend, pattern, conversation, or shift",
      hint:
        "Paste a trend, observation, viral pattern, market shift, or audience behavior you want to turn into content.",
    },
    brandContext: {
      label: "Brand Context",
      placeholder: "Add brand context, offer context, or positioning notes",
      hint:
        "Explain your brand angle, offer, positioning, or content style so the ideas feel more aligned.",
    },
  },
  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "General",
  },
  outputTypeOptions: {
    contentIdeas: "Content Ideas",
    hooks: "Hooks",
    captions: "Captions",
    scriptAngles: "Script Angles",
    carouselIdeas: "Carousel Ideas",
    videoConcepts: "Video Concepts",
  },
  actions: {
    generating: "Generating...",
    generate: "Generate",
    clearResults: "Clear Results",
    reset: "Reset",
    copied: "Copied",
    copy: "Copy",
  },
  tip:
    "Tip: the more specific the trend and brand context, the more usable and differentiated the output will be.",
  messages: {
    trendRequired: "Please enter a trend first.",
    generateFailed: "Failed to generate trend-based content.",
    copyFailed: "Failed to copy the result.",
  },
  results: {
    title: "Results",
    summary: "Summary",
    trendInsight: "Trend Insight",
    generatedIdeas: "Generated Ideas",
    hookOptions: "Hook Options",
    captionOptions: "Caption Options",
    angles: "Angles",
    recommendations: "Recommendations",
    rawResult: "Raw Result",
  },
  exportLabels: {
    summary: "Summary",
    trendInsight: "Trend Insight",
    generatedIdeas: "Generated Ideas",
    hookOptions: "Hook Options",
    captionOptions: "Caption Options",
    angles: "Angles",
    recommendations: "Recommendations",
  },
},

  hookScoreAnalyzerTool: {
  badge: "Hook score analyzer",
  title: "Hook Score Analyzer",
  description:
    "Analyze how strong your hook is before you publish. Get a score, breakdown, strengths, weak points, improvement suggestions, and better hook directions.",
  fields: {
    topic: {
      label: "Topic",
      placeholder: "e.g. fitness, business growth, skincare tips",
      hint: "Optional, but useful for more accurate analysis around your content angle."
    },
    platform: {
      label: "Platform",
      hint: "Platform context changes how hooks are judged for clarity, pacing, and attention."
    },
    goal: {
      label: "Goal",
      placeholder: "e.g. get more clicks, boost watch time, drive comments",
      hint: "What this hook is supposed to achieve."
    },
    targetAudience: {
      label: "Target Audience",
      placeholder: "e.g. beginners, founders, students, creators",
      hint: "Who the hook is meant to pull in."
    },
    hook: {
      label: "Hook",
      placeholder: "Paste the hook you want Hookora to analyze...",
      hint: "Paste the exact opening line or hook you want scored."
    }
  },
  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "General"
  },
  actions: {
    analyze: "Analyze Hook",
    analyzing: "Analyzing...",
    clearResults: "Clear Results",
    reset: "Reset",
    copy: "Copy",
    copied: "Copied"
  },
  messages: {
    hookRequired: "Please paste a hook to analyze.",
    analyzeFailed: "Something went wrong while analyzing this hook.",
    copyFailed: "Could not copy the analysis right now. Please try again."
  },
  tip:
    "Tip: include the actual hook you want to publish, not just the topic. The more exact the hook, the better the score feedback.",
  exportLabels: {
    overallScore: "Overall Score",
    summary: "Summary",
    breakdown: "Breakdown",
    strengths: "Strengths",
    weaknesses: "Weaknesses",
    suggestions: "Suggestions",
    improvedHooks: "Improved Hooks"
  },
  results: {
    title: "Hook Score Analysis",
    summary: "Summary",
    breakdown: "Score Breakdown",
    whatWorks: "What Works",
    weakPoints: "Weak Points",
    howToImprove: "How to Improve",
    improvedHooks: "Improved Hooks",
    rawResult: "Raw Result",
    scoreLabels: {
      strong: "Strong Hook",
      decent: "Decent Hook",
      weak: "Weak Hook"
    }
  },
  
},

  carouselGeneratorTool: {
  title: "Carousel Generator",
  description:
    "Build structured carousel concepts with a strong opening slide, clean progression, and a closing CTA.",

  fields: {
    topic: {
      label: "Topic",
      placeholder: "e.g. Why social media posts underperform",
      hint:
        "Use the main lesson, framework, or problem the carousel should cover."
    },
    platform: {
      label: "Platform",
      hint:
        "Choose the platform where the carousel will be used."
    },
    tone: {
      label: "Tone"
    },
    style: {
      label: "Style"
    },
    language: {
      label: "Language"
    },
    audience: {
      label: "Audience",
      placeholder: "e.g. Coaches and creators"
    },
    goal: {
      label: "Goal",
      placeholder: "e.g. More saves and shares"
    },
    offer: {
      label: "Offer",
      placeholder: "e.g. Free audit or guide"
    },
    painPoint: {
      label: "Pain Point",
      placeholder: "e.g. Weak hooks and low engagement"
    },
    desiredOutcome: {
      label: "Desired Outcome",
      placeholder: "e.g. Clearer messaging and stronger posts"
    },
    angle: {
      label: "Angle",
      placeholder: "e.g. Mistakes most people make"
    },
    callToAction: {
      label: "Call to Action",
      placeholder: "e.g. Save this and follow"
    },
    brandVoice: {
      label: "Brand Voice",
      placeholder: "e.g. Smart, clear, practical"
    },
    keywords: {
      label: "Keywords",
      placeholder: "e.g. content strategy, social media growth"
    },
    avoidPhrases: {
      label: "Avoid Phrases",
      placeholder: "e.g. game changer, secret hack"
    },
    extraContext: {
      label: "Extra Context",
      placeholder:
        "Optional campaign note, structure idea, slide direction, or audience context..."
    }
  },

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "General"
  },

  toneOptions: {
    educational: "Educational",
    bold: "Bold",
    conversational: "Conversational",
    professional: "Professional",
    persuasive: "Persuasive",
    warm: "Warm",
    confident: "Confident"
  },

  styleOptions: {
    educationalBreakdown: "Educational Breakdown",
    mistakeSeries: "Mistake Series",
    frameworkSeries: "Framework Series"
  },

  languageOptions: {
    english: "English",
    spanish: "Spanish",
    french: "French",
    portuguese: "Portuguese"
  },

  actions: {
    generating: "Generating...",
    generate: "Generate Carousel Slides",
    reset: "Reset",
    copied: "Copied!",
    copy: "Copy"
  },

  results: {
    title: "Carousel Concepts",
    description:
      "Each concept includes a hook slide, body flow, closing slide, and caption.",
    empty: "No carousel concepts yet. Generate a batch to see them here."
  },

  resultLabels: {
    carousel: "Carousel",
    hookSlide: "Hook Slide",
    slide: "Slide",
    closingSlide: "Closing Slide",
    caption: "Caption",
    score: "Score"
  },

  messages: {
    topicRequired: "Please enter a topic first.",
    generateFailed: "Failed to generate carousel slides.",
    copyFailed: "Failed to copy carousel."
  }
},

  adCopyTool: {
    title: "Ad Copy Generator",
    description:
      "Create ad-ready primary text, headlines, descriptions, and CTAs for your offer or campaign.",

    fields: {
      topic: {
        label: "Topic",
        placeholder: "e.g. Social media content tool for creators",
        help:
          "Use the product, offer, or campaign focus you want the ads built around."
      },
      platform: {
        label: "Platform",
        placeholder: "e.g. Facebook",
        help: "Pick the platform where the ad will run first."
      },
      tone: {
        label: "Tone"
      },
      language: {
        label: "Language"
      },
      audience: {
        label: "Audience",
        placeholder: "e.g. Small business owners"
      },
      goal: {
        label: "Goal",
        placeholder: "e.g. Drive signups"
      },
      offer: {
        label: "Offer",
        placeholder: "e.g. Free trial for new users"
      },
      painPoint: {
        label: "Pain Point",
        placeholder: "e.g. Weak copy and low engagement"
      },
      desiredOutcome: {
        label: "Desired Outcome",
        placeholder: "e.g. Faster content creation and better conversions"
      },
      angle: {
        label: "Angle",
        placeholder: "e.g. Better copy without wasting time"
      },
      callToAction: {
        label: "Call to Action",
        placeholder: "e.g. Start your free trial"
      },
      brandVoice: {
        label: "Brand Voice",
        placeholder: "e.g. Clear, sharp, modern"
      },
      keywords: {
        label: "Keywords",
        placeholder: "e.g. AI copywriting, social media ads"
      },
      avoidPhrases: {
        label: "Avoid Phrases",
        placeholder: "e.g. game changer, secret hack"
      },
      extraContext: {
        label: "Extra Context",
        placeholder:
          "Optional campaign note, pricing context, audience objections, landing page message, or extra direction..."
      }
    },

    toneOptions: {
      Persuasive: "Persuasive",
      Bold: "Bold",
      Professional: "Professional",
      Confident: "Confident",
      Warm: "Warm",
      Conversational: "Conversational",
      Direct: "Direct"
    },

    languageOptions: {
      english: "English",
      spanish: "Spanish",
      french: "French",
      portuguese: "Portuguese"
    },

    actions: {
      generate: "Generate Ad Copy",
      generating: "Generating...",
      reset: "Reset",
      copy: "Copy",
      copied: "Copied!"
    },

    results: {
      title: "Ad Variants",
      description:
        "Review each variant, copy the strongest one, and save the best ads to your library.",
      empty: "No ad copy yet. Generate a batch to see your ad variants here.",
      variantFallback: "Ad Variant",
      score: "Score"
    },

    output: {
      primaryText: "Primary Text",
      headline: "Headline",
      description: "Description",
      cta: "CTA"
    },

    messages: {
      topicRequired: "Please enter a topic first.",
      generateFailed: "Failed to generate ad copy.",
      copyFailed: "Failed to copy output."
    }
  },
  

  aiHumanizerTool: {
  hero: {
    badge: "Rewrite polish tool",
    title: "AI Humanizer",
    description:
      "Use this tool to turn stiff, robotic, or overly AI-sounding text into writing that feels more natural, believable, and human. Adjust tone, strength, and rewrite settings to get a version that feels right for the platform."
  },

  fields: {
    originalText: {
      label: "Original Text",
      hint:
        "Paste the AI-written or stiff copy you want to make sound more human, natural, and believable.",
      placeholder: "Paste the original text you want to humanize"
    },
    platform: {
      label: "Platform",
      hint:
        "Choose where the rewrite will be used. This affects rhythm, phrasing, and style."
    },
    tone: {
      label: "Tone",
      hint: "Choose the personality of the rewrite."
    },
    audience: {
      label: "Audience",
      hint:
        "Who the rewritten copy is for. This helps the output feel more relevant and human.",
      placeholder: "Audience"
    },
    humanizationStrength: {
      label: "Humanization Strength",
      hint:
        "Light keeps the structure close. Balanced improves flow. Strong rewrites more boldly while keeping the core message."
    },
    preserveOriginalMeaning: {
      label: "Preserve original meaning"
    },
    styleNotes: {
      label: "Style Notes",
      hint:
        "Add guidance like shorter sentences, more direct tone, less formal phrasing, more emotion, or fewer buzzwords.",
      placeholder: "Style notes or rewrite guidance"
    }
  },

  sections: {
    rewriteSettings: "Rewrite Settings"
  },

  platformOptions: {
    General: "General",
    Instagram: "Instagram",
    TikTok: "TikTok",
    X: "X",
    LinkedIn: "LinkedIn",
    YouTube: "YouTube",
    WhatsApp: "WhatsApp"
  },

  toneOptions: {
    Natural: "Natural",
    Conversational: "Conversational",
    Bold: "Bold",
    Professional: "Professional",
    Warm: "Warm",
    Confident: "Confident",
    Persuasive: "Persuasive",
    "Story-driven": "Story-driven"
  },

  strengthOptions: {
    Light: "Light",
    Balanced: "Balanced",
    Strong: "Strong"
  },

  actions: {
    humanizing: "Humanizing...",
    humanizeText: "Humanize Text",
    clearResults: "Clear Results",
    resetForm: "Reset Form",
    copied: "Copied",
    copy: "Copy"
  },

  labels: {
    alternative: "Alternative {{number}}",
    whyItFeelsMoreHuman: "Why it feels more human",
    score: "Score"
  },

  resultsIntro:
    "These are humanized rewrite options. Pick the one that feels most natural, most believable, and closest to your intended voice.",

  errors: {
    originalTextRequired: "Please enter the original text first.",
    generateFailed: "Failed to humanize text."
  }
},

brandVoiceTrainerTool: {
  badge: "Brand Voice Trainer",
  title: "Train a more consistent brand voice",
  description:
    "Turn your brand description, tone preferences, and writing sample into a usable voice profile with clearer messaging rules and stronger consistency.",

  fields: {
    topic: {
      label: "Topic",
      placeholder: "e.g. creator tools, skincare, business education",
      hint: "Shared topic helps keep your Studio workflow aligned across tools."
    },
    platform: {
      label: "Platform",
      hint: "Platform can influence the voice style and delivery recommendations."
    },
    outputType: {
      label: "Output Type",
      hint: "Choose whether you want a voice profile, rules, or example output."
    },
    targetAudience: {
      label: "Target Audience",
      placeholder: "e.g. founders, women 20-35, Nigerian creators",
      hint: "Audience context helps shape how the voice should sound."
    },
    preferredToneWords: {
      label: "Preferred Tone Words",
      placeholder: "e.g. bold, human, sharp, warm",
      hint: "Add words that describe how your brand should feel."
    },
    avoidWords: {
      label: "Avoid These Words / Styles",
      placeholder: "e.g. robotic, too formal, slang-heavy, pushy",
      hint: "Optional. Helps Hookora avoid the wrong tone."
    },
    goal: {
      label: "Goal",
      placeholder: "e.g. sound more premium, more human, more consistent",
      hint: "Optional. Gives the tool a clearer direction."
    },
    brandDescription: {
      label: "Brand Description",
      placeholder:
        "Describe what your brand does, what it stands for, and how it should be perceived...",
      hint: "This is the most important field. Be as clear as possible."
    },
    sampleWriting: {
      label: "Sample Writing",
      placeholder:
        "Paste an example of how your brand currently writes, if you have one...",
      hint:
        "Optional, but very useful when you want the output to reflect your existing style."
    }
  },

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    xTwitter: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "General"
  },

  outputTypeOptions: {
    voiceProfile: "Voice Profile",
    rewriteSample: "Rewrite Sample",
    brandGuidelines: "Brand Guidelines",
    dosAndDonts: "Do's and Don'ts"
  },

  actions: {
    generating: "Generating...",
    generate: "Train Brand Voice",
    clearResults: "Clear Results",
    reset: "Reset",
    copied: "Copied",
    copy: "Copy"
  },

  sections: {
    brandVoiceProfile: "Brand Voice Profile",
    summary: "Summary",
    voicePillars: "Voice Pillars",
    dos: "Do's",
    donts: "Don'ts",
    sampleRewrite: "Sample Rewrite",
    notes: "Notes",
    rawResult: "Raw Result"
  },

  resultTitle: "Brand Voice Result",

  tip:
    "Tip: brand voice gets better when your description includes audience, values, and how you want people to feel after reading your content.",

  messages: {
    copyFailed: "Could not copy the brand voice result right now. Please try again.",
    brandDescriptionRequired:
      "Please describe the brand before generating a voice profile.",
    generateFailed:
      "Something went wrong while generating the brand voice output."
  }
},



commentToContentTool: {
  badge: "Comment To Content",
  title: "Turn audience comments into useful content",
  description:
    "Transform a comment, question, or audience reaction into a reply, hook, caption, script angle, or fresh content idea you can actually use.",

  fields: {
    topic: {
      label: "Topic",
      placeholder: "e.g. hooks, fitness, business growth",
      hint:
        "Shared topic helps tie this result into the rest of your Studio workflow."
    },
    platform: {
      label: "Platform",
      hint:
        "Platform context helps Hookora shape the output properly."
    },
    outputType: {
      label: "Output Type",
      hint:
        "Choose whether you want a reply, content idea, caption, or script-style output."
    },
    tone: {
      label: "Tone",
      hint:
        "Tone shapes how the final output feels."
    },
    targetAudience: {
      label: "Target Audience",
      placeholder: "e.g. beginner creators, founders, beauty customers",
      hint:
        "Optional audience context helps improve relevance."
    },
    goal: {
      label: "Goal",
      placeholder: "e.g. boost engagement, answer clearly, turn into a post",
      hint:
        "Optional. Use this when you want the output aimed at a specific result."
    },
    comment: {
      label: "Comment",
      placeholder: "Paste the audience comment, DM, reply, or question here...",
      hint:
        "This can be a question, objection, compliment, criticism, or interesting reaction."
    },
    extraContext: {
      label: "Extra Context",
      placeholder:
        "Add any context about the original post, your brand, your product, or what you want the comment to become...",
      hint:
        "Optional, but useful when you want more tailored content."
    }
  },

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "General"
  },

  outputTypeOptions: {
    reply: "Reply",
    hook: "Hook",
    caption: "Caption",
    shortScript: "Short Script",
    contentIdea: "Content Idea",
    carouselAngle: "Carousel Angle"
  },

  toneOptions: {
    natural: "Natural",
    friendly: "Friendly",
    bold: "Bold",
    educational: "Educational",
    sales: "Sales",
    professional: "Professional"
  },

  actions: {
    generating: "Generating...",
    generate: "Generate From Comment",
    clearResults: "Clear Results",
    reset: "Reset",
    copy: "Copy",
    copied: "Copied"
  },

  results: {
    title: "Generated Result"
  },

  resultLabels: {
    output: "Output",
    angle: "Angle",
    summary: "Summary",
    ideas: "Ideas",
    notes: "Notes",
    rawResult: "Raw Result"
  },

  tip:
    "Tip: strong audience comments often reveal hidden content angles, objections, and topics worth turning into posts.",

  messages: {
    commentRequired: "Please paste the comment before generating content.",
    generateFailed: "Something went wrong while turning the comment into content.",
    copyFailed: "Could not copy the generated result right now. Please try again."
  }
},

contentDifferentiationTool: {
  badge: "Content Differentiation",
  title: "Find a sharper angle in crowded content spaces",
  description:
    "Analyze your niche, audience, and competitor style to uncover stronger positioning, clearer uniqueness, and content angles that stand out.",

  fields: {
    topic: {
      label: "Topic",
      placeholder: "e.g. personal finance, fitness, AI tools",
      hint:
        "Shared topic helps this tool stay aligned with the rest of your Studio workflow."
    },
    platform: {
      label: "Platform",
      hint:
        "Platform context helps Hookora tailor the differentiation ideas to the right format."
    },
    contentType: {
      label: "Content Type",
      hint:
        "Choose the type of content you want stronger positioning for."
    },
    targetAudience: {
      label: "Target Audience",
      placeholder: "e.g. beginner creators, startup founders, students",
      hint:
        "Audience context helps reveal better angles and content gaps."
    },
    goal: {
      label: "Goal",
      placeholder: "e.g. stand out, sound smarter, get more saves",
      hint:
        "Optional. Use this when you want the output focused on a clear result."
    },
    brandContext: {
      label: "Brand Context",
      placeholder:
        "Describe your brand, offer, perspective, strengths, and what makes you different...",
      hint:
        "This gives Hookora the context it needs to find stronger positioning."
    },
    competitorStyle: {
      label: "Competitor Style / Market Pattern",
      placeholder:
        "Describe how others in your niche usually talk, sell, or create content...",
      hint:
        "Optional, but very helpful when you want Hookora to find a clearer point of difference."
    },
    currentContent: {
      label: "Current Content or Messaging",
      placeholder:
        "Paste your current hook, caption, script, or messaging if you want Hookora to improve it...",
      hint:
        "Optional. Use this if you want more practical differentiation suggestions tied to existing content."
    }
  },

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "General"
  },

  contentTypeOptions: {
    hooks: "Hooks",
    captions: "Captions",
    scripts: "Scripts",
    carousels: "Carousels",
    ads: "Ads",
    generalContent: "General Content"
  },

  actions: {
    generating: "Generating...",
    generate: "Differentiate Content",
    clearResults: "Clear Results",
    reset: "Reset",
    copy: "Copy",
    copied: "Copied"
  },

  results: {
    title: "Differentiation Result"
  },

  resultLabels: {
    differentiationAngle: "Differentiation Angle",
    summary: "Summary",
    marketGaps: "Market Gaps",
    uniqueStrengths: "Unique Strengths",
    differentiationIdeas: "Differentiation Ideas",
    avoid: "Avoid",
    sampleOutput: "Sample Output",
    rawResult: "Raw Result"
  },

  tip:
    "Tip: the more clearly you explain your market and what others sound like, the better Hookora can help you stand apart.",

  messages: {
    topicOrContextRequired:
      "Please enter a topic or brand context before generating differentiation ideas.",
    generateFailed:
      "Something went wrong while generating the differentiation output.",
    copyFailed:
      "Could not copy the differentiation result right now. Please try again."
  }
},

contentRepurposerTool: {
  title: "Content Repurposer",
  description:
    "Turn one source piece into multiple platform-ready versions you can post, test, and save.",

  fields: {
    topic: {
      label: "Topic",
      placeholder: "e.g. Personal branding for creators",
      hint:
        "Shared across Studio tools so you can keep the same working idea."
    },
    platform: {
      label: "Platform",
      placeholder: "e.g. LinkedIn",
      hint:
        "Use the main destination platform for the repurposed batch."
    },
    sourceText: {
      label: "Source Text",
      placeholder:
        "Paste the original post, caption, thread, script, email, or raw draft here...",
      hint:
        "Paste the original piece you want to transform into different platform-ready versions."
    },
    audience: {
      label: "Audience",
      placeholder: "e.g. Small business owners"
    },
    goal: {
      label: "Goal",
      placeholder: "e.g. More engagement and saves"
    },
    tone: {
      label: "Tone"
    },
    language: {
      label: "Language"
    },
    brandVoice: {
      label: "Brand Voice",
      placeholder: "e.g. Clear, sharp, trustworthy"
    },
    callToAction: {
      label: "Call to Action",
      placeholder: "e.g. Comment or save this post"
    },
    keywords: {
      label: "Keywords",
      placeholder: "e.g. social media growth, content strategy"
    },
    avoidPhrases: {
      label: "Avoid Phrases",
      placeholder: "e.g. game changer, secret hack"
    },
    extraContext: {
      label: "Extra Context",
      placeholder:
        "Optional extra instruction, campaign context, offer details, audience pain points, or platform note..."
    }
  },

  toneOptions: {
    natural: "Natural",
    conversational: "Conversational",
    bold: "Bold",
    professional: "Professional",
    warm: "Warm",
    confident: "Confident",
    persuasive: "Persuasive"
  },

  languageOptions: {
    en: "English",
    es: "Spanish",
    fr: "French",
    pt: "Portuguese"
  },

  actions: {
    generating: "Repurposing...",
    generate: "Generate Repurposed Outputs",
    reset: "Reset",
    copy: "Copy",
    copied: "Copied!"
  },

  results: {
    title: "Repurposed Outputs",
    description:
      "Your generated variations appear here. Copy the strongest ones and save the best versions to your library.",
    empty:
      "No outputs yet. Generate a batch to see your repurposed content here.",
    outputFallback: "Output {{number}}",
    score: "Score: {{score}}",
    whyItWorks: "Why it works:"
  },

  messages: {
    sourceRequired: "Please paste or type your source text first.",
    generateFailed: "Failed to generate repurposed outputs.",
    copyFailed: "Failed to copy output."
  }
},

ctaTool: {
  hero: {
    badge: "Conversion writing tool",
    title: "CTA Generator",
    descriptionStart: "Use this tool to generate",
    descriptionStrong: "stronger call-to-actions",
    descriptionEnd:
      "that feel clearer, more persuasive, and more natural for your content, offer, and platform."
  },
  fields: {
    topic: {
      label: "Topic",
      hint:
        "The main subject of the content or offer. Be clear about what the CTA should connect to.",
      placeholder: "What is the CTA about?"
    },
    platform: {
      label: "Platform",
      hint:
        "Choose where the CTA will be used. Platform affects style, urgency, and phrasing."
    },
    audience: {
      label: "Audience",
      hint:
        "Who the CTA is for. Example: creators, founders, students, business owners.",
      placeholder: "Audience"
    },
    goal: {
      label: "Goal",
      hint:
        "What you want people to do or what result the CTA should support.",
      placeholder: "Goal"
    },
    offer: {
      label: "Offer",
      hint:
        "The product, service, value, lead magnet, or promise behind the CTA.",
      placeholder: "Offer or product"
    },
    painPoint: {
      label: "Pain Point",
      hint:
        "The frustration, struggle, or problem your audience is dealing with.",
      placeholder: "Pain point"
    },
    desiredOutcome: {
      label: "Desired Outcome",
      hint:
        "The result your audience wants. Example: more growth, more sales, less stress.",
      placeholder: "Desired outcome"
    },
    brandVoice: {
      label: "Brand Voice",
      hint:
        "How the CTA should sound. Example: bold, warm, direct, premium, playful.",
      placeholder: "Brand voice"
    },
    ctaDirection: {
      label: "CTA Direction",
      hint:
        "The kind of action you want the CTA to push. Example: subscribe, comment, buy, save, click.",
      placeholder: "CTA direction"
    },
    avoidPhrases: {
      label: "Avoid Phrases",
      hint:
        "Words or cliché phrases you do not want included in the CTA.",
      placeholder: "Avoid phrases"
    },
    extraContext: {
      label: "Extra Context",
      hint:
        "Add campaign notes, urgency, timing, audience behavior, or other helpful details.",
      placeholder: "Extra context"
    }
  },
  context: {
    title: "CTA Context"
  },
  actions: {
    generating: "Generating...",
    generate: "Generate CTAs",
    clearResults: "Clear Results",
    resetForm: "Reset Form",
    copied: "Copied",
    copy: "Copy"
  },
  resultsIntro: {
    start: "These are CTA options designed to feel more",
    strong: "actionable and convincing",
    end: "for your audience and platform."
  },
  output: {
    score: "Score",
    whyItWorks: "Why it works",
    untitledTopic: "Untitled Topic"
  },
  errors: {
    topicRequired: "Please enter a topic first.",
    generateFailed: "Failed to generate CTAs."
  }
},

descriptionTool: {
  hero: {
    badge: "Rewrite tool",
    title: "Descriptions",
    description:
      "Use this tool to generate stronger description rewrites for your content. You can paste an existing description to improve it, or generate fresh direction from your topic and context."
  },
  fields: {
    topic: {
      label: "Topic",
      hint:
        "The main subject of the description. Be clear about what the content or asset is about.",
      placeholder: "What is the description about?"
    },
    platform: {
      label: "Platform",
      hint:
        "Choose the platform where this description will be used."
    },
    originalDescription: {
      label: "Original Description",
      hint:
        "Paste the current description here if you want it improved instead of generating a fresh version from context alone.",
      placeholder: "Paste the current description if you want it rewritten."
    },
    audience: {
      label: "Audience",
      hint:
        "Who this description is intended for. Example: students, creators, brand owners, startup founders.",
      placeholder: "Audience"
    },
    goal: {
      label: "Goal",
      hint:
        "What the description should help with. Example: conversions, clarity, watch time, clicks, signups.",
      placeholder: "Goal"
    },
    offer: {
      label: "Offer",
      hint:
        "The product, freebie, service, resource, or promise behind the content.",
      placeholder: "Offer or product"
    },
    painPoint: {
      label: "Pain Point",
      hint:
        "The challenge or frustration your audience is facing.",
      placeholder: "Pain point"
    },
    desiredOutcome: {
      label: "Desired Outcome",
      hint:
        "The result your audience wants. Example: more growth, better content, faster results, less stress.",
      placeholder: "Desired outcome"
    },
    angle: {
      label: "Angle",
      hint:
        "The positioning or framing. Example: educational, premium, urgent, beginner-focused, direct-response.",
      placeholder: "Angle"
    },
    cta: {
      label: "CTA",
      hint:
        "The action the description should encourage. Example: subscribe, watch, comment, save, visit the link.",
      placeholder: "CTA"
    },
    keywords: {
      label: "Keywords",
      hint:
        "Important search or SEO words to naturally include. Separate them with commas.",
      placeholder: "Keywords"
    },
    brandVoice: {
      label: "Brand Voice",
      hint:
        "How the description should sound. Example: bold, expert, playful, premium, direct.",
      placeholder: "Brand voice"
    },
    avoidPhrases: {
      label: "Avoid Phrases",
      hint:
        "Words or patterns you want the generator to avoid repeating.",
      placeholder: "Avoid phrases"
    },
    extraContext: {
      label: "Extra Context",
      hint:
        "Add proof, supporting details, target emotion, campaign notes, or other extra context to improve the rewrite.",
      placeholder: "Extra context, unique proof, product notes, or supporting detail"
    }
  },
  contextTitle: "Rewrite Context",
  actions: {
    generating: "Generating...",
    generate: "Generate Rewrites",
    clearResults: "Clear Results",
    resetForm: "Reset Form",
    copied: "Copied",
    copy: "Copy"
  },
  messages: {
    topicRequired: "Please enter a topic first.",
    generateFailed: "Failed to rewrite description.",
    ready:
      "These are ready-to-use description rewrites. Pick the one that best matches your platform, clarity goal, and conversion direction."
  },
  result: {
    score: "Score"
  }
},

hashtagTool: {
  hero: {
    badge: "Discovery tool",
    title: "Hashtags",
    description:
      "Use this tool to build strategic hashtag sets for different content goals. Add platform, audience, content type, and extra context so Hookora can return better hashtag mixes for discovery, relevance, and engagement."
  },
  fields: {
    topic: {
      label: "Topic",
      hint:
        "The subject, niche, or campaign the hashtag sets should support.",
      placeholder: "What is the hashtag set for?"
    },
    platform: {
      label: "Platform",
      hint:
        "Choose the platform because hashtag behavior differs by platform."
    },
    contentType: {
      label: "Content Type",
      hint:
        "The type of post or content. Example: tutorial, product demo, educational post, personal story, carousel.",
      placeholder: "Content type"
    },
    audience: {
      label: "Audience",
      hint:
        "Who you want to attract with these hashtags. Example: beauty lovers, startup founders, student creators.",
      placeholder: "Audience"
    },
    goal: {
      label: "Goal",
      hint:
        "What you want the hashtag set to help with. Example: discovery, relevance, engagement, niche reach.",
      placeholder: "Goal"
    },
    keywords: {
      label: "Keywords",
      hint:
        "Important topic words to reflect in the hashtags. Separate them with commas.",
      placeholder: "Keywords"
    },
    brandVoice: {
      label: "Brand Voice",
      hint:
        "The vibe of the brand or content. Helps keep the hashtag sets aligned with the content style.",
      placeholder: "Brand voice"
    },
    extraContext: {
      label: "Extra Context",
      hint:
        "Add subtopic details, campaign context, audience specifics, or niche notes to improve the sets.",
      placeholder: "Extra context, niche, subtopic, or campaign notes"
    }
  },
  contextTitle: "Hashtag Context",
  errors: {
    enterTopic: "Please enter a topic first.",
    generateFailed: "Failed to generate hashtags."
  },
  actions: {
    generating: "Generating...",
    generate: "Generate Hashtag Sets",
    clearResults: "Clear Results",
    resetForm: "Reset Form",
    copied: "Copied",
    copy: "Copy"
  },
  resultsHelper:
    "These are ready-to-use hashtag sets. Pick the one that best matches your content goal and platform behavior.",
  scoreLabel: "Score",
  assetTitlePrefix: "Hashtag Set",
  untitledTopic: "Untitled Topic"
},

nigerianAudienceRewriteTool: {
  badge: "Localized rewrite tool",
  title: "Nigerian Audience Rewrite",
  subtitle:
    "Adapt existing content so it feels more natural, more relevant, and more culturally aligned for a Nigerian audience without losing clarity or intent.",

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "General"
  },

  toneOptions: {
    natural: "Natural",
    casual: "Casual",
    confident: "Confident",
    sales: "Sales",
    storytelling: "Storytelling",
    professional: "Professional"
  },

  formatOptions: {
    caption: "Caption",
    hook: "Hook",
    shortScript: "Short Script",
    adCopy: "Ad Copy",
    carouselCopy: "Carousel Copy",
    general: "General Rewrite"
  },

  fields: {
    topic: {
      label: "Topic",
      placeholder: "What is this content about?",
      hint: "The main topic or message behind the rewrite."
    },
    platform: {
      label: "Platform",
      hint: "Choose where the rewritten version will be used."
    },
    tone: {
      label: "Tone",
      hint: "Choose how the localized version should sound."
    },
    contentFormat: {
      label: "Content Format",
      hint: "Choose the type of content you are rewriting."
    },
    goal: {
      label: "Goal",
      placeholder: "What should this rewrite help achieve?",
      hint: "The outcome you want from the rewrite, such as clarity, conversion, engagement, or relatability."
    },
    brandContext: {
      label: "Brand Context",
      placeholder: "Add brand voice, positioning, audience cues, or important context",
      hint: "Optional context to make the rewrite feel more on-brand and audience-aware."
    },
    originalContent: {
      label: "Original Content",
      placeholder: "Paste the original content you want rewritten for a Nigerian audience",
      hint: "Paste the exact text you want Hookora to adapt."
    }
  },

  buttons: {
    rewrite: "Rewrite for Nigerian Audience",
    rewriting: "Rewriting...",
    clearResults: "Clear Results",
    reset: "Reset",
    copy: "Copy",
    copied: "Copied"
  },

  messages: {
    missingOriginalText: "Please enter the original content first.",
    copyError: "Failed to copy result.",
    genericError: "We couldn’t rewrite this content right now."
  },

  tip:
    "Use this tool when you want content to feel more familiar, more natural, and more locally relevant for Nigerian readers or viewers.",

  result: {
    title: "Rewrite Result",
    assetTitle: "Nigerian Audience Rewrite",
    sections: {
      rewrittenOutput: "Rewritten Output",
      summary: "Summary",
      adaptationNotes: "Adaptation Notes",
      alternativeVersions: "Alternative Versions",
      suggestions: "Suggestions",
      rawResult: "Raw Result"
    }
  },

  exportLabels: {
    rewrittenOutput: "Rewritten Output",
    summary: "Summary",
    adaptationNotes: "Adaptation Notes",
    alternativeVersions: "Alternative Versions",
    suggestions: "Suggestions"
  }
},

offerPositioningTool: {
  title: "Offer Positioning",
  description:
    "Use this tool to sharpen how your offer is framed, explained, and differentiated so it feels clearer, more relevant, and more compelling to the right audience.",

  fields: {
    topic: {
      label: "Topic",
      placeholder: "What is this positioning about?",
      hint: "The main topic, niche, or focus of the offer positioning."
    },
    platform: {
      label: "Platform",
      placeholder: "Where will this positioning be used?",
      hint: "Choose where this messaging will appear, such as website, sales page, email, or social content."
    },
    tone: {
      label: "Tone"
    },
    language: {
      label: "Language"
    },
    offer: {
      label: "Offer",
      placeholder: "Describe the offer, product, or service",
      hint: "Explain what you are selling, delivering, or helping people achieve."
    },
    audience: {
      label: "Audience",
      placeholder: "Who is this for?"
    },
    goal: {
      label: "Goal",
      placeholder: "What should this positioning achieve?"
    },
    painPoint: {
      label: "Pain Point",
      placeholder: "What problem does the audience face?"
    },
    desiredOutcome: {
      label: "Desired Outcome",
      placeholder: "What result does the audience want?"
    },
    angle: {
      label: "Angle",
      placeholder: "What angle should the positioning take?"
    },
    callToAction: {
      label: "Call to Action",
      placeholder: "What action should people take?"
    },
    brandVoice: {
      label: "Brand Voice",
      placeholder: "How should the brand sound?"
    },
    keywords: {
      label: "Keywords",
      placeholder: "Keywords to include"
    },
    avoidPhrases: {
      label: "Avoid Phrases",
      placeholder: "Words or phrases to avoid"
    },
    extraContext: {
      label: "Extra Context",
      placeholder: "Add extra context, proof, audience details, or positioning notes"
    }
  },

  toneOptions: {
    confident: "Confident",
    professional: "Professional",
    bold: "Bold",
    conversational: "Conversational",
    warm: "Warm",
    persuasive: "Persuasive",
    direct: "Direct"
  },

  languageOptions: {
    en: "English",
    es: "Spanish",
    fr: "French",
    pt: "Portuguese"
  },

  actions: {
    generate: "Generate Positioning",
    generating: "Generating...",
    reset: "Reset",
    copy: "Copy",
    copied: "Copied"
  },

  messages: {
    topicOrOfferRequired: "Please enter a topic or describe the offer first.",
    generateFailed: "Failed to generate offer positioning.",
    copyFailed: "Failed to copy positioning output."
  },

  results: {
    title: "Positioning Results",
    description:
      "Review different positioning directions, value propositions, audience framing, and messaging examples for your offer.",
    empty: "No positioning results yet. Generate offer positioning to see results here.",
    fallbackTitle: "Positioning Option {{number}}",
    score: "Score: {{score}}",
    positioningAngle: "Positioning Angle",
    valueProposition: "Value Proposition",
    whoItsFor: "Who It’s For",
    problemFrame: "Problem Frame",
    differentiator: "Differentiator",
    messageExample: "Message Example"
  }
},

retentionIntroTool: {
  hero: {
    badge: "Retention Intro",
    title: "Generate stronger opening seconds",
    description:
      "Create intros built to hold attention early with a strong hook, preview feel, deeper theme, and a clear reason to keep watching."
  },

  fields: {
    topic: {
      label: "Topic",
      placeholder: "e.g. why most creators stay stuck",
      hint: "This tool works best when the topic is sharp and specific."
    },
    platform: {
      label: "Platform",
      hint: "Platform context helps shape the pacing and structure of the intro."
    },
    contentType: {
      label: "Content Type",
      hint: "Choose the type of content you want the opening built for."
    },
    tone: {
      label: "Tone",
      hint: "Tone affects the emotional pull and voice of the intro."
    },
    targetAudience: {
      label: "Target Audience",
      placeholder: "e.g. beginner creators, founders, students",
      hint: "Optional audience context helps the intro feel more targeted."
    },
    goal: {
      label: "Goal",
      placeholder: "e.g. increase watch time, boost retention, drive curiosity",
      hint: "Optional. Useful when you want the intro optimized for a specific outcome."
    },
    extraContext: {
      label: "Extra Context",
      placeholder:
        "Describe the content idea, story angle, lesson, product, or emotional direction...",
      hint: "Add context when you want the opening to feel more tailored and accurate."
    }
  },

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "General"
  },

  contentTypeOptions: {
    short_video: "Short Video",
    long_video: "Long Video",
    ad: "Ad",
    ugc: "UGC",
    educational: "Educational",
    storytelling: "Storytelling"
  },

  toneOptions: {
    bold: "Bold",
    natural: "Natural",
    dramatic: "Dramatic",
    confident: "Confident",
    curious: "Curious",
    urgent: "Urgent"
  },

  actions: {
    generateIntro: "Generate Intro",
    generating: "Generating...",
    clearResults: "Clear Results",
    reset: "Reset",
    copy: "Copy",
    copied: "Copied"
  },

  messages: {
    topicRequired: "Please enter a topic before generating a retention intro.",
    generateFailed: "Something went wrong while generating the retention intro.",
    copyFailed: "Could not copy the intro right now. Please try again."
  },

  tip:
    "Tip: great retention intros usually create tension fast, preview value early, and make the viewer feel they should not scroll away.",

  output: {
    resultTitle: "Retention Intro Result",
    retentionIntro: "Retention Intro",
    previewOpeningVisual: "Preview / Opening Visual",
    valuePromise: "Value Promise",
    deeperTheme: "Deeper Theme",
    notes: "Notes",
    alternativeVersions: "Alternative Versions",
    rawResult: "Raw Result"
  }
},

scriptTool: {
  hero: {
    badge: "Script Builder",
    title: "Create short-form scripts faster",
    description: {
      before: "Build ",
      strong: "ready-to-record scripts",
      after:
        " with stronger hooks, clearer structure, and CTA direction tailored to your topic, audience, and platform."
    }
  },

  fields: {
    topic: {
      label: "Topic",
      hint: "What the script should be about. Be specific for stronger output.",
      placeholder: "What should the script talk about?"
    },
    platform: {
      label: "Platform",
      hint: "Choose where the script will be used so the pacing and structure fit the format."
    },
    style: {
      label: "Script Style",
      hint: "Choose the script format you want Hookora to generate."
    },
    audience: {
      label: "Audience",
      hint: "Who the script is for.",
      placeholder: "Audience"
    },
    goal: {
      label: "Goal",
      hint: "What the script should help achieve.",
      placeholder: "Goal"
    },
    offer: {
      label: "Offer",
      hint: "The product, service, value, or message behind the script.",
      placeholder: "Offer or product"
    },
    painPoint: {
      label: "Pain Point",
      hint: "The problem or frustration the audience is dealing with.",
      placeholder: "Pain point"
    },
    desiredOutcome: {
      label: "Desired Outcome",
      hint: "What result the audience wants.",
      placeholder: "Desired outcome"
    },
    angle: {
      label: "Angle",
      hint: "The perspective or framing you want the script to follow.",
      placeholder: "Angle"
    },
    callToAction: {
      label: "CTA",
      hint: "The action you want the viewer to take.",
      placeholder: "CTA"
    },
    keywords: {
      label: "Keywords",
      hint: "Important words or phrases to naturally work into the script.",
      placeholder: "Keywords"
    },
    brandVoice: {
      label: "Brand Voice",
      hint: "How the script should sound.",
      placeholder: "Brand voice"
    },
    extraContext: {
      label: "Extra Context",
      hint: "Add details, proof, product info, or creator notes to improve the script.",
      placeholder: "Extra context"
    }
  },

  styleOptions: {
    "direct-to-camera": "Direct to Camera",
    "story-led": "Story-Led",
    "problem-solution": "Problem-Solution",
    "myth-busting": "Myth-Busting"
  },

  context: {
    title: "Script Context"
  },

  actions: {
    generate: "Generate Scripts",
    generating: "Generating...",
    clear: "Clear Results",
    reset: "Reset Form",
    copy: "Copy",
    copied: "Copied"
  },

  errors: {
    topicRequired: "Please enter a topic first.",
    generateFailed: "Failed to generate scripts."
  },

  output: {
    helper: {
      before: "These are ",
      strong: "ready-to-use script options",
      after: ". Pick the one that best fits your content angle, platform, and conversion goal."
    },
    score: "Score",
    hookLabel: "Hook",
    bodyLabel: "Body",
    ctaLabel: "CTA",
    untitledTopic: "Untitled Topic"
  }
},


viralRewriterTool: {
  title: "Viral Rewriter",
  description:
    "Rewrite existing content into stronger, more viral-ready versions while keeping the core message intact.",
  fields: {
    topic: {
      label: "Topic",
      placeholder: "What is this rewrite about?",
      hint:
        "Optional. Add a topic if you want the rewrite to stay anchored to a specific subject.",
    },
    platform: {
      label: "Platform",
      placeholder: "Where will this be posted?",
    },
    tone: {
      label: "Tone",
    },
    language: {
      label: "Language",
    },
    sourceText: {
      label: "Source Text",
      placeholder: "Paste the content you want rewritten",
      hint:
        "Paste the original text, caption, script, or post you want Hookora to rewrite.",
    },
    audience: {
      label: "Audience",
      placeholder: "Who is this for?",
    },
    goal: {
      label: "Goal",
      placeholder: "What should this rewrite achieve?",
    },
    brandVoice: {
      label: "Brand Voice",
      placeholder: "How should it sound?",
    },
    callToAction: {
      label: "Call to Action",
      placeholder: "What action should it drive?",
    },
    keywords: {
      label: "Keywords",
      placeholder: "Important words or phrases to include",
    },
    avoidPhrases: {
      label: "Avoid Phrases",
      placeholder: "Words or phrases to avoid",
    },
    extraContext: {
      label: "Extra Context",
      placeholder: "Anything else Hookora should know?",
    },
  },
  toneOptions: {
    bold: "Bold",
    conversational: "Conversational",
    confident: "Confident",
    persuasive: "Persuasive",
    professional: "Professional",
    warm: "Warm",
    natural: "Natural",
  },
  languageOptions: {
    en: "English",
    es: "Spanish",
    fr: "French",
    pt: "Portuguese",
  },
  actions: {
    generating: "Generating...",
    generate: "Generate Rewrites",
    reset: "Reset",
    copied: "Copied",
    copy: "Copy",
  },
  messages: {
    sourceRequired: "Please paste content to rewrite first.",
    generateFailed: "Failed to generate viral rewrites.",
    copyFailed: "Could not copy the rewrite right now.",
  },
  results: {
    title: "Rewrite Results",
    description:
      "Choose the version that feels strongest, sharpest, and most likely to hold attention.",
    empty: "Your viral-ready rewrites will appear here.",
    rewriteFallback: "Rewrite {number}",
    score: "Score: {score}",
    whyItWorks: "Why it works:",
  },
},
},

  homeCta: {
    badge: "Start with clarity",
    title: "Give your content a stronger first impression",
    description:
      "Hookora helps you create with more confidence, more speed, and more consistency. Start free and build a cleaner workflow for hooks, captions, scripts, rewrites, retention, and stronger content packaging."
  },

  homeHero: {
    badge: "AI workflow platform for creators",
    title: "Create stronger content faster with Hookora’s 22-tool Studio",
    description:
      "Start free with core writing tools, then unlock premium workflow tools and advanced strategic tools as you grow. Hookora helps creators generate hooks, titles, captions, hashtags, descriptions, scripts, rewrites, retention intros, voice systems, and more from one cleaner workflow.",
    trustPoints: {
      tools: "22 Studio tools across one workflow",
      free: "Start free with core tools",
      upgrade: "Upgrade for premium and strategic tools"
    },
    ideaLabel: "Content idea",
    ideaText: "“Why most creators lose attention in the first 3 seconds”",
    metrics: {
      tools: "Tools",
      workflow: "Workflow"
    },
    footerScale: "Built to scale"
  },

  navbar: {
    hi: "Hi",
    publicToolPages: "Public tool pages",
    moreToolsInStudio: "More tools available inside Studio",
    loggedIn: "Logged in",
    openSettingsMenu: "Open settings menu",
    toggleNavigationMenu: "Toggle navigation menu",
    display: "Display"
  },

  

  plans: {
  free: "Free",
  creator: "Creator",
  pro: "Pro",
  freeBadge: "Free",
  creatorBadge: "✔ Creator",
  proBadge: "✔ Pro"
},

  pricing: {
  heading: "Plans built for creators",
  seoTitle: "Hookora Pricing | Free, Creator and Pro Plans",
  seoDescription:
    "Manage your Hookora subscription, billing status, billing interval, Reward Credits, Wallet Balance, and plan upgrades for Hookora’s growing AI content workflow tools.",
  softwareSchemaDescription:
    "Hookora is an AI workflow platform for creators with tools for hooks, titles, captions, hashtags, descriptions, scripts, thumbnail text analysis, rewrites, retention tools, and stronger content workflow support.",
  billing: "Billing",
  billingPageIntro:
    "Review your current plan, billing status, billing interval, Reward Credits, Wallet Balance, and subscription controls.",
  heroDescription:
    "Choose the Hookora plan that matches your workflow, usage level, and creator goals.",
  save17: "Save 17%",
  twoMonthsFree: "2 months free",
  inactive: "Inactive",
  monthly: "Monthly",
  yearly: "Yearly",
  periodMonth: "/month",
  periodYear: "/year",
  approx: "Approx.",
  billedMonthly: "Billed monthly",
  choose: "Choose",
  currentPlan: "Current plan",
  switchToFree: "Switch to Free",
  updating: "Updating...",
  redirecting: "Redirecting...",
  mostPopular: "Most popular",
  startFree: "Start Free",
  chooseCreator: "Choose Creator",
  choosePro: "Choose Pro",
  startWithCoreWorkflow: "Start with the core workflow",
  freeDescription:
    "Ideal for testing Hookora and getting familiar with the core workflow before unlocking more power.",
  creatorDescription:
    "Ideal for individual creators who want premium tools for packaging, rewrites, retention, and stronger content quality.",
  proDescription:
    "Ideal for serious creators and small teams looking for deeper strategy tools, more volume, and stronger creative control.",
  coreWritingTools: "Core writing tools",
  premiumWorkflowTools: "Premium workflow tools",
  advancedStrategicTools: "Advanced strategic tools",
  currentUsage: "Current usage",
  plan: "Plan",
  billingStatus: "Billing status",
  billingInterval: "Billing interval",
  monthlyGenerations: "Monthly generations",
  remainingThisMonth: "Remaining this month",
  savedHooks: "Saved hooks",
  unlimited: "Unlimited",
  resetIn: "Reset in",
  days: "days",
  billingPeriodEnd: "Billing period end",
  creatorRewards: "Creator Rewards",
  creatorRewardsDescription:
    "Share Hookora, earn reward credits, and keep your creator workflow growing.",
  rewardCreditsBalance: "Reward Credits Balance",
  shareYourReferral: "Share your referral",
  referralCode: "Referral code",
  referralLink: "Referral link",
  referralRewardNote:
    "Reward Credits are applied before Wallet Balance, and Wallet Balance is applied before card payment during checkout.",
  rewardsSummary: "Rewards summary",
  rewardCredits: "Reward Credits",
  walletBalance: "Wallet Balance",
  totalReferrals: "Total referrals",
  verifiedReferrals: "Verified referrals",
  paidReferrals: "Paid referrals",
  fundWalletBalance: "Fund Wallet Balance",
  fundWalletDescription:
    "Add money to your wallet so future billing can use your Wallet Balance before charging your card.",
  enterAmount: "Enter amount",
  enterWalletFundingAmount: "Enter wallet funding amount",
  fundWallet: "Fund Wallet",
  walletTransactionHistory: "Wallet transaction history",
  noWalletTransactions: "No wallet transactions yet.",
  walletFunding: "Wallet funding",
  appliedToBilling: "Applied to billing",
  transaction: "Transaction",
  checkoutOrderPrefix: "At checkout, Hookora applies",
  then: "then",
  cardPayment: "Card Payment",
  checkoutOrderSuffix: "to cover the remaining amount.",
  whatEachPlanUnlocks: "What each plan unlocks",
  planUnlockFreeText: "Core writing tools for everyday content creation.",
  planUnlockCreatorText:
    "Premium workflow tools for stronger packaging, rewrites, retention, and execution.",
  planUnlockProText:
    "Advanced strategic tools for deeper creator systems and differentiation.",
  proPlanAccess: "Pro plan access",
  proPlanAccessDescription:
    "You currently have access to Hookora’s advanced strategic tools.",
  creatorPlanAccess: "Creator plan access",
  creatorPlanAccessDescription:
    "You currently have access to Hookora’s premium workflow tools.",
  freePlanAccess: "Free plan access",
  freePlanAccessDescription:
    "You are currently using Hookora’s core free tools.",
  subscriptionStatus: "Subscription status",
  subscriptionStatusDescription:
    "You do not currently have an active recurring paid subscription to manage here.",
  cancelSubscription: "Cancel subscription",
  cancelSubscriptionDescription:
    "You can cancel your current paid subscription here. Your access remains active until the current billing period ends.",
  cancelling: "Cancelling...",
  cancellationFooter:
    "After cancellation, your plan will remain active until the end of the current billing period.",
  billingPreview: "Billing preview",
  planAmount: "Plan amount",
  billingCycle: "Billing cycle",
  rewardCreditsApplied: "Reward Credits applied",
  walletBalanceApplied: "Wallet Balance applied",
  cardDueNow: "Card due now",
  secureRecurringCheckoutPrefix: "Secure recurring checkout for",
  secureRecurringCheckoutSuffix: "billing.",
  billingAndSubscriptions: "Billing and subscriptions",
  billingAndSubscriptionsDescription:
    "Choose your plan, manage upgrades, and keep your billing details aligned with your Hookora usage.",
  bothPlans: "Both",
  emailLabel: "Email",
  emailPlaceholder: "you@example.com",
  interestedInLabel: "Interested in",
  notesLabel: "Notes",
  notesPlaceholder: "Tell us what you’d want most from paid plans...",
  joinBillingWaitlist: "Join Billing Waitlist",
  submitting: "Submitting...",
  waitlistTitle: "Get notified when billing goes live",
  waitlistDescription:
    "Join the waitlist for paid plans and early billing access. This helps us know which users want Creator or Pro first.",
  waitlistSuccess: "Added to the billing waitlist successfully.",
  waitlistError: "Failed to join the billing waitlist.",
  creatorRewardsBalance: "Creator Rewards Balance",

  features: {
    free: {
      generations: "Monthly generations included",
      savedHooks: "Saved hooks included",
      coreToolsOnly: "Core tools only",
      coreToolsList: "Hooks, titles, captions, hashtags, descriptions, and scripts",
      simpleWorkflow: "Simple workflow for everyday creation",
      oneUser: "Single-user access"
    },
    creator: {
      generations: "More monthly generations",
      savedHooks: "More saved hooks",
      everythingInFree: "Everything in Free",
      premiumTools: "Premium workflow tools",
      workflowSupport: "Better workflow support",
      analytics: "Richer creator workflow support",
      fullCreatorWorkflowSupport:
        "Stronger support for packaging, rewrites, retention, and execution"
    },
    pro: {
      generations: "Highest monthly generations",
      everythingInCreator: "Everything in Creator",
      advancedTools: "Advanced strategic tools",
      strategySupport: "Deeper strategy support",
      futurePresets: "Priority access to future workflow expansion",
      prioritySupport: "Priority support"
    }
  },

  messages: {
    couldNotLoadBillingDetails: "Could not load billing details.",
    confirmCancelSubscription:
      "Are you sure you want to cancel your subscription?",
    subscriptionCancelledSuccessfully:
      "Subscription cancelled successfully.",
    couldNotCancelSubscription:
      "Could not cancel subscription.",
    invalidWalletFundingAmount: "Enter a valid wallet funding amount.",
    walletFundingUrlNotReturned: "Wallet funding URL was not returned.",
    couldNotStartWalletFunding:
      "Could not start wallet funding.",
    paymentSubmittedPaystack:
      "Your payment was submitted successfully. Your plan should update shortly.",
    paymentReceived:
      "Payment received. Your plan should update shortly.",
    walletSubmittedPaystack:
      "Your wallet funding payment was submitted successfully.",
    walletReceived:
      "Wallet funding received successfully.",
    checkoutCancelled: "Checkout was cancelled.",
    loginToManageBilling:
      "Log in to manage your billing and rewards.",
    failedToLoadBilling:
      "Failed to load billing details.",
    failedToCopy: "Failed to copy.",
    alreadyOnFree: "You are already on the Free plan.",
    changedToFree: "Plan changed to Free successfully.",
    failedToChangePlan: "Failed to change plan.",
    alreadyOnPlan: "You are already on {{plan}} {{cycle}}.",
    checkoutUrlMissing: "Checkout URL is missing.",
    failedToStartCheckout: "Failed to start checkout.",
    invalidWalletAmount: "Enter a valid wallet funding amount.",
    walletUrlMissing: "Wallet funding URL is missing.",
    failedToStartWalletFunding:
      "Failed to start wallet funding.",
    failedToLoadBillingDetails:
      "Failed to load billing details. Please refresh and try again."
  }
},

pricingFaq: {
  title: "Pricing FAQ",
  subtitle: "A few quick answers to common questions about Hookora pricing.",
  items: {
    freePlan: {
      question: "Can I start with the Free plan?",
      answer:
        "Yes. The Free plan is designed to help you try Hookora, explore the workflow, and see how the tools fit your content process before upgrading."
    },
    moveToCreator: {
      question: "When should I move to Creator?",
      answer:
        "Creator is best when you are publishing more consistently and want more generations, unlimited saved hooks, and stronger workflow support."
    },
    whoIsProFor: {
      question: "Who is Pro for?",
      answer:
        "Pro is best for serious creators and power users who want higher limits, more flexibility, and deeper workflow value as Hookora continues to grow."
    },
    upgradeLater: {
      question: "Can I upgrade later?",
      answer:
        "Yes. You can start free and upgrade when your content workflow needs more room."
    },
    moreThanOneTool: {
      question: "Does Hookora include more than one tool?",
      answer:
        "Yes. Hookora includes hooks, title optimization, captions, hashtags, description rewriting, short scripts, thumbnail text analysis, and more."
    },
    beginnerCreators: {
      question: "Is Hookora built for beginner creators too?",
      answer:
        "Yes. Hookora is designed to feel useful whether you are just starting or already creating content regularly."
    }
  }
},

pricingCta: {
  badge: "Start free with Hookora",
  title: "Choose a Hookora plan and build your content workflow with more clarity",
  description:
    "Start with the Free plan, upgrade when you need more creative room, and use Hookora to make content creation feel faster, sharper, and more consistent.",
  startFree: "Create Free Account",
  exploreFeatures: "Explore Features"
},

  tools: {
  hooks: "Hooks",
  hookAngles: "Hook Angles",
  titles: "Titles",
  captions: "Captions",
  hashtags: "Hashtags",
  descriptions: "Descriptions",
  scripts: "Scripts",
  ctas: "CTAs",
  thumbnailAnalyzer: "Thumbnail Analyzer",
  aiHumanizer: "AI Humanizer",
  repurposer: "Repurposer",
  adCopy: "Ad Copy",
  carousels: "Carousels",
  viralRewrite: "Viral Rewrite",
  offerPositioning: "Offer Positioning",
  hookScore: "Hook Score",
  trendConverter: "Trend Converter",
  nigerianRewrite: "Nigerian Rewrite",
  retentionIntro: "Retention Intro",
  brandVoiceTrainer: "Brand Voice Trainer",
  contentDifferentiation: "Content Differentiation",
  commentIdeas: "Comment Ideas",
  viralHookGenerator: "Viral Hook Generator",
  youtubeTitleOptimizer: "YouTube Title Optimizer",
  captionGenerator: "Caption Generator",
  hashtagGenerator: "Hashtag Generator",
  descriptionRewriter: "Description Rewriter",
  shortScriptGenerator: "Short Script Generator",
  thumbnailTextAnalyzer: "Thumbnail Text Analyzer",
  hookAngleGenerator: "Hook Angle Generator",
  ctaGenerator: "CTA Generator",
  contentRepurposer: "Content Repurposer",
  adCopyGenerator: "Ad Copy Generator",
  carouselGenerator: "Carousel Generator",
  viralRewriter: "Viral Rewriter",
  trendToContent: "Trend to Content",
  nigerianAudienceRewrite: "Nigerian Audience Rewrite",
  retentionIntroGenerator: "Retention Intro Generator",
  commentToContent: "Comment to Content"
},

  home: {
    seoTitle: "Hookora | AI Content Workflow Platform for Creators",
    seoDescription:
      "Hookora helps creators generate hooks, titles, captions, scripts, rewrites, retention ideas, and stronger content assets from one polished workflow.",
    featuredGuidesBadge: "Featured Hookora Guides",
    learnSectionTitle: "Learn how Hookora helps creators move faster",
    learnSectionDescription:
      "Explore quick guides about what Hookora is, who it helps, and how it makes hook writing, content ideation, rewrites, packaging, and creator workflow feel clearer and faster.",
    guides: {
      whatIsHookora: {
        title: "What Is Hookora?",
        description:
          "Learn what Hookora is, how it helps creators move from idea to stronger content faster, and why it now goes beyond basic hook generation."
      },
      nigerianCreators: {
        title: "Hookora for Nigerian Creators",
        description:
          "See how Hookora helps Nigerian creators build stronger hooks, titles, captions, scripts, localized rewrites, and better content packaging."
      },
      writeHooksFaster: {
        title: "How Hookora Helps Creators Write Hooks Faster",
        description:
          "Explore how Hookora helps creators write hooks faster, find sharper angles, improve retention, and turn more ideas into publish-ready content."
      }
    },
    promo: {
      badge: "Creator Workflow Spotlight",
      closeLabel: "Close Hookora promo",
      title: "Create stronger content faster with Hookora",
      subtitle:
        "Hookora helps creators generate hooks, titles, captions, scripts, rewrites, retention ideas, and stronger content assets from one polished workflow built to improve both speed and output quality.",
      features: {
        writeFasterTitle: "Write faster",
        writeFasterText:
          "Move from rough idea to stronger copy without staring at a blank page.",
        packageBetterTitle: "Package better",
        packageBetterText:
          "Improve hooks, titles, rewrites, retention, and overall content positioning.",
        upgradeSmarterTitle: "Upgrade smarter",
        upgradeSmarterText:
          "Unlock deeper Studio tools designed for serious creators and growth."
      },
      offerTitle: "Yearly plans now hit harder",
      offerTextPrefix: "Save",
      offerTextMiddle: "on yearly Creator and Pro plans and get the feel of",
      offerTextSuffix:
        "while unlocking Hookora’s full premium workflow.",
      metrics: {
        studioTools: "Studio Tools",
        cleanerWorkflow: "Cleaner Workflow",
        yearlySavings: "Yearly Savings"
      },
      seeYearlyPlans: "See Yearly Plans",
      subtleText:
        "Explore Hookora first, then upgrade when you want the full Studio power."
    }
  },

  homeFeatures: {
    title: "Why creators use Hookora",
    subtitle:
      "A cleaner workflow for hooks, titles, captions, scripts, and stronger content packaging.",
    cards: {
      createFaster: {
        title: "Create faster",
        text:
          "Move from rough idea to usable content faster without getting stuck on wording."
      },
      keepMessageSharp: {
        title: "Keep your message sharp",
        text:
          "Generate clearer hooks, titles, captions, and scripts that feel more intentional."
      },
      upgradeOnlyWhenNeeded: {
        title: "Upgrade only when needed",
        text:
          "Start with Free, then unlock deeper Creator and Pro tools as your workflow grows."
      }
    }
  },

  studioToolsBadge: "Studio tools overview",
  workflowTitle: "One workflow, multiple content tools",
  workflowDescription:
    "Hookora connects your writing workflow so you can move from hooks to stronger titles, captions, scripts, rewrites, and more without switching between messy tools.",
  toolCards: {
    free: "Free tools for hooks, titles, captions, hashtags, descriptions, and scripts.",
    creatorOne: "Creator tools for hook angles, CTAs, and stronger content packaging.",
    creatorTwo: "Retention support, rewrites, and thumbnail text improvement.",
    creatorThree: "Repurposing, ad copy, carousel writing, and offer positioning.",
    creatorFour: "Hook score analysis, Nigerian rewrites, and comment-driven content ideas.",
    proOne: "Pro tools for trend conversion and deeper creative strategy.",
    proTwo: "Brand voice systems to make content feel more consistent.",
    proThree: "Content differentiation tools to help creators stand out more clearly."
  },

  featuresHero: {
    badge: "Plan-based Studio access",
    title: "22 Studio tools built for modern creator workflow",
    subtitle:
      "Hookora now gives creators a clearer upgrade path with core writing tools on Free, premium workflow tools on Creator, and advanced strategic tools on Pro.",
    startFree: "Start Free",
    viewPricing: "View Pricing",
    cards: {
      free: {
        label: "FREE",
        text: "Hooks, Titles, Captions, Hashtags, Descriptions, and Scripts."
      },
      creator: {
        label: "CREATOR",
        text:
          "Premium workflow tools for packaging, rewrites, retention, CTAs, carousels, repurposing, and more."
      },
      pro: {
        label: "PRO",
        text:
          "Advanced strategic tools for trend conversion, brand voice training, and content differentiation."
      },
      oneStudio: {
        label: "One Studio",
        text:
          "Create across hooks, titles, captions, scripts, rewrites, and advanced workflow tools in one place."
      }
    },
    footerStats: {
      tools: "22 Studio tools",
      tieredAccess: "Tiered access",
      cleanerWorkflow: "Cleaner workflow"
    }
  },
    featuresGrid: {
    title: "What Hookora helps you create",
    subtitle:
      "A full creator workflow built across free, premium, and advanced strategic tools.",
    items: {
      workflow22: {
        title: "22-tool workflow",
        text:
          "Move across hooks, titles, captions, descriptions, scripts, rewrites, and strategic creator tools from one connected workspace."
      },
      freeCoreWriting: {
        title: "Free core writing tools",
        text:
          "Start with essential tools for hooks, titles, captions, hashtags, descriptions, and scripts."
      },
      creatorPremium: {
        title: "Creator premium workflow",
        text:
          "Unlock stronger packaging, rewrites, repurposing, CTA support, thumbnail support, offer positioning, and retention help."
      },
      proStrategic: {
        title: "Pro strategic tools",
        text:
          "Use advanced tools for turning trends into content, training brand voice, and differentiating your content."
      },
      hooksAngles: {
        title: "Hooks and hook angles",
        text:
          "Generate stronger openings and explore multiple directions from one topic."
      },
      titlesCaptions: {
        title: "Titles and captions",
        text:
          "Write clearer titles and stronger captions that better support content packaging."
      },
      descriptionsScripts: {
        title: "Descriptions and scripts",
        text:
          "Rewrite descriptions and generate short scripts with more structure and flow."
      },
      thumbnailHooksScore: {
        title: "Thumbnail text and hook score",
        text:
          "Improve first impression and analyze how strong your hooks are before publishing."
      }
    }
  },

  features: {
    seoTitle:
      "Hookora Features | 22 AI Tools for Hooks, Titles, Captions, Scripts and More",
    seoDescription:
      "Explore Hookora features for creators, including 22 Studio tools across hooks, hook angles, titles, captions, hashtags, descriptions, scripts, CTAs, thumbnail text, rewrites, retention tools, localization, trend conversion, brand voice, and more.",
    softwareSchemaDescription:
      "Hookora is an AI workflow platform for creators with 22 Studio tools for hooks, hook angles, titles, captions, hashtags, descriptions, scripts, CTAs, thumbnail text, rewrites, localization, retention intros, trend conversion, brand voice training, differentiation, and more.",
    planBasedStudioAccess: "Plan-based Studio access",
    planSectionTitle: "22 Studio tools across Free, Creator, and Pro",
    planSectionDescription:
      "Hookora is now structured to give users a clear upgrade path: core tools on Free, premium workflow tools on Creator, and advanced strategic tools on Pro.",
    guidesSectionTitle: "Learn more about how Hookora works",
    guidesSectionDescription:
      "Explore quick guides about what Hookora is, who it helps, and how it improves hook writing, content ideation, rewrites, packaging, and creator workflow.",
    guides: {
      whatIsHookora: {
        title: "What Is Hookora?",
        description:
          "Learn what Hookora is, how it works, and why it helps creators move from idea to stronger content faster across a full 22-tool Studio workflow."
      },
      nigerianCreators: {
        title: "Hookora for Nigerian Creators",
        description:
          "See how Hookora helps Nigerian creators build stronger hooks, titles, captions, scripts, localized rewrites, and better content packaging."
      },
      writeHooksFaster: {
        title: "How Hookora Helps Creators Write Hooks Faster",
        description:
          "Explore how Hookora helps creators write hooks faster with clearer angles, stronger first lines, better retention support, and more flexible creator workflow tools."
      }
    },
    planGroups: {
      free: {
        title: "Free plan",
        subtitle: "Start with the core writing workflow",
        description:
          "Free users can access Hookora’s essential Studio tools for everyday content writing and idea shaping."
      },
      creator: {
        title: "Creator plan",
        subtitle: "Unlock premium workflow tools",
        description:
          "Creator unlocks the premium middle layer of Hookora for packaging, rewrites, retention, repurposing, and stronger content execution."
      },
      pro: {
        title: "Pro plan",
        subtitle: "Unlock advanced strategic tools",
        description:
          "Pro is built for deeper strategic creator work, helping users turn stronger thinking into stronger content systems."
      }
    }
  },

  publicPricing: {
    title: "Choose the Hookora plan that fits your workflow",
    subtitle:
      "Start with Free, then move to Creator or Pro when you need more output, more premium tools, and stronger workflow support."
  },

  about: {
    seoTitle: "About Hookora | AI Workflow Platform for Creators",
    seoDescription:
      "Learn what Hookora does, why it exists, and how it helps creators build stronger hooks, titles, captions, scripts, and better content workflow.",
    heroBadge: "About Hookora",
    heroTitle: "Hookora helps creators move from idea to better content faster",
    heroDescription:
      "Hookora is an AI workflow platform built for creators who want a cleaner, faster, and more confident way to generate hooks, titles, captions, hashtags, descriptions, scripts, and thumbnail text.",
    createFreeAccount: "Create Free Account",
    exploreFeatures: "Explore Features",
    storyBadge: "Why Hookora exists",
    storyTitle: "Content creation should feel clearer, not heavier",
    storyParagraph1:
      "A lot of creators know what they want to say, but still get slowed down by the wording. The hook feels weak. The title does not land. The caption feels flat. The script takes too long. That friction adds up and makes publishing harder than it should be.",
    storyParagraph2:
      "Hookora was built to reduce that friction. Instead of forcing creators to jump between scattered tools, random notes, and messy workflows, Hookora brings the process into one cleaner system that helps ideas turn into stronger outputs faster.",
    storyParagraph3:
      "Whether someone is creating from Lagos, London, or Los Angeles, the problem is often the same: content takes too much mental energy to package well. Hookora helps creators feel more supported, more consistent, and more in control of their workflow.",
    whyTitle: "What makes Hookora useful",
    whySubtitle:
      "Hookora is not meant to make creators feel replaced. It is meant to make creators feel sharper, faster, and more supported.",
    ctaBadge: "Start with Hookora",
    ctaTitle: "Build your content workflow with more clarity and less friction",
    ctaDescription:
      "If you want a cleaner way to generate hooks, titles, captions, scripts, and more, Hookora gives you a practical place to start.",
    viewPricing: "View Pricing",
    cards: {
      builtForCreators: "Built for creators who want less friction",
      keepsOutputMoving: "Designed to keep content output moving",
      oneWorkflow:
        "Made to connect hooks, titles, captions, and scripts in one workflow",
      globalCreators: "Useful for creators in Nigeria and beyond",
      connectedWorkflow: {
        title: "One connected workflow",
        text:
          "Hooks, titles, captions, hashtags, scripts, descriptions, and thumbnail text work better when they are created in a connected system."
      },
      momentum: {
        title: "Faster creative momentum",
        text:
          "Hookora helps creators spend less time stuck on wording and more time actually moving toward publish-ready content."
      },
      packaging: {
        title: "Cleaner content packaging",
        text:
          "Strong content is not just about ideas. It is also about how those ideas are presented, framed, and made easier to click or watch."
      },
      growingCreators: {
        title: "Useful for growing creators",
        text:
          "Hookora is designed to be helpful whether you are just starting out or already creating consistently and need a smoother workflow."
      }
    }
  },

  blog: {
    seoTitle: "Hookora Blog | Creator Guides, Workflow Tips and Tool Pages",
    seoDescription:
      "Explore Hookora blog posts and public creator guides about hooks, titles, captions, scripts, content tools, and smoother creator workflow.",
    title: "Hookora Blog",
    subtitle:
      "Creator guides, workflow ideas, and public tool pages that explain how Hookora helps with stronger content creation.",
    heroBadge: "Hookora Blog",
    heroTitle: "Creator guides, workflow tips, and discovery content",
    heroDescription:
      "Explore practical content guides built to help creators write better hooks, captions, titles, and packaging with a smoother workflow.",
    startFree: "Start Free",
    exploreFeatures: "Explore Features",
    readGuide: "Read Guide",
    relatedReading: "Related reading",

    posts: {
      whatIsHookora: {
        badge: "Hookora",
        title: "What Is Hookora?",
        description:
          "Learn what Hookora is, how it works, and why Hookora helps creators generate hooks, titles, captions, hashtags, scripts, descriptions, and thumbnail text faster."
      },
      hookoraForNigerianCreators: {
        badge: "Hookora",
        title: "Hookora for Nigerian Creators",
        description:
          "Learn how Hookora helps Nigerian creators write stronger hooks, titles, captions, scripts, and content packaging faster with a smoother workflow."
      },
      writeHooksFaster: {
        badge: "Hookora",
        title: "How Hookora Helps Creators Write Hooks Faster",
        description:
          "Learn how Hookora helps creators write hooks faster with clearer angles, stronger first lines, and a smoother content workflow."
      },
      aiToolsForNigerianCreators: {
        badge: "Creator Workflow",
        title: "AI Tools for Nigerian Creators",
        description:
          "Discover AI tools for Nigerian creators who want faster content workflow, stronger hooks, better captions, sharper titles, and more consistent output."
      },
      bestHookGenerator: {
        badge: "Hooks",
        title: "Best Hook Generator for Nigerian Creators",
        description:
          "See how Nigerian creators can use Hookora to create stronger hooks, opening lines, and first impressions for videos and social content."
      },
      contentToolsNigeria: {
        badge: "Content Tools",
        title: "Content Tools for Creators in Nigeria",
        description:
          "Explore content tools for creators in Nigeria who want a clearer workflow for hooks, captions, titles, scripts, and content packaging."
      },
      bestCaptionGenerator: {
        badge: "Captions",
        title: "Best Caption Generator for Nigerian Creators",
        description:
          "Learn how Hookora helps Nigerian creators write clearer and faster captions for Instagram, TikTok, Shorts, and broader creator workflow."
      },
      betterHooksNigeria: {
        badge: "Hook Writing",
        title: "How Nigerian Creators Can Write Better Hooks",
        description:
          "Learn practical ways Nigerian creators can write better hooks for Reels, Shorts, TikTok, and social content with stronger first lines."
      }
    },

    articles: {
      whatIsHookora: {
        seoTitle: "What Is Hookora? | Hookora Blog",
        seoDescription:
          "Learn what Hookora is, how it works, and why Hookora helps creators generate hooks, titles, captions, hashtags, scripts, descriptions, and thumbnail text faster.",
        heroBadge: "Hookora Blog",
        title: "What is Hookora?",
        intro:
          "Hookora is an AI workflow platform for creators that helps turn rough ideas into stronger hooks, titles, captions, hashtags, descriptions, scripts, and thumbnail text from one cleaner content system.",
        sections: {
          whatHookoraHelpsDo: "What Hookora helps creators do",
          whyHookoraExists: "Why Hookora exists",
          whoHookoraIsFor: "Who Hookora is for",
          whatMakesHookoraDifferent: "What makes Hookora different",
          simpleTerms: "In simple terms"
        },
        paragraphs: {
          p1:
            "Hookora is built for creators who know that content quality is not only about having good ideas. It is also about packaging those ideas well. A strong hook, a clearer title, a better caption, and a sharper script can change how content feels before anyone even watches or reads the full thing.",
          p2:
            "Hookora helps creators move faster when the wording feels slow. Instead of getting stuck at the start of the process, creators can generate better options for the parts of content that usually cause friction. That includes opening hooks, video titles, social captions, hashtag sets, rewritten descriptions, short-form scripts, and thumbnail text direction.",
          p3:
            "The goal is not to replace creativity. The goal is to support it. Hookora is designed to help creators feel more confident when they are shaping an idea into something publishable.",
          p4:
            "A lot of creators lose time in the same places. The idea is there, but the hook feels weak. The title does not feel clickable enough. The caption feels flat. The script takes too long to structure. These small delays create a lot of creative friction over time.",
          p5:
            "Hookora exists to reduce that friction. Instead of forcing creators to use scattered tools and disconnected workflows, Hookora brings the content process into one place that feels clearer and easier to use.",
          p6:
            "Hookora is for creators, personal brands, marketers, and social media users who want stronger content packaging without wasting too much time on wording. It is useful for creators making YouTube videos, Shorts, Instagram posts, Reels, TikToks, and other forms of social content.",
          p7:
            "It is also useful for growing creators in places like Nigeria, where more people are building online brands, audience-driven businesses, and creative income streams and need tools that help them move faster.",
          p8:
            "Hookora is a content workflow platform that helps creators go from idea to stronger output faster. If someone wants better hooks, titles, captions, scripts, and packaging without making the process feel heavier, Hookora is built for that job."
        },
        bullets: [
          "It connects multiple content tasks inside one workflow instead of treating each task like a separate random tool.",
          "It helps creators build momentum when they feel stuck on wording.",
          "It focuses on practical creator output, not just generic AI text generation.",
          "It is designed to make content creation feel smoother, clearer, and more repeatable."
        ],
        related: {
          one: "Hookora for Nigerian Creators",
          two: "How Hookora Helps Creators Write Hooks Faster"
        },
        ctaBadge: "Start free with Hookora",
        ctaTitle: "Try Hookora and build your content faster with a smoother workflow",
        ctaDescription:
          "If you want a cleaner way to create hooks, titles, captions, scripts, and more, Hookora gives you a practical place to start.",
        ctaPrimary: "Create Free Account",
        ctaSecondary: "Explore Features"
      },

      hookoraForNigerianCreators: {
        seoTitle: "Hookora for Nigerian Creators | Hookora Blog",
        seoDescription:
          "Learn how Hookora helps Nigerian creators write stronger hooks, titles, captions, scripts, and content packaging faster with a smoother workflow.",
        heroBadge: "Hookora Blog",
        title: "Hookora for Nigerian creators",
        intro:
          "Hookora helps Nigerian creators turn rough content ideas into stronger hooks, titles, captions, scripts, hashtags, descriptions, and thumbnail text without making the workflow feel heavier.",
        sections: {
          whyItMatters: "Why Hookora matters for Nigerian creators",
          whatItHelpsWith: "What Hookora helps with",
          cleanerWorkflow: "A cleaner workflow for creators who post often",
          notOneType: "Hookora is not just for one type of creator",
          simpleTerms: "In simple terms"
        },
        paragraphs: {
          p1:
            "More creators in Nigeria are building audiences, personal brands, businesses, and media pages online. But one thing still slows many creators down: turning good ideas into content that feels strong enough to publish. The message may be clear in your head, but the hook is weak, the caption feels flat, or the title does not feel sharp enough.",
          p2:
            "That is where Hookora fits in. Hookora is built to make content creation feel clearer and faster, especially for creators who need help with the words that shape first impression.",
          p3:
            "Nigerian creators are working across many formats at once. Some are making YouTube Shorts, some are building Instagram pages, some are posting on TikTok, and others are growing personal brands through educational or entertainment content. In all of these cases, content packaging matters.",
          p4:
            "A stronger first line can help a viewer stop scrolling. A clearer title can make a video idea easier to understand. A better caption can make a post feel more intentional. A more structured short script can make recording easier. Hookora helps creators move through those steps with less friction.",
          p5:
            "One of the hardest parts of content creation is not always the idea itself. Sometimes it is the repetition. When you post often, the pressure to keep coming up with better hooks, better captions, and better titles can become tiring. Hookora reduces that pressure by helping you move faster from rough idea to usable output.",
          p6:
            "This is especially useful for Nigerian creators trying to stay consistent while balancing content with work, school, business, or other responsibilities. A smoother workflow can make consistency feel more realistic.",
          p7:
            "Hookora can help entertainment creators, education creators, business creators, personal brands, marketers, and social media managers. Whether your content is fun, informative, persuasive, or community-driven, the way you package the idea still matters. Hookora helps strengthen that layer.",
          p8:
            "Hookora helps Nigerian creators create with more speed, more confidence, and less friction. If you already have ideas but need help turning them into stronger hooks, titles, captions, and scripts, Hookora is built to support that process."
        },
        bullets: [
          "Generating stronger hooks for social videos and creator content",
          "Improving YouTube title ideas so they feel clearer and more clickable",
          "Writing captions faster for Instagram, TikTok, and Shorts",
          "Creating hashtag sets that feel more relevant to the content",
          "Rewriting descriptions so the message feels cleaner",
          "Structuring short-form scripts with a better hook and flow",
          "Improving thumbnail text for stronger first impression"
        ],
        related: {
          one: "What Is Hookora?",
          two: "How Hookora Helps Creators Write Hooks Faster"
        },
        ctaBadge: "Start free with Hookora",
        ctaTitle: "Use Hookora to build stronger content faster",
        ctaDescription:
          "If you want a cleaner workflow for hooks, titles, captions, scripts, and more, Hookora gives you a practical place to start.",
        ctaPrimary: "Create Free Account",
        ctaSecondary: "Explore Features"
      },

      writeHooksFaster: {
        seoTitle: "How Hookora Helps Creators Write Hooks Faster | Hookora Blog",
        seoDescription:
          "Learn how Hookora helps creators write hooks faster with clearer angles, stronger first lines, and a smoother content workflow.",
        heroBadge: "Hookora Blog",
        title: "How Hookora helps creators write hooks faster",
        intro:
          "Hookora helps creators move from rough ideas to stronger opening lines faster, so the first part of the content process feels less frustrating and more useful.",
        sections: {
          whyHooksSlowDown: "Why hooks slow creators down",
          howHookoraHelps: "How Hookora makes hook writing faster",
          strongerFirstLine: "From idea to stronger first line",
          shortAndLongForm: "Useful for short-form and long-form creators",
          practice: "What this means in practice",
          simpleTerms: "In simple terms"
        },
        paragraphs: {
          p1:
            "Writing a hook sounds simple until you actually try to do it every day. Many creators know their topic, know the message, and even know the value they want to deliver, but still get stuck at the beginning. The first line does not feel strong enough. The opening feels too plain. The hook sounds like something people would scroll past.",
          p2:
            "Hookora helps reduce that delay. Instead of forcing creators to sit in front of a blank screen trying to invent the perfect opener from scratch, Hookora helps generate stronger directions faster.",
          p3:
            "Hooks carry a lot of pressure. They shape first impression. They influence whether someone stops scrolling, clicks, or keeps watching. Because of that, creators often overthink them. A simple video idea can take too long to start because the opening line still feels weak.",
          p4:
            "That delay affects the rest of the workflow. When the hook is unclear, the script feels slower. The title becomes harder to shape. The caption feels less focused. Hookora helps by solving the first part faster.",
          p5:
            "A creator may know they want to talk about money mistakes, content tips, relationships, business lessons, or social commentary, but the best first line is not always obvious. Hookora helps shape that topic into stronger hook directions that feel more structured, more curiosity-driven, or more direct, depending on what the idea needs.",
          p6:
            "That speed matters because content creation works better when momentum is not constantly interrupted. The faster a creator can move from idea to usable hook, the easier it becomes to keep building the rest of the content.",
          p7:
            "Hookora is especially helpful for short-form creators because hooks matter so much in Reels, Shorts, and TikTok videos. But it also helps long-form creators, because better opening angles can improve titles, intros, and the overall presentation of a video or post.",
          p8:
            "Instead of spending too much time trying to phrase one opening line, creators can use Hookora to generate better starting points, choose a stronger angle, and move into the rest of the workflow faster. That means less friction at the start and more room to focus on the quality of the full content.",
          p9:
            "Hookora helps creators write hooks faster by making the opening part of content creation feel clearer, less stressful, and easier to move through. If writing first lines is where your content process often slows down, Hookora is built to help with that."
        },
        bullets: [
          "It helps turn rough ideas into clearer opening angles.",
          "It gives creators stronger starting points instead of forcing them to begin from zero.",
          "It reduces hesitation when multiple hook directions are possible.",
          "It helps creators test different approaches around the same topic.",
          "It makes it easier to keep creating without getting stuck too long on the first line."
        ],
        related: {
          one: "What Is Hookora?",
          two: "Hookora for Nigerian Creators"
        },
        ctaBadge: "Start free with Hookora",
        ctaTitle: "Use Hookora to create stronger hooks with less friction",
        ctaDescription:
          "If you want a faster way to shape better first lines, stronger angles, and smoother content workflow, Hookora gives you a practical place to start.",
        ctaPrimary: "Create Free Account",
        ctaSecondary: "Explore Features"
      }
    },

    categories: {
      all: "All",
      hooks: "Hooks",
      contentTools: "Content Tools",
      captions: "Captions",
      hookWriting: "Hook Writing"
    }
  },

  auth: {
    loginBadge: "🚀 Welcome back",
    loginTitle: "Login to Hookora",
    loginSubtitle:
      "Access your dashboard, generator, hook library, and pricing tools.",
    loginButton: "Login",
    forgotPassword: "Forgot password?",
    noAccount: "Don’t have an account?",
    registerLink: "Register",
    registerBadge: "✨ Create your account",
    registerTitle: "Create your Hookora account",
    registerSubtitle:
      "Start generating, saving, and managing hooks in one place.",
    registerButton: "Create Account",
    referralNotice:
      "Use a referral code to help someone earn Reward Credits. Referrers earn ₦300 after signup plus email verification, and an extra ₦700 after a paid upgrade.",
    username: "Username",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm Password",
    enterEmail: "Enter your email",
    enterPassword: "Enter your password",
    enterUsername: "Enter your username",
    createAccount: "Create your account",
    alreadyHaveAccount: "Already have an account?",
    loginLinkText: "Login",
    passwordShouldInclude: "Password should include:",
    atLeast6Characters: "At least 6 characters"
  },

  dashboard: {
    title: "Dashboard",
    subtitle:
      "Track your hook performance, Studio activity, trends, and top-performing content.",

    loading: "Loading dashboard...",

    errors: {
      notLoggedIn: "You are not logged in. Please log in first.",
      loadFailed: "Failed to load dashboard.",
      copyFailed: "Failed to copy. Please try again.",
      copyFailedConsole: "Failed to copy:",
      copyRewardFailedConsole: "Failed to copy reward text:",
      toggleFavoriteConsole: "Failed to toggle favorite:",
      favoriteUpdateFailed: "Failed to update favorite status.",
      deleteHookConsole: "Failed to delete hook:",
      deleteHookFailed: "Failed to delete hook.",
      deleteStudioAssetConsole: "Failed to delete Studio asset:",
      deleteStudioAssetFailed: "Failed to delete Studio asset.",
      regenerateConsole: "Failed to regenerate hook:",
      regenerateFailed: "Failed to regenerate hook."
    },

    confirm: {
      deleteHook: "Are you sure you want to delete this hook?",
      deleteStudioAsset: "Are you sure you want to delete this saved Studio asset?"
    },

    actions: {
      copied: "Copied",
      copyHook: "Copy Hook",
      copyAsset: "Copy Asset",
      copyCode: "Copy Code",
      copyLink: "Copy Link",
      regenerate: "Regenerate",
      favorite: "Favorite",
      unfavorite: "Unfavorite",
      delete: "Delete",
      working: "Working...",
      saving: "Saving...",
      dismiss: "Dismiss",
      seeProPlan: "See Pro Plan",
      upgradePlan: "Upgrade Plan",
      goToStudio: "Go to Studio",
      openBilling: "Open Billing",
      viewSignupFlow: "View Signup Flow",
      openMilestoneWatch: "Open Milestone Watch"
    },

    labels: {
      best: "Best",
      favorite: "Favorite",
      score: "Score",
      toolType: "Tool type",
      plan: "Plan",
      pro: "PRO",
      proInsights: "PRO INSIGHTS",
      saved: "saved",
      runs: "runs",
      hooks: "hooks",
      success: "Success",
      failed: "Failed",
      avgTime: "Avg time"
    },

    plan: {
      free: "Free",
      freeLowercase: "free",
      creator: "✔ Creator",
      pro: "✔ Pro"
    },

    upgradePrompt: {
      message:
        "{{featureName}} is available on the Pro plan. Upgrade to unlock this advanced analytics feature.",
      clickToUnlock: "Click to unlock with Pro",
      clickForPro: "Click for Pro analytics upgrade"
    },

    filters: {
      allPlatforms: "All",
      allTools: "All",
      platform: "Platform",
      studioTool: "Studio Tool",
      allStudioTools: "All Studio Tools",
      sortBy: "Sort By",
      score: "Score",
      topic: "Topic",
      searchTopic: "Search Topic",
      searchPlaceholder: "Type to search..."
    },

    metrics: {
      totalHooks: "Total Hooks",
      studioAssets: "Studio Assets",
      totalSavedItems: "Total Saved Items",
      studioRuns: "Studio Runs",
      actualTrackedToolRuns: "Actual tracked tool runs",
      averageScore: "Average Score",
      favoriteHooks: "Favorite Hooks",
      topHookScore: "Top Hook Score",
      successRate: "Success Rate",
      studioToolRunSuccess: "Studio tool run success"
    },
        rewards: {
      title: "Hookora Creator Rewards",
      subtitle:
        "Keep an eye on your Reward Credits and referral growth from one place.",
      rewardCredits: "Reward Credits",
      quickShare: "Referral quick share",
      referralCode: "Referral Code",
      referralLink: "Referral Link",
      snapshot: "Rewards snapshot",
      walletBalance: "Wallet Balance",
      totalReferrals: "Total Referrals",
      verifiedReferrals: "Verified Referrals",
      paidReferrals: "Paid Referrals"
    },

    founder: {
      title: "Founder Overview",
      subtitle: "Company-level growth and conversion snapshot.",
      totalUsers: "Total Users",
      activeUsers: "Active Users",
      paidUsers: "Paid Users",
      freeUsers: "Free Users",
      creatorUsers: "Creator Users",
      proUsers: "Pro Users",
      newUsers7d: "New Users (7d)",
      newUsers30d: "New Users (30d)",
      signupToPaid: "Signup → Paid",
      weeklyFocus: "Weekly Founder Focus",
      recentSignups: "Recent Signups",
      noSignupData: "No signup data yet."
    },

    usage: {
      title: "Plan Usage",
      monthlyGenerationsUsed: "Monthly generations used",
      remainingThisMonth: "Remaining this month",
      savedHooks: "Saved hooks"
    },

    studioSnapshot: {
      title: "Studio Tool Snapshot",
      subtitle:
        "Includes Hook Angles, CTA Generator, AI Humanizer, and other Studio tools"
    },

    charts: {
      platformDistribution: "Platform Distribution",
      studioToolUsage: "Studio Tool Usage",
      studioRunTrend: "Studio Run Trend",
      studioUsageSummary: "Studio Usage Summary"
    },

    summary: {
      mostUsedTool: "Most Used Tool",
      successfulRuns: "Successful Runs",
      failedRuns: "Failed Runs",
      averageGenerationTime: "Average Generation Time"
    },

    pro: {
      averageScoreInsights: "Average Score Insights",
      topHookScoreAnalytics: "Top Hook Score Analytics",
      dailyAverageScoreTrend: "Daily Average Score Trend",
      dailyAverageScoreTrendDescription:
        "Track score movement over time with richer historical insight and trend visibility.",
      bestTopicsByAverageScore: "Best Topics by Average Score",
      bestTopicsByAverageScoreDescription:
        "Discover your strongest-performing topics with deeper topic-level scoring insight."
    },

    sections: {
      favoriteHooks: "Favorite Hooks",
      strongestHookPerTopic: "Strongest Hook per Topic",
      topHooks: "Top Hooks",
      recentHooks: "Recent Hooks",
      recentStudioAssets: "Recent Studio Assets",
      combinedRecentActivity: "Combined Recent Activity"
    },

    empty: {
      noPlatformAnalytics: "No platform analytics yet.",
      noStudioActivity: "No Studio activity yet.",
      noStudioRunHistory:
        "No Studio run history yet. Use Studio tools to populate this chart.",
      noTrendData:
        "No trend data yet. Generate hooks over time to populate this chart.",
      noTopicAnalytics: "No topic analytics yet.",
      noContentTitle: "No content generated yet",
      noContentDescription:
        "Start using Studio tools to generate hooks, titles, captions, and scripts. Your personal analytics will appear here as you create and save content.",
      noFavoriteHooks: "No favorite hooks yet.",
      noStudioAssetsSaved: "No Studio assets saved yet.",
      noRecentActivity: "No recent activity yet."
    },

    tools: {
      hooks: {
        label: "Viral Hook Generator",
        badge: "HOOKS"
      },
      youtubeTitleOptimizer: {
        label: "YouTube Title Optimizer",
        badge: "TITLE"
      },
      captionGenerator: {
        label: "Caption Generator",
        badge: "CAPTION"
      },
      hashtagGenerator: {
        label: "Hashtag Generator",
        badge: "HASHTAG"
      },
      descriptionRewriter: {
        label: "Description Rewriter",
        badge: "DESCRIPTION"
      },
      shortScriptGenerator: {
        label: "Short Script Generator",
        badge: "SCRIPT"
      },
      thumbnailTextAnalyzer: {
        label: "Thumbnail Text Analyzer",
        badge: "THUMBNAIL"
      },
      hookAngles: {
        label: "Hook Angles",
        badge: "ANGLES"
      },
      ctaGenerator: {
        label: "CTA Generator",
        badge: "CTA"
      },
      humanizer: {
        label: "AI Humanizer",
        badge: "HUMANIZER"
      },
      other: {
        label: "Other",
        badge: "OTHER"
      }
    },
    welcome: {
  creator: "Creator",
  time: {
    morning: "Good morning",
    afternoon: "Good afternoon",
    evening: "Good evening",
    night: "Good evening"
  },
  messages: {
    one: "Welcome back to Hookora. Let’s create something strong today.",
    two: "Your dashboard is ready. Let’s build sharper content.",
    three: "Good to see you again. Your creative workspace is waiting.",
    four: "Let’s turn today’s ideas into stronger hooks and content.",
    five: "Hookora is ready when you are."
  }
},
  },

  library: {
    title: "Library",
    subtitle:
      "Browse saved hooks and saved Studio outputs from your content workflow.",
    yourCurrentPlan: "Your current plan:",
    planNoticeLine1: "Hooks and Studio assets are both stored here now.",
    planNoticeLine2:
      "CSV export and bulk actions are available on Creator and Pro.",
    planNoticeLine3:
      "Advanced library intelligence and future workspaces are planned for Pro.",
    upgradeToCreator: "Upgrade to Creator",
    savedHooks: "Saved Hooks",
    savedStudioAssets: "Saved Studio Assets",
    favoriteHooks: "Favorite Hooks",
    averageHookScore: "Average Hook Score",
    studioToolBreakdown: "Studio Tool Breakdown",
    search: "Search",
    searchPlaceholder: "Search topic, content, or tool...",
    platform: "Platform",
    studioTool: "Studio Tool",
    sortBy: "Sort By",
    newest: "Newest",
    oldest: "Oldest",
    highestScore: "Highest Score",
    lowestScore: "Lowest Score",
    topic: "Topic",
    favoritesOnly: "Favorites only",
    selectVisibleHooks: "Select Visible Hooks",
    unselectVisibleHooks: "Unselect Visible Hooks",
    clearSelection: "Clear Selection",
    copyVisibleHooks: "Copy Visible Hooks",
    copiedAll: "Copied All!",
    exportHooksTxt: "Export Hooks TXT",
    exportHooksCsv: "Export Hooks CSV",
    favoriteSelected: "Favorite Selected",
    unfavoriteSelected: "Unfavorite Selected",
    deleteSelected: "Delete Selected",
    selectedHooksCount: "{{count}} hooks selected",
    bulkActionsLocked:
      "Bulk actions and CSV export are locked on Free.",
    proPreview:
      "Pro preview: future library intelligence, saved presets, and workspaces will be available on Pro.",
    topicFolders: "Topic Folders",
    noFoldersMatch:
      "No folders match your current filters.",
    studioAssets: "Studio Assets",
    hooks: "Hooks",
    rename: "Rename",
    save: "Save",
    cancel: "Cancel",
    deleteFolder: "Delete Folder",
    deleting: "Deleting...",
    copy: "Copy",
    regenerate: "Regenerate",
    favorite: "Favorite",
    unfavorite: "Unfavorite",
    delete: "Delete",
    locked: "Locked",
    noDate: "No date",
    toolType: "Tool type",
    loadingLibrary: "Loading hook library...",
    notLoggedIn: "You are not logged in. Please log in first.",
    failedToLoadLibrary: "Failed to load hook library."
  },

  studioLayout: {
    headerChip: "Creative workspace",
    description:
      "Create hooks, hook angles, titles, captions, hashtags, descriptions, scripts, CTAs, thumbnail text, repurposed content, ad copy, carousel copy, viral rewrites, offer positioning, hook score analysis, trend conversions, Nigerian audience rewrites, retention intros, brand voice systems, differentiation ideas, and comment-driven content from one polished workspace.",
    replayTour: "Replay Tour",
    selectTool: "Select a tool.",
    planNotice: {
      free:
        "Free plan gives access to core tools. Upgrade to unlock Creator and Pro Studio tools.",
      creator:
        "Creator plan unlocks most premium tools. Upgrade to Pro for the highest-level strategic Studio tools."
    },
    tour: {
      welcome: {
        title: "Welcome to Studio",
        description:
          "Studio is where you generate hooks, hook angles, titles, captions, hashtags, descriptions, scripts, CTAs, thumbnail text, and many advanced content tools from one workspace."
      },
      tools: {
        title: "Switch tools fast",
        description:
          "Use the horizontal tools rail to move across Hookora tools quickly without losing your shared topic and context."
      },
      workspace: {
        title: "Build inside one workspace",
        description:
          "Start with the topic, then add optional context like audience, goal, pain point, angle, desired outcome, source text, or rewrite settings depending on the tool."
      },
      results: {
        title: "Review, copy, and save",
        description:
          "Generated results appear in the active tool area. Copy the strongest outputs and save the best ones to your library."
      },
      replay: {
        title: "Replay anytime",
        description: "Use Replay Tour whenever you want a quick refresher."
      }
    },
    upgradeModal: {
      studioTool: "Studio Tool",
      unlock: "Unlock",
      descriptionStart: "This tool requires the",
      descriptionEnd:
        "plan. Upgrade to unlock stronger tools for content quality, positioning, rewrites, retention, packaging, and creator growth.",
      yearlyTitle: "Yearly plans hit harder",
      andGet: "and get the feel of",
      yearlyDescriptionEnd: "when you upgrade yearly.",
      upgradeTo: "Upgrade to",
      maybeLater: "Maybe Later",
      benefits: {
        requiredTier: "{{tier}} access for premium tools",
        strongerContent: "Create stronger content faster",
        yearlyValue: "Get more value from yearly plans"
      }
    }
  },

  studioSidebar: {
    title: "Studio Tools",
    subtitle: "Swipe or scroll across and jump into any tool.",
    lockedSuffix: "tool — upgrade to unlock",
    toolDescriptions: {
      hooks: "Generate strong hooks",
      hookAngles: "Find stronger content angles",
      titles: "Create clickable titles",
      captions: "Write better captions",
      hashtags: "Build hashtag sets",
      descriptions: "Rewrite descriptions",
      scripts: "Create short scripts",
      ctas: "Generate stronger CTAs",
      thumbnail: "Improve thumbnail text",
      humanizer: "Make copy sound human",
      repurpose: "Turn one post into many",
      adCopy: "Create ad-ready copy",
      carousels: "Build slide-by-slide carousels",
      viralRewrite: "Make copy more scroll-stopping",
      positioning: "Clarify your offer message",
      hookScore: "Analyze hook strength",
      trendToContent: "Turn trends into content",
      nigerianRewrite: "Localize copy for Nigerians",
      retentionIntro: "Create stronger video intros",
      brandVoiceTrainer: "Train your content voice",
      contentDifferentiation: "Stand out more clearly",
      commentToContent: "Turn comments into content"
    }
  }
};

const es = deepMerge(en, {
  common: {
    login: "Iniciar sesión",
    register: "Registrarse",
    logout: "Cerrar sesión",
    dashboard: "Panel",
    studio: "Studio",
    library: "Biblioteca",
    billing: "Facturación",
    home: "Inicio",
    features: "Funciones",
    pricing: "Precios",
    about: "Acerca de",
    blog: "Blog",
    settings: "Configuración de cuenta",
    waitlistAdmin: "Administrador de lista de espera",
    fullscreenEnter: "Entrar en pantalla completa",
    fullscreenExit: "Salir en pantalla completa",
    light: "Claro",
    dark: "Oscuro",
    language: "Idioma",
    theme: "Tema",
    current: "Actual",
    tools: "Herramientas",
    unlockAllStudioTools: "Desbloquear todas las herramientas de Studio",
    readGuide: "Leer guía",
    loading: "Cargando",
    copied: "Copiado",
    copyCode: "Copiar código",
    copyLink: "Copiar enlace"
  },

  languages: {
    en: "Inglés",
    es: "Español",
    fr: "Francés",
    pt: "Portugués"
  },

  
  navbar: {
    hi: "Hola",
    publicToolPages: "Páginas públicas de herramientas",
    moreToolsInStudio: "Más herramientas disponibles dentro de Studio",
    loggedIn: "Sesión iniciada",
    openSettingsMenu: "Abrir menú de configuración",
    toggleNavigationMenu: "Abrir menú de navegación",
    display: "Pantalla"
  },

  loginPage: {
  seoTitle: "Iniciar sesión | Hookora",
  seoDescription:
    "Inicia sesión en Hookora para acceder a tu panel, herramientas de Studio, biblioteca guardada y controles de facturación.",
  badge: "🚀 Bienvenido de nuevo",
  title: "Inicia sesión en Hookora",
  description:
    "Accede a tu panel, generador, biblioteca de hooks y herramientas de precios.",
  emailPlaceholder: "Correo electrónico",
  passwordPlaceholder: "Contraseña",
  showPassword: "Mostrar contraseña",
  hidePassword: "Ocultar contraseña",
  loginButton: "Iniciar sesión",
  loggingIn: "Iniciando sesión...",
  forgotPassword: "¿Olvidaste tu contraseña?",
  noAccount: "¿No tienes una cuenta?",
  registerLink: "Registrarse",
  errors: {
    default: "Error al iniciar sesión."
  }
},

registerPage: {
  seoTitle: "Registro | Hookora",
  seoDescription:
    "Crea tu cuenta de Hookora y empieza a generar hooks, títulos, captions, guiones y más desde un solo flujo.",
  badge: "✨ Crea tu cuenta",
  title: "Crea tu cuenta de Hookora",
  subtitle:
    "Empieza a generar, guardar y gestionar hooks en un solo lugar.",
  referralBanner:
    "Usa un código de referido para ayudar a alguien a ganar <strong>Reward Credits</strong>. Los referidores ganan ₦300 después del registro más la verificación del correo, y ₦700 extra después de una mejora de pago.",
  referralDetected:
    "Código de referido detectado y aplicado a tu registro.",
  fields: {
    username: {
      label: "Nombre de usuario",
      placeholder: "Elige un nombre de usuario"
    },
    email: {
      label: "Correo electrónico",
      placeholder: "Introduce tu correo electrónico"
    },
    password: {
      label: "Contraseña",
      placeholder: "Crea una contraseña"
    },
    referralCode: {
      label: "Código de referido",
      optional: "(opcional)",
      placeholder: "Introduce el código de referido",
      help:
        "Registrarte mediante un referido ayuda a tu referidor a ganar Reward Credits después de que tu correo sea verificado."
    }
  },
  passwordHints: {
    title: "La contraseña debe incluir:",
    minLength: "Al menos 6 caracteres",
    hasLetter: "Al menos 1 letra",
    hasNumberOrSymbol: "Al menos 1 número o símbolo"
  },
  actions: {
    register: "Registrarse",
    creatingAccount: "Creando cuenta...",
    resendVerificationEmail: "Reenviar correo de verificación",
    sending: "Enviando...",
    goToLogin: "Ir a iniciar sesión",
    login: "Iniciar sesión"
  },
  messages: {
    accountCreated:
      "Cuenta creada correctamente. Verifica tu correo antes de iniciar sesión.",
    verifyInbox:
      "Revisa tu bandeja de entrada para ver el correo de verificación. Después de verificar, podrás iniciar sesión y tu elegibilidad de referido se contará correctamente.",
    enterEmailForResend:
      "Introduce primero tu correo para que podamos reenviar el enlace de verificación.",
    verificationSent:
      "Correo de verificación enviado correctamente.",
    resendFailed:
      "No se pudo reenviar el correo de verificación. Inténtalo de nuevo.",
    registrationFailed: "El registro falló.",
    referralNote: "Nota de referido:"
  },
  links: {
    alreadyHaveAccount: "¿Ya tienes una cuenta?"
  },
  aria: {
    showPassword: "Mostrar contraseña",
    hidePassword: "Ocultar contraseña"
  }
},

dashboard: {
  title: "Panel",
  subtitle:
    "Sigue el rendimiento de tus hooks, la actividad de Studio, las tendencias y tu contenido con mejor rendimiento.",

  loading: "Cargando panel...",

  errors: {
    notLoggedIn: "No has iniciado sesión. Por favor, inicia sesión primero.",
    loadFailed: "No se pudo cargar el panel.",
    copyFailed: "No se pudo copiar. Inténtalo de nuevo.",
    copyFailedConsole: "No se pudo copiar:",
    copyRewardFailedConsole: "No se pudo copiar el texto de recompensa:",
    toggleFavoriteConsole: "No se pudo cambiar el estado de favorito:",
    favoriteUpdateFailed: "No se pudo actualizar el estado de favorito.",
    deleteHookConsole: "No se pudo eliminar el hook:",
    deleteHookFailed: "No se pudo eliminar el hook.",
    deleteStudioAssetConsole: "No se pudo eliminar el activo de Studio:",
    deleteStudioAssetFailed: "No se pudo eliminar el activo de Studio.",
    regenerateConsole: "No se pudo regenerar el hook:",
    regenerateFailed: "No se pudo regenerar el hook."
  },

  confirm: {
    deleteHook: "¿Seguro que quieres eliminar este hook?",
    deleteStudioAsset: "¿Seguro que quieres eliminar este activo guardado de Studio?"
  },

  actions: {
    copied: "Copiado",
    copyHook: "Copiar hook",
    copyAsset: "Copiar activo",
    copyCode: "Copiar código",
    copyLink: "Copiar enlace",
    regenerate: "Regenerar",
    favorite: "Favorito",
    unfavorite: "Quitar de favoritos",
    delete: "Eliminar",
    working: "Procesando...",
    saving: "Guardando...",
    dismiss: "Descartar",
    seeProPlan: "Ver plan Pro",
    upgradePlan: "Mejorar plan",
    goToStudio: "Ir a Studio",
    openBilling: "Abrir facturación",
    viewSignupFlow: "Ver flujo de registro",
    openMilestoneWatch: "Abrir Milestone Watch"
  },

  labels: {
    best: "Mejor",
    favorite: "Favorito",
    score: "Puntuación",
    toolType: "Tipo de herramienta",
    plan: "Plan",
    pro: "PRO",
    proInsights: "PRO INSIGHTS",
    saved: "guardados",
    runs: "ejecuciones",
    hooks: "hooks",
    success: "Éxito",
    failed: "Fallidos",
    avgTime: "Tiempo medio"
  },

  plan: {
    free: "Free",
    freeLowercase: "free",
    creator: "✔ Creator",
    pro: "✔ Pro"
  },

  upgradePrompt: {
    message:
      "{{featureName}} está disponible en el plan Pro. Mejora para desbloquear esta función avanzada de analítica.",
    clickToUnlock: "Haz clic para desbloquear con Pro",
    clickForPro: "Haz clic para mejorar a analítica Pro"
  },

  filters: {
    allPlatforms: "Todos",
    allTools: "Todos",
    platform: "Plataforma",
    studioTool: "Herramienta de Studio",
    allStudioTools: "Todas las herramientas de Studio",
    sortBy: "Ordenar por",
    score: "Puntuación",
    topic: "Tema",
    searchTopic: "Buscar tema",
    searchPlaceholder: "Escribe para buscar..."
  },

  metrics: {
    totalHooks: "Hooks totales",
    studioAssets: "Activos de Studio",
    totalSavedItems: "Elementos guardados totales",
    studioRuns: "Ejecuciones de Studio",
    actualTrackedToolRuns: "Ejecuciones reales de herramientas rastreadas",
    averageScore: "Puntuación media",
    favoriteHooks: "Hooks favoritos",
    topHookScore: "Mejor puntuación de hook",
    successRate: "Tasa de éxito",
    studioToolRunSuccess: "Éxito de ejecución de herramientas de Studio"
  },

  rewards: {
    title: "Hookora Creator Rewards",
    subtitle:
      "Supervisa tus Reward Credits y el crecimiento de tus referidos desde un solo lugar.",
    rewardCredits: "Reward Credits",
    quickShare: "Compartir referido rápidamente",
    referralCode: "Código de referido",
    referralLink: "Enlace de referido",
    snapshot: "Resumen de recompensas",
    walletBalance: "Saldo de billetera",
    totalReferrals: "Referidos totales",
    verifiedReferrals: "Referidos verificados",
    paidReferrals: "Referidos de pago"
  },

  founder: {
    title: "Resumen del fundador",
    subtitle: "Resumen del crecimiento de la empresa y de la conversión.",
    totalUsers: "Usuarios totales",
    activeUsers: "Usuarios activos",
    paidUsers: "Usuarios de pago",
    freeUsers: "Usuarios Free",
    creatorUsers: "Usuarios Creator",
    proUsers: "Usuarios Pro",
    newUsers7d: "Nuevos usuarios (7d)",
    newUsers30d: "Nuevos usuarios (30d)",
    signupToPaid: "Registro → Pago",
    weeklyFocus: "Enfoque semanal del fundador",
    recentSignups: "Registros recientes",
    noSignupData: "Todavía no hay datos de registros."
  },

  usage: {
    title: "Uso del plan",
    monthlyGenerationsUsed: "Generaciones mensuales usadas",
    remainingThisMonth: "Restantes este mes",
    savedHooks: "Hooks guardados"
  },

  studioSnapshot: {
    title: "Resumen de herramientas de Studio",
    subtitle:
      "Incluye Hook Angles, CTA Generator, AI Humanizer y otras herramientas de Studio."
  },

  charts: {
    platformDistribution: "Distribución por plataforma",
    studioToolUsage: "Uso de herramientas de Studio",
    studioRunTrend: "Tendencia de ejecuciones de Studio",
    studioUsageSummary: "Resumen de uso de Studio"
  },

  summary: {
    mostUsedTool: "Herramienta más usada",
    successfulRuns: "Ejecuciones exitosas",
    failedRuns: "Ejecuciones fallidas",
    averageGenerationTime: "Tiempo medio de generación"
  },

  pro: {
    averageScoreInsights: "Insights de puntuación media",
    topHookScoreAnalytics: "Analítica de mejor puntuación de hook",
    dailyAverageScoreTrend: "Tendencia diaria de puntuación media",
    dailyAverageScoreTrendDescription:
      "Sigue el movimiento de la puntuación con una visión histórica más rica y mejor visibilidad de tendencias.",
    bestTopicsByAverageScore: "Mejores temas por puntuación media",
    bestTopicsByAverageScoreDescription:
      "Descubre tus temas con mejor rendimiento con una visión más profunda a nivel de tema."
  },

  sections: {
    favoriteHooks: "Hooks favoritos",
    strongestHookPerTopic: "Hook más fuerte por tema",
    topHooks: "Mejores hooks",
    recentHooks: "Hooks recientes",
    recentStudioAssets: "Activos recientes de Studio",
    combinedRecentActivity: "Actividad reciente combinada"
  },

  empty: {
    noPlatformAnalytics: "Todavía no hay analítica por plataforma.",
    noStudioActivity: "Todavía no hay actividad en Studio.",
    noStudioRunHistory:
      "Todavía no hay historial de ejecuciones de Studio. Usa herramientas de Studio para llenar este gráfico.",
    noTrendData:
      "Todavía no hay datos de tendencia. Genera hooks con el tiempo para llenar este gráfico.",
    noTopicAnalytics: "Todavía no hay analítica de temas.",
    noContentTitle: "Todavía no se ha generado contenido",
    noContentDescription:
      "Empieza a usar herramientas de Studio para generar hooks, títulos, captions y guiones. Tu analítica personal aparecerá aquí a medida que crees y guardes contenido.",
    noFavoriteHooks: "Todavía no hay hooks favoritos.",
    noStudioAssetsSaved: "Todavía no hay activos de Studio guardados.",
    noRecentActivity: "Todavía no hay actividad reciente."
  },

  tools: {
    hooks: {
      label: "Generador de Hooks Virales",
      badge: "HOOKS"
    },
    youtubeTitleOptimizer: {
      label: "Optimizador de Títulos de YouTube",
      badge: "TITLE"
    },
    captionGenerator: {
      label: "Generador de Subtítulos",
      badge: "CAPTION"
    },
    hashtagGenerator: {
      label: "Generador de Hashtags",
      badge: "HASHTAG"
    },
    descriptionRewriter: {
      label: "Reescritor de Descripciones",
      badge: "DESCRIPTION"
    },
    shortScriptGenerator: {
      label: "Generador de Guiones Cortos",
      badge: "SCRIPT"
    },
    thumbnailTextAnalyzer: {
      label: "Analizador de Texto para Miniaturas",
      badge: "THUMBNAIL"
    },
    hookAngles: {
      label: "Ángulos de Hook",
      badge: "ANGLES"
    },
    ctaGenerator: {
      label: "Generador de CTA",
      badge: "CTA"
    },
    humanizer: {
      label: "Humanizador IA",
      badge: "HUMANIZER"
    },
    other: {
      label: "Otro",
      badge: "OTHER"
    }
  },

  welcome: {
  time: {
    morning: "Buenos días",
    afternoon: "Buenas tardes",
    evening: "Buenas noches"
  },
  messages: {
    one: "Qué bueno verte otra vez. Vamos a crear algo fuerte hoy.",
    two: "Tu espacio creativo ya está listo.",
    three: "Es un buen momento para convertir ideas en contenido sólido.",
    four: "Sigamos construyendo algo nítido y valioso.",
    five: "Tu próximo gran contenido puede empezar ahora mismo.",
    six: "Vuelve a tu ritmo creativo y haz que hoy cuente."
  }
},
},

libraryPage: {
  title: "Biblioteca",
  subtitle: "Explora los hooks guardados y los elementos guardados del Studio de tu flujo de contenido.",

  currentPlanLabel: "Tu plan actual:",

  planLabels: {
    free: "Gratis",
    creator: "✔ Creator",
    pro: "✔ Pro"
  },

  planBox: {
    line1: "Los hooks y los elementos del Studio ahora se almacenan aquí.",
    line2: {
      before: "La exportación CSV y las acciones masivas están disponibles en los planes",
      creator: "Creator",
      middle: "y",
      pro: "Pro"
    },
    line3: {
      before: "La inteligencia avanzada de la biblioteca y los futuros espacios de trabajo están planeados para",
      pro: "Pro"
    }
  },

  badges: {
    creatorPlus: "CREATOR+",
    proInsights: "INSIGHTS PRO",
    locked: "Bloqueado"
  },

  tools: {
    hooks: {
      label: "Generador de Hooks Virales",
      badge: "HOOKS"
    },
    youtubeTitleOptimizer: {
      label: "Optimizador de Títulos de YouTube",
      badge: "TÍTULO"
    },
    captionGenerator: {
      label: "Generador de Captions",
      badge: "CAPTION"
    },
    hashtagGenerator: {
      label: "Generador de Hashtags",
      badge: "HASHTAG"
    },
    descriptionRewriter: {
      label: "Reescritor de Descripciones",
      badge: "DESCRIPCIÓN"
    },
    shortScriptGenerator: {
      label: "Generador de Guiones Cortos",
      badge: "GUION"
    },
    thumbnailTextAnalyzer: {
      label: "Analizador de Texto para Thumbnail",
      badge: "THUMBNAIL"
    },
    hookAngles: {
      label: "Ángulos de Hook",
      badge: "ÁNGULOS"
    },
    ctaGenerator: {
      label: "Generador de CTA",
      badge: "CTA"
    },
    humanizer: {
      label: "Humanizador con IA",
      badge: "HUMANIZADOR"
    },
    other: {
      label: "Otro",
      badge: "OTRO"
    }
  },

  stats: {
    savedHooks: "Hooks guardados",
    savedStudioAssets: "Elementos guardados del Studio",
    favoriteHooks: "Hooks favoritos",
    averageHookScore: "Puntuación promedio de hooks"
  },

  toolBreakdown: {
    title: "Desglose de herramientas del Studio",
    savedItemsCount: "{{count}} elemento guardado del studio",
    savedItemsCount_plural: "{{count}} elementos guardados del studio"
  },

  filters: {
    search: "Buscar",
    searchPlaceholder: "Buscar tema, contenido o herramienta...",
    platform: "Plataforma",
    allPlatforms: "Todas",
    studioTool: "Herramienta del Studio",
    allStudioTools: "Todas las herramientas del Studio",
    sortBy: "Ordenar por",
    favoritesOnly: "Solo favoritos"
  },

  sort: {
    newest: "Más recientes",
    oldest: "Más antiguos",
    highestScore: "Puntuación más alta",
    lowestScore: "Puntuación más baja",
    topic: "Tema"
  },

  actions: {
    upgradeToCreator: "Actualizar a Creator",
    unselectVisibleHooks: "Deseleccionar hooks visibles",
    selectVisibleHooks: "Seleccionar hooks visibles",
    clearSelection: "Limpiar selección",
    copiedAll: "¡Todo copiado!",
    copyVisibleHooks: "Copiar hooks visibles",
    exportHooksTxt: "Exportar hooks TXT",
    exportHooksCsv: "Exportar hooks CSV",
    favoriteSelected: "Marcar seleccionados como favoritos",
    unfavoriteSelected: "Quitar de favoritos los seleccionados",
    deleteSelected: "Eliminar seleccionados",
    copied: "¡Copiado!",
    copy: "Copiar",
    copyHook: "Copiar hook",
    regenerate: "Regenerar",
    favorite: "Favorito",
    unfavorite: "Quitar de favoritos",
    delete: "Eliminar",
    rename: "Renombrar",
    deleteFolder: "Eliminar carpeta",
    save: "Guardar",
    saving: "Guardando...",
    cancel: "Cancelar",
    deleting: "Eliminando..."
  },

  states: {
    loading: "Cargando biblioteca de hooks...",
    working: "Procesando...",
    noFoldersMatch: "Ninguna carpeta coincide con tus filtros actuales."
  },

  labels: {
    untitled: "Sin título",
    score: "Puntuación",
    selectedHooksCount: "{{count}} hooks seleccionados",
    toolType: "Tipo de herramienta",
    noDate: "Sin fecha",
    favorite: "Favorito"
  },

  export: {
    topic: "Tema",
    platform: "Plataforma",
    score: "Puntuación",
    favorite: "Favorito",
    created: "Creado",
    hook: "Hook",
    yes: "Sí",
    no: "No"
  },

  features: {
    bulkSelection: "Selección masiva",
    csvExport: "Exportación CSV",
    bulkDelete: "Eliminación masiva",
    bulkFavorite: "Favoritos masivos",
    bulkUnfavorite: "Quitar favoritos masivamente"
  },

  notices: {
    freeLock: {
      before: "Las acciones masivas y la exportación CSV están bloqueadas en el plan Gratis.",
      upgradeLink: "Actualiza a Creator"
    },
    proPreview: {
      before: "Vista previa Pro: la futura inteligencia de biblioteca, los ajustes guardados y los espacios de trabajo estarán disponibles en",
      pro: "Pro"
    }
  },

  folders: {
    title: "Carpetas de temas",
    folderNamePlaceholder: "Escribe el nombre de la carpeta",
    itemsCount: "{{count}} elemento",
    itemsCount_plural: "{{count}} elementos",
    hooksCount: "{{count}} hook",
    hooksCount_plural: "{{count}} hooks",
    assetsCount: "{{count}} recurso",
    assetsCount_plural: "{{count}} recursos"
  },

  sections: {
    studioAssets: "Elementos del Studio",
    hooks: "Hooks"
  },

  confirmations: {
    deleteHook: "¿Seguro que quieres eliminar este hook?",
    deleteAsset: "¿Seguro que quieres eliminar este elemento guardado?",
    deleteSelectedHooks: "¿Seguro que quieres eliminar {{count}} hook(s) seleccionado(s)?",
    deleteFolder: "¿Seguro que quieres eliminar esta carpeta y todo su contenido?\n\nCarpeta: {{folderName}}\nElementos: {{itemCount}}"
  },

  messages: {
    notLoggedIn: "No has iniciado sesión. Inicia sesión primero.",
    loadFailed: "No se pudo cargar la biblioteca de hooks.",
    upgradeRequired: "{{featureName}} está disponible en los planes Creator y Pro. Actualiza para desbloquear esta función.",
    favoriteUpdateFailed: "No se pudo actualizar el estado de favorito.",
    deleteHookFailed: "No se pudo eliminar el hook.",
    deleteAssetFailed: "No se pudo eliminar el elemento guardado.",
    deleteFolderFailed: "No se pudo eliminar la carpeta.",
    regenerateFailed: "No se pudo regenerar el hook.",
    folderNameRequired: "El nombre de la carpeta no puede estar vacío.",
    renameFolderFailed: "No se pudo renombrar la carpeta.",
    copyVisibleFailed: "No se pudieron copiar los hooks visibles.",
    csvExportFailed: "La exportación CSV falló.",
    bulkDeleteFailed: "No se pudieron eliminar los hooks seleccionados.",
    bulkUpdateFailed: "No se pudieron actualizar los hooks seleccionados."
  }
},

  about: {
    seoTitle: "Acerca de Hookora | Plataforma de flujo de IA para creadores",
    seoDescription:
      "Descubre qué hace Hookora, por qué existe y cómo ayuda a los creadores a construir mejores hooks, títulos, captions, guiones y un mejor flujo de contenido.",
    heroBadge: "Acerca de Hookora",
    heroTitle: "Hookora ayuda a los creadores a pasar de la idea a un mejor contenido más rápido",
    heroDescription:
      "Hookora es una plataforma de flujo con IA creada para creadores que quieren una forma más limpia, rápida y segura de generar hooks, títulos, captions, hashtags, descripciones, guiones y texto para thumbnails.",
    createFreeAccount: "Crear cuenta gratis",
    exploreFeatures: "Explorar funciones",
    storyBadge: "Por qué existe Hookora",
    storyTitle: "Crear contenido debería sentirse más claro, no más pesado",
    storyParagraph1:
      "Muchos creadores saben lo que quieren decir, pero aun así se ralentizan por las palabras. El hook se siente débil. El título no funciona. El caption se siente plano. El guion tarda demasiado. Esa fricción se acumula y hace que publicar sea más difícil de lo que debería.",
    storyParagraph2:
      "Hookora fue creado para reducir esa fricción. En lugar de obligar a los creadores a saltar entre herramientas dispersas, notas aleatorias y flujos desordenados, Hookora reúne el proceso en un sistema más limpio que ayuda a convertir ideas en mejores resultados más rápido.",
    storyParagraph3:
      "Ya sea que alguien cree desde Lagos, Londres o Los Ángeles, el problema suele ser el mismo: crear contenido consume demasiada energía mental para empaquetarlo bien. Hookora ayuda a los creadores a sentirse más apoyados, más consistentes y con más control sobre su flujo.",
    whyTitle: "Lo que hace útil a Hookora",
    whySubtitle:
      "Hookora no está pensado para hacer que los creadores se sientan reemplazados. Está pensado para hacer que se sientan más nítidos, más rápidos y más apoyados.",
    ctaBadge: "Empieza con Hookora",
    ctaTitle: "Construye tu flujo de contenido con más claridad y menos fricción",
    ctaDescription:
      "Si quieres una forma más limpia de generar hooks, títulos, captions, guiones y más, Hookora te da un lugar práctico para empezar.",
    viewPricing: "Ver precios",
    guidesSectionTitle: "Aprende más sobre Hookora y cómo ayuda a los creadores",
    guidesSectionDescription:
      "Estas guías rápidas explican qué hace Hookora, a quién ayuda y cómo hace que la escritura de hooks y el flujo de contenido se sientan más claros y rápidos.",
    schemaDescription:
      "Descubre qué es Hookora, a quién ayuda y cómo apoya a los creadores con un flujo de contenido más fluido.",
    guides: {
      whatIsHookora: {
        description:
          "Aprende qué es Hookora, cómo funciona y por qué ayuda a los creadores a pasar de una idea a un contenido más fuerte más rápido."
      },
      nigerianCreators: {
        description:
          "Descubre cómo Hookora ayuda a los creadores nigerianos a construir mejores hooks, títulos, captions, guiones y un mejor empaquetado del contenido."
      },
      writeHooksFaster: {
        description:
          "Explora cómo Hookora ayuda a los creadores a escribir hooks más rápido con ángulos más claros y primeras líneas más fuertes."
      }
    },
    cards: {
      builtForCreators: "Creado para creadores que quieren menos fricción",
      keepsOutputMoving: "Diseñado para mantener en movimiento la producción de contenido",
      oneWorkflow:
        "Pensado para conectar hooks, títulos, captions y guiones en un solo flujo",
      globalCreators: "Útil para creadores en Nigeria y más allá",
      connectedWorkflow: {
        title: "Un flujo conectado",
        text:
          "Hooks, títulos, captions, hashtags, guiones, descripciones y texto para thumbnails funcionan mejor cuando se crean dentro de un sistema conectado."
      },
      momentum: {
        title: "Más impulso creativo",
        text:
          "Hookora ayuda a los creadores a pasar menos tiempo bloqueados con las palabras y más tiempo avanzando hacia contenido listo para publicar."
      },
      packaging: {
        title: "Mejor empaquetado de contenido",
        text:
          "Un buen contenido no trata solo de ideas. También trata de cómo se presentan, se encuadran y se vuelven más fáciles de clicar o ver."
      },
      growingCreators: {
        title: "Útil para creadores en crecimiento",
        text:
          "Hookora está diseñado para ser útil tanto si estás empezando como si ya creas con frecuencia y necesitas un flujo más suave."
      }
    }
  },

 studioLayout: {
  headerChip: "Flujo creativo impulsado por IA",
  description:
    "Usa las herramientas de Studio de Hookora para generar, refinar y fortalecer tu contenido más rápido desde un solo flujo.",
  replayTour: "Repetir recorrido",
  selectTool: "Selecciona una herramienta para empezar.",

  planNotice: {
    free:
      "Estás en Free. Mejora para desbloquear herramientas premium y estratégicas de Studio.",
    creator:
      "Estás en Creator. Mejora a Pro para desbloquear herramientas estratégicas avanzadas."
  },

  tour: {
    welcome: {
      title: "Bienvenido a Studio",
      description:
        "Aquí es donde construyes hooks, títulos, captions, guiones y más desde un solo flujo conectado."
    },
    tools: {
      title: "Explora tus herramientas",
      description:
        "Cambia entre las herramientas de Studio según el tipo de contenido que quieras crear o mejorar."
    },
    workspace: {
      title: "Tu espacio de trabajo",
      description:
        "Aquí es donde introduces contexto, ajustas direcciones y generas resultados más fuertes."
    },
    results: {
      title: "Revisa tus resultados",
      description:
        "Tus resultados aparecerán aquí para que puedas revisarlos, refinarlos y reutilizarlos."
    },
    replay: {
      title: "Vuelve a ver el recorrido cuando quieras",
      description:
        "Puedes reiniciar este recorrido en cualquier momento para recordar cómo funciona el espacio."
    }
  },

  upgradeModal: {
    studioTool: "de Studio",
    unlock: "Desbloquear",
    descriptionStart: "Esta herramienta requiere el plan",
    descriptionEnd:
      "para acceder a funciones más fuertes, más flexibilidad y mejores resultados.",
    yearlyTitle: "Obtén más valor con anual",
    andGet: "y obtén",
    yearlyDescriptionEnd:
      "al elegir facturación anual para tu flujo de Hookora.",
    upgradeTo: "Mejorar a",
    maybeLater: "Tal vez después",

    benefits: {
      requiredTier: "Requiere {{tier}}",
      strongerContent: "Resultados más fuertes y más estratégicos",
      yearlyValue: "Mejor valor con facturación anual"
    }
  }
},

studioSidebar: {
  title: "Herramientas de Studio",
  subtitle:
    "Elige una herramienta según el tipo de contenido que quieras crear.",
  lockedSuffix: "bloqueado",

  toolDescriptions: {
    hooks: "Genera líneas de apertura directas que puedes usar rápido.",
    hookAngles:
      "Explora varias direcciones antes de elegir tu mejor hook.",
    titles:
      "Crea títulos más fuertes para videos, publicaciones y empaquetado de contenido.",
    captions:
      "Escribe captions más claros que apoyen tu mensaje y tu plataforma.",
    hashtags:
      "Genera hashtags relevantes para apoyar el descubrimiento y el alcance.",
    descriptions:
      "Reescribe o mejora descripciones para una claridad y un flujo más fuertes.",
    scripts:
      "Construye guiones cortos con mejor estructura y entrega.",
    ctas:
      "Crea llamadas a la acción que se sientan más claras e intencionales.",
    thumbnail:
      "Mejora el texto de miniaturas para una primera impresión más fuerte.",
    humanizer:
      "Haz que tu texto se sienta más natural, humano y fácil de leer.",
    repurpose:
      "Convierte una idea en varias salidas de contenido utilizables.",
    adCopy:
      "Crea texto promocional más preciso para ofertas y campañas.",
    carousels:
      "Convierte ideas en contenido de carrusel estructurado más rápido.",
    viralRewrite:
      "Reescribe texto débil en algo más preciso y que detenga mejor el scroll.",
    positioning:
      "Aclara cómo debe presentarse tu oferta para que se sienta más fuerte.",
    hookScore:
      "Analiza qué tan fuerte se siente tu hook antes de publicarlo.",
    trendToContent:
      "Convierte tendencias en ángulos e ideas de contenido utilizables.",
    nigerianRewrite:
      "Reescribe contenido para que se sienta más natural para una audiencia nigeriana.",
    retentionIntro:
      "Construye intros más fuertes diseñadas para retener atención antes.",
    brandVoiceTrainer:
      "Entrena los resultados para que se alineen mejor con tu voz y tono.",
    contentDifferentiation:
      "Haz que tu contenido se sienta más distinto y menos genérico.",
    commentToContent:
      "Convierte comentarios de la audiencia en direcciones de contenido más fuertes."
  }
},

plans: {
  free: "Free",
  creator: "Creator",
  pro: "Pro",
  freeBadge: "Free",
  creatorBadge: "✔ Creator",
  proBadge: "✔ Pro"
},

thumbnailTool: {
  hero: {
    badge: "Herramienta de mejora de CTR",
    title: "Texto para Thumbnail",
    description:
      "Usa esta herramienta para analizar el texto actual del thumbnail y generar alternativas más fuertes. Añade contexto del tema y del posicionamiento para que Hookora sugiera palabras para thumbnail más precisas y más clicables."
  },
  fields: {
    topic: {
      label: "Tema",
      hint:
        "Contexto opcional del tema para el analizador de thumbnails. Esto ayuda a que las sugerencias se sientan más relevantes para el contenido.",
      placeholder: "Tema opcional para el análisis del thumbnail"
    },
    thumbnailText: {
      label: "Texto del Thumbnail",
      hint:
        "Las palabras exactas del thumbnail que quieres analizar. Mantenlo corto e impactante para obtener mejores resultados.",
      placeholder: "Introduce el texto actual del thumbnail"
    },
    audience: {
      label: "Audiencia",
      hint:
        "A quién intenta atraer el thumbnail. Ejemplo: creadores, estudiantes, founders, amantes del fitness.",
      placeholder: "Audiencia"
    },
    angle: {
      label: "Ángulo",
      hint:
        "El posicionamiento o enfoque emocional. Ejemplo: impactante, premium, directo, urgente, apto para principiantes.",
      placeholder: "Ángulo"
    },
    desiredOutcome: {
      label: "Resultado Deseado",
      hint:
        "Qué resultado debería ayudar a conseguir el thumbnail. Ejemplo: clics, curiosidad, tiempo de visualización, conversiones.",
      placeholder: "Resultado deseado"
    },
    avoidPhrases: {
      label: "Frases a Evitar",
      hint:
        "Palabras o expresiones que no quieres que el analizador repita en sus sugerencias.",
      placeholder: "Frases a evitar"
    },
    extraContext: {
      label: "Contexto Extra",
      hint:
        "Añade objetivo emocional, notas de posicionamiento, estilo de la competencia o contexto extra para mejorar las sugerencias.",
      placeholder: "Contexto extra, objetivo emocional o nota de posicionamiento"
    }
  },
  analysisContext: {
    title: "Contexto del Análisis"
  },
  analysis: {
    title: "Análisis del Texto Actual"
  },
  labels: {
    score: "Puntuación",
    words: "Palabras",
    characters: "Caracteres",
    untitledTopic: "Tema sin título"
  },
  actions: {
    analyze: "Analizar Texto del Thumbnail",
    analyzing: "Analizando...",
    clearResults: "Limpiar Resultados",
    resetForm: "Restablecer Formulario",
    copy: "Copiar",
    copied: "Copiado"
  },
  errors: {
    enterTextOrTopic: "Introduce primero el texto del thumbnail o un tema.",
    analyzeFailed: "No se pudo analizar el texto del thumbnail."
  },
  suggestionsIntro:
    "Estas son alternativas sugeridas de texto para thumbnail. Elige la línea que se sienta más clara, más contundente y más clicable para tu audiencia."
},

hookAngleTool: {
  hero: {
    badge: "Herramienta de estrategia",
    title: "Ángulos de Hook",
    description: {
      before: "Usa esta herramienta para descubrir la",
      strong: "mejor dirección de enfoque",
      after:
        "antes de escribir el hook final. En lugar de dar solo líneas de apertura, Hookora devuelve ángulos estratégicos como enfoque por error, verdad oculta, contraria, urgencia o prueba, sobre los que puedes construir contenido."
    }
  },

  fields: {
    topic: {
      label: "Tema",
      hint:
        "La idea principal de contenido para la que quieres ángulos estratégicos más fuertes. Sé específico para que Hookora devuelva direcciones de posicionamiento más precisas.",
      placeholder: "¿Cuál es el tema, la oferta, la idea o el mensaje?"
    },
    platform: {
      label: "Plataforma",
      hint:
        "Elige dónde usarás el ángulo. La plataforma afecta si el enfoque debe sentirse más directo, social o educativo."
    },
    audience: {
      label: "Audiencia",
      hint:
        "Para quién es el contenido. Ejemplo: founders, creadores nigerianos, dueños de agencias, coaches, estudiantes.",
      placeholder: "Audiencia"
    },
    goal: {
      label: "Objetivo",
      hint:
        "Qué debe lograr el contenido. Ejemplo: más clics, mejor retención, comentarios más cualificados, mejores conversiones.",
      placeholder: "Objetivo"
    },
    offer: {
      label: "Oferta",
      hint:
        "El producto, servicio, lección, recurso gratuito o valor detrás del contenido.",
      placeholder: "Oferta o producto"
    },
    painPoint: {
      label: "Punto de dolor",
      hint:
        "La frustración, cuello de botella o error con el que tu audiencia está lidiando ahora mismo.",
      placeholder: "Punto de dolor"
    },
    desiredOutcome: {
      label: "Resultado deseado",
      hint:
        "El resultado que tu audiencia quiere. Ejemplo: mejor alcance, más confianza, más ingresos, contenido más fuerte.",
      placeholder: "Resultado deseado"
    },
    brandVoice: {
      label: "Voz de marca",
      hint:
        "Cómo debería sentirse el ángulo. Ejemplo: atrevido, premium, cercano, directo, agudo, divertido.",
      placeholder: "Voz de marca"
    },
    avoidPhrases: {
      label: "Evitar frases",
      hint:
        "Palabras, clichés o expresiones demasiado usadas que no quieres que Hookora utilice.",
      placeholder: "Evitar frases"
    },
    extraContext: {
      label: "Contexto extra",
      hint:
        "Añade prueba, objeciones, notas de campaña, detalles de posicionamiento, ejemplos o contexto del producto para obtener ángulos más fuertes.",
      placeholder: "Contexto extra, nota de posicionamiento, información de campaña, prueba o ejemplos"
    }
  },

  contextTitle: "Contexto estratégico",

  actions: {
    generate: "Generar ángulos de hook",
    generating: "Generando...",
    copy: "Copiar ángulo",
    copied: "Copiado"
  },

  errors: {
    missingTopic: "Primero escribe un tema.",
    generateFailed: "No se pudieron generar los ángulos de hook."
  },

  results: {
    intro: {
      before: "Estas son",
      strong: "direcciones estratégicas de ángulo",
      after:
        "Úsalas para construir tu hook final, apertura de carrusel, inicio de caption o introducción de guion."
    },
    score: "Puntuación",
    angleType: "Tipo de ángulo",
    bestUseCase: "Mejor uso",
    sampleHook: "Hook de ejemplo"
  }
},

hookTool: {
  heroBadge: "Herramienta centrada en resultados",
  title: "Hooks",
  description:
    "Usa esta herramienta cuando quieras <strong>líneas de apertura listas para usar</strong>. Empieza con tu tema, elige un estilo de hook y Hookora generará hooks directos que puedes usar para captions, videos, carruseles, títulos, intros y aperturas de publicaciones.",

  fields: {
    topic: "Tema",
    hookStyle: "Estilo de hook",
    platform: "Plataforma",
    audience: "Audiencia",
    goal: "Objetivo",
    offer: "Oferta",
    painPoint: "Punto de dolor",
    desiredOutcome: "Resultado deseado",
    angle: "Ángulo",
    ctaDirection: "Dirección del CTA",
    keywords: "Palabras clave",
    brandVoice: "Voz de marca",
    avoidPhrases: "Evitar frases",
    extraContext: "Contexto adicional"
  },

  placeholders: {
    topic: "¿Sobre qué quieres que trate el hook?",
    audience: "Audiencia",
    goal: "Objetivo",
    offer: "Oferta o producto",
    painPoint: "Punto de dolor",
    desiredOutcome: "Resultado deseado",
    angle: "Ángulo",
    ctaDirection: "Dirección del CTA",
    keywords: "Palabras clave",
    brandVoice: "Voz de marca",
    avoidPhrases: "Evitar frases",
    extraContext:
      "Contexto adicional, nota de posicionamiento, prueba, ejemplos o detalles de campaña"
  },

  hints: {
    topic:
      "La idea específica de contenido para la que quieres hooks. Los temas claros producen hooks más fuertes y más utilizables.",
    hookStyle:
      "Elige el tipo de hook que quieres que Hookora priorice: curiosidad, autoridad, problema, beneficio, controversia o historia.",
    platform:
      "Elige dónde se usará el hook. La plataforma afecta el ritmo, la claridad y la capacidad de detener el scroll.",
    audience:
      "A quién intenta detener el hook. Ejemplo: fundadores, creadores, coaches, pequeños negocios o estudiantes.",
    goal:
      "Qué debe ayudar a lograr el hook. Ejemplo: más clics, mejor retención, más comentarios o más registros.",
    offer:
      "El producto, servicio, lección o valor que está detrás del hook.",
    painPoint:
      "La frustración, problema o error exacto que el hook debe tocar.",
    desiredOutcome:
      "El resultado que tu audiencia quiere. Ejemplo: más alcance, más ventas, mejor posicionamiento o mejor contenido.",
    angle:
      "Dirección opcional para influir en el hook. Ejemplo: contrarian, verdad oculta, basado en errores, premium o emocional.",
    ctaDirection:
      "Acción opcional hacia la que quieres que conduzca el contenido. Ejemplo: comentar, guardar, hacer clic, enviar DM o registrarse.",
    keywords:
      "Palabras o frases importantes que deberían influir en el hook.",
    brandVoice:
      "Cómo debería sentirse el hook. Ejemplo: audaz, premium, cercano, afilado, juguetón o directo.",
    avoidPhrases:
      "Palabras, clichés o expresiones demasiado usadas que no quieres que Hookora utilice.",
    extraContext:
      "Añade prueba, notas de campaña, objeciones, ejemplos, detalles de posicionamiento o contexto adicional para mejorar los hooks finales."
  },

  templates: {
    curiosity: {
      label: "Curiosidad",
      description: "Crea intriga y hace que la gente quiera leer la siguiente línea."
    },
    authority: {
      label: "Autoridad",
      description: "Se siente seguro, informado y guiado por experiencia."
    },
    problem: {
      label: "Problema",
      description: "Señala rápidamente dolores, bloqueos o errores."
    },
    benefit: {
      label: "Beneficio",
      description: "Comienza con el resultado, la ganancia o la transformación."
    },
    controversy: {
      label: "Controversia",
      description: "Usa tensión, contraste o desacuerdo fuerte."
    },
    story: {
      label: "Historia",
      description: "Se siente personal, humano y emocionalmente conectado."
    }
  },

  familyFallback: "Hook",

  actions: {
    generate: "Generar hooks",
    generating: "Generando...",
    clearResults: "Borrar resultados",
    copyHook: "Copiar hook",
    copied: "Copiado"
  },

  contextSectionTitle: "Contexto del hook",

  messages: {
    topicRequired: "Por favor, introduce primero un tema.",
    generateFailed: "No se pudieron generar los hooks.",
    readyToUse:
      "Estos son <strong>hooks listos para usar</strong>. Elige el más fuerte y úsalo directamente como línea de apertura, inicio de título, intro de video, apertura de caption o primera diapositiva de carrusel.",
    score: "Puntuación"
  }
  
},

titleTool: {
  hero: {
    badge: "Herramienta enfocada en clics",
    title: "Títulos",
    description:
      "Usa esta herramienta para generar opciones de títulos atractivos para videos y piezas de contenido. Añade intención de búsqueda, ángulo y contexto para que Hookora devuelva variaciones de títulos más fuertes, más claras y con más probabilidades de captar atención."
  },

  contextSectionTitle: "Contexto del título",

  fields: {
    topic: {
      label: "Tema",
      hint:
        "El tema principal del video o de la pieza de contenido. Cuanto más claro sea tu tema, mejores serán las variaciones de títulos.",
      placeholder: "¿Para qué video o tema son estos títulos?"
    },
    audience: {
      label: "Audiencia",
      hint:
        "Para quién es el contenido. Ejemplo: estudiantes, creadores, fundadores de ecommerce, freelancers nigerianos.",
      placeholder: "Audiencia"
    },
    goal: {
      label: "Objetivo",
      hint:
        "Qué debería optimizar el título. Ejemplo: clics, curiosidad, tráfico de búsqueda, autoridad, conversiones.",
      placeholder: "Objetivo"
    },
    angle: {
      label: "Ángulo",
      hint:
        "El enfoque del título. Ejemplo: centrado en errores, para principiantes, contrarian, orientado a resultados.",
      placeholder: "Ángulo"
    },
    keywords: {
      label: "Palabras clave",
      hint:
        "Términos de búsqueda importantes que deberían integrarse de forma natural en los títulos. Sepáralos con comas.",
      placeholder: "Palabras clave (separadas por comas)"
    },
    brandVoice: {
      label: "Voz de marca",
      hint:
        "Cómo debería sentirse el título. Ejemplo: directo, premium, claro, audaz, juguetón.",
      placeholder: "Voz de marca"
    },
    avoidPhrases: {
      label: "Frases a evitar",
      hint:
        "Palabras o patrones repetidos que no quieres que el generador de títulos use.",
      placeholder: "Frases a evitar"
    },
    extraContext: {
      label: "Contexto extra",
      hint:
        "Añade pruebas, resultados específicos, emoción objetivo o notas extra de posicionamiento para mejorar la calidad de los títulos.",
      placeholder: "Contexto extra, prueba única, resultado, ejemplo o nota de posicionamiento"
    }
  },

  actions: {
    generate: "Generar títulos",
    generating: "Generando...",
    clearResults: "Limpiar resultados",
    resetForm: "Restablecer formulario",
    copy: "Copiar",
    copied: "Copiado"
  },

  labels: {
    score: "Puntuación"
  },

  errors: {
    topicRequired: "Introduce primero un tema.",
    generateFailed: "No se pudieron generar los títulos."
  },

  resultsIntro:
    "Estas son opciones de títulos listas para probar. Elige la más fuerte según su capacidad de atraer clics, claridad y ajuste para tu audiencia.",

  fallbackUntitledTopic: "Tema sin título"
},

captionTool: {
  hero: {
    badge: "Herramienta de escritura social",
    title: "Captions",
    description:
      "Usa esta herramienta para generar captions listos para publicar en contenido social. Añade plataforma, tono, dirección del CTA y contexto extra para que Hookora cree opciones de captions más fuertes y más relevantes para tu publicación o video."
  },

  contextSectionTitle: "Contexto del caption",

  fields: {
    topic: {
      label: "Tema",
      hint:
        "El tema principal del caption. Sé claro sobre de qué trata la publicación o el video.",
      placeholder: "¿De qué trata el caption?"
    },
    platform: {
      label: "Plataforma",
      hint:
        "Elige dónde se publicará el caption. La plataforma afecta el tono, el ritmo y el estilo del CTA."
    },
    tone: {
      label: "Tono",
      hint:
        "Cómo debería sonar el caption. Ejemplo: atractivo, audaz, juguetón, educativo, premium, directo.",
      placeholder: "Tono"
    },
    audience: {
      label: "Audiencia",
      hint:
        "Para quién es el caption. Ejemplo: marcas de skincare, creadores, fundadores de startups, estudiantes nigerianos.",
      placeholder: "Audiencia"
    },
    goal: {
      label: "Objetivo",
      hint:
        "Qué quieres que logre el caption. Ejemplo: más guardados, comentarios, clics, registros o compartidos.",
      placeholder: "Objetivo"
    },
    offer: {
      label: "Oferta",
      hint:
        "El producto, servicio, propuesta de valor, recurso gratuito o mensaje detrás del contenido.",
      placeholder: "Oferta o producto"
    },
    painPoint: {
      label: "Punto de dolor",
      hint:
        "El problema o frustración con el que está lidiando tu audiencia.",
      placeholder: "Punto de dolor"
    },
    desiredOutcome: {
      label: "Resultado deseado",
      hint:
        "El resultado que tu audiencia quiere. Ejemplo: más engagement, más leads, mejor contenido, más ventas.",
      placeholder: "Resultado deseado"
    },
    angle: {
      label: "Ángulo",
      hint:
        "La perspectiva o enfoque. Ejemplo: para principiantes, urgente, basado en historia, rompe-mitos, premium.",
      placeholder: "Ángulo"
    },
    callToAction: {
      label: "CTA",
      hint:
        "La acción que quieres que la gente realice. Ejemplo: comentar, compartir, guardar, seguir, hacer clic en el enlace.",
      placeholder: "CTA"
    },
    keywords: {
      label: "Palabras clave",
      hint:
        "Palabras importantes de búsqueda o del tema para incluir de forma natural. Sepáralas con comas.",
      placeholder: "Palabras clave"
    },
    brandVoice: {
      label: "Voz de marca",
      hint:
        "Cómo debería sentirse el caption. Ejemplo: directo, ingenioso, experto, juguetón, premium.",
      placeholder: "Voz de marca"
    },
    avoidPhrases: {
      label: "Frases a evitar",
      hint:
        "Palabras repetidas, clichés o frases que no quieres en el caption.",
      placeholder: "Frases a evitar"
    },
    extraContext: {
      label: "Contexto extra",
      hint:
        "Añade ejemplos, detalles del producto, notas de campaña, pruebas o contexto adicional para mejorar el resultado.",
      placeholder: "Contexto extra, nota de campaña, prueba o ángulo del creador"
    }
  },

  actions: {
    generate: "Generar captions",
    generating: "Generando...",
    clearResults: "Limpiar resultados",
    resetForm: "Restablecer formulario",
    copy: "Copiar",
    copied: "Copiado"
  },

  labels: {
    score: "Puntuación"
  },

  errors: {
    topicRequired: "Introduce primero un tema.",
    generateFailed: "No se pudieron generar los captions."
  },

  resultsIntro:
    "Estas son opciones de captions listas para publicar. Elige la que mejor coincida con tu plataforma, tono y objetivo de conversión.",

  fallbackUntitledTopic: "Tema sin título"
},

studioTools: {
  hashtagTool: {
    hero: {
      badge: "Herramienta de descubrimiento",
      title: "Hashtags",
      description:
        "Usa esta herramienta para crear conjuntos estratégicos de hashtags para diferentes objetivos de contenido. Añade plataforma, audiencia, tipo de contenido y contexto extra para que Hookora devuelva mejores combinaciones de hashtags para descubrimiento, relevancia e interacción."
    },
    contextTitle: "Contexto de hashtags",
    fields: {
      topic: {
        label: "Tema",
        hint:
          "El tema, nicho o campaña que deben apoyar los conjuntos de hashtags.",
        placeholder: "¿Para qué es este conjunto de hashtags?"
      },
      platform: {
        label: "Plataforma",
        hint:
          "Elige la plataforma porque el comportamiento de los hashtags cambia según la plataforma."
      },
      contentType: {
        label: "Tipo de contenido",
        hint:
          "El tipo de publicación o contenido. Ejemplo: tutorial, demo de producto, publicación educativa, historia personal, carrusel.",
        placeholder: "Tipo de contenido"
      },
      audience: {
        label: "Audiencia",
        hint:
          "A quién quieres atraer con estos hashtags. Ejemplo: amantes de la belleza, fundadores de startups, creadores estudiantes.",
        placeholder: "Audiencia"
      },
      goal: {
        label: "Objetivo",
        hint:
          "Qué quieres que logre el conjunto de hashtags. Ejemplo: descubrimiento, relevancia, interacción, alcance de nicho.",
        placeholder: "Objetivo"
      },
      keywords: {
        label: "Palabras clave",
        hint:
          "Palabras importantes del tema que deben reflejarse en los hashtags. Sepáralas con comas.",
        placeholder: "Palabras clave"
      },
      brandVoice: {
        label: "Voz de marca",
        hint:
          "La vibra de la marca o del contenido. Ayuda a mantener los conjuntos de hashtags alineados con el estilo del contenido.",
        placeholder: "Voz de marca"
      },
      extraContext: {
        label: "Contexto extra",
        hint:
          "Añade detalles del subtema, contexto de campaña, detalles de la audiencia o notas de nicho para mejorar los conjuntos.",
        placeholder: "Contexto extra, nicho, subtema o notas de campaña"
      }
    },
    actions: {
      generating: "Generando...",
      generate: "Generar conjuntos de hashtags",
      clearResults: "Borrar resultados",
      resetForm: "Restablecer formulario",
      copy: "Copiar",
      copied: "Copiado"
    },
    errors: {
      enterTopic: "Por favor, introduce primero un tema.",
      generateFailed: "No se pudieron generar los hashtags."
    },
    resultsHelper:
      "Estos son conjuntos de hashtags listos para usar. Elige el que mejor coincida con el objetivo de tu contenido y el comportamiento de la plataforma.",
    scoreLabel: "Puntuación",
    assetTitlePrefix: "Conjunto de hashtags",
    untitledTopic: "Tema sin título"
  },

  aiHumanizerTool: {
  hero: {
    badge: "Herramienta de pulido de reescritura",
    title: "Humanizador de IA",
    description:
      "Usa esta herramienta para convertir texto rígido, robótico o demasiado generado por IA en una escritura que se sienta más natural, creíble y humana. Ajusta el tono, la intensidad y la configuración de reescritura para obtener una versión que se sienta adecuada para la plataforma."
  },
  fields: {
    originalText: {
      label: "Texto original",
      hint:
        "Pega el texto escrito por IA o rígido que quieres hacer más humano, natural y creíble.",
      placeholder: "Pega el texto original que quieres humanizar"
    },
    platform: {
      label: "Plataforma",
      hint:
        "Elige dónde se usará la reescritura. Esto afecta el ritmo, la redacción y el estilo."
    },
    tone: {
      label: "Tono",
      hint: "Elige la personalidad de la reescritura."
    },
    audience: {
      label: "Audiencia",
      hint:
        "Para quién es el texto reescrito. Esto ayuda a que el resultado se sienta más relevante y humano.",
      placeholder: "Audiencia"
    },
    humanizationStrength: {
      label: "Intensidad de humanización",
      hint:
        "Ligera mantiene la estructura cercana. Equilibrada mejora el flujo. Fuerte reescribe con más decisión mientras conserva el mensaje central."
    },
    preserveOriginalMeaning: {
      label: "Conservar el significado original"
    },
    styleNotes: {
      label: "Notas de estilo",
      hint:
        "Añade indicaciones como frases más cortas, tono más directo, menos formalidad, más emoción o menos palabras vacías.",
      placeholder: "Notas de estilo o guía para la reescritura"
    }
  },
  sections: {
    rewriteSettings: "Configuración de reescritura"
  },
  actions: {
    humanizing: "Humanizando...",
    humanizeText: "Humanizar texto",
    clearResults: "Borrar resultados",
    resetForm: "Restablecer formulario",
    copied: "Copiado",
    copy: "Copiar"
  },
  labels: {
    alternative: "Alternativa {{number}}",
    whyItFeelsMoreHuman: "Por qué se siente más humano",
    score: "Puntuación"
  },
  errors: {
    originalTextRequired: "Primero introduce el texto original.",
    generateFailed: "No se pudo humanizar el texto."
  },
  resultsIntro:
    "Estas son opciones de reescritura humanizada. Elige la que se sienta más natural, más creíble y más cercana a la voz que buscas.",
  platformOptions: {
    General: "General",
    Instagram: "Instagram",
    TikTok: "TikTok",
    X: "X",
    LinkedIn: "LinkedIn",
    YouTube: "YouTube",
    WhatsApp: "WhatsApp"
  },
  toneOptions: {
    Natural: "Natural",
    Conversational: "Conversacional",
    Bold: "Atrevido",
    Professional: "Profesional",
    Warm: "Cálido",
    Confident: "Seguro",
    Persuasive: "Persuasivo",
    "Story-driven": "Narrativo"
  },
  strengthOptions: {
    Light: "Ligera",
    Balanced: "Equilibrada",
    Strong: "Fuerte"
  }
},

retentionIntroTool: {
  hero: {
    badge: "Intro de retención",
    title: "Genera aperturas iniciales más fuertes",
    description:
      "Crea intros diseñadas para retener atención temprano con un hook fuerte, sensación de adelanto, un tema más profundo y una razón clara para seguir viendo."
  },
  fields: {
    topic: {
      label: "Tema",
      placeholder: "ej. por qué la mayoría de los creadores siguen estancados",
      hint:
        "Esta herramienta funciona mejor cuando el tema es claro y específico."
    },
    platform: {
      label: "Plataforma",
      hint:
        "El contexto de la plataforma ayuda a definir el ritmo y la estructura de la intro."
    },
    contentType: {
      label: "Tipo de contenido",
      hint:
        "Elige el tipo de contenido para el que quieres construir la apertura."
    },
    tone: {
      label: "Tono",
      hint:
        "El tono influye en la carga emocional y en la voz de la intro."
    },
    targetAudience: {
      label: "Audiencia objetivo",
      placeholder: "ej. creadores principiantes, fundadores, estudiantes",
      hint:
        "El contexto opcional de audiencia ayuda a que la intro se sienta más dirigida."
    },
    goal: {
      label: "Objetivo",
      placeholder: "ej. aumentar tiempo de visualización, mejorar retención, despertar curiosidad",
      hint:
        "Opcional. Útil cuando quieres optimizar la intro para un resultado específico."
    },
    extraContext: {
      label: "Contexto adicional",
      placeholder:
        "Describe la idea del contenido, el ángulo de la historia, la lección, el producto o la dirección emocional...",
      hint:
        "Añade contexto cuando quieras que la apertura se sienta más precisa y personalizada."
    }
  },
  actions: {
    generating: "Generando...",
    generateIntro: "Generar intro",
    clearResults: "Borrar resultados",
    reset: "Restablecer",
    copy: "Copiar",
    copied: "Copiado"
  },
  messages: {
    topicRequired:
      "Introduce un tema antes de generar una intro de retención.",
    generateFailed:
      "Algo salió mal al generar la intro de retención.",
    copyFailed:
      "No se pudo copiar la intro ahora mismo. Inténtalo de nuevo."
  },
  tip:
    "Consejo: las buenas intros de retención suelen crear tensión rápido, mostrar valor desde el principio y hacer que el espectador sienta que no debe seguir deslizando.",
  output: {
    resultTitle: "Resultado de la intro de retención",
    retentionIntro: "Intro de retención",
    previewOpeningVisual: "Adelanto / Visual de apertura",
    valuePromise: "Promesa de valor",
    deeperTheme: "Tema más profundo",
    notes: "Notas",
    alternativeVersions: "Versiones alternativas",
    rawResult: "Resultado en bruto"
  },
  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "General"
  },
  contentTypeOptions: {
    short_video: "Video corto",
    long_video: "Video largo",
    ad: "Anuncio",
    ugc: "UGC",
    educational: "Educativo",
    storytelling: "Storytelling"
  },
  toneOptions: {
    bold: "Atrevido",
    natural: "Natural",
    dramatic: "Dramático",
    confident: "Seguro",
    curious: "Curioso",
    urgent: "Urgente"
  }
},

adCopyTool: {
  title: "Generador de texto publicitario",
  description:
    "Crea texto principal, titulares, descripciones y CTAs listos para anuncios para tu oferta o campaña.",
  actions: {
    generate: "Generar texto publicitario",
    generating: "Generando...",
    reset: "Restablecer",
    copy: "Copiar",
    copied: "¡Copiado!"
  },
  messages: {
    topicRequired: "Introduce primero un tema.",
    generateFailed: "No se pudo generar el texto publicitario.",
    copyFailed: "No se pudo copiar el resultado."
  },
  fields: {
    topic: {
      label: "Tema",
      placeholder: "ej. Herramienta de contenido para redes sociales para creadores",
      help:
        "Usa el producto, la oferta o el enfoque de campaña sobre el que quieres construir los anuncios."
    },
    platform: {
      label: "Plataforma",
      placeholder: "ej. Facebook",
      help: "Elige primero la plataforma donde se publicará el anuncio."
    },
    tone: {
      label: "Tono"
    },
    language: {
      label: "Idioma"
    },
    audience: {
      label: "Audiencia",
      placeholder: "ej. Dueños de pequeños negocios"
    },
    goal: {
      label: "Objetivo",
      placeholder: "ej. Conseguir registros"
    },
    offer: {
      label: "Oferta",
      placeholder: "ej. Prueba gratuita para usuarios nuevos"
    },
    painPoint: {
      label: "Punto de dolor",
      placeholder: "ej. Texto débil y baja interacción"
    },
    desiredOutcome: {
      label: "Resultado deseado",
      placeholder: "ej. Creación de contenido más rápida y mejores conversiones"
    },
    angle: {
      label: "Ángulo",
      placeholder: "ej. Mejor texto sin perder tiempo"
    },
    callToAction: {
      label: "Llamado a la acción",
      placeholder: "ej. Empieza tu prueba gratuita"
    },
    brandVoice: {
      label: "Voz de marca",
      placeholder: "ej. Clara, precisa, moderna"
    },
    keywords: {
      label: "Palabras clave",
      placeholder: "ej. copywriting con IA, anuncios para redes sociales"
    },
    avoidPhrases: {
      label: "Evitar frases",
      placeholder: "ej. cambia las reglas del juego, truco secreto"
    },
    extraContext: {
      label: "Contexto adicional",
      placeholder:
        "Nota opcional de campaña, contexto de precio, objeciones de la audiencia, mensaje de landing page o dirección extra..."
    }
  },
  toneOptions: {
    Persuasive: "Persuasivo",
    Bold: "Atrevido",
    Professional: "Profesional",
    Confident: "Seguro",
    Warm: "Cálido",
    Conversational: "Conversacional",
    Direct: "Directo"
  },
  languageOptions: {
    english: "Inglés",
    spanish: "Español",
    french: "Francés",
    portuguese: "Portugués"
  },
  results: {
    title: "Variantes del anuncio",
    description:
      "Revisa cada variante, copia la más fuerte y guarda los mejores anuncios en tu biblioteca.",
    empty:
      "Todavía no hay texto publicitario. Genera un lote para ver aquí tus variantes de anuncios.",
    variantFallback: "Variante de anuncio",
    score: "Puntuación"
  },
  output: {
    primaryText: "Texto principal",
    headline: "Titular",
    description: "Descripción",
    cta: "CTA"
  }
},

brandVoiceTrainerTool: {
  badge: "Entrenador de voz de marca",
  title: "Entrena una voz de marca más consistente",
  description:
    "Convierte la descripción de tu marca, tus preferencias de tono y tu muestra de escritura en un perfil de voz útil con reglas de mensaje más claras y una consistencia más fuerte.",
  resultTitle: "Resultado de voz de marca",
  tip:
    "Consejo: la voz de marca mejora cuando tu descripción incluye audiencia, valores y cómo quieres que se sienta la gente después de leer tu contenido.",
  actions: {
    generate: "Entrenar voz de marca",
    generating: "Generando...",
    clearResults: "Borrar resultados",
    reset: "Restablecer",
    copy: "Copiar",
    copied: "Copiado"
  },
  messages: {
    brandDescriptionRequired:
      "Describe primero la marca antes de generar un perfil de voz.",
    generateFailed:
      "Algo salió mal al generar el resultado de voz de marca.",
    copyFailed:
      "No se pudo copiar el resultado de voz de marca en este momento. Inténtalo de nuevo."
  },
  fields: {
    topic: {
      label: "Tema",
      placeholder: "ej. herramientas para creadores, cuidado de la piel, educación de negocios",
      hint:
        "El tema compartido ayuda a mantener alineado tu flujo de Studio entre herramientas."
    },
    platform: {
      label: "Plataforma",
      hint:
        "La plataforma puede influir en el estilo de voz y en las recomendaciones de entrega."
    },
    outputType: {
      label: "Tipo de salida",
      hint:
        "Elige si quieres un perfil de voz, reglas o un ejemplo de salida."
    },
    targetAudience: {
      label: "Audiencia objetivo",
      placeholder: "ej. fundadores, mujeres de 20-35, creadores nigerianos",
      hint:
        "El contexto de audiencia ayuda a dar forma a cómo debe sonar la voz."
    },
    preferredToneWords: {
      label: "Palabras de tono preferidas",
      placeholder: "ej. atrevida, humana, precisa, cálida",
      hint:
        "Agrega palabras que describan cómo debe sentirse tu marca."
    },
    avoidWords: {
      label: "Evitar estas palabras / estilos",
      placeholder: "ej. robótico, demasiado formal, exceso de jerga, agresivo",
      hint:
        "Opcional. Ayuda a Hookora a evitar el tono equivocado."
    },
    goal: {
      label: "Objetivo",
      placeholder: "ej. sonar más premium, más humana, más consistente",
      hint:
        "Opcional. Le da a la herramienta una dirección más clara."
    },
    brandDescription: {
      label: "Descripción de la marca",
      placeholder:
        "Describe qué hace tu marca, qué representa y cómo debería ser percibida...",
      hint:
        "Este es el campo más importante. Sé lo más claro posible."
    },
    sampleWriting: {
      label: "Muestra de escritura",
      placeholder:
        "Pega un ejemplo de cómo escribe actualmente tu marca, si tienes uno...",
      hint:
        "Opcional, pero muy útil si quieres que la salida refleje tu estilo actual."
    }
  },
  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    xTwitter: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "General"
  },
  outputTypeOptions: {
    voiceProfile: "Perfil de voz",
    rewriteSample: "Ejemplo de reescritura",
    brandGuidelines: "Guías de marca",
    dosAndDonts: "Qué hacer y qué no hacer"
  },
  sections: {
    brandVoiceProfile: "Perfil de voz de marca",
    summary: "Resumen",
    voicePillars: "Pilares de voz",
    dos: "Qué hacer",
    donts: "Qué no hacer",
    sampleRewrite: "Ejemplo de reescritura",
    notes: "Notas",
    rawResult: "Resultado sin procesar"
  }
},

carouselGeneratorTool: {
  title: "Generador de carruseles",
  description:
    "Crea conceptos de carrusel estructurados con una diapositiva inicial fuerte, una progresión clara y un CTA final.",
  actions: {
    generate: "Generar diapositivas de carrusel",
    generating: "Generando...",
    reset: "Restablecer",
    copy: "Copiar",
    copied: "¡Copiado!"
  },
  messages: {
    topicRequired: "Por favor, introduce primero un tema.",
    generateFailed: "No se pudieron generar las diapositivas del carrusel.",
    copyFailed: "No se pudo copiar el carrusel."
  },
  fields: {
    topic: {
      label: "Tema",
      placeholder: "ej. Por qué las publicaciones en redes rinden poco",
      hint:
        "Usa la lección principal, el marco o el problema que debe cubrir el carrusel."
    },
    platform: {
      label: "Plataforma",
      hint:
        "Se usa mejor para carruseles en Instagram, LinkedIn o Facebook."
    },
    tone: {
      label: "Tono"
    },
    style: {
      label: "Estilo"
    },
    language: {
      label: "Idioma"
    },
    audience: {
      label: "Audiencia",
      placeholder: "ej. Coaches y creadores"
    },
    goal: {
      label: "Objetivo",
      placeholder: "ej. Más guardados y compartidos"
    },
    offer: {
      label: "Oferta",
      placeholder: "ej. Auditoría o guía gratuita"
    },
    painPoint: {
      label: "Punto de dolor",
      placeholder: "ej. Hooks débiles y bajo engagement"
    },
    desiredOutcome: {
      label: "Resultado deseado",
      placeholder: "ej. Mensajes más claros y publicaciones más fuertes"
    },
    angle: {
      label: "Ángulo",
      placeholder: "ej. Errores que comete la mayoría"
    },
    callToAction: {
      label: "Llamado a la acción",
      placeholder: "ej. Guarda esto y sigue la cuenta"
    },
    brandVoice: {
      label: "Voz de marca",
      placeholder: "ej. Inteligente, clara, práctica"
    },
    keywords: {
      label: "Palabras clave",
      placeholder: "ej. estrategia de contenido, crecimiento en redes sociales"
    },
    avoidPhrases: {
      label: "Evitar frases",
      placeholder: "ej. cambia el juego, truco secreto"
    },
    extraContext: {
      label: "Contexto extra",
      placeholder:
        "Nota opcional de campaña, idea de estructura, dirección de diapositivas o contexto de audiencia..."
    }
  },
  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "General"
  },
  styleOptions: {
    educationalBreakdown: "Desglose educativo",
    mistakeSeries: "Serie de errores",
    frameworkSeries: "Serie de marcos"
  },
  toneOptions: {
    educational: "Educativo",
    bold: "Atrevido",
    conversational: "Conversacional",
    professional: "Profesional",
    persuasive: "Persuasivo",
    warm: "Cálido",
    confident: "Seguro"
  },
  languageOptions: {
    english: "Inglés",
    spanish: "Español",
    french: "Francés",
    portuguese: "Portugués"
  },
  results: {
    title: "Conceptos de carrusel",
    description:
      "Cada concepto incluye una diapositiva gancho, desarrollo, diapositiva final y caption.",
    empty:
      "Todavía no hay conceptos de carrusel. Genera un lote para verlos aquí."
  },
  resultLabels: {
    carousel: "Carrusel",
    slide: "Diapositiva",
    hookSlide: "Diapositiva gancho",
    closingSlide: "Diapositiva final",
    caption: "Caption",
    score: "Puntuación"
  }
},

commentToContentTool: {
  badge: "Comentario a contenido",
  title: "Convierte comentarios de la audiencia en contenido útil",
  description:
    "Transforma un comentario, pregunta o reacción de la audiencia en una respuesta, hook, caption, ángulo de guion o una nueva idea de contenido que realmente puedas usar.",
  tip:
    "Consejo: los buenos comentarios de la audiencia suelen revelar ángulos ocultos de contenido, objeciones y temas que vale la pena convertir en publicaciones.",
  actions: {
    generate: "Generar desde comentario",
    generating: "Generando...",
    clearResults: "Borrar resultados",
    reset: "Restablecer",
    copy: "Copiar",
    copied: "Copiado"
  },
  messages: {
    commentRequired: "Pega primero el comentario antes de generar contenido.",
    generateFailed: "Algo salió mal al convertir el comentario en contenido.",
    copyFailed: "No se pudo copiar el resultado generado en este momento. Inténtalo de nuevo."
  },
  fields: {
    topic: {
      label: "Tema",
      placeholder: "ej. hooks, fitness, crecimiento de negocio",
      hint: "El tema compartido ayuda a conectar este resultado con el resto de tu flujo en Studio."
    },
    platform: {
      label: "Plataforma",
      hint: "El contexto de la plataforma ayuda a Hookora a dar la forma adecuada al resultado."
    },
    outputType: {
      label: "Tipo de resultado",
      hint: "Elige si quieres una respuesta, idea de contenido, caption o un resultado tipo guion."
    },
    tone: {
      label: "Tono",
      hint: "El tono define cómo se siente el resultado final."
    },
    targetAudience: {
      label: "Audiencia objetivo",
      placeholder: "ej. creadores principiantes, fundadores, clientes de belleza",
      hint: "El contexto opcional de audiencia ayuda a mejorar la relevancia."
    },
    goal: {
      label: "Objetivo",
      placeholder: "ej. aumentar engagement, responder con claridad, convertirlo en publicación",
      hint: "Opcional. Úsalo cuando quieras orientar el resultado a un objetivo específico."
    },
    comment: {
      label: "Comentario",
      placeholder: "Pega aquí el comentario, DM, respuesta o pregunta de la audiencia...",
      hint: "Puede ser una pregunta, objeción, cumplido, crítica o reacción interesante."
    },
    extraContext: {
      label: "Contexto extra",
      placeholder:
        "Añade contexto sobre la publicación original, tu marca, tu producto o en qué quieres convertir el comentario...",
      hint: "Opcional, pero útil cuando quieres un contenido más personalizado."
    }
  },
  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "General"
  },
  outputTypeOptions: {
    reply: "Respuesta",
    hook: "Hook",
    caption: "Caption",
    shortScript: "Guion corto",
    contentIdea: "Idea de contenido",
    carouselAngle: "Ángulo de carrusel"
  },
  toneOptions: {
    natural: "Natural",
    friendly: "Amigable",
    bold: "Atrevido",
    educational: "Educativo",
    sales: "Ventas",
    professional: "Profesional"
  },
  results: {
    title: "Resultado generado"
  },
  resultLabels: {
    output: "Resultado",
    angle: "Ángulo",
    summary: "Resumen",
    ideas: "Ideas",
    notes: "Notas",
    rawResult: "Resultado sin procesar"
  }
},

contentDifferentiationTool: {
  badge: "Diferenciación de contenido",
  title: "Encuentra un ángulo más fuerte en espacios de contenido saturados",
  description:
    "Analiza tu nicho, audiencia y estilo de competidores para descubrir un mejor posicionamiento, una diferenciación más clara y ángulos de contenido que destaquen.",
  tip:
    "Consejo: cuanto más claramente expliques tu mercado y cómo suenan los demás, mejor podrá Hookora ayudarte a destacar.",
  actions: {
    generate: "Diferenciar contenido",
    generating: "Generando...",
    clearResults: "Borrar resultados",
    reset: "Restablecer",
    copy: "Copiar",
    copied: "Copiado"
  },
  messages: {
    topicOrContextRequired:
      "Introduce un tema o contexto de marca antes de generar ideas de diferenciación.",
    generateFailed:
      "Algo salió mal al generar el resultado de diferenciación.",
    copyFailed:
      "No se pudo copiar el resultado de diferenciación en este momento. Inténtalo de nuevo."
  },
  fields: {
    topic: {
      label: "Tema",
      placeholder: "ej. finanzas personales, fitness, herramientas de IA",
      hint:
        "El tema compartido ayuda a mantener esta herramienta alineada con el resto de tu flujo en Studio."
    },
    platform: {
      label: "Plataforma",
      hint:
        "El contexto de plataforma ayuda a Hookora a adaptar las ideas de diferenciación al formato correcto."
    },
    contentType: {
      label: "Tipo de contenido",
      hint:
        "Elige el tipo de contenido para el que quieres un mejor posicionamiento."
    },
    targetAudience: {
      label: "Audiencia objetivo",
      placeholder: "ej. creadores principiantes, fundadores, estudiantes",
      hint:
        "El contexto de audiencia ayuda a descubrir mejores ángulos y vacíos de contenido."
    },
    goal: {
      label: "Objetivo",
      placeholder: "ej. destacar, sonar más inteligente, conseguir más guardados",
      hint:
        "Opcional. Úsalo cuando quieras que el resultado se enfoque en una meta clara."
    },
    brandContext: {
      label: "Contexto de marca",
      placeholder:
        "Describe tu marca, oferta, perspectiva, fortalezas y lo que te hace diferente...",
      hint:
        "Esto le da a Hookora el contexto necesario para encontrar un posicionamiento más fuerte."
    },
    competitorStyle: {
      label: "Estilo de competidores / patrón del mercado",
      placeholder:
        "Describe cómo suelen hablar, vender o crear contenido los demás en tu nicho...",
      hint:
        "Opcional, pero muy útil cuando quieres que Hookora encuentre un punto de diferencia más claro."
    },
    currentContent: {
      label: "Contenido o mensaje actual",
      placeholder:
        "Pega tu hook, caption, guion o mensaje actual si quieres que Hookora lo mejore...",
      hint:
        "Opcional. Úsalo si quieres sugerencias de diferenciación más prácticas y ligadas a contenido existente."
    }
  },
  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "General"
  },
  contentTypeOptions: {
    hooks: "Hooks",
    captions: "Captions",
    scripts: "Guiones",
    carousels: "Carruseles",
    ads: "Anuncios",
    generalContent: "Contenido general"
  },
  results: {
    title: "Resultado de diferenciación"
  },
  resultLabels: {
    differentiationAngle: "Ángulo de diferenciación",
    summary: "Resumen",
    marketGaps: "Vacíos del mercado",
    uniqueStrengths: "Fortalezas únicas",
    differentiationIdeas: "Ideas de diferenciación",
    avoid: "Evitar",
    sampleOutput: "Ejemplo de resultado",
    rawResult: "Resultado sin procesar"
  }
},

contentRepurposerTool: {
  title: "Reutilizador de contenido",
  description:
    "Convierte una sola pieza de origen en múltiples versiones listas para plataforma que puedas publicar, probar y guardar.",
  actions: {
    generate: "Generar versiones reutilizadas",
    generating: "Reutilizando...",
    reset: "Restablecer",
    copy: "Copiar",
    copied: "¡Copiado!"
  },
  messages: {
    sourceRequired: "Primero pega o escribe tu texto de origen.",
    generateFailed: "No se pudieron generar las versiones reutilizadas.",
    copyFailed: "No se pudo copiar el resultado."
  },
  fields: {
    topic: {
      label: "Tema",
      placeholder: "ej. Marca personal para creadores",
      hint:
        "Compartido entre herramientas de Studio para que puedas mantener la misma idea de trabajo."
    },
    platform: {
      label: "Plataforma",
      placeholder: "ej. LinkedIn",
      hint:
        "Usa la plataforma principal de destino para este lote reutilizado."
    },
    sourceText: {
      label: "Texto de origen",
      placeholder:
        "Pega aquí la publicación original, caption, hilo, guion, email o borrador...",
      hint:
        "Pega la pieza original que quieres transformar en distintas versiones listas para plataforma."
    },
    audience: {
      label: "Audiencia",
      placeholder: "ej. Dueños de pequeños negocios"
    },
    goal: {
      label: "Objetivo",
      placeholder: "ej. Más interacción y guardados"
    },
    tone: {
      label: "Tono"
    },
    language: {
      label: "Idioma"
    },
    brandVoice: {
      label: "Voz de marca",
      placeholder: "ej. Clara, aguda, confiable"
    },
    callToAction: {
      label: "Llamado a la acción",
      placeholder: "ej. Comenta o guarda esta publicación"
    },
    keywords: {
      label: "Palabras clave",
      placeholder: "ej. crecimiento en redes, estrategia de contenido"
    },
    avoidPhrases: {
      label: "Evitar frases",
      placeholder: "ej. cambia el juego, truco secreto"
    },
    extraContext: {
      label: "Contexto extra",
      placeholder:
        "Instrucción opcional extra, contexto de campaña, detalles de la oferta, dolores de la audiencia o nota de plataforma..."
    }
  },
  toneOptions: {
    natural: "Natural",
    conversational: "Conversacional",
    bold: "Audaz",
    professional: "Profesional",
    warm: "Cálido",
    confident: "Seguro",
    persuasive: "Persuasivo"
  },
  languageOptions: {
    en: "Inglés",
    es: "Español",
    fr: "Francés",
    pt: "Portugués"
  },
  results: {
    title: "Versiones reutilizadas",
    description:
      "Tus variaciones generadas aparecerán aquí. Copia las más fuertes y guarda las mejores versiones en tu biblioteca.",
    empty:
      "Todavía no hay resultados. Genera un lote para ver aquí tu contenido reutilizado.",
    outputFallback: "Resultado {{number}}",
    score: "Puntuación: {{score}}",
    whyItWorks: "Por qué funciona:"
  }
},

viralRewriterTool: {
  title: "Reescritor viral",
  description:
    "Reescribe textos débiles o promedio en versiones más fuertes, más llamativas y más listas para volverse virales.",
  actions: {
    generate: "Generar reescrituras virales",
    generating: "Reescribiendo...",
    reset: "Restablecer",
    copy: "Copiar",
    copied: "¡Copiado!"
  },
  messages: {
    sourceRequired: "Pega primero el texto de origen que quieres reescribir.",
    generateFailed: "No se pudieron generar las reescrituras virales.",
    copyFailed: "No se pudo copiar el resultado."
  },
  fields: {
    topic: {
      label: "Tema",
      placeholder: "ej. Productividad para creadores",
      hint:
        "Opcional, pero útil para mantener la reescritura bien enfocada en el tema correcto."
    },
    platform: {
      label: "Plataforma",
      placeholder: "ej. Instagram Reels"
    },
    tone: {
      label: "Tono"
    },
    language: {
      label: "Idioma"
    },
    sourceText: {
      label: "Texto de origen",
      placeholder:
        "Pega aquí el caption, publicación, hook, hilo o borrador original que quieres reescribir...",
      hint:
        "Pega aquí la versión original. La herramienta generará reescrituras más fuertes a partir de ella."
    },
    audience: {
      label: "Audiencia",
      placeholder: "ej. Dueños de pequeños negocios"
    },
    goal: {
      label: "Objetivo",
      placeholder: "ej. Más compartidos e interacción"
    },
    brandVoice: {
      label: "Voz de marca",
      placeholder: "ej. Clara, impactante, moderna"
    },
    callToAction: {
      label: "Llamado a la acción",
      placeholder: "ej. Comenta lo que piensas"
    },
    keywords: {
      label: "Palabras clave",
      placeholder: "ej. crecimiento de creadores, estrategia de contenido"
    },
    avoidPhrases: {
      label: "Evitar frases",
      placeholder: "ej. cambia el juego, truco secreto"
    },
    extraContext: {
      label: "Contexto extra",
      placeholder:
        "Dolor opcional de la audiencia, emoción deseada, nota de campaña o instrucción adicional..."
    }
  },
  toneOptions: {
    bold: "Audaz",
    conversational: "Conversacional",
    confident: "Seguro",
    persuasive: "Persuasivo",
    professional: "Profesional",
    warm: "Cálido",
    natural: "Natural"
  },
  languageOptions: {
    en: "Inglés",
    es: "Español",
    fr: "Francés",
    pt: "Portugués"
  },
  results: {
    title: "Variantes reescritas",
    description:
      "Revisa las versiones reescritas, copia la más fuerte y guarda la mejor salida en tu biblioteca.",
    empty: "Todavía no hay reescrituras. Genera un lote para verlas aquí.",
    rewriteFallback: "Reescritura {{number}}",
    score: "Puntuación: {{score}}",
    whyItWorks: "Por qué funciona:"
  }
},

offerPositioningTool: {
  title: "Herramienta de posicionamiento de oferta",
  description:
    "Aclara cómo debe presentarse tu oferta para que se sienta más precisa, más relevante y más fácil de aceptar para las personas correctas.",
  actions: {
    generate: "Generar posicionamiento",
    generating: "Generando...",
    reset: "Restablecer",
    copy: "Copiar",
    copied: "¡Copiado!"
  },
  messages: {
    topicOrOfferRequired: "Introduce primero un tema o una oferta.",
    generateFailed: "No se pudo generar el posicionamiento de la oferta.",
    copyFailed: "No se pudo copiar el resultado."
  },
  fields: {
    topic: {
      label: "Tema",
      placeholder: "ej. Herramienta de contenido con IA para creadores",
      hint:
        "Usa el producto, servicio o idea de negocio que quieres posicionar."
    },
    platform: {
      label: "Plataforma",
      placeholder: "ej. Landing Page",
      hint: "Dónde se usará principalmente este posicionamiento."
    },
    tone: {
      label: "Tono"
    },
    language: {
      label: "Idioma"
    },
    offer: {
      label: "Oferta",
      placeholder:
        "Describe lo que vendes, lo que hace o la transformación que crea...",
      hint:
        "Puede ser una breve descripción de tu producto, servicio, suscripción u oferta de consultoría."
    },
    audience: {
      label: "Audiencia",
      placeholder: "ej. Dueños de pequeños negocios"
    },
    goal: {
      label: "Objetivo",
      placeholder: "ej. Aumentar conversiones"
    },
    painPoint: {
      label: "Punto de dolor",
      placeholder: "ej. Contenido débil y baja interacción"
    },
    desiredOutcome: {
      label: "Resultado deseado",
      placeholder: "ej. Crecimiento más rápido y mejores conversiones"
    },
    angle: {
      label: "Ángulo",
      placeholder: "ej. Más simple que contratar un equipo completo"
    },
    callToAction: {
      label: "Llamado a la acción",
      placeholder: "ej. Empieza una prueba gratis"
    },
    brandVoice: {
      label: "Voz de marca",
      placeholder: "ej. Inteligente, práctica, moderna"
    },
    keywords: {
      label: "Palabras clave",
      placeholder: "ej. copywriting con IA, crecimiento de creadores"
    },
    avoidPhrases: {
      label: "Evitar frases",
      placeholder: "ej. cambia el juego, truco secreto"
    },
    extraContext: {
      label: "Contexto extra",
      placeholder:
        "Contexto opcional de competidores, nota de precio, insight de categoría o reto de posicionamiento..."
    }
  },
  toneOptions: {
    confident: "Seguro",
    professional: "Profesional",
    bold: "Audaz",
    conversational: "Conversacional",
    warm: "Cálido",
    persuasive: "Persuasivo",
    direct: "Directo"
  },
  languageOptions: {
    en: "Inglés",
    es: "Español",
    fr: "Francés",
    pt: "Portugués"
  },
  results: {
    title: "Ángulos de posicionamiento",
    description:
      "Compara distintas formas de presentar tu oferta y conserva la más fuerte.",
    empty: "Todavía no hay resultados de posicionamiento. Genera un lote para verlos aquí.",
    fallbackTitle: "Posicionamiento {{number}}",
    score: "Puntuación: {{score}}",
    positioningAngle: "Ángulo de posicionamiento",
    valueProposition: "Propuesta de valor",
    whoItsFor: "Para quién es",
    problemFrame: "Marco del problema",
    differentiator: "Diferenciador",
    messageExample: "Ejemplo de mensaje"
  }
},
hookScoreAnalyzerTool: {
  badge: "Analizador de puntuación de hooks",
  title: "Califica tu hook antes de publicarlo",
  description:
    "Analiza la fuerza del hook, la claridad, la curiosidad y el poder de detener el scroll. Descubre qué funciona, qué debilita el rendimiento y cómo mejorarlo rápido.",
  tip:
    "Consejo: los grandes hooks suelen combinar claridad, tensión, curiosidad o una promesa fuerte.",
  actions: {
    analyze: "Analizar hook",
    analyzing: "Analizando...",
    clearResults: "Limpiar resultados",
    reset: "Restablecer",
    copy: "Copiar",
    copied: "Copiado"
  },
  messages: {
    hookRequired: "Pega un hook para analizar.",
    analyzeFailed: "Algo salió mal al analizar este hook.",
    copyFailed: "No se pudo copiar el análisis en este momento. Inténtalo de nuevo."
  },
  fields: {
    topic: {
      label: "Tema",
      placeholder: "ej. presupuesto para principiantes",
      hint:
        "El tema compartido ayuda a mantener conectado tu flujo de trabajo de Studio entre herramientas."
    },
    platform: {
      label: "Plataforma",
      hint:
        "El contexto de la plataforma ayuda al analizador a evaluar con más precisión el tono, el ritmo y el estilo del hook."
    },
    goal: {
      label: "Objetivo",
      placeholder: "ej. generar clics, aumentar retención, vender un producto",
      hint:
        "Opcional, pero útil cuando el hook está pensado para convertir, vender o retener atención."
    },
    targetAudience: {
      label: "Audiencia objetivo",
      placeholder: "ej. creadores nigerianos, estudiantes, dueños de pequeños negocios",
      hint:
        "El contexto de la audiencia es opcional, pero ayuda a mejorar la relevancia de la puntuación."
    },
    hook: {
      label: "Hook para analizar",
      placeholder: "Pega el hook que quieres que Hookora evalúe...",
      hint:
        "Los mejores resultados vienen de una sola línea de hook clara, no de un guion completo."
    }
  },
  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "General"
  },
  results: {
    title: "Resultado del análisis",
    summary: "Resumen",
    breakdown: "Desglose de puntuación",
    whatWorks: "Lo que funciona",
    weakPoints: "Puntos débiles",
    howToImprove: "Cómo mejorarlo",
    improvedHooks: "Opciones de hooks mejoradas",
    rawResult: "Resultado bruto",
    scoreLabels: {
      strong: "Hook fuerte",
      decent: "Aceptable, necesita pulido",
      weak: "Hook débil"
    }
  },
  exportLabels: {
    overallScore: "Puntuación general",
    summary: "Resumen",
    breakdown: "Desglose",
    strengths: "Fortalezas",
    weaknesses: "Debilidades",
    suggestions: "Sugerencias",
    improvedHooks: "Opciones de hooks mejoradas"
  }
},

trendToContentTool: {
  badge: "Tendencia a contenido",
  title: "Convierte tendencias en contenido que encaje con tu marca",
  description:
    "Pega una tendencia, tema o momento viral y conviértelo en ideas de contenido, hooks, captions o ángulos de concepto que realmente encajen con tu audiencia y plataforma.",
  tip:
    "Consejo: cuanto más clara sea la tendencia y el contexto de tu marca, más fuerte y útil será el resultado.",
  actions: {
    generate: "Generar contenido",
    generating: "Generando...",
    clearResults: "Limpiar resultados",
    reset: "Restablecer",
    copy: "Copiar",
    copied: "Copiado"
  },
  messages: {
    trendRequired: "Introduce la tendencia que quieres convertir en contenido.",
    generateFailed: "Algo salió mal al generar contenido a partir de la tendencia.",
    copyFailed: "No se pudo copiar el resultado en este momento. Inténtalo de nuevo."
  },
  fields: {
    topic: {
      label: "Tema",
      placeholder: "ej. finanzas personales, belleza, fitness",
      hint:
        "El tema compartido mantiene esta herramienta alineada con tus otros flujos de Studio."
    },
    platform: {
      label: "Plataforma",
      hint:
        "La plataforma ayuda a Hookora a dar forma al estilo de salida y al formato del contenido."
    },
    outputType: {
      label: "Tipo de salida",
      hint:
        "Elige el tipo de resultado en el que quieres que Hookora se enfoque."
    },
    goal: {
      label: "Objetivo",
      placeholder: "ej. alcance, engagement, conversiones",
      hint:
        "Opcional. Útil cuando quieres que el contenido esté orientado a un resultado claro."
    },
    audience: {
      label: "Audiencia",
      placeholder: "ej. creadores nigerianos, fundadores de startups, estudiantes",
      hint:
        "El contexto de la audiencia ayuda a generar ángulos de contenido más relevantes."
    },
    trend: {
      label: "Tendencia / momento viral / tema",
      placeholder:
        "Describe la tendencia, publicación viral, formato meme, opinión polémica o tema del momento...",
      hint:
        "Puedes pegar una idea de tendencia en bruto, un resumen de una publicación social o un formato viral que quieras adaptar."
    },
    brandContext: {
      label: "Contexto de marca",
      placeholder:
        "Explica tu nicho, tono, oferta, producto o lo que representa tu marca...",
      hint:
        "Esto ayuda a Hookora a convertir tendencias amplias en contenido que siga sintiéndose propio de tu marca."
    }
  },
  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "General"
  },
  outputTypeOptions: {
    contentIdeas: "Ideas de contenido",
    hooks: "Hooks",
    captions: "Captions",
    scriptAngles: "Ángulos de guion",
    carouselIdeas: "Ideas de carrusel",
    videoConcepts: "Conceptos de video"
  },
  results: {
    title: "Resultado generado",
    summary: "Resumen",
    trendInsight: "Insight de la tendencia",
    generatedIdeas: "Ideas generadas",
    hookOptions: "Opciones de hook",
    captionOptions: "Opciones de caption",
    angles: "Ángulos",
    recommendations: "Recomendaciones",
    rawResult: "Resultado bruto"
  },
  exportLabels: {
    summary: "Resumen",
    trendInsight: "Insight de la tendencia",
    generatedIdeas: "Ideas generadas",
    hookOptions: "Opciones de hook",
    captionOptions: "Opciones de caption",
    angles: "Ángulos",
    recommendations: "Recomendaciones"
  }
},

nigerianAudienceRewriteTool: {
  badge: "Reescritura para audiencia nigeriana",
  title: "Localiza tu contenido para una audiencia nigeriana",
  subtitle:
    "Reescribe contenido genérico para que se sienta más natural, cercano y mejor adaptado a lectores nigerianos sin sonar forzado ni extraño.",
  tip:
    "Consejo: pega primero contenido claro y simple. Hookora puede localizarlo de forma más natural cuando el texto base es claro y no está sobrecargado.",
  buttons: {
    rewrite: "Reescribir contenido",
    rewriting: "Reescribiendo...",
    clearResults: "Borrar resultados",
    reset: "Restablecer",
    copy: "Copiar",
    copied: "Copiado",
  },
  messages: {
    missingOriginalText: "Por favor, pega el contenido que quieres reescribir.",
    copyError: "No se pudo copiar la reescritura ahora mismo. Inténtalo de nuevo.",
    genericError: "Algo salió mal al reescribir este contenido.",
  },
  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "General",
  },
  toneOptions: {
    natural: "Natural",
    casual: "Casual",
    confident: "Seguro",
    sales: "Ventas",
    storytelling: "Narrativo",
    professional: "Profesional",
  },
  formatOptions: {
    caption: "Caption",
    hook: "Hook",
    shortScript: "Guion corto",
    adCopy: "Texto publicitario",
    carouselCopy: "Texto para carrusel",
    general: "Reescritura general",
  },
  fields: {
    topic: {
      label: "Tema",
      placeholder: "ej. ingresos extra, ahorro, cuidado de la piel",
      hint: "El tema compartido mantiene esta herramienta alineada con tu flujo actual en Studio.",
    },
    platform: {
      label: "Plataforma",
      hint: "El contexto de la plataforma ayuda a dar forma al estilo final y al ritmo del texto.",
    },
    tone: {
      label: "Tono",
      hint: "Elige el tono hacia el que quieres llevar la reescritura.",
    },
    contentFormat: {
      label: "Formato del contenido",
      hint: "Esto ayuda a Hookora a adaptar la reescritura a la estructura correcta.",
    },
    goal: {
      label: "Objetivo",
      placeholder: "ej. hacerlo más cercano, más persuasivo, más natural",
      hint: "Opcional. Útil cuando quieres orientar la reescritura hacia un resultado específico.",
    },
    brandContext: {
      label: "Contexto de marca",
      placeholder:
        "Describe la voz de tu marca, el tipo de audiencia, el producto o cómo quieres sonar...",
      hint: "Esto ayuda a que la reescritura siga siendo relevante para la audiencia y fiel a tu marca.",
    },
    originalContent: {
      label: "Contenido original",
      placeholder:
        "Pega el contenido que quieres reescribir para una audiencia nigeriana...",
      hint: "Puedes pegar un hook, caption, texto publicitario, guion corto o contenido general.",
    },
  },
  result: {
    title: "Resultado de la reescritura",
    assetTitle: "Reescritura para audiencia nigeriana",
    sections: {
      rewrittenOutput: "Texto reescrito",
      summary: "Resumen",
      adaptationNotes: "Notas de adaptación",
      alternativeVersions: "Versiones alternativas",
      suggestions: "Sugerencias",
      rawResult: "Resultado bruto",
    },
  },
  exportLabels: {
    rewrittenOutput: "Texto reescrito",
    summary: "Resumen",
    adaptationNotes: "Notas de adaptación",
    alternativeVersions: "Versiones alternativas",
    suggestions: "Sugerencias",
  },
},

  descriptionTool: {
  hero: {
    badge: "Herramienta de reescritura",
    title: "Descripciones",
    description:
      "Usa esta herramienta para generar <strong>reescrituras de descripción más fuertes</strong> para tu contenido. Puedes pegar una descripción existente para mejorarla o generar una nueva dirección a partir de tu tema y contexto."
  },

  contextTitle: "Contexto de reescritura",

  fields: {
    topic: {
      label: "Tema",
      placeholder: "¿De qué trata la descripción?",
      hint: "El tema principal de la descripción. Sé claro sobre de qué trata el contenido o activo."
    },
    platform: {
      label: "Plataforma",
      hint: "Elige la plataforma donde se usará esta descripción."
    },
    originalDescription: {
      label: "Descripción original",
      placeholder: "Pega la descripción actual si quieres que se reescriba.",
      hint: "Pega aquí la descripción actual si quieres mejorarla en lugar de generar una versión nueva solo a partir del contexto."
    },
    audience: {
      label: "Audiencia",
      placeholder: "Audiencia",
      hint: "Para quién está pensada esta descripción. Ejemplo: estudiantes, creadores, dueños de marca, fundadores de startups."
    },
    goal: {
      label: "Objetivo",
      placeholder: "Objetivo",
      hint: "Qué debería ayudar a conseguir la descripción. Ejemplo: conversiones, claridad, tiempo de visualización, clics o registros."
    },
    offer: {
      label: "Oferta",
      placeholder: "Oferta o producto",
      hint: "El producto, recurso gratuito, servicio o promesa detrás del contenido."
    },
    painPoint: {
      label: "Punto de dolor",
      placeholder: "Punto de dolor",
      hint: "El desafío o frustración que enfrenta tu audiencia."
    },
    desiredOutcome: {
      label: "Resultado deseado",
      placeholder: "Resultado deseado",
      hint: "El resultado que tu audiencia quiere. Ejemplo: más crecimiento, mejor contenido, resultados más rápidos o menos estrés."
    },
    angle: {
      label: "Ángulo",
      placeholder: "Ángulo",
      hint: "El posicionamiento o enfoque. Ejemplo: educativo, premium, urgente, para principiantes o respuesta directa."
    },
    cta: {
      label: "CTA",
      placeholder: "CTA",
      hint: "La acción que la descripción debería fomentar. Ejemplo: suscribirse, ver, comentar, guardar o visitar el enlace."
    },
    keywords: {
      label: "Palabras clave",
      placeholder: "Palabras clave",
      hint: "Palabras importantes de búsqueda o SEO para incluir de forma natural. Sepáralas con comas."
    },
    brandVoice: {
      label: "Voz de marca",
      placeholder: "Voz de marca",
      hint: "Cómo debería sonar la descripción. Ejemplo: atrevida, experta, divertida, premium o directa."
    },
    avoidPhrases: {
      label: "Evitar frases",
      placeholder: "Evitar frases",
      hint: "Palabras o patrones que quieres que el generador evite repetir."
    },
    extraContext: {
      label: "Contexto adicional",
      placeholder: "Contexto adicional, prueba única, notas del producto o detalle de apoyo",
      hint: "Añade pruebas, detalles de apoyo, emoción objetivo, notas de campaña u otro contexto extra para mejorar la reescritura."
    }
  },

  actions: {
    generate: "Generar reescrituras",
    generating: "Generando...",
    clearResults: "Borrar resultados",
    resetForm: "Restablecer formulario",
    copy: "Copiar",
    copied: "Copiado"
  },

  messages: {
    topicRequired: "Primero escribe un tema.",
    generateFailed: "No se pudo reescribir la descripción.",
    ready:
      "Estas son <strong>reescrituras de descripción listas para usar</strong>. Elige la que mejor encaje con tu plataforma, objetivo de claridad y dirección de conversión."
  },

  result: {
    score: "Puntuación"
  }
},

scriptTool: {
  hero: {
    badge: "Herramienta de escritura de guiones",
    title: "Guiones",
    description: {
      before: "Usa esta herramienta para generar ",
      strong: "guiones de formato corto",
      after:
        " con hook, cuerpo y CTA. Añade plataforma, estilo de guion y contexto para que Hookora cree guiones más fuertes, más naturales y más utilizables al instante."
    }
  },

  context: {
    title: "Contexto del guion"
  },

  fields: {
    topic: {
      label: "Tema",
      hint:
        "El tema que debe cubrir el guion. Sé específico para que el guion suene enfocado y útil, no genérico.",
      placeholder: "¿Sobre qué debe tratar el guion?"
    },
    platform: {
      label: "Plataforma",
      hint:
        "Elige dónde se usará el guion. Las diferentes plataformas afectan el ritmo, el tono y el estilo de entrega."
    },
    style: {
      label: "Estilo del guion",
      hint:
        "Elige el formato de guion que quieres. Ejemplo: directo a cámara, guiado por historia, problema-solución, rompiendo mitos."
    },
    audience: {
      label: "Audiencia",
      hint:
        "Para quién es el guion. Ejemplo: creadores, fundadores de startups, vendedores ecommerce, estudiantes, coaches fitness.",
      placeholder: "Audiencia"
    },
    goal: {
      label: "Objetivo",
      hint:
        "Qué debe lograr el guion. Ejemplo: retención, confianza, clics, comentarios, conversiones, compartidos.",
      placeholder: "Objetivo"
    },
    offer: {
      label: "Oferta",
      hint:
        "El producto, servicio, lección o valor que el guion apoya o hacia el que dirige.",
      placeholder: "Oferta o producto"
    },
    painPoint: {
      label: "Pain point",
      hint:
        "El problema específico con el que tu audiencia está lidiando ahora mismo.",
      placeholder: "Pain point"
    },
    desiredOutcome: {
      label: "Resultado deseado",
      hint:
        "Qué resultado quiere la audiencia. Ejemplo: más vistas, más leads, más confianza, mejores sistemas.",
      placeholder: "Resultado deseado"
    },
    angle: {
      label: "Ángulo",
      hint:
        "Cómo debe enfocarse el guion. Ejemplo: urgente, para principiantes, premium, directo, emocional, contrarian.",
      placeholder: "Ángulo"
    },
    callToAction: {
      label: "CTA",
      hint:
        "Qué quieres que hagan los espectadores después de escuchar el guion. Ejemplo: seguir, comentar, guardar, comprar, hacer clic.",
      placeholder: "CTA"
    },
    keywords: {
      label: "Palabras clave",
      hint:
        "Palabras importantes del tema o SEO para incluir de forma natural. Sepáralas con comas.",
      placeholder: "Palabras clave"
    },
    brandVoice: {
      label: "Voz de marca",
      hint:
        "Cómo debe sonar el guion. Ejemplo: ingenioso, afilado, pulido, juguetón, atrevido, premium.",
      placeholder: "Voz de marca"
    },
    extraContext: {
      label: "Contexto extra",
      hint:
        "Añade contexto de historia, pruebas, ejemplos, limitaciones o dirección extra para que el guion se sienta más personalizado.",
      placeholder: "Contexto extra, pruebas, pistas de historia o instrucciones extra"
    }
  },

  styleOptions: {
    "direct-to-camera": "Directo a cámara",
    "story-led": "Guiado por historia",
    "problem-solution": "Problema-solución",
    "myth-busting": "Rompiendo mitos"
  },

  actions: {
    generate: "Generar guiones",
    generating: "Generando...",
    clear: "Borrar resultados",
    reset: "Restablecer formulario",
    copy: "Copiar",
    copied: "Copiado"
  },

  errors: {
    topicRequired: "Por favor, introduce primero un tema.",
    generateFailed: "No se pudieron generar los guiones."
  },

  output: {
    helper: {
      before: "Estas son ",
      strong: "opciones de guion listas para usar",
      after:
        ". Elige la que mejor se adapte a tu plataforma, tono y objetivo de conversión."
    },
    score: "Puntuación",
    hookLabel: "Hook",
    bodyLabel: "Cuerpo",
    ctaLabel: "CTA",
    untitledTopic: "Tema sin título"
  }
},

ctaTool: {
  hero: {
    badge: "Herramienta de conversión",
    title: "CTAs",
    descriptionStart: "Usa esta herramienta para generar",
    descriptionStrong: "llamadas a la acción más fuertes",
    descriptionEnd:
      "para publicaciones, videos y campañas. Añade contexto sobre audiencia, objetivo y oferta para que Hookora cree CTAs que se sientan más naturales y más persuasivos."
  },

  fields: {
    topic: {
      label: "Tema",
      hint: "La idea de contenido para la que quieres llamadas a la acción más fuertes.",
      placeholder: "¿Cuál es el tema, idea de contenido o mensaje?"
    },
    platform: {
      label: "Plataforma",
      hint: "Elige dónde se usará el CTA."
    },
    audience: {
      label: "Audiencia",
      hint: "A quién va dirigido el CTA.",
      placeholder: "Audiencia"
    },
    goal: {
      label: "Objetivo",
      hint: "Qué acción quieres de la audiencia.",
      placeholder: "Objetivo"
    },
    offer: {
      label: "Oferta",
      hint: "Qué valor, producto, lección u oferta hay detrás del CTA.",
      placeholder: "Oferta o producto"
    },
    painPoint: {
      label: "Punto de dolor",
      hint: "Qué frustración u obstáculo está enfrentando la audiencia.",
      placeholder: "Punto de dolor"
    },
    desiredOutcome: {
      label: "Resultado deseado",
      hint: "Qué quiere lograr la audiencia.",
      placeholder: "Resultado deseado"
    },
    brandVoice: {
      label: "Voz de marca",
      hint: "Cómo debe sonar el CTA: audaz, cercano, premium, directo, juguetón.",
      placeholder: "Voz de marca"
    },
    ctaDirection: {
      label: "Dirección del CTA",
      hint: "Ejemplo: comentarios, seguir, guardar, compartir, suscribirse, venta suave.",
      placeholder: "Dirección de CTA preferida"
    },
    avoidPhrases: {
      label: "Evitar frases",
      hint: "Palabras o clichés que no quieres que se usen.",
      placeholder: "Evitar frases"
    },
    extraContext: {
      label: "Contexto extra",
      hint: "Añade notas de campaña, posicionamiento, objeciones o detalles para mejorar la calidad del CTA.",
      placeholder: "Contexto extra, notas de campaña o detalles de posicionamiento"
    }
  },

  context: {
    title: "Contexto del CTA"
  },

  actions: {
    generate: "Generar CTAs",
    generating: "Generando...",
    clearResults: "Borrar resultados",
    resetForm: "Restablecer formulario",
    copy: "Copiar",
    copied: "Copiado"
  },

  errors: {
    topicRequired: "Primero escribe un tema.",
    generateFailed: "No se pudieron generar los CTAs."
  },

  resultsIntro: {
    start: "Estas son",
    strong: "opciones de CTA listas para usar",
    end: "Elige la que mejor encaje con tu audiencia, plataforma y objetivo de conversión."
  },

  output: {
    score: "Puntuación",
    whyItWorks: "Por qué funciona",
    untitledTopic: "Tema sin título"
  }
},

},


  home: {
    featuredGuidesBadge: "Guías destacadas de Hookora",
    learnSectionTitle: "Aprende cómo Hookora ayuda a los creadores a avanzar más rápido",
    learnSectionDescription:
      "Explora guías rápidas sobre qué es Hookora, a quién ayuda y cómo hace que la escritura de hooks, la ideación de contenido, las reescrituras, el empaquetado y el flujo del creador se sientan más claros y rápidos.",
    guides: {
      whatIsHookora: {
        title: "¿Qué es Hookora?",
        description:
          "Aprende qué es Hookora, cómo ayuda a los creadores a pasar de una idea a un contenido más fuerte más rápido y por qué ahora va más allá de la generación básica de hooks."
      },
      nigerianCreators: {
        title: "Hookora para creadores nigerianos",
        description:
          "Descubre cómo Hookora ayuda a los creadores nigerianos a construir mejores hooks, títulos, captions, guiones, reescrituras localizadas y un mejor empaquetado del contenido."
      },
      writeHooksFaster: {
        title: "Cómo Hookora ayuda a los creadores a escribir hooks más rápido",
        description:
          "Explora cómo Hookora ayuda a los creadores a escribir hooks más rápido, encontrar ángulos más fuertes, mejorar la retención y convertir más ideas en contenido listo para publicar."
      }
    }
  },
  

  homeHero: {
    badge: "Plataforma de flujo de IA para creadores",
    title: "Crea contenido más fuerte más rápido con el Studio de 22 herramientas de Hookora",
    description:
      "Empieza gratis con herramientas principales de escritura y luego desbloquea herramientas premium de flujo y herramientas estratégicas avanzadas a medida que creces. Hookora ayuda a los creadores a generar hooks, títulos, captions, hashtags, descripciones, guiones, reescrituras, intros de retención, sistemas de voz y más desde un flujo más limpio.",
    trustPoints: {
      tools: "22 herramientas de Studio en un solo flujo",
      free: "Empieza gratis con herramientas principales",
      upgrade: "Mejora para herramientas premium y estratégicas"
    },
    ideaLabel: "Idea de contenido",
    ideaText: "“Por qué la mayoría de los creadores pierden atención en los primeros 3 segundos”",
    metrics: {
      tools: "Herramientas",
      workflow: "Flujo"
    },
    footerScale: "Hecho para escalar"
  },

  homeFeatures: {
    title: "Por qué los creadores usan Hookora",
    subtitle:
      "Un flujo más limpio para hooks, títulos, captions, guiones y un mejor empaquetado del contenido.",
    cards: {
      createFaster: {
        title: "Crea más rápido",
        text:
          "Pasa de una idea inicial a contenido útil más rápido sin quedarte atascado con las palabras."
      },
      keepMessageSharp: {
        title: "Mantén tu mensaje claro",
        text:
          "Genera hooks, títulos, captions y guiones más claros y con más intención."
      },
      upgradeOnlyWhenNeeded: {
        title: "Mejora solo cuando lo necesites",
        text:
          "Empieza con Free y desbloquea herramientas Creator y Pro a medida que crece tu flujo."
      }
    },
    studioToolsBadge: "Resumen de herramientas de Studio",
    workflowTitle: "Un flujo, múltiples herramientas de contenido",
    workflowDescription:
      "Hookora conecta tu flujo de escritura para que puedas pasar de hooks a mejores títulos, captions, guiones, reescrituras y más sin cambiar entre herramientas desordenadas.",
    toolCards: {
      free: "Herramientas gratuitas para hooks, títulos, captions, hashtags, descripciones y guiones.",
      creatorOne: "Herramientas Creator para ángulos de hook, CTAs y mejor empaquetado del contenido.",
      creatorTwo: "Soporte de retención, reescrituras y mejora del texto de thumbnail.",
      creatorThree: "Reutilización, texto publicitario, escritura de carruseles y posicionamiento de oferta.",
      creatorFour: "Análisis de puntuación de hook, reescrituras nigerianas e ideas de contenido desde comentarios.",
      proOne: "Herramientas Pro para conversión de tendencias y estrategia creativa más profunda.",
      proTwo: "Sistemas de voz de marca para que el contenido se sienta más consistente.",
      proThree: "Herramientas de diferenciación de contenido para ayudar a los creadores a destacar con más claridad."
    }
  },

  homeCta: {
    badge: "Empieza con claridad",
    title: "Dale a tu contenido una primera impresión más fuerte",
    description:
      "Hookora te ayuda a crear con más confianza, más velocidad y más consistencia. Empieza gratis y construye un flujo más limpio para hooks, captions, guiones, reescrituras, retención y un empaquetado de contenido más fuerte."
  },

  features: {
    seoTitle:
      "Funciones de Hookora | 22 herramientas de IA para Hooks, Títulos, Captions, Guiones y más",
    seoDescription:
      "Explora las funciones de Hookora para creadores, incluidas 22 herramientas de Studio para hooks, ángulos de hook, títulos, captions, hashtags, descripciones, guiones, CTAs, texto de thumbnail, reescrituras, retención, localización, tendencias, voz de marca y más.",
    softwareSchemaDescription:
      "Hookora es una plataforma de flujo de trabajo con IA para creadores con 22 herramientas de Studio para hooks, ángulos de hook, títulos, captions, hashtags, descripciones, guiones, CTAs, texto de thumbnail, reescrituras, localización, intros de retención, conversión de tendencias, entrenamiento de voz de marca, diferenciación y más.",
    planBasedStudioAccess: "Acceso a Studio por plan",
    planSectionTitle: "22 herramientas de Studio en Free, Creator y Pro",
    planSectionDescription:
      "Hookora ahora está estructurado para dar a los usuarios una ruta clara de mejora: herramientas básicas en Free, herramientas premium en Creator y herramientas estratégicas avanzadas en Pro.",
    guidesSectionTitle: "Aprende más sobre cómo funciona Hookora",
    guidesSectionDescription:
      "Explora guías rápidas sobre qué es Hookora, a quién ayuda y cómo mejora la escritura de hooks, la ideación, las reescrituras, el empaquetado y el flujo del creador.",
    guides: {
      whatIsHookora: {
        title: "¿Qué es Hookora?",
        description:
          "Descubre qué es Hookora, cómo funciona y por qué ayuda a los creadores a pasar de una idea a un contenido más fuerte más rápido con un flujo de 22 herramientas."
      },
      nigerianCreators: {
        title: "Hookora para creadores nigerianos",
        description:
          "Mira cómo Hookora ayuda a los creadores nigerianos a construir mejores hooks, títulos, captions, guiones, reescrituras localizadas y mejor empaquetado de contenido."
      },
      writeHooksFaster: {
        title: "Cómo Hookora ayuda a escribir hooks más rápido",
        description:
          "Explora cómo Hookora ayuda a los creadores a escribir hooks más rápido con ángulos más claros, primeras líneas más fuertes, mejor soporte de retención y herramientas más flexibles."
      }
    },
    planGroups: {
      free: {
        title: "Plan Free",
        subtitle: "Empieza con el flujo básico de escritura",
        description:
          "Los usuarios Free pueden acceder a las herramientas esenciales de Studio para escribir contenido e idear mejor cada día."
      },
      creator: {
        title: "Plan Creator",
        subtitle: "Desbloquea herramientas premium de flujo",
        description:
          "Creator desbloquea la capa premium intermedia de Hookora para empaquetado, reescrituras, retención, reutilización y mejor ejecución del contenido."
      },
      pro: {
        title: "Plan Pro",
        subtitle: "Desbloquea herramientas estratégicas avanzadas",
        description:
          "Pro está pensado para un trabajo estratégico más profundo, ayudando a convertir mejores ideas en mejores sistemas de contenido."
      }
    }
  },

  featuresHero: {
    badge: "Acceso a Studio por plan",
    title: "22 herramientas de Studio para el flujo moderno del creador",
    subtitle:
      "Hookora ahora ofrece una ruta de mejora más clara con herramientas principales en Free, herramientas premium de flujo en Creator y herramientas estratégicas avanzadas en Pro.",
    startFree: "Empieza gratis",
    viewPricing: "Ver precios",
    cards: {
      free: {
        label: "FREE",
        text: "Hooks, Títulos, Captions, Hashtags, Descripciones y Guiones."
      },
      creator: {
        label: "CREATOR",
        text:
          "Herramientas premium de flujo para empaquetado, reescrituras, retención, CTAs, carruseles, reutilización y más."
      },
      pro: {
        label: "PRO",
        text:
          "Herramientas estratégicas avanzadas para conversión de tendencias, entrenamiento de voz de marca y diferenciación de contenido."
      },
      oneStudio: {
        label: "Un Studio",
        text:
          "Crea hooks, títulos, captions, guiones, reescrituras y herramientas avanzadas en un solo lugar."
      }
    },
    footerStats: {
      tools: "22 herramientas de Studio",
      tieredAccess: "Acceso por niveles",
      cleanerWorkflow: "Flujo más limpio"
    }
  },

  featuresGrid: {
    title: "Lo que Hookora te ayuda a crear",
    subtitle:
      "Un flujo completo para creadores con herramientas gratuitas, premium y estratégicas avanzadas.",
    items: {
      workflow22: {
        title: "Flujo de 22 herramientas",
        text:
          "Muévete entre hooks, títulos, captions, descripciones, guiones, reescrituras y herramientas estratégicas desde un solo espacio conectado."
      },
      freeCoreWriting: {
        title: "Herramientas gratuitas principales",
        text:
          "Empieza con herramientas esenciales para hooks, títulos, captions, hashtags, descripciones y guiones."
      },
      creatorPremium: {
        title: "Flujo premium Creator",
        text:
          "Desbloquea mejor empaquetado, reescrituras, reutilización, soporte para CTA, soporte para thumbnails, posicionamiento de oferta y ayuda de retención."
      },
      proStrategic: {
        title: "Herramientas estratégicas Pro",
        text:
          "Usa herramientas avanzadas para convertir tendencias en contenido, entrenar voz de marca y diferenciar tu contenido."
      },
      hooksAngles: {
        title: "Hooks y ángulos de hook",
        text:
          "Genera mejores aperturas y explora varias direcciones desde un mismo tema."
      },
      titlesCaptionsHashtags: {
        title: "Títulos, captions y hashtags",
        text:
          "Escribe elementos de empaquetado más fuertes que hagan que tu contenido sea más claro, más clicable y más fácil de publicar."
      },
      descriptionsScriptsCtas: {
        title: "Descripciones, guiones y CTAs",
        text:
          "Construye mejor soporte para contenido largo, guiones cortos y llamadas a la acción más claras desde una misma idea central."
      },
      thumbnailHookScore: {
        title: "Texto de thumbnail y puntuación de hook",
        text:
          "Mejora la primera impresión y analiza qué tan fuerte es tu hook antes de publicar."
      },
      humanizerRewriteRepurpose: {
        title: "Humanizador, reescrituras y reutilización",
        text:
          "Haz que tu texto suene más natural, reformula ideas y convierte una sola buena entrada en varias salidas de contenido."
      },
      retentionVoiceDifferentiation: {
        title: "Retención, voz de marca y diferenciación",
        text:
          "Afina cómo suena tu contenido, cómo retiene la atención y cómo se diferencia del texto genérico para creadores."
      },
      trendCommentToContent: {
        title: "Tendencias y contenido desde comentarios",
        text:
          "Convierte tendencias y señales de la audiencia en ideas de contenido, ángulos y caminos de ejecución más fuertes."
      },
      librarySavedAssets: {
        title: "Biblioteca y activos guardados",
        text:
          "Guarda tus mejores hooks y salidas de Studio para que tu flujo de trabajo sea más rápido con el tiempo."
      }
    }
  },

  blog: {
    seoTitle: "Blog de Hookora | Guías para creadores, consejos de flujo y páginas de herramientas",
    seoDescription:
      "Explora publicaciones del blog de Hookora y guías públicas para creadores sobre hooks, títulos, captions, guiones, herramientas de contenido y un flujo creativo más fluido.",
    title: "Blog de Hookora",
    subtitle:
      "Guías para creadores, ideas de flujo de trabajo y páginas públicas de herramientas que explican cómo Hookora ayuda a crear contenido más fuerte.",
    heroBadge: "Blog de Hookora",
    heroTitle: "Guías para creadores, consejos de flujo y contenido de descubrimiento",
    heroDescription:
      "Explora guías prácticas de contenido diseñadas para ayudar a los creadores a escribir mejores hooks, captions, títulos y empaquetado con un flujo de trabajo más fluido.",
    startFree: "Empieza gratis",
    exploreFeatures: "Explorar funciones",
    readGuide: "Leer guía",
    relatedReading: "Lecturas relacionadas",
    categories: {
      all: "Todo",
      hooks: "Hooks",
      contentTools: "Herramientas de contenido",
      captions: "Captions",
      hookWriting: "Escritura de hooks"
    },
    posts: {
      whatIsHookora: {
        title: "¿Qué es Hookora?",
        description:
          "Descubre qué es Hookora, cómo funciona y por qué Hookora ayuda a los creadores a generar hooks, títulos, captions, hashtags, guiones, descripciones y texto para thumbnails más rápido.",
        badge: "Hookora"
      },
      nigerianCreators: {
        title: "Hookora para creadores nigerianos",
        description:
          "Descubre cómo Hookora ayuda a los creadores nigerianos a escribir hooks, títulos, captions, guiones y empaquetado de contenido más fuertes con un flujo de trabajo más fluido.",
        badge: "Hookora"
      },
      writeHooksFaster: {
        title: "Cómo Hookora ayuda a los creadores a escribir hooks más rápido",
        description:
          "Descubre cómo Hookora ayuda a los creadores a escribir hooks más rápido con ángulos más claros, primeras líneas más fuertes y un flujo de contenido más fluido.",
        badge: "Hookora"
      },
      aiToolsNigeria: {
        title: "Herramientas de IA para creadores nigerianos",
        description:
          "Descubre herramientas de IA para creadores nigerianos que quieren un flujo de contenido más rápido, hooks más fuertes, mejores captions, títulos más precisos y resultados más consistentes.",
        badge: "Flujo de trabajo creativo"
      },
      bestHookGeneratorNigeria: {
        title: "El mejor generador de hooks para creadores nigerianos",
        description:
          "Descubre cómo los creadores nigerianos pueden usar Hookora para crear hooks, líneas de apertura y primeras impresiones más fuertes para videos y contenido social.",
        badge: "Hooks"
      },
      contentToolsNigeria: {
        title: "Herramientas de contenido para creadores en Nigeria",
        description:
          "Explora herramientas de contenido para creadores en Nigeria que quieren un flujo más claro para hooks, captions, títulos, guiones y empaquetado de contenido.",
        badge: "Herramientas de contenido"
      },
      bestCaptionGeneratorNigeria: {
        title: "El mejor generador de captions para creadores nigerianos",
        description:
          "Descubre cómo Hookora ayuda a los creadores nigerianos a escribir captions más claros y rápidos para Instagram, TikTok, Shorts y un flujo creativo más amplio.",
        badge: "Captions"
      },
      betterHooksNigeria: {
        title: "Cómo los creadores nigerianos pueden escribir mejores hooks",
        description:
          "Descubre formas prácticas en que los creadores nigerianos pueden escribir mejores hooks para Reels, Shorts, TikTok y contenido social con primeras líneas más fuertes.",
        badge: "Escritura de hooks"
      }
    },
    articles: {
      whatIsHookora: {
        seoTitle: "¿Qué es Hookora? | Blog de Hookora",
        seoDescription:
          "Descubre qué es Hookora, cómo funciona y por qué Hookora ayuda a los creadores a generar hooks, títulos, captions, hashtags, guiones, descripciones y texto para thumbnails más rápido.",
        schemaDescription:
          "Descubre qué es Hookora, cómo funciona y por qué ayuda a los creadores a pasar de una idea a un contenido más fuerte más rápido.",
        title: "¿Qué es Hookora?",
        heroDescription:
          "Hookora es una plataforma de flujo de trabajo con IA para creadores que ayuda a convertir ideas iniciales en hooks, títulos, captions, hashtags, descripciones, guiones y texto para thumbnails más fuertes dentro de un sistema de contenido más limpio.",
        paragraphs: {
          0: "Hookora está creado para creadores que saben que la calidad del contenido no depende solo de tener buenas ideas. También depende de empaquetar bien esas ideas. Un hook más fuerte, un título más claro, un mejor caption y un guion más preciso pueden cambiar cómo se siente el contenido incluso antes de que alguien vea o lea la pieza completa."
        },
        sections: {
          whatHookoraHelps: {
            heading: "Lo que Hookora ayuda a hacer a los creadores",
            paragraph1:
              "Hookora ayuda a los creadores a avanzar más rápido cuando las palabras se sienten lentas. En lugar de quedarse atascados al inicio del proceso, los creadores pueden generar mejores opciones para las partes del contenido que normalmente generan fricción. Eso incluye hooks de apertura, títulos de video, captions para redes sociales, conjuntos de hashtags, descripciones reescritas, guiones cortos y dirección para texto de thumbnail.",
            paragraph2:
              "El objetivo no es reemplazar la creatividad. El objetivo es apoyarla. Hookora está diseñado para ayudar a los creadores a sentirse más seguros cuando convierten una idea en algo publicable."
          },
          whyHookoraExists: {
            heading: "Por qué existe Hookora",
            paragraph1:
              "Muchos creadores pierden tiempo en los mismos puntos. La idea existe, pero el hook se siente débil. El título no parece lo bastante clickable. El caption se siente plano. El guion tarda demasiado en estructurarse. Esos pequeños retrasos crean mucha fricción creativa con el tiempo.",
            paragraph2:
              "Hookora existe para reducir esa fricción. En lugar de obligar a los creadores a usar herramientas dispersas y flujos desconectados, Hookora reúne el proceso de contenido en un solo lugar que se siente más claro y más fácil de usar."
          },
          whoHookoraIsFor: {
            heading: "Para quién es Hookora",
            paragraph1:
              "Hookora es para creadores, marcas personales, marketers y usuarios de redes sociales que quieren un empaquetado de contenido más fuerte sin perder demasiado tiempo con las palabras. Es útil para creadores que hacen videos de YouTube, Shorts, publicaciones de Instagram, Reels, TikToks y otras formas de contenido social.",
            paragraph2:
              "También es útil para creadores en crecimiento en lugares como Nigeria, donde más personas están construyendo marcas online, negocios impulsados por audiencias y fuentes creativas de ingresos, y necesitan herramientas que les ayuden a avanzar más rápido."
          },
          whatMakesHookoraDifferent: {
            heading: "Qué hace diferente a Hookora"
          },
          inSimpleTerms: {
            heading: "En términos simples",
            paragraph:
              "Hookora es una plataforma de flujo de contenido que ayuda a los creadores a pasar de una idea a una mejor salida más rápido. Si alguien quiere mejores hooks, títulos, captions, guiones y empaquetado sin que el proceso se vuelva más pesado, Hookora está hecho para ese trabajo."
          }
        },
        list: {
          0: "Conecta múltiples tareas de contenido dentro de un mismo flujo en lugar de tratar cada tarea como una herramienta aleatoria separada.",
          1: "Ayuda a los creadores a mantener el impulso cuando se quedan atascados con las palabras.",
          2: "Se enfoca en resultados prácticos para creadores, no solo en generación de texto genérico con IA.",
          3: "Está diseñado para que crear contenido se sienta más fluido, más claro y más repetible."
        },
        ctaTitle: "Prueba Hookora y crea tu contenido más rápido con un flujo más fluido",
        ctaDescription:
          "Si quieres una forma más limpia de crear hooks, títulos, captions, guiones y más, Hookora te ofrece un punto de partida práctico."
      },

      nigerianCreators: {
        seoTitle: "Hookora para creadores nigerianos | Blog de Hookora",
        seoDescription:
          "Descubre cómo Hookora ayuda a los creadores nigerianos a escribir mejores hooks, títulos, captions, guiones y empaquetado de contenido más rápido con un flujo más fluido.",
        schemaDescription:
          "Descubre cómo Hookora ayuda a los creadores nigerianos a pasar de una idea a un empaquetado de contenido más fuerte más rápido.",
        title: "Hookora para creadores nigerianos",
        heroDescription:
          "Hookora ayuda a los creadores nigerianos a convertir ideas iniciales de contenido en hooks, títulos, captions, guiones, hashtags, descripciones y texto de thumbnail más fuertes sin hacer que el flujo se sienta más pesado.",
        sections: {
          intro1:
            "Cada vez más creadores en Nigeria están construyendo audiencias, marcas personales, negocios y páginas de medios online. Pero hay algo que todavía ralentiza a muchos: convertir buenas ideas en contenido que se sienta lo bastante fuerte para publicar. Puede que el mensaje esté claro en tu cabeza, pero el hook es débil, el caption se siente plano o el título no parece lo suficientemente preciso.",
          intro2:
            "Ahí es donde entra Hookora. Hookora está creado para que la creación de contenido se sienta más clara y más rápida, especialmente para creadores que necesitan ayuda con las palabras que moldean la primera impresión.",
          heading1: "Por qué Hookora importa para los creadores nigerianos",
          paragraph1:
            "Los creadores nigerianos trabajan en muchos formatos al mismo tiempo. Algunos hacen YouTube Shorts, otros están construyendo páginas de Instagram, otros publican en TikTok y otros hacen crecer marcas personales mediante contenido educativo o de entretenimiento. En todos esos casos, el empaquetado del contenido importa.",
          paragraph2:
            "Una primera línea más fuerte puede ayudar a que alguien deje de hacer scroll. Un título más claro puede hacer que una idea de video sea más fácil de entender. Un mejor caption puede hacer que una publicación se sienta más intencional. Un guion corto mejor estructurado puede facilitar la grabación. Hookora ayuda a los creadores a avanzar por esos pasos con menos fricción.",
          heading2: "En qué ayuda Hookora",
          list: {
            0: "Generar hooks más fuertes para videos sociales y contenido de creador",
            1: "Mejorar ideas de títulos de YouTube para que se sientan más claras y más clickables",
            2: "Escribir captions más rápido para Instagram, TikTok y Shorts",
            3: "Crear conjuntos de hashtags que se sientan más relevantes para el contenido",
            4: "Reescribir descripciones para que el mensaje se sienta más limpio",
            5: "Estructurar guiones cortos con mejor hook y mejor flujo",
            6: "Mejorar el texto del thumbnail para una primera impresión más fuerte"
          },
          heading3: "Un flujo más limpio para creadores que publican a menudo",
          paragraph3:
            "Una de las partes más difíciles de crear contenido no siempre es la idea. A veces es la repetición. Cuando publicas con frecuencia, la presión de seguir creando mejores hooks, mejores captions y mejores títulos puede volverse agotadora. Hookora reduce esa presión ayudándote a pasar más rápido de una idea inicial a una salida usable.",
          paragraph4:
            "Esto es especialmente útil para creadores nigerianos que intentan mantenerse constantes mientras equilibran contenido con trabajo, escuela, negocio u otras responsabilidades. Un flujo más fluido puede hacer que la constancia se sienta más realista.",
          heading4: "Hookora no es solo para un tipo de creador",
          paragraph5:
            "Hookora puede ayudar a creadores de entretenimiento, creadores educativos, creadores de negocios, marcas personales, marketers y social media managers. Ya sea que tu contenido sea divertido, informativo, persuasivo o impulsado por comunidad, la forma en que empaquetas la idea sigue importando. Hookora ayuda a fortalecer esa capa.",
          heading5: "En términos simples",
          paragraph6:
            "Hookora ayuda a los creadores nigerianos a crear con más rapidez, más confianza y menos fricción. Si ya tienes ideas pero necesitas ayuda para convertirlas en hooks, títulos, captions y guiones más fuertes, Hookora está diseñado para apoyar ese proceso."
        },
        relatedLinks: {
          first: "¿Qué es Hookora?",
          second: "Cómo Hookora ayuda a los creadores a escribir hooks más rápido"
        },
        cta: {
          badge: "Empieza gratis con Hookora",
          title: "Usa Hookora para construir contenido más fuerte más rápido",
          description:
            "Si quieres un flujo más limpio para hooks, títulos, captions, guiones y más, Hookora te ofrece un punto de partida práctico.",
          primaryLabel: "Crear cuenta gratis",
          secondaryLabel: "Explorar funciones"
        }
      },

      writeHooksFaster: {
        seoTitle: "Cómo Hookora ayuda a los creadores a escribir hooks más rápido | Blog de Hookora",
        seoDescription:
          "Descubre cómo Hookora ayuda a los creadores a escribir hooks más rápido con ángulos más claros, primeras líneas más fuertes y un flujo de contenido más fluido.",
        schemaDescription:
          "Descubre cómo Hookora ayuda a los creadores a escribir hooks más rápido con mejor estructura y opciones de primeras líneas más fuertes.",
        title: "Cómo Hookora ayuda a los creadores a escribir hooks más rápido",
        heroDescription:
          "Hookora ayuda a los creadores a pasar de ideas iniciales a primeras líneas más fuertes con mayor rapidez, para que la primera parte del proceso de contenido se sienta menos frustrante y más útil.",
        paragraphs: {
          0: "Escribir un hook parece simple hasta que intentas hacerlo todos los días. Muchos creadores conocen su tema, conocen el mensaje e incluso conocen el valor que quieren entregar, pero aun así se bloquean al principio. La primera línea no parece lo bastante fuerte. La apertura es demasiado plana. El hook suena como algo que la gente pasaría de largo.",
          1: "Hookora ayuda a reducir ese retraso. En lugar de obligar a los creadores a quedarse frente a una pantalla en blanco intentando inventar la apertura perfecta desde cero, Hookora ayuda a generar direcciones más fuertes más rápido."
        },
        list: {
          0: "Ayuda a convertir ideas iniciales en ángulos de apertura más claros.",
          1: "Da a los creadores puntos de partida más fuertes en lugar de obligarlos a empezar desde cero.",
          2: "Reduce la duda cuando existen múltiples direcciones posibles para un hook.",
          3: "Ayuda a probar distintos enfoques alrededor del mismo tema.",
          4: "Facilita seguir creando sin quedarse demasiado tiempo atascado en la primera línea."
        },
        sections: {
          whyHooksSlowCreators: {
            heading: "Por qué los hooks ralentizan a los creadores",
            paragraph1:
              "Los hooks cargan con mucha presión. Moldean la primera impresión. Influyen en si alguien deja de hacer scroll, hace clic o sigue mirando. Por eso, los creadores suelen pensarlos demasiado. Una idea de video sencilla puede tardar demasiado en arrancar porque la línea de apertura todavía se siente débil.",
            paragraph2:
              "Ese retraso afecta al resto del flujo. Cuando el hook no está claro, el guion avanza más lento. El título se vuelve más difícil de definir. El caption se siente menos enfocado. Hookora ayuda solucionando más rápido esa primera parte."
          },
          howHookoraMakesItFaster: {
            heading: "Cómo Hookora hace más rápida la escritura de hooks"
          },
          fromIdeaToFirstLine: {
            heading: "De la idea a una primera línea más fuerte",
            paragraph1:
              "Un creador puede saber que quiere hablar sobre errores financieros, consejos de contenido, relaciones, lecciones de negocio o comentario social, pero la mejor primera línea no siempre es evidente. Hookora ayuda a convertir ese tema en direcciones de hook más fuertes que se sienten más estructuradas, más impulsadas por curiosidad o más directas, según lo que necesite la idea.",
            paragraph2:
              "Esa velocidad importa porque crear contenido funciona mejor cuando el impulso no se interrumpe constantemente. Cuanto más rápido un creador pueda pasar de la idea a un hook usable, más fácil será construir el resto del contenido."
          },
          usefulForAllCreators: {
            heading: "Útil para creadores de formato corto y largo",
            paragraph:
              "Hookora es especialmente útil para creadores de formato corto porque los hooks importan muchísimo en Reels, Shorts y TikTok. Pero también ayuda a creadores de formato largo, porque mejores ángulos de apertura pueden mejorar títulos, intros y la presentación general de un video o publicación."
          },
          whatThisMeans: {
            heading: "Qué significa esto en la práctica",
            paragraph:
              "En lugar de pasar demasiado tiempo intentando formular una sola línea de apertura, los creadores pueden usar Hookora para generar mejores puntos de partida, elegir un ángulo más fuerte y avanzar más rápido al resto del flujo. Eso significa menos fricción al principio y más espacio para enfocarse en la calidad del contenido completo."
          },
          inSimpleTerms: {
            heading: "En términos simples",
            paragraph:
              "Hookora ayuda a los creadores a escribir hooks más rápido haciendo que la parte de apertura del proceso de contenido se sienta más clara, menos estresante y más fácil de atravesar. Si escribir primeras líneas es la parte donde tu proceso suele frenarse, Hookora está diseñado para ayudar con eso."
          }
        },
        ctaTitle: "Usa Hookora para crear hooks más fuertes con menos fricción",
        ctaDescription:
          "Si quieres una forma más rápida de crear mejores primeras líneas, ángulos más fuertes y un flujo de contenido más fluido, Hookora te ofrece un punto de partida práctico."
      },

      aiToolsNigeria: {
        seoTitle: "Herramientas de IA para creadores nigerianos | Hookora",
        seoDescription:
          "Descubre herramientas de IA para creadores nigerianos que quieren un flujo de contenido más rápido, hooks más fuertes, mejores captions, títulos más precisos y una producción más constante.",
        title: "Herramientas de IA para creadores nigerianos",
        heroBadge:
          "Guía de Hookora • Herramientas de IA para creadores nigerianos",
        heroTitle:
          "Herramientas de IA para creadores nigerianos que quieren un flujo de contenido más rápido y más claro",
        heroDescription:
          "Los creadores nigerianos están construyendo en un espacio rápido y competitivo donde la atención se mueve deprisa y la constancia importa. Hookora ayuda a reducir la fricción de escritura detrás de hooks, títulos, captions, guiones, hashtags y empaquetado de contenido para que los creadores avancen con más rapidez y más confianza.",
        highlights: {
          0: "Flujo de contenido más rápido",
          1: "Mejores hooks y títulos",
          2: "Menos fricción al escribir",
          3: "Pensado para creadores modernos"
        },
        previewTitle:
          "Lo que las herramientas de IA ayudan a hacer a los creadores nigerianos",
        previewItems: {
          0: {
            label: "Velocidad",
            text: "Pasa de una idea inicial a una salida usable más rápido sin quedarte bloqueado con las palabras."
          },
          1: {
            label: "Constancia",
            text: "Sigue publicando con más estructura incluso cuando la creatividad se sienta lenta."
          },
          2: {
            label: "Flujo",
            text: "Conecta hooks, captions, guiones y títulos dentro de un sistema más fluido."
          }
        },
        eyebrow: "Por qué importa",
        benefitsTitle:
          "Los creadores en Nigeria necesitan herramientas que hagan la creación de contenido más ligera, más precisa y más fácil de repetir",
        benefitsDescription:
          "Muchos creadores no están luchando porque les falten ideas. Están luchando porque convertir ideas en resultados pulidos lleva demasiado tiempo. Hookora ayuda a cerrar esa brecha más rápido.",
        benefits: {
          0: {
            title: "Reduce la fricción de escritura en todo tu flujo",
            text: "Muchos creadores saben lo que quieren decir, pero aun así pierden tiempo dándole forma. Hookora ayuda a que ese proceso sea más fácil."
          },
          1: {
            title: "Crea primeras impresiones más fuertes con mayor rapidez",
            text: "Hooks, títulos y texto de thumbnail influyen en si las personas se detienen y prestan atención. Hookora ayuda a fortalecer esos primeros puntos de contacto."
          },
          2: {
            title: "Apoya una creación de contenido más constante",
            text: "La constancia es más fácil cuando tu flujo se siente repetible. Hookora ayuda a producir más rápido sin hacer que el resultado se sienta aleatorio."
          },
          3: {
            title: "Útil para creadores de formato corto y social-first",
            text: "Ya hagas skits, videos educativos, comentarios, lifestyle o contenido de marca personal, Hookora ayuda con la capa de escritura detrás del empaquetado."
          },
          4: {
            title: "Ayuda a creadores solitarios a moverse como un pequeño equipo",
            text: "Un solo creador suele tener que pensar en hooks, títulos, captions, guiones y publicación a la vez. Hookora ayuda a simplificar esa carga."
          },
          5: {
            title: "Pensado para creadores que quieren resultados más claros",
            text: "El objetivo no es solo crear más contenido. El objetivo es crear contenido que se sienta más claro, más fuerte y más fácil de publicar."
          }
        },
        faqTitle: "FAQ sobre herramientas de IA para creadores nigerianos",
        faqIntro:
          "Aquí tienes algunas preguntas comunes sobre herramientas de IA para creadores nigerianos y dónde encaja Hookora.",
        faqs: {
          0: {
            question: "¿Qué son las herramientas de IA para creadores nigerianos?",
            answer:
              "Las herramientas de IA para creadores nigerianos son plataformas que ayudan a acelerar tareas de contenido como hooks, captions, guiones, títulos y planificación. Son útiles para creadores que quieren mantener la constancia sin hacerlo todo manualmente."
          },
          1: {
            question: "¿Por qué los creadores nigerianos necesitan herramientas de flujo con IA?",
            answer:
              "Muchos creadores en Nigeria construyen contenido con poco tiempo, poco apoyo y mucha presión por mantenerse visibles online. Las herramientas de flujo con IA ayudan a reducir la fricción de escritura y hacen que crear contenido sea más rápido y más repetible."
          },
          2: {
            question: "¿Hookora es solo para creadores nigerianos?",
            answer:
              "No. Hookora está creado para creadores en general, pero también es útil para creadores nigerianos que quieren un flujo más fluido para hooks, títulos, captions, hashtags, guiones y texto para thumbnails."
          },
          3: {
            question: "¿Qué tipo de creadores pueden usar Hookora?",
            answer:
              "Hookora es útil para YouTubers, creadores de TikTok, creadores de Instagram, skit makers, marcas personales, marketers, social media managers y pequeños creadores de negocios."
          },
          4: {
            question: "¿Hookora ayuda con más cosas además de hooks?",
            answer:
              "Sí. Hookora incluye herramientas para hooks virales, títulos de YouTube, captions, hashtags, reescritura de descripciones, guiones cortos y análisis de texto para thumbnails."
          },
          5: {
            question: "¿Pueden usar Hookora los principiantes?",
            answer:
              "Sí. Hookora está diseñado para sentirse útil tanto si estás empezando como si ya creas contenido con constancia."
          }
        },
        ctaTitle:
          "Usa Hookora para construir un flujo de contenido más rápido como creador nigeriano",
        ctaDescription:
          "Si quieres una forma más fluida de crear hooks, títulos, captions, guiones, hashtags y empaquetado de contenido, Hookora te ofrece un punto de partida práctico."
      },

      bestHookGeneratorNigeria: {
        seoTitle:
          "Mejor generador de hooks para creadores nigerianos | Hookora",
        seoDescription:
          "¿Buscas el mejor generador de hooks para creadores nigerianos? Usa Hookora para crear hooks de contenido más fuertes, líneas de apertura más impactantes e ideas más llamativas con mayor rapidez.",
        title: "El mejor generador de hooks para creadores nigerianos",
        heroBadge:
          "Guía de Hookora • Mejor generador de hooks para creadores nigerianos",
        heroTitle:
          "El mejor generador de hooks para creadores nigerianos que quieren captar más atención desde la primera línea",
        heroDescription:
          "Un hook fuerte puede cambiar cómo responde la gente a tu contenido desde el primer segundo. Hookora ayuda a los creadores nigerianos a generar ideas de hooks más precisas con mayor rapidez para crear aperturas más fuertes para videos, captions y contenido social sin perder tiempo mirando una página en blanco.",
        highlights: {
          0: "Primeras líneas más fuertes",
          1: "Ideación de hooks más rápida",
          2: "Mejor atención para el contenido",
          3: "Pensado para el flujo del creador"
        },
        primaryCtaLabel: "Empieza gratis",
        secondaryCtaLabel: "Probar funciones de hooks",
        previewTitle:
          "Lo que un generador de hooks debería ayudarte a hacer",
        previewItems: {
          attention: {
            label: "Atención",
            text: "Crea líneas de apertura que hagan que la gente se detenga más rápido y preste atención a tu idea."
          },
          clarity: {
            label: "Claridad",
            text: "Convierte ángulos iniciales de contenido en direcciones de hook que se sientan más precisas y más fáciles de usar."
          },
          options: {
            label: "Opciones",
            text: "Explora múltiples ángulos de hook desde un solo tema en lugar de quedarte atrapado en una apertura débil."
          }
        },
        benefitsEyebrow: "Por qué los creadores lo usan",
        benefitsTitle:
          "El mejor generador de hooks para creadores nigerianos debe ayudar a que el contenido se sienta más fuerte antes de que la audiencia vea el mensaje completo",
        benefitsDescription:
          "Los hooks no son solo un detalle de escritura. Moldean la primera impresión, la curiosidad y si la gente sigue mirando. Hookora ayuda a llegar a líneas de apertura más fuertes con más rapidez y menos fricción.",
        benefits: {
          one: {
            title: "Empieza el contenido más rápido desde una idea inicial",
            text: "No necesitas un concepto perfecto antes de empezar. Hookora ayuda a convertir pensamientos iniciales en direcciones de apertura más fuertes rápidamente."
          },
          two: {
            title: "Crea múltiples ángulos de hook desde un mismo tema",
            text: "Una sola idea puede enmarcarse de formas distintas. Hookora ayuda a explorar esas opciones sin perder demasiado tiempo reescribiendo manualmente."
          },
          three: {
            title: "Haz más fuerte el empaquetado del contenido corto",
            text: "Para creadores que hacen Reels, Shorts, TikToks y captions cortos, las aperturas fuertes importan aún más porque la atención se mueve muy rápido."
          },
          four: {
            title: "Reduce la duda creativa",
            text: "Muchos creadores retrasan la publicación porque se quedan atascados en cómo empezar. Hookora ayuda a eliminar parte de esa duda y mantiene el flujo en movimiento."
          },
          five: {
            title: "Apoya un empaquetado de contenido más amplio",
            text: "Un hook funciona mejor cuando conecta con el título, el caption, el guion y el thumbnail. Hookora apoya ese flujo más completo del creador."
          },
          six: {
            title: "Útil para creadores que están construyendo constancia",
            text: "Ya publiques con frecuencia o estés intentando ser más constante, un mejor apoyo para hooks hace que publicar sea más fácil."
          }
        },
        faqTitle:
          "FAQ sobre el mejor generador de hooks para creadores nigerianos",
        faqIntro:
          "Aquí tienes algunas preguntas comunes sobre generadores de hooks para creadores nigerianos y cómo ayuda Hookora.",
        faqs: {
          one: {
            question:
              "¿Cuál es el mejor generador de hooks para creadores nigerianos?",
            answer:
              "El mejor generador de hooks para creadores nigerianos es el que ayuda a convertir ideas iniciales en líneas de apertura más fuertes de forma rápida y clara. Hookora está diseñado para ayudar a crear hooks, títulos, captions y guiones más fuertes desde un solo flujo."
          },
          two: {
            question:
              "¿Por qué importan tanto los hooks para los creadores nigerianos?",
            answer:
              "Los hooks importan porque la primera línea suele decidir si alguien sigue mirando, leyendo o deslizando. En plataformas rápidas, hooks más fuertes ayudan a captar atención con mayor rapidez."
          },
          three: {
            question: "¿Puede Hookora ayudar con Reels, Shorts y TikTok?",
            answer:
              "Sí. Hookora es útil para creadores de formato corto que quieren aperturas más fuertes para YouTube Shorts, Instagram Reels, TikTok, captions y contenido social."
          },
          four: {
            question: "¿Hookora es solo un generador de hooks?",
            answer:
              "No. Hookora también incluye herramientas para títulos de YouTube, captions, hashtags, reescritura de descripciones, guiones cortos y análisis de texto para thumbnails."
          },
          five: {
            question:
              "¿Pueden los creadores principiantes en Nigeria usar Hookora?",
            answer:
              "Sí. Hookora es útil tanto para principiantes como para creadores activos porque ayuda a reducir la presión de empezar desde una página en blanco."
          },
          six: {
            question: "¿Hookora me ayuda a crear múltiples ángulos de hook?",
            answer:
              "Sí. Uno de sus beneficios principales es explorar múltiples direcciones de hook desde un solo tema para que puedas elegir una redacción más fuerte sin pensarlo demasiado."
          }
        },
        cta: {
          badge: "Empieza gratis con Hookora",
          title:
            "Usa Hookora como tu generador de hooks para aperturas de contenido más fuertes",
          description:
            "Si quieres hooks más fuertes, mejores primeras impresiones y un flujo del creador más fluido, Hookora te ofrece un punto de partida práctico.",
          primaryLabel: "Crear cuenta gratis",
          secondaryLabel: "Explorar generador de hooks"
        }
      },

      contentToolsNigeria: {
        seoTitle: "Herramientas de contenido para creadores en Nigeria | Hookora",
        seoDescription:
          "Descubre herramientas de contenido para creadores en Nigeria que quieren un flujo más rápido, hooks más fuertes, mejores captions, títulos más precisos y una creación de contenido más sencilla.",
        title: "Herramientas de contenido para creadores en Nigeria",
        heroBadge:
          "Guía de Hookora • Herramientas de contenido para creadores en Nigeria",
        heroTitle:
          "Herramientas de contenido para creadores en Nigeria que quieren un flujo más rápido y más organizado",
        heroDescription:
          "Los creadores en Nigeria a menudo tienen que hacerlo todo por sí mismos, desde ideas hasta captions, títulos, hooks, guiones y publicación. Hookora ayuda a simplificar ese proceso haciendo que la parte de escritura y empaquetado del contenido se sienta más rápida, más clara y más fácil de repetir.",
        highlights: {
          0: "Creación de contenido más rápida",
          1: "Flujo más claro",
          2: "Empaquetado de contenido más fuerte",
          3: "Pensado para creadores modernos"
        },
        primaryCtaLabel: "Empieza gratis",
        secondaryCtaLabel: "Explorar funciones",
        previewTitle:
          "Lo que las herramientas de contenido deberían ayudar a hacer a los creadores",
        previewItems: {
          0: {
            label: "Organizar",
            text: "Reúne hooks, captions, guiones y títulos dentro de un flujo más limpio."
          },
          1: {
            label: "Acelerar",
            text: "Reduce el tiempo que lleva pasar de la idea al contenido usable."
          },
          2: {
            label: "Mejorar el resultado",
            text: "Haz que el contenido se sienta más preciso y más fácil de publicar con constancia."
          }
        },
        benefitsEyebrow: "Por qué los creadores las usan",
        benefitsTitle:
          "Las herramientas de contenido deberían hacer que crear se sienta más ligero, no más disperso",
        benefitsDescription:
          "Las mejores herramientas para creadores no solo añaden funciones. Eliminan fricción, hacen que el contenido sea más fácil de moldear y ayudan a avanzar sin perder claridad.",
        benefits: {
          0: {
            title: "Reduce la presión de hacerlo todo manualmente",
            text: "Muchos creadores se encargan solos de la ideación, escritura, empaquetado y publicación. Hookora ayuda a reducir parte de esa carga acelerando la escritura de contenido."
          },
          1: {
            title: "Apoya hooks, captions, títulos y guiones en un solo flujo",
            text: "En lugar de saltar entre herramientas desconectadas, los creadores pueden construir mejores resultados desde un flujo más fluido."
          },
          2: {
            title: "Ayuda a los creadores a mantenerse más constantes",
            text: "La constancia se vuelve más fácil cuando el sistema detrás del contenido es más simple y más fácil de repetir."
          },
          3: {
            title: "Útil para distintos tipos de creadores",
            text: "Ya crees videos educativos, skits, talking-head, lifestyle o contenido de marca, un flujo más claro marca una diferencia real."
          },
          4: {
            title: "Hace más fuerte el empaquetado del contenido",
            text: "No se trata solo de la idea. El empaquetado también importa. Hookora ayuda con la capa de escritura detrás de ese empaquetado."
          },
          5: {
            title: "Da más impulso a los creadores solitarios",
            text: "Un flujo de contenido más fluido puede ayudar a un creador individual a avanzar más rápido y crear con más confianza con el tiempo."
          }
        },
        faqTitle:
          "FAQ sobre herramientas de contenido para creadores en Nigeria",
        faqIntro:
          "Aquí tienes algunas preguntas comunes sobre herramientas de contenido para creadores en Nigeria y cómo Hookora encaja en ese flujo.",
        faqs: {
          0: {
            question:
              "¿Qué son las herramientas de contenido para creadores en Nigeria?",
            answer:
              "Las herramientas de contenido para creadores en Nigeria son plataformas que ayudan a hacer la creación de contenido más fácil, más rápida y más organizada. Pueden ayudar con hooks, títulos, captions, guiones, hashtags y empaquetado."
          },
          1: {
            question:
              "¿Por qué los creadores en Nigeria necesitan herramientas de flujo?",
            answer:
              "Muchos creadores producen contenido con constancia sin equipos grandes ni demasiado tiempo extra. Las herramientas de flujo ayudan a reducir el estrés, acelerar la escritura y hacer que la producción sea más fácil de repetir."
          },
          2: {
            question: "¿Qué tipo de creadores pueden usar Hookora?",
            answer:
              "Hookora es útil para YouTubers, creadores de TikTok, creadores de Instagram, skit makers, creadores de lifestyle, educadores, marcas personales, marketers y social media managers."
          },
          3: {
            question: "¿Hookora reemplaza las herramientas de edición?",
            answer:
              "No. Hookora se enfoca en la parte de escritura y empaquetado del proceso creativo. Ayuda con hooks, títulos, captions, guiones, hashtags, descripciones y texto para thumbnails."
          },
          4: {
            question:
              "¿Puede Hookora ayudar a creadores que publican con frecuencia?",
            answer:
              "Sí. Hookora es especialmente útil para creadores que necesitan una forma más fluida y rápida de seguir produciendo contenido con constancia."
          },
          5: {
            question:
              "¿Hookora es útil para creadores principiantes en Nigeria?",
            answer:
              "Sí. Es útil tanto para principiantes como para creadores activos porque ayuda a eliminar la presión de la página en blanco y apoya un flujo de contenido más claro."
          }
        },
        ctaTitle:
          "Usa Hookora como parte de tu flujo de contenido en Nigeria",
        ctaDescription:
          "Si quieres una forma más clara de crear hooks, captions, títulos, guiones, hashtags y empaquetado de contenido, Hookora te ofrece un punto de partida práctico.",
        cta: {
          badge: "Empieza gratis con Hookora",
          title:
            "Usa Hookora como parte de tu flujo de contenido en Nigeria",
          description:
            "Si quieres una forma más clara de crear hooks, captions, títulos, guiones, hashtags y empaquetado de contenido, Hookora te ofrece un punto de partida práctico.",
          primaryLabel: "Crear cuenta gratis",
          secondaryLabel: "Ver funciones"
        }
      },

      bestCaptionNigeria: {
        seoTitle:
          "Mejor generador de captions para creadores nigerianos | Hookora",
        seoDescription:
          "¿Buscas el mejor generador de captions para creadores nigerianos? Usa Hookora para escribir captions más claros, más fuertes y más rápidos para contenido social y flujo creativo.",
        heroBadge:
          "Guía de Hookora • Mejor generador de captions para creadores nigerianos",
        heroTitle:
          "Mejor generador de captions para creadores nigerianos que quieren captions sociales más rápidos y más claros",
        heroDescription:
          "Escribir captions cada vez que publicas puede ralentizar tu flujo, especialmente cuando ya conoces la idea del contenido pero no sabes cómo expresarla bien. Hookora ayuda a los creadores nigerianos a generar direcciones de captions más claras con mayor rapidez para que publicar en redes se sienta más fluido y más constante.",
        highlights: {
          0: "Escribe captions más rápido",
          1: "Mejora la claridad del mensaje",
          2: "Apoya el flujo social",
          3: "Pensado para creadores"
        },
        secondaryCta: "Explorar herramienta de captions",
        previewTitle:
          "Lo que un generador de captions debería ayudarte a hacer",
        previewItems: {
          0: {
            label: "Velocidad",
            text: "Pasa de la idea a un caption usable con mayor rapidez sin quedarte bloqueado con las palabras."
          },
          1: {
            label: "Mensaje",
            text: "Moldea captions que se sientan más claros, más intencionales y más fáciles de publicar."
          },
          2: {
            label: "Constancia",
            text: "Sigue publicando con un flujo de captions más fluido en distintas plataformas."
          }
        },
        benefitsEyebrow: "Por qué los creadores lo usan",
        benefitsTitle:
          "El mejor generador de captions para creadores nigerianos debería ayudar a que el contenido se sienta más completo sin que los captions se vuelvan genéricos",
        benefitsDescription:
          "Los captions forman parte de cómo tu audiencia entiende lo que quieres decir. Hookora ayuda a los creadores a dar forma a esa capa escrita con más rapidez para que el empaquetado del contenido se sienta más claro y más fácil de gestionar.",
        benefits: {
          0: {
            title: "Escribe captions más rápido desde una sola idea de contenido",
            text: "No necesitas seguir reescribiendo desde cero cada vez que publicas. Hookora ayuda a pasar de un pensamiento inicial a un caption usable con más rapidez."
          },
          1: {
            title: "Mejora la claridad en las publicaciones sociales",
            text: "Un caption funciona mejor cuando el mensaje es fácil de entender. Hookora ayuda a reforzar esa claridad sin hacer que la escritura se sienta rígida."
          },
          2: {
            title: "Apoya distintas plataformas de creador",
            text: "Ya publiques en Instagram, TikTok, YouTube Shorts u otras plataformas, un mejor soporte para captions hace que el flujo se sienta más suave."
          },
          3: {
            title: "Reduce la fricción repetitiva al publicar",
            text: "Escribir captions puede volverse repetitivo cuando creas con frecuencia. Hookora ayuda a que esa parte del flujo sea más fácil."
          },
          4: {
            title: "Conecta los captions con el resto de tu sistema de contenido",
            text: "Los captions funcionan mejor cuando encajan con tu hook, título, guion y mensaje. Hookora apoya ese flujo más amplio del creador."
          },
          5: {
            title: "Útil para principiantes y creadores activos",
            text: "Tanto si todavía estás aprendiendo a empaquetar contenido como si ya publicas con frecuencia, un mejor soporte para captions te ayuda a crear con más confianza."
          }
        },
        faqTitle:
          "FAQ sobre el mejor generador de captions para creadores nigerianos",
        faqIntro:
          "Aquí tienes algunas preguntas comunes sobre generadores de captions para creadores nigerianos y cómo ayuda Hookora.",
        faqs: {
          0: {
            question:
              "¿Cuál es el mejor generador de captions para creadores nigerianos?",
            answer:
              "El mejor generador de captions para creadores nigerianos es el que ayuda a convertir ideas de contenido en captions más claros y más usables con rapidez. Hookora ayuda a escribir captions más rápido mientras también apoya hooks, títulos, guiones, hashtags y empaquetado."
          },
          1: {
            question:
              "¿Por qué importan los captions para los creadores nigerianos?",
            answer:
              "Los captions ayudan a explicar tu mensaje, añadir contexto y hacer que el contenido se sienta más completo. Captions más fuertes pueden ayudar a comunicar con claridad y publicar con más confianza."
          },
          2: {
            question:
              "¿Puede Hookora ayudar con captions de Instagram y TikTok?",
            answer:
              "Sí. Hookora es útil para creadores que hacen contenido para Instagram, TikTok, YouTube Shorts y otras plataformas sociales donde los captions apoyan la publicación completa."
          },
          3: {
            question: "¿Hookora solo genera captions?",
            answer:
              "No. Hookora también incluye herramientas para hooks virales, títulos de YouTube, hashtags, reescritura de descripciones, guiones cortos y análisis de texto para thumbnails."
          },
          4: {
            question:
              "¿Pueden usar Hookora los principiantes para escribir captions?",
            answer:
              "Sí. Hookora es útil tanto si estás empezando como si ya publicas con constancia. Ayuda a reducir la fricción al escribir y hace que crear captions sea más fácil."
          },
          5: {
            question:
              "¿Puede Hookora ayudarme a escribir captions más rápido desde una sola idea?",
            answer:
              "Sí. Uno de sus beneficios principales es usar un mismo tema o ángulo de contenido para generar direcciones de captions más claras con mayor rapidez."
          }
        },
        ctaTitle:
          "Usa Hookora para escribir captions más fuertes como creador nigeriano",
        ctaDescription:
          "Si quieres escribir captions más rápido, reforzar la claridad del mensaje y tener un flujo de contenido más fluido, Hookora te ofrece un punto de partida práctico.",
        ctaSecondaryLabel: "Probar generador de captions"
      },

      bestCaptionGeneratorNigeria: {
        seoTitle:
          "Mejor generador de captions para creadores nigerianos | Hookora",
        seoDescription:
          "¿Buscas el mejor generador de captions para creadores nigerianos? Usa Hookora para escribir captions más claros, más fuertes y más rápidos para contenido social y flujo creativo.",
        title: "El mejor generador de captions para creadores nigerianos",
        heroBadge:
          "Guía de Hookora • Mejor generador de captions para creadores nigerianos",
        heroTitle:
          "Mejor generador de captions para creadores nigerianos que quieren captions sociales más rápidos y más claros",
        heroDescription:
          "Escribir captions cada vez que publicas puede ralentizar tu flujo, especialmente cuando ya conoces la idea del contenido pero no sabes cómo expresarla bien. Hookora ayuda a los creadores nigerianos a generar direcciones de captions más claras con mayor rapidez para que publicar en redes se sienta más fluido y más constante.",
        highlights: [
          "Escribe captions más rápido",
          "Mejora la claridad del mensaje",
          "Apoya el flujo social",
          "Pensado para creadores"
        ],
        primaryCtaLabel: "Empieza gratis",
        secondaryCtaLabel: "Explorar herramienta de captions",
        previewTitle:
          "Lo que un generador de captions debería ayudarte a hacer",
        previewItems: {
          speed: {
            label: "Velocidad",
            text: "Pasa de la idea a un caption usable con mayor rapidez sin quedarte bloqueado con las palabras."
          },
          message: {
            label: "Mensaje",
            text: "Moldea captions que se sientan más claros, más intencionales y más fáciles de publicar."
          },
          consistency: {
            label: "Constancia",
            text: "Sigue publicando con un flujo de captions más fluido en distintas plataformas."
          }
        },
        benefitsEyebrow: "Por qué los creadores lo usan",
        benefitsTitle:
          "El mejor generador de captions para creadores nigerianos debería ayudar a que el contenido se sienta más completo sin que los captions se vuelvan genéricos",
        benefitsDescription:
          "Los captions forman parte de cómo tu audiencia entiende lo que quieres decir. Hookora ayuda a los creadores a dar forma a esa capa escrita con más rapidez para que el empaquetado del contenido se sienta más claro y más fácil de gestionar.",
        benefits: {
          one: {
            title: "Escribe captions más rápido desde una sola idea de contenido",
            text: "No necesitas seguir reescribiendo desde cero cada vez que publicas. Hookora ayuda a pasar de un pensamiento inicial a un caption usable con más rapidez."
          },
          two: {
            title: "Mejora la claridad en las publicaciones sociales",
            text: "Un caption funciona mejor cuando el mensaje es fácil de entender. Hookora ayuda a reforzar esa claridad sin hacer que la escritura se sienta rígida."
          },
          three: {
            title: "Apoya distintas plataformas de creador",
            text: "Ya publiques en Instagram, TikTok, YouTube Shorts u otras plataformas, un mejor soporte para captions hace que el flujo se sienta más suave."
          },
          four: {
            title: "Reduce la fricción repetitiva al publicar",
            text: "Escribir captions puede volverse repetitivo cuando creas con frecuencia. Hookora ayuda a que esa parte del flujo sea más fácil."
          },
          five: {
            title: "Conecta los captions con el resto de tu sistema de contenido",
            text: "Los captions funcionan mejor cuando encajan con tu hook, título, guion y mensaje. Hookora apoya ese flujo más amplio del creador."
          },
          six: {
            title: "Útil para principiantes y creadores activos",
            text: "Tanto si todavía estás aprendiendo a empaquetar contenido como si ya publicas con frecuencia, un mejor soporte para captions te ayuda a crear con más confianza."
          }
        },
        faqTitle:
          "FAQ sobre el mejor generador de captions para creadores nigerianos",
        faqIntro:
          "Aquí tienes algunas preguntas comunes sobre generadores de captions para creadores nigerianos y cómo ayuda Hookora.",
        faqs: {
          one: {
            question:
              "¿Cuál es el mejor generador de captions para creadores nigerianos?",
            answer:
              "El mejor generador de captions para creadores nigerianos es el que ayuda a convertir ideas de contenido en captions más claros y más usables con rapidez. Hookora ayuda a escribir captions más rápido mientras también apoya hooks, títulos, guiones, hashtags y empaquetado."
          },
          two: {
            question:
              "¿Por qué importan los captions para los creadores nigerianos?",
            answer:
              "Los captions ayudan a explicar tu mensaje, añadir contexto y hacer que el contenido se sienta más completo. Captions más fuertes pueden ayudar a comunicar con claridad y publicar con más confianza."
          },
          three: {
            question:
              "¿Puede Hookora ayudar con captions de Instagram y TikTok?",
            answer:
              "Sí. Hookora es útil para creadores que hacen contenido para Instagram, TikTok, YouTube Shorts y otras plataformas sociales donde los captions apoyan la publicación completa."
          },
          four: {
            question: "¿Hookora solo genera captions?",
            answer:
              "No. Hookora también incluye herramientas para hooks virales, títulos de YouTube, hashtags, reescritura de descripciones, guiones cortos y análisis de texto para thumbnails."
          },
          five: {
            question:
              "¿Pueden usar Hookora los principiantes para escribir captions?",
            answer:
              "Sí. Hookora es útil tanto si estás empezando como si ya publicas con constancia. Ayuda a reducir la fricción al escribir y hace que crear captions sea más fácil."
          },
          six: {
            question:
              "¿Puede Hookora ayudarme a escribir captions más rápido desde una sola idea?",
            answer:
              "Sí. Uno de sus beneficios principales es usar un mismo tema o ángulo de contenido para generar direcciones de captions más claras con mayor rapidez."
          }
        },
        cta: {
          badge: "Empieza gratis con Hookora",
          title:
            "Usa Hookora para escribir captions más fuertes como creador nigeriano",
          description:
            "Si quieres escribir captions más rápido, reforzar la claridad del mensaje y tener un flujo de contenido más fluido, Hookora te ofrece un punto de partida práctico.",
          primaryLabel: "Crear cuenta gratis",
          secondaryLabel: "Probar generador de captions"
        }
      },

      betterHooksNigeria: {
        seoTitle:
          "Cómo los creadores nigerianos pueden escribir mejores hooks | Hookora",
        seoDescription:
          "Aprende cómo los creadores nigerianos pueden escribir mejores hooks para Reels, Shorts, TikTok y contenido social con primeras líneas más claras y una atención más fuerte.",
        title: "Cómo los creadores nigerianos pueden escribir mejores hooks",
        heroBadge:
          "Guía de Hookora • Mejores hooks para creadores nigerianos",
        heroTitle:
          "Cómo los creadores nigerianos pueden escribir mejores hooks para lograr más atención en su contenido",
        heroDescription:
          "Muchos creadores ya tienen buenas ideas, pero unas aperturas débiles hacen que el contenido sea más fácil de ignorar. Hookora ayuda a los creadores nigerianos a construir mejores hooks haciendo que las primeras líneas sean más claras, más precisas y más fáciles de explorar desde un solo tema.",
        highlights: {
          0: "Primeras líneas más fuertes",
          1: "Creación de hooks más rápida",
          2: "Mejor empaquetado short-form",
          3: "Pensado para el flujo del creador"
        },
        secondaryCta: "Probar generador de hooks",
        previewTitle:
          "Lo que unos mejores hooks ayudan a hacer a los creadores",
        previewItems: {
          0: {
            label: "Detener el scroll",
            text: "Haz que las personas presten atención más rápido antes de pasar a otra cosa."
          },
          1: {
            label: "Aclarar",
            text: "Convierte ángulos iniciales en aperturas que se sientan más fáciles de entender."
          },
          2: {
            label: "Fortalecer",
            text: "Dale a tu contenido una mejor primera impresión antes de que se despliegue el resto del mensaje."
          }
        },
        benefitsEyebrow: "Cómo mejorar hooks",
        benefitsTitle:
          "Escribir mejores hooks empieza por hacer que tu primera línea sea más clara, más precisa y más digna de atención",
        benefitsDescription:
          "La mayoría de los hooks débiles no fallan porque el tema sea malo. Fallan porque la apertura se siente demasiado plana, demasiado vaga o demasiado fácil de saltar. Hookora ayuda a mejorar ese primer momento.",
        benefits: {
          0: {
            title: "Empieza con el ángulo más fuerte de tu idea",
            text: "Un hook más fuerte empieza con lo más interesante, sorprendente, útil o emocionalmente potente de tu contenido."
          },
          1: {
            title: "Evita aperturas débiles y lentas",
            text: "Si tu primera línea se siente demasiado genérica o demasiado segura, la gente tiene más probabilidades de seguir haciendo scroll. Los mejores hooks van al punto más rápido."
          },
          2: {
            title: "Explora más de una dirección de hook",
            text: "Muchos creadores se detienen en la primera versión de un hook. El contenido más fuerte suele surgir al probar múltiples direcciones antes de elegir una."
          },
          3: {
            title: "Ajusta el hook a la plataforma",
            text: "El contenido short-form suele necesitar lenguaje que capture atención de forma más inmediata. Mejores hooks ayudan a adaptar el mensaje a cómo consume contenido la gente."
          },
          4: {
            title: "Conecta los hooks con el resto del contenido",
            text: "Un buen hook funciona todavía mejor cuando encaja con el título, el caption, el guion y el thumbnail. Hookora ayuda a moldear ese empaquetado más completo."
          },
          5: {
            title: "Usa herramientas que reduzcan la presión de la página en blanco",
            text: "Cuando los creadores no saben cómo empezar, el proceso se frena. Hookora ayuda a reducir esa fricción y hace que escribir mejores hooks sea más repetible."
          }
        },
        faqTitle:
          "FAQ sobre cómo los creadores nigerianos pueden escribir mejores hooks",
        faqIntro:
          "Aquí tienes algunas preguntas comunes sobre cómo escribir mejores hooks como creador nigeriano y cómo ayuda Hookora.",
        faqs: {
          0: {
            question:
              "¿Por qué importan tanto los hooks para los creadores nigerianos?",
            answer:
              "Los hooks importan porque moldean la primera impresión. En plataformas rápidas, la línea de apertura suele decidir si la gente sigue mirando, leyendo o se va."
          },
          1: {
            question: "¿Qué hace que un hook sea más fuerte?",
            answer:
              "Un hook más fuerte suele ser más claro, más amigable con la atención y más interesante emocionalmente. Le da a la gente una razón para quedarse más tiempo con el contenido."
          },
          2: {
            question:
              "¿Puede Hookora ayudar a los creadores nigerianos a escribir mejores hooks más rápido?",
            answer:
              "Sí. Hookora ayuda a generar direcciones de hooks más precisas desde un solo tema para que los creadores pasen menos tiempo adivinando y más tiempo creando."
          },
          3: {
            question:
              "¿Hookora solo es útil para creadores de formato corto?",
            answer:
              "No. Hookora es especialmente útil para short-form, pero también ayuda con captions, títulos, guiones, hashtags, descripciones y texto para thumbnails dentro de flujos creativos más amplios."
          },
          4: {
            question:
              "¿Pueden los principiantes usar Hookora para mejorar sus hooks?",
            answer:
              "Sí. Hookora es útil para principiantes porque reduce la presión de la página en blanco y facilita explorar direcciones de apertura más fuertes."
          },
          5: {
            question: "¿Un solo tema puede producir múltiples hooks?",
            answer:
              "Sí. Una idea de contenido a menudo puede enmarcarse de varias maneras. Explorar múltiples ángulos de hook es una de las mejores formas de mejorar el empaquetado del contenido."
          }
        },
        ctaTitle: "Usa Hookora para escribir mejores hooks con menos fricción",
        ctaDescription:
          "Si quieres primeras líneas más fuertes, direcciones de hooks más claras y una forma más fluida de empaquetar contenido, Hookora te ofrece un punto de partida práctico.",
        ctaSecondaryLabel: "Explorar generador de hooks"
      }
    }
  },

  pricingCta: {
  badge: "Empieza gratis con Hookora",
  title:
    "Elige un plan de Hookora y construye tu flujo de contenido con más claridad",
  description:
    "Empieza con el plan Free, mejora cuando necesites más espacio creativo y usa Hookora para que la creación de contenido se sienta más rápida, más precisa y más consistente.",
  startFree: "Crear cuenta gratis",
  exploreFeatures: "Explorar funciones"
},

publicPricingHero: {
  badge: "Precios mensuales o anuales para creadores",
  title: "Elige el plan de Hookora que se adapta a tu ritmo creativo",
  description:
    "Empieza gratis, gana impulso y mejora cuando necesites más generaciones, más recursos guardados y un flujo más sólido para una producción de contenido constante. Elige facturación mensual o ahorra 17% con la facturación anual.",
  startFree: "Empieza gratis",
  exploreFeatures: "Explorar funciones",
  summaryTitle: "Elige el plan de Hookora que se adapta a tu flujo",
  summaryDescription:
    "Empieza con Free y luego pasa a Creator o Pro cuando necesites más producción, más herramientas premium y un soporte de flujo más sólido."
},

publicPricing: {
  title: "Elige el plan de Hookora que se adapta a tu flujo",
  subtitle:
    "Empieza con Free y luego pasa a Creator o Pro cuando necesites más producción, más herramientas premium y un soporte de flujo más sólido."
},

  pricing: {
  heading: "Planes pensados para creadores",
  seoTitle: "Precios de Hookora | Planes Free, Creator y Pro",
  seoDescription:
    "Consulta los precios de Hookora para los planes Free, Creator y Pro. Empieza gratis y mejora para obtener más generaciones, más recursos guardados y un soporte de flujo más sólido para creadores.",
  softwareSchemaDescription:
    "Hookora es una plataforma de flujo de hooks con IA para creadores con herramientas para hooks, títulos, captions, hashtags, descripciones, guiones y texto para miniaturas.",
  billing: "Facturación",
  billingPageIntro:
    "Revisa tu plan actual, estado de facturación, intervalo de facturación, Reward Credits, Wallet Balance y controles de suscripción.",
  save17: "Ahorra 17%",
  twoMonthsFree: "2 meses gratis",
  inactive: "Inactivo",
  monthly: "Mensual",
  yearly: "Anual",
  periodMonth: "/mes",
  periodYear: "/año",
  approx: "Aprox.",
  billedMonthly: "Facturado mensualmente",
  choose: "Elegir",
  currentPlan: "Plan actual",
  switchToFree: "Cambiar a Free",
  updating: "Actualizando...",
  redirecting: "Redirigiendo...",
  mostPopular: "Más popular",
  startFree: "Empieza gratis",
  chooseCreator: "Elegir Creator",
  choosePro: "Elegir Pro",
  startWithCoreWorkflow: "Empieza con el flujo principal",
  freeDescription:
    "Ideal para probar Hookora y familiarizarte con el flujo principal antes de desbloquear más potencia.",
  creatorDescription:
    "Ideal para creadores individuales que quieren herramientas premium para empaquetado, reescrituras, retención y una mejor calidad de contenido.",
  proDescription:
    "Ideal para creadores serios y equipos pequeños que buscan herramientas estratégicas más profundas, más volumen y un control creativo más sólido.",
  coreWritingTools: "Herramientas principales de escritura",
  premiumWorkflowTools: "Herramientas premium de flujo",
  advancedStrategicTools: "Herramientas estratégicas avanzadas",
  heroDescription:
    "Elige el plan de Hookora que encaje con tu flujo de trabajo, tu nivel de uso y tus objetivos como creador.",
  currentUsage: "Uso actual",
  plan: "Plan",
  billingStatus: "Estado de facturación",
  billingInterval: "Intervalo de facturación",
  monthlyGenerations: "Generaciones mensuales",
  remainingThisMonth: "Restantes este mes",
  savedHooks: "Hooks guardados",
  unlimited: "Ilimitado",
  resetIn: "Reinicio en",
  days: "días",
  billingPeriodEnd: "Fin del período de facturación",
  creatorRewards: "Hookora Creator Rewards",
  creatorRewardsDescription:
    "Comparte Hookora, gana Reward Credits y sigue impulsando tu flujo creativo.",
  rewardCreditsBalance: "Saldo de Reward Credits",
  shareYourReferral: "Comparte tu referido",
  referralCode: "Código de referido",
  referralLink: "Enlace de referido",
  referralRewardNote:
    "Los Reward Credits se aplican antes del Wallet Balance, y el Wallet Balance se aplica antes del pago con tarjeta durante el checkout.",
  rewardsSummary: "Resumen de recompensas",
  rewardCredits: "Reward Credits",
  walletBalance: "Wallet Balance",
  totalReferrals: "Referidos totales",
  verifiedReferrals: "Referidos verificados",
  paidReferrals: "Referidos de pago",
  fundWalletBalance: "Recargar Wallet Balance",
  fundWalletBalanceDescription:
    "Añade dinero a tu Wallet Balance mediante Paystack y úsalo para pagos futuros.",
  fundWalletDescription:
    "Añade dinero a tu Wallet Balance para pagos de suscripción actuales o futuros.",
  enterAmount: "Introduce un monto",
  enterWalletFundingAmount: "Introduce el monto para recargar la billetera",
  fundWallet: "Recargar billetera",
  walletTransactionHistory: "Historial de transacciones de la billetera",
  noWalletTransactions: "Todavía no hay transacciones en la billetera.",
  walletFunding: "Recarga de billetera",
  appliedToBilling: "Aplicado a la facturación",
  transaction: "Transacción",
  checkoutOrderPrefix: "En el checkout, Hookora aplica",
  then: "después",
  cardPayment: "Pago con tarjeta",
  checkoutOrderSuffix: "para cubrir el monto restante.",
  whatEachPlanUnlocks: "Lo que desbloquea cada plan",
  planUnlockFreeText:
    "Herramientas principales de escritura para la creación diaria de contenido.",
  planUnlockCreatorText:
    "Herramientas premium de flujo para un mejor empaquetado, reescrituras, retención y ejecución.",
  planUnlockProText:
    "Herramientas estratégicas avanzadas para sistemas de creador más profundos y una diferenciación más clara.",
  proPlanAccess: "Acceso al plan Pro",
  proPlanAccessDescription:
    "Actualmente tienes acceso a las herramientas estratégicas avanzadas de Hookora.",
  creatorPlanAccess: "Acceso al plan Creator",
  creatorPlanAccessDescription:
    "Actualmente tienes acceso a las herramientas premium de flujo de Hookora.",
  freePlanAccess: "Acceso al plan Free",
  freePlanAccessDescription:
    "Actualmente estás usando las herramientas básicas gratuitas de Hookora.",
  subscriptionStatus: "Estado de la suscripción",
  subscriptionStatusDescription:
    "Actualmente no tienes una suscripción de pago recurrente activa para gestionar aquí.",
  cancelSubscription: "Cancelar suscripción",
  cancelSubscriptionDescription:
    "Puedes cancelar tu suscripción actual en cualquier momento. Tu acceso seguirá activo hasta el final del período de facturación actual.",
  cancelling: "Cancelando...",
  cancellationFooter:
    "Después de la cancelación, tu plan seguirá activo hasta el final del período de facturación actual.",
  billingPreview: "Vista previa de facturación",
  planAmount: "Importe del plan",
  billingCycle: "Ciclo de facturación",
  rewardCreditsApplied: "Reward Credits aplicados",
  walletBalanceApplied: "Wallet Balance aplicado",
  cardDueNow: "Pago con tarjeta ahora",
  secureRecurringCheckoutPrefix: "Checkout recurrente seguro para facturación",
  secureRecurringCheckoutSuffix: ".",
  billingAndSubscriptions: "Facturación y suscripciones",
  billingAndSubscriptionsDescription:
    "Elige tu plan, gestiona mejoras y mantén tus datos de facturación alineados con tu uso de Hookora.",
  waitlistTitle: "Recibe una notificación cuando la facturación esté disponible",
  waitlistDescription:
    "Únete a la lista de espera para los planes de pago y el acceso anticipado a la facturación. Esto nos ayuda a saber qué usuarios quieren Creator o Pro primero.",
  waitlistSuccess: "Te has añadido correctamente a la lista de espera de facturación.",
  waitlistError: "No se pudo unir a la lista de espera de facturación.",
  emailLabel: "Correo electrónico",
  emailPlaceholder: "tu@ejemplo.com",
  interestedInLabel: "Interesado en",
  bothPlans: "Ambos",
  notesLabel: "Notas",
  notesPlaceholder: "Cuéntanos qué te gustaría más de los planes de pago...",
  submitting: "Enviando...",
  joinBillingWaitlist: "Unirme a la lista de espera de facturación",
  freePlan: "Plan Free",
  moveToCreator: "Pasar a Creator",
  whoIsProFor: "¿Para quién es Pro?",
  upgradeLater: "¿Puedo mejorar después?",
  moreThanOneTool: "¿Hookora incluye más de una herramienta?",
  beginnerCreators: "¿Hookora también está pensado para creadores principiantes?",
  features: {
    free: {
      generations: "Generaciones mensuales incluidas",
      savedHooks: "Hooks guardados incluidos",
      coreToolsOnly: "Solo herramientas principales",
      coreToolsList:
        "Hooks, títulos, captions, hashtags, descripciones y guiones",
      simpleWorkflow: "Flujo simple para la creación diaria",
      oneUser: "Acceso para un solo usuario"
    },
    creator: {
      generations: "Más generaciones mensuales",
      savedHooks: "Más hooks guardados",
      everythingInFree: "Todo lo de Free",
      premiumTools: "Herramientas premium de flujo",
      workflowSupport:
        "Mejor soporte para empaquetado, reescrituras y retención",
      analytics: "Soporte más sólido para el flujo del creador",
      fullCreatorWorkflowSupport:
        "Soporte más sólido para empaquetado, reescrituras, retención y ejecución"
    },
    pro: {
      generations: "Mayor cantidad de generaciones mensuales",
      everythingInCreator: "Todo lo de Creator",
      advancedTools: "Herramientas estratégicas avanzadas",
      strategySupport: "Soporte estratégico más sólido para creadores",
      futurePresets: "Acceso prioritario a una expansión más profunda del flujo",
      prioritySupport: "Soporte prioritario"
    }
  },
  messages: {
    couldNotLoadBillingDetails:
      "No se pudieron cargar tus datos de facturación en este momento.",
    confirmCancelSubscription:
      "¿Seguro que quieres cancelar tu suscripción?",
    subscriptionCancelledSuccessfully:
      "La suscripción se canceló correctamente.",
    couldNotCancelSubscription:
      "No se pudo cancelar la suscripción.",
    invalidWalletFundingAmount:
      "Introduce un monto válido para recargar la billetera.",
    walletFundingUrlNotReturned:
      "No se devolvió la URL de recarga de la billetera.",
    couldNotStartWalletFunding:
      "No se pudo iniciar la recarga de la billetera.",
    paymentSubmittedPaystack:
      "Tu pago se envió correctamente. Tu plan debería actualizarse pronto.",
    paymentReceived:
      "Pago recibido. Tu plan debería actualizarse pronto.",
    walletSubmittedPaystack:
      "El pago para recargar la billetera se envió correctamente.",
    walletReceived:
      "La recarga de la billetera se recibió correctamente.",
    checkoutCancelled: "El checkout fue cancelado.",
    loginToManageBilling:
      "Inicia sesión para gestionar tu facturación y recompensas.",
    failedToLoadBilling:
      "No se pudieron cargar los datos de facturación.",
    failedToCopy: "No se pudo copiar.",
    alreadyOnFree:
      "Ya estás en el plan Free.",
    changedToFree:
      "El plan se cambió correctamente a Free.",
    failedToChangePlan:
      "No se pudo cambiar el plan.",
    alreadyOnPlan:
      "Ya estás en el plan {{plan}} {{cycle}}.",
    checkoutUrlMissing:
      "Falta la URL de checkout.",
    failedToStartCheckout:
      "No se pudo iniciar el checkout.",
    invalidWalletAmount:
      "Introduce un monto válido para recargar la billetera.",
    walletUrlMissing:
      "Falta la URL de recarga de la billetera.",
    failedToStartWalletFunding:
      "No se pudo iniciar la recarga de la billetera."
  }
},

pricingFaq: {
  title: "Preguntas frecuentes sobre precios",
  subtitle:
    "Algunas respuestas rápidas a preguntas comunes sobre los precios de Hookora.",
  items: {
    freePlan: {
      question: "¿Puedo empezar con el plan Free?",
      answer:
        "Sí. El plan Free está diseñado para ayudarte a probar Hookora, explorar el flujo y ver cómo encajan las herramientas en tu proceso de contenido antes de mejorar."
    },
    moveToCreator: {
      question: "¿Cuándo debería pasar a Creator?",
      answer:
        "Creator es mejor cuando publicas con más constancia y quieres más generaciones, hooks guardados ilimitados y un soporte de flujo más sólido."
    },
    whoIsProFor: {
      question: "¿Para quién es Pro?",
      answer:
        "Pro es ideal para creadores serios y usuarios avanzados que quieren límites más altos, más flexibilidad y más valor estratégico a medida que Hookora sigue creciendo."
    },
    upgradeLater: {
      question: "¿Puedo mejorar después?",
      answer:
        "Sí. Puedes empezar gratis y mejorar cuando tu flujo de contenido necesite más espacio."
    },
    moreThanOneTool: {
      question: "¿Hookora incluye más de una herramienta?",
      answer:
        "Sí. Hookora incluye hooks, optimización de títulos, captions, hashtags, reescritura de descripciones, guiones cortos, análisis de texto para miniaturas y más."
    },
    beginnerCreators: {
      question: "¿Hookora también está pensado para creadores principiantes?",
      answer:
        "Sí. Hookora está diseñado para resultar útil tanto si estás empezando como si ya creas contenido con frecuencia."
    }
  }
},

  footer: {
    description:
      "Hookora es una plataforma de flujo social media impulsada por IA para generar hooks, títulos, captions, guiones, hashtags y más.",
    refundPolicy: "Política de reembolso",
    privacyPolicy: "Política de privacidad",
    terms: "Términos",
    contactSupport: "Contacto y soporte",
    copyright: "© {{year}} Hookora. Todos los derechos reservados."
  },

  tools: {
  hooks: "Hooks",
  hookAngles: "Ángulos de Hook",
  titles: "Títulos",
  captions: "Captions",
  hashtags: "Hashtags",
  descriptions: "Descripciones",
  scripts: "Guiones",
  ctas: "CTAs",
  thumbnailAnalyzer: "Analizador de Miniaturas",
  aiHumanizer: "Humanizador de IA",
  repurposer: "Reutilizador",
  adCopy: "Texto Publicitario",
  carousels: "Carruseles",
  viralRewrite: "Reescritura Viral",
  offerPositioning: "Posicionamiento de Oferta",
  hookScore: "Puntuación del Hook",
  trendConverter: "Conversor de Tendencias",
  nigerianRewrite: "Reescritura Nigeriana",
  retentionIntro: "Intro de Retención",
  brandVoiceTrainer: "Entrenador de Voz de Marca",
  contentDifferentiation: "Diferenciación de Contenido",
  commentIdeas: "Ideas desde Comentarios",
  viralHookGenerator: "Generador de Hooks Virales",
  youtubeTitleOptimizer: "Optimizador de Títulos de YouTube",
  captionGenerator: "Generador de Captions",
  hashtagGenerator: "Generador de Hashtags",
  descriptionRewriter: "Reescritor de Descripciones",
  shortScriptGenerator: "Generador de Guiones Cortos",
  thumbnailTextAnalyzer: "Analizador de Texto para Miniaturas",
  hookAngleGenerator: "Generador de Ángulos de Hook",
  ctaGenerator: "Generador de CTA",
  contentRepurposer: "Reutilizador de Contenido",
  adCopyGenerator: "Generador de Texto Publicitario",
  carouselGenerator: "Generador de Carruseles",
  viralRewriter: "Reescritor Viral",
  trendToContent: "Tendencia a Contenido",
  nigerianAudienceRewrite: "Reescritura para Audiencia Nigeriana",
  retentionIntroGenerator: "Generador de Intro de Retención",
  commentToContent: "Comentario a Contenido"
},
});

const fr = deepMerge(en, {
  common: {
    login: "Connexion",
    register: "S’inscrire",
    logout: "Déconnexion",
    dashboard: "Tableau de bord",
    studio: "Studio",
    library: "Bibliothèque",
    billing: "Facturation",
    home: "Accueil",
    features: "Fonctionnalités",
    pricing: "Tarifs",
    about: "À propos",
    blog: "Blog",
    settings: "Paramètres du compte",
    waitlistAdmin: "Admin liste d’attente",
    fullscreenEnter: "Activer le plein écran",
    fullscreenExit: "Quitter le plein écran",
    light: "Clair",
    dark: "Sombre",
    language: "Langue",
    theme: "Thème",
    current: "Actuel",
    tools: "Outils",
    unlockAllStudioTools: "Débloquer tous les outils Studio",
    readGuide: "Lire le guide",
    loading: "Chargement",
    copied: "Copié",
    copyCode: "Copier le code",
    copyLink: "Copier le lien"
  },

  languages: {
    en: "Anglais",
    es: "Espagnol",
    fr: "Français",
    pt: "Portugais"
  },

  navbar: {
    hi: "Salut",
    publicToolPages: "Pages publiques des outils",
    moreToolsInStudio: "Plus d’outils disponibles dans Studio",
    loggedIn: "Connecté",
    openSettingsMenu: "Ouvrir le menu des paramètres",
    toggleNavigationMenu: "Ouvrir le menu de navigation",
    display: "Affichage"
  },

  loginPage: {
  seoTitle: "Connexion | Hookora",
  seoDescription:
    "Connectez-vous à Hookora pour accéder à votre tableau de bord, aux outils Studio, à votre bibliothèque enregistrée et aux contrôles de facturation.",
  badge: "🚀 Bon retour",
  title: "Connectez-vous à Hookora",
  description:
    "Accédez à votre tableau de bord, générateur, bibliothèque de hooks et outils de tarification.",
  emailPlaceholder: "E-mail",
  passwordPlaceholder: "Mot de passe",
  showPassword: "Afficher le mot de passe",
  hidePassword: "Masquer le mot de passe",
  loginButton: "Connexion",
  loggingIn: "Connexion en cours...",
  forgotPassword: "Mot de passe oublié ?",
  noAccount: "Vous n’avez pas de compte ?",
  registerLink: "S’inscrire",
  errors: {
    default: "La connexion a échoué."
  }
},

registerPage: {
  seoTitle: "Inscription | Hookora",
  seoDescription:
    "Créez votre compte Hookora et commencez à générer des hooks, titres, légendes, scripts et plus encore depuis un seul workflow.",
  badge: "✨ Créez votre compte",
  title: "Créez votre compte Hookora",
  subtitle:
    "Commencez à générer, enregistrer et gérer vos hooks dans un seul espace.",
  referralBanner:
    "Utilisez un code de parrainage pour aider quelqu’un à gagner des <strong>Reward Credits</strong>. Les parrains gagnent ₦300 après l’inscription et la vérification de l’e-mail, puis ₦700 supplémentaires après une offre payante.",
  referralDetected:
    "Code de parrainage détecté et appliqué à votre inscription.",
  fields: {
    username: {
      label: "Nom d’utilisateur",
      placeholder: "Choisissez un nom d’utilisateur"
    },
    email: {
      label: "E-mail",
      placeholder: "Entrez votre e-mail"
    },
    password: {
      label: "Mot de passe",
      placeholder: "Créez un mot de passe"
    },
    referralCode: {
      label: "Code de parrainage",
      optional: "(optionnel)",
      placeholder: "Entrez le code de parrainage",
      help:
        "S’inscrire via un parrainage aide votre parrain à gagner des Reward Credits après la vérification de votre e-mail."
    }
  },
  passwordHints: {
    title: "Le mot de passe doit inclure :",
    minLength: "Au moins 6 caractères",
    hasLetter: "Au moins 1 lettre",
    hasNumberOrSymbol: "Au moins 1 chiffre ou symbole"
  },
  actions: {
    register: "S’inscrire",
    creatingAccount: "Création du compte...",
    resendVerificationEmail: "Renvoyer l’e-mail de vérification",
    sending: "Envoi...",
    goToLogin: "Aller à la connexion",
    login: "Connexion"
  },
  messages: {
    accountCreated:
      "Compte créé avec succès. Veuillez vérifier votre e-mail avant de vous connecter.",
    verifyInbox:
      "Vérifiez votre boîte de réception pour trouver l’e-mail de vérification. Après vérification, vous pourrez vous connecter et votre éligibilité au parrainage sera correctement prise en compte.",
    enterEmailForResend:
      "Entrez d’abord votre e-mail pour que nous puissions renvoyer le lien de vérification.",
    verificationSent:
      "E-mail de vérification envoyé avec succès.",
    resendFailed:
      "Impossible de renvoyer l’e-mail de vérification. Veuillez réessayer.",
    registrationFailed: "L’inscription a échoué.",
    referralNote: "Note de parrainage :"
  },
  links: {
    alreadyHaveAccount: "Vous avez déjà un compte ?"
  },
  aria: {
    showPassword: "Afficher le mot de passe",
    hidePassword: "Masquer le mot de passe"
  }
},

dashboard: {
  title: "Tableau de bord",
  subtitle:
    "Suivez les performances de vos hooks, l’activité de Studio, les tendances et vos contenus les plus performants.",

  loading: "Chargement du tableau de bord...",

  errors: {
    notLoggedIn: "Vous n’êtes pas connecté. Veuillez d’abord vous connecter.",
    loadFailed: "Impossible de charger le tableau de bord.",
    copyFailed: "Échec de la copie. Veuillez réessayer.",
    copyFailedConsole: "Échec de la copie :",
    copyRewardFailedConsole: "Échec de la copie du texte de récompense :",
    toggleFavoriteConsole: "Échec du changement de favori :",
    favoriteUpdateFailed: "Impossible de mettre à jour le statut favori.",
    deleteHookConsole: "Échec de la suppression du hook :",
    deleteHookFailed: "Impossible de supprimer le hook.",
    deleteStudioAssetConsole: "Échec de la suppression de l’élément Studio :",
    deleteStudioAssetFailed: "Impossible de supprimer l’élément Studio.",
    regenerateConsole: "Échec de la régénération du hook :",
    regenerateFailed: "Impossible de régénérer le hook."
  },

  confirm: {
    deleteHook: "Êtes-vous sûr de vouloir supprimer ce hook ?",
    deleteStudioAsset: "Êtes-vous sûr de vouloir supprimer cet élément Studio enregistré ?"
  },

  actions: {
    copied: "Copié",
    copyHook: "Copier le hook",
    copyAsset: "Copier l’élément",
    copyCode: "Copier le code",
    copyLink: "Copier le lien",
    regenerate: "Régénérer",
    favorite: "Favori",
    unfavorite: "Retirer des favoris",
    delete: "Supprimer",
    working: "Traitement...",
    saving: "Enregistrement...",
    dismiss: "Fermer",
    seeProPlan: "Voir le plan Pro",
    upgradePlan: "Passer à un plan supérieur",
    goToStudio: "Aller à Studio",
    openBilling: "Ouvrir la facturation",
    viewSignupFlow: "Voir le parcours d’inscription",
    openMilestoneWatch: "Ouvrir Milestone Watch"
  },

  labels: {
    best: "Meilleur",
    favorite: "Favori",
    score: "Score",
    toolType: "Type d’outil",
    plan: "Plan",
    pro: "PRO",
    proInsights: "PRO INSIGHTS",
    saved: "enregistrés",
    runs: "exécutions",
    hooks: "hooks",
    success: "Réussite",
    failed: "Échecs",
    avgTime: "Temps moy."
  },

  plan: {
    free: "Free",
    freeLowercase: "free",
    creator: "✔ Creator",
    pro: "✔ Pro"
  },

  upgradePrompt: {
    message:
      "{{featureName}} est disponible avec le plan Pro. Passez au niveau supérieur pour débloquer cette fonctionnalité d’analyse avancée.",
    clickToUnlock: "Cliquez pour débloquer avec Pro",
    clickForPro: "Cliquez pour accéder aux analyses Pro"
  },

  filters: {
    allPlatforms: "Tous",
    allTools: "Tous",
    platform: "Plateforme",
    studioTool: "Outil Studio",
    allStudioTools: "Tous les outils Studio",
    sortBy: "Trier par",
    score: "Score",
    topic: "Sujet",
    searchTopic: "Rechercher un sujet",
    searchPlaceholder: "Tapez pour rechercher..."
  },

  metrics: {
    totalHooks: "Total des hooks",
    studioAssets: "Éléments Studio",
    totalSavedItems: "Total des éléments enregistrés",
    studioRuns: "Exécutions Studio",
    actualTrackedToolRuns: "Exécutions réelles des outils suivies",
    averageScore: "Score moyen",
    favoriteHooks: "Hooks favoris",
    topHookScore: "Meilleur score de hook",
    successRate: "Taux de réussite",
    studioToolRunSuccess: "Réussite des exécutions des outils Studio"
  },

  rewards: {
    title: "Hookora Creator Rewards",
    subtitle:
      "Gardez un œil sur vos Reward Credits et la croissance de vos parrainages depuis un seul endroit.",
    rewardCredits: "Reward Credits",
    quickShare: "Partage rapide du parrainage",
    referralCode: "Code de parrainage",
    referralLink: "Lien de parrainage",
    snapshot: "Aperçu des récompenses",
    walletBalance: "Solde du portefeuille",
    totalReferrals: "Parrainages totaux",
    verifiedReferrals: "Parrainages vérifiés",
    paidReferrals: "Parrainages payants"
  },

  founder: {
    title: "Vue d’ensemble du fondateur",
    subtitle: "Aperçu de la croissance globale et de la conversion.",
    totalUsers: "Utilisateurs totaux",
    activeUsers: "Utilisateurs actifs",
    paidUsers: "Utilisateurs payants",
    freeUsers: "Utilisateurs Free",
    creatorUsers: "Utilisateurs Creator",
    proUsers: "Utilisateurs Pro",
    newUsers7d: "Nouveaux utilisateurs (7j)",
    newUsers30d: "Nouveaux utilisateurs (30j)",
    signupToPaid: "Inscription → Payant",
    weeklyFocus: "Priorité hebdomadaire du fondateur",
    recentSignups: "Inscriptions récentes",
    noSignupData: "Aucune donnée d’inscription pour le moment."
  },

  usage: {
    title: "Utilisation du plan",
    monthlyGenerationsUsed: "Générations mensuelles utilisées",
    remainingThisMonth: "Restant ce mois-ci",
    savedHooks: "Hooks enregistrés"
  },

  studioSnapshot: {
    title: "Aperçu des outils Studio",
    subtitle:
      "Inclut Hook Angles, CTA Generator, AI Humanizer et d’autres outils Studio"
  },

  charts: {
    platformDistribution: "Répartition par plateforme",
    studioToolUsage: "Utilisation des outils Studio",
    studioRunTrend: "Tendance des exécutions Studio",
    studioUsageSummary: "Résumé d’utilisation de Studio"
  },

  summary: {
    mostUsedTool: "Outil le plus utilisé",
    successfulRuns: "Exécutions réussies",
    failedRuns: "Exécutions échouées",
    averageGenerationTime: "Temps moyen de génération"
  },

  pro: {
    averageScoreInsights: "Analyses du score moyen",
    topHookScoreAnalytics: "Analyses du meilleur score de hook",
    dailyAverageScoreTrend: "Tendance quotidienne du score moyen",
    dailyAverageScoreTrendDescription:
      "Suivez l’évolution du score dans le temps avec une visibilité historique et des tendances plus riches.",
    bestTopicsByAverageScore: "Meilleurs sujets par score moyen",
    bestTopicsByAverageScoreDescription:
      "Découvrez vos sujets les plus performants avec une analyse plus approfondie au niveau des sujets."
  },

  sections: {
    favoriteHooks: "Hooks favoris",
    strongestHookPerTopic: "Hook le plus fort par sujet",
    topHooks: "Meilleurs hooks",
    recentHooks: "Hooks récents",
    recentStudioAssets: "Éléments Studio récents",
    combinedRecentActivity: "Activité récente combinée"
  },

  empty: {
    noPlatformAnalytics: "Aucune analyse de plateforme pour le moment.",
    noStudioActivity: "Aucune activité Studio pour le moment.",
    noStudioRunHistory:
      "Aucun historique d’exécution Studio pour le moment. Utilisez les outils Studio pour alimenter ce graphique.",
    noTrendData:
      "Aucune donnée de tendance pour le moment. Générez des hooks au fil du temps pour alimenter ce graphique.",
    noTopicAnalytics: "Aucune analyse de sujet pour le moment.",
    noContentTitle: "Aucun contenu généré pour le moment",
    noContentDescription:
      "Commencez à utiliser les outils Studio pour générer des hooks, des titres, des légendes et des scripts. Vos analyses personnelles apparaîtront ici à mesure que vous créez et enregistrez du contenu.",
    noFavoriteHooks: "Aucun hook favori pour le moment.",
    noStudioAssetsSaved: "Aucun élément Studio enregistré pour le moment.",
    noRecentActivity: "Aucune activité récente pour le moment."
  },
  

  tools: {
    hooks: {
      label: "Générateur de hooks viraux",
      badge: "HOOKS"
    },
    youtubeTitleOptimizer: {
      label: "Optimiseur de titres YouTube",
      badge: "TITLE"
    },
    captionGenerator: {
      label: "Générateur de légendes",
      badge: "CAPTION"
    },
    hashtagGenerator: {
      label: "Générateur de hashtags",
      badge: "HASHTAG"
    },
    descriptionRewriter: {
      label: "Réécriture de descriptions",
      badge: "DESCRIPTION"
    },
    shortScriptGenerator: {
      label: "Générateur de scripts courts",
      badge: "SCRIPT"
    },
    thumbnailTextAnalyzer: {
      label: "Analyseur de texte de miniature",
      badge: "THUMBNAIL"
    },
    hookAngles: {
      label: "Angles de hook",
      badge: "ANGLES"
    },
    ctaGenerator: {
      label: "Générateur de CTA",
      badge: "CTA"
    },
    humanizer: {
      label: "Humaniseur IA",
      badge: "HUMANIZER"
    },
    other: {
      label: "Autre",
      badge: "OTHER"
    }
  },

  welcome: {
  time: {
    morning: "Bonjour",
    afternoon: "Bon après-midi",
    evening: "Bonsoir"
  },
  messages: {
    one: "Heureux de te revoir. Créons quelque chose de fort aujourd’hui.",
    two: "Ton espace créatif est prêt.",
    three: "C’est le bon moment pour transformer des idées en contenu solide.",
    four: "Continuons à construire quelque chose de net et de précieux.",
    five: "Ton prochain excellent contenu peut commencer maintenant.",
    six: "Reprends ton élan créatif et fais compter cette journée."
  }
},
},

libraryPage: {
  title: "Bibliothèque",
  subtitle: "Parcourez les hooks enregistrés et les éléments Studio sauvegardés de votre flux de contenu.",

  currentPlanLabel: "Votre forfait actuel :", 

  planLabels: {
    free: "Gratuit",
    creator: "✔ Creator",
    pro: "✔ Pro"
  },

  planBox: {
    line1: "Les hooks et les éléments Studio sont désormais tous stockés ici.",
    line2: {
      before: "L’export CSV et les actions groupées sont disponibles sur",
      creator: "Creator",
      middle: "et",
      pro: "Pro"
    },
    line3: {
      before: "L’intelligence avancée de la bibliothèque et les futurs espaces de travail sont prévus pour",
      pro: "Pro"
    }
  },

  badges: {
    creatorPlus: "CREATOR+",
    proInsights: "APERÇUS PRO",
    locked: "Verrouillé"
  },

  tools: {
    hooks: { label: "Générateur de Hooks Viraux", badge: "HOOKS" },
    youtubeTitleOptimizer: { label: "Optimiseur de Titres YouTube", badge: "TITRE" },
    captionGenerator: { label: "Générateur de Légendes", badge: "LÉGENDE" },
    hashtagGenerator: { label: "Générateur de Hashtags", badge: "HASHTAG" },
    descriptionRewriter: { label: "Réécriture de Description", badge: "DESCRIPTION" },
    shortScriptGenerator: { label: "Générateur de Script Court", badge: "SCRIPT" },
    thumbnailTextAnalyzer: { label: "Analyseur de Texte de Miniature", badge: "MINIATURE" },
    hookAngles: { label: "Angles de Hook", badge: "ANGLES" },
    ctaGenerator: { label: "Générateur de CTA", badge: "CTA" },
    humanizer: { label: "Humaniseur IA", badge: "HUMANISEUR" },
    other: { label: "Autre", badge: "AUTRE" }
  },

  stats: {
    savedHooks: "Hooks enregistrés",
    savedStudioAssets: "Éléments Studio enregistrés",
    favoriteHooks: "Hooks favoris",
    averageHookScore: "Score moyen des hooks"
  },

  toolBreakdown: {
    title: "Répartition des outils Studio",
    savedItemsCount: "{{count}} élément Studio enregistré",
    savedItemsCount_plural: "{{count}} éléments Studio enregistrés"
  },

  filters: {
    search: "Rechercher",
    searchPlaceholder: "Rechercher un sujet, contenu ou outil...",
    platform: "Plateforme",
    allPlatforms: "Tous",
    studioTool: "Outil Studio",
    allStudioTools: "Tous les outils Studio",
    sortBy: "Trier par",
    favoritesOnly: "Favoris uniquement"
  },

  sort: {
    newest: "Plus récents",
    oldest: "Plus anciens",
    highestScore: "Score le plus élevé",
    lowestScore: "Score le plus bas",
    topic: "Sujet"
  },

  actions: {
    upgradeToCreator: "Passer à Creator",
    unselectVisibleHooks: "Désélectionner les hooks visibles",
    selectVisibleHooks: "Sélectionner les hooks visibles",
    clearSelection: "Effacer la sélection",
    copiedAll: "Tout copié !",
    copyVisibleHooks: "Copier les hooks visibles",
    exportHooksTxt: "Exporter les hooks en TXT",
    exportHooksCsv: "Exporter les hooks en CSV",
    favoriteSelected: "Mettre en favori la sélection",
    unfavoriteSelected: "Retirer des favoris la sélection",
    deleteSelected: "Supprimer la sélection",
    copied: "Copié !",
    copy: "Copier",
    copyHook: "Copier le hook",
    regenerate: "Régénérer",
    favorite: "Favori",
    unfavorite: "Retirer des favoris",
    delete: "Supprimer",
    rename: "Renommer",
    deleteFolder: "Supprimer le dossier",
    save: "Enregistrer",
    saving: "Enregistrement...",
    cancel: "Annuler",
    deleting: "Suppression..."
  },

  states: {
    loading: "Chargement de la bibliothèque de hooks...",
    working: "Traitement...",
    noFoldersMatch: "Aucun dossier ne correspond à vos filtres actuels."
  },

  labels: {
    untitled: "Sans titre",
    score: "Score",
    selectedHooksCount: "{{count}} hooks sélectionnés",
    toolType: "Type d’outil",
    noDate: "Aucune date",
    favorite: "Favori"
  },

  export: {
    topic: "Sujet",
    platform: "Plateforme",
    score: "Score",
    favorite: "Favori",
    created: "Créé le",
    hook: "Hook",
    yes: "Oui",
    no: "Non"
  },

  features: {
    bulkSelection: "Sélection groupée",
    csvExport: "Export CSV",
    bulkDelete: "Suppression groupée",
    bulkFavorite: "Ajout groupé aux favoris",
    bulkUnfavorite: "Retrait groupé des favoris"
  },

  notices: {
    freeLock: {
      before: "Les actions groupées et l’export CSV sont verrouillés sur le forfait Gratuit.",
      upgradeLink: "Passez à Creator"
    },
    proPreview: {
      before: "Aperçu Pro : l’intelligence future de la bibliothèque, les préréglages enregistrés et les espaces de travail seront disponibles sur",
      pro: "Pro"
    }
  },

  folders: {
    title: "Dossiers de sujets",
    folderNamePlaceholder: "Entrez le nom du dossier",
    itemsCount: "{{count}} élément",
    itemsCount_plural: "{{count}} éléments",
    hooksCount: "{{count}} hook",
    hooksCount_plural: "{{count}} hooks",
    assetsCount: "{{count}} élément",
    assetsCount_plural: "{{count}} éléments"
  },

  sections: {
    studioAssets: "Éléments Studio",
    hooks: "Hooks"
  },

  confirmations: {
    deleteHook: "Voulez-vous vraiment supprimer ce hook ?",
    deleteAsset: "Voulez-vous vraiment supprimer cet élément enregistré ?",
    deleteSelectedHooks: "Voulez-vous vraiment supprimer {{count}} hook(s) sélectionné(s) ?",
    deleteFolder: "Voulez-vous vraiment supprimer ce dossier et tout son contenu ?\n\nDossier : {{folderName}}\nÉléments : {{itemCount}}"
  },

  messages: {
    notLoggedIn: "Vous n’êtes pas connecté. Veuillez d’abord vous connecter.",
    loadFailed: "Échec du chargement de la bibliothèque de hooks.",
    upgradeRequired: "{{featureName}} est disponible sur les forfaits Creator et Pro. Passez à une offre supérieure pour débloquer cette fonctionnalité.",
    favoriteUpdateFailed: "Échec de la mise à jour du statut favori.",
    deleteHookFailed: "Échec de la suppression du hook.",
    deleteAssetFailed: "Échec de la suppression de l’élément enregistré.",
    deleteFolderFailed: "Échec de la suppression du dossier.",
    regenerateFailed: "Échec de la régénération du hook.",
    folderNameRequired: "Le nom du dossier ne peut pas être vide.",
    renameFolderFailed: "Échec du renommage du dossier.",
    copyVisibleFailed: "Échec de la copie des hooks visibles.",
    csvExportFailed: "Échec de l’export CSV.",
    bulkDeleteFailed: "Échec de la suppression des hooks sélectionnés.",
    bulkUpdateFailed: "Échec de la mise à jour des hooks sélectionnés."
  }
},
studioLayout: {
  headerChip: "Espace créatif",
  description:
    "Créez des hooks, des angles de hook, des titres, des légendes, des hashtags, des descriptions, des scripts, des CTA, du texte de miniature, du contenu réutilisé, du texte publicitaire, du copy de carrousel, des réécritures virales, du positionnement d’offre, des analyses de score de hook, des conversions de tendances, des réécritures pour le public nigérian, des intros de rétention, des systèmes de voix de marque, des idées de différenciation et du contenu inspiré des commentaires depuis un espace de travail soigné.",
},

studioSidebar: {
  title: "Outils Studio",
  subtitle: "Faites glisser ou défiler pour accéder à n’importe quel outil.",
  lockedSuffix: "outil — passez à niveau pour le débloquer",
  toolDescriptions: {
    hooks: "Générez des hooks percutants",
    hookAngles: "Trouvez de meilleurs angles de contenu",
    titles: "Créez des titres plus cliquables",
    captions: "Rédigez de meilleures légendes",
    hashtags: "Créez des groupes de hashtags",
    descriptions: "Réécrivez des descriptions",
    scripts: "Créez des scripts courts",
    ctas: "Générez des CTA plus forts",
    thumbnail: "Améliorez le texte des miniatures",
    humanizer: "Rendez le texte plus humain",
    repurpose: "Transformez un post en plusieurs formats",
    adCopy: "Créez du texte publicitaire prêt à l’emploi",
    carousels: "Créez des carrousels diapositive par diapositive",
    viralRewrite: "Rendez le texte plus accrocheur",
    positioning: "Clarifiez le message de votre offre",
    hookScore: "Analysez la force du hook",
    trendToContent: "Transformez les tendances en contenu",
    nigerianRewrite: "Localisez le texte pour le public nigérian",
    retentionIntro: "Créez des intros vidéo plus fortes",
    brandVoiceTrainer: "Développez la voix de votre contenu",
    contentDifferentiation: "Démarquez votre contenu plus clairement",
    commentToContent: "Transformez des commentaires en contenu"
  }
},

hookTool: {
  heroBadge: "Outil axé sur le résultat",
  title: "Hooks",
  description:
    "Utilisez cet outil lorsque vous voulez des <strong>phrases d’ouverture prêtes à l’emploi</strong>. Commencez par votre sujet, choisissez un style de hook, et Hookora générera des hooks directs que vous pourrez utiliser pour les légendes, vidéos, carrousels, titres, intros et ouvertures de posts.",

  familyFallback: "Hook",

  templates: {
    curiosity: {
      label: "Curiosité",
      description: "Crée de l’intrigue et donne envie de lire la suite."
    },
    authority: {
      label: "Autorité",
      description: "Donne une impression de confiance, d’expertise et de maîtrise."
    },
    problem: {
      label: "Problème",
      description: "Met rapidement en avant les douleurs, blocages ou erreurs."
    },
    benefit: {
      label: "Bénéfice",
      description: "Commence par le résultat, le gain ou la transformation."
    },
    controversy: {
      label: "Controverse",
      description: "Utilise la tension, le contraste ou un désaccord assumé."
    },
    story: {
      label: "Histoire",
      description: "Donne une impression plus personnelle, humaine et émotionnelle."
    }
  },

  fields: {
    topic: "Sujet",
    hookStyle: "Style de Hook",
    platform: "Plateforme",
    audience: "Audience",
    goal: "Objectif",
    offer: "Offre",
    painPoint: "Problème",
    desiredOutcome: "Résultat Souhaité",
    angle: "Angle",
    ctaDirection: "Direction du CTA",
    keywords: "Mots-clés",
    brandVoice: "Voix de Marque",
    avoidPhrases: "Phrases à Éviter",
    extraContext: "Contexte Supplémentaire"
  },

  hints: {
    topic:
      "L’idée de contenu précise pour laquelle vous voulez des hooks. Des sujets clairs produisent des hooks plus forts et plus utiles.",
    hookStyle:
      "Choisissez le type de hook que Hookora doit privilégier : curiosité, autorité, problème, bénéfice, controverse ou histoire.",
    platform:
      "Choisissez où le hook sera utilisé. La plateforme influence le rythme, la formulation et la puissance d’arrêt.",
    audience:
      "Le public que le hook doit capter. Exemple : fondateurs, créateurs, coachs, petites entreprises, étudiants.",
    goal:
      "Ce que le hook doit aider à obtenir. Exemple : plus de clics, une meilleure rétention, plus de commentaires, plus d’inscriptions.",
    offer:
      "Le produit, service, leçon ou valeur derrière le hook.",
    painPoint:
      "La frustration, difficulté ou erreur exacte que le hook doit toucher.",
    desiredOutcome:
      "Le résultat que votre audience veut. Exemple : plus de portée, plus de ventes, meilleur positionnement, contenu plus fort.",
    angle:
      "Direction optionnelle pour orienter le hook. Exemple : contrariant, vérité cachée, centré sur une erreur, premium, émotionnel.",
    ctaDirection:
      "Action optionnelle vers laquelle le contenu doit conduire. Exemple : commenter, enregistrer, cliquer, envoyer un DM, s’inscrire.",
    keywords:
      "Mots ou expressions importants qui doivent influencer le hook.",
    brandVoice:
      "La façon dont le hook doit sonner. Exemple : audacieux, premium, chaleureux, percutant, ludique, direct.",
    avoidPhrases:
      "Mots, clichés ou expressions trop utilisées que vous ne voulez pas voir dans les hooks.",
    extraContext:
      "Ajoutez des preuves, notes de campagne, objections, exemples, détails de positionnement ou tout autre contexte utile pour améliorer les hooks."
  },

  placeholders: {
    topic: "À propos de quoi voulez-vous que le hook parle ?",
    audience: "Audience",
    goal: "Objectif",
    offer: "Offre ou produit",
    painPoint: "Problème",
    desiredOutcome: "Résultat souhaité",
    angle: "Angle",
    ctaDirection: "Direction du CTA",
    keywords: "Mots-clés",
    brandVoice: "Voix de marque",
    avoidPhrases: "Phrases à éviter",
    extraContext:
      "Contexte supplémentaire, note de positionnement, preuve, exemples ou détails de campagne"
  },

  contextSectionTitle: "Contexte du Hook",

  actions: {
    generating: "Génération...",
    generate: "Générer des Hooks",
    clearResults: "Effacer les Résultats",
    copied: "Copié",
    copyHook: "Copier le Hook"
  },

  messages: {
    topicRequired: "Veuillez d’abord saisir un sujet.",
    generateFailed: "Échec de la génération des hooks.",
    readyToUse:
      "Voici des <strong>hooks prêts à l’emploi</strong>. Choisissez le plus fort et utilisez-le directement comme phrase d’ouverture, début de titre, intro vidéo, début de légende ou premier slide de carrousel.",
    score: "Score"
  }
},

studioTools: {
  adCopyTool: {
    title: "Générateur de texte publicitaire",
    description:
      "Créez des textes principaux, titres, descriptions et CTA prêts pour vos annonces et campagnes.",

    fields: {
      topic: {
        label: "Sujet",
        placeholder: "ex. Outil de contenu social pour créateurs",
        help:
          "Utilisez le produit, l’offre ou l’axe de campagne autour duquel vous voulez créer les annonces."
      },
      platform: {
        label: "Plateforme",
        placeholder: "ex. Facebook",
        help: "Choisissez d’abord la plateforme où la publicité sera diffusée."
      },
      tone: {
        label: "Ton"
      },
      language: {
        label: "Langue"
      },
      audience: {
        label: "Audience",
        placeholder: "ex. Petits entrepreneurs"
      },
      goal: {
        label: "Objectif",
        placeholder: "ex. Générer des inscriptions"
      },
      offer: {
        label: "Offre",
        placeholder: "ex. Essai gratuit pour les nouveaux utilisateurs"
      },
      painPoint: {
        label: "Problème",
        placeholder: "ex. Texte faible et faible engagement"
      },
      desiredOutcome: {
        label: "Résultat souhaité",
        placeholder: "ex. Création de contenu plus rapide et meilleures conversions"
      },
      angle: {
        label: "Angle",
        placeholder: "ex. Un meilleur copywriting sans perdre de temps"
      },
      callToAction: {
        label: "Appel à l’action",
        placeholder: "ex. Commencez votre essai gratuit"
      },
      brandVoice: {
        label: "Voix de marque",
        placeholder: "ex. Clair, percutant, moderne"
      },
      keywords: {
        label: "Mots-clés",
        placeholder: "ex. copywriting IA, publicités réseaux sociaux"
      },
      avoidPhrases: {
        label: "Phrases à éviter",
        placeholder: "ex. révolutionnaire, astuce secrète"
      },
      extraContext: {
        label: "Contexte supplémentaire",
        placeholder:
          "Note de campagne facultative, contexte tarifaire, objections du public, message de landing page ou autre direction..."
      }
    },

    toneOptions: {
      Persuasive: "Persuasif",
      Bold: "Audacieux",
      Professional: "Professionnel",
      Confident: "Confiant",
      Warm: "Chaleureux",
      Conversational: "Conversationnel",
      Direct: "Direct"
    },

    languageOptions: {
      english: "Anglais",
      spanish: "Espagnol",
      french: "Français",
      portuguese: "Portugais"
    },

    actions: {
      generate: "Générer le texte publicitaire",
      generating: "Génération...",
      reset: "Réinitialiser",
      copy: "Copier",
      copied: "Copié !"
    },

    results: {
      title: "Variantes publicitaires",
      description:
        "Examinez chaque variante, copiez la plus forte et enregistrez les meilleures annonces dans votre bibliothèque.",
      empty:
        "Aucun texte publicitaire pour le moment. Générez un lot pour voir vos variantes ici.",
      variantFallback: "Variante publicitaire",
      score: "Score"
    },

    output: {
      primaryText: "Texte principal",
      headline: "Titre",
      description: "Description",
      cta: "CTA"
    },

    messages: {
      topicRequired: "Veuillez d’abord saisir un sujet.",
      generateFailed: "Échec de la génération du texte publicitaire.",
      copyFailed: "Échec de la copie du contenu."
    }
  },

  aiHumanizerTool: {
  hero: {
    badge: "Outil d’amélioration de réécriture",
    title: "Humaniseur IA",
    description:
      "Utilisez cet outil pour transformer un texte rigide, robotique ou trop marqué par l’IA en une écriture plus naturelle, crédible et humaine. Ajustez le ton, l’intensité et les paramètres de réécriture pour obtenir une version adaptée à votre plateforme."
  },

  fields: {
    originalText: {
      label: "Texte original",
      hint:
        "Collez le texte généré par IA ou trop rigide que vous voulez rendre plus humain, naturel et crédible.",
      placeholder: "Collez le texte original à humaniser"
    },
    platform: {
      label: "Plateforme",
      hint:
        "Choisissez où la réécriture sera utilisée. Cela influence le rythme, la formulation et le style."
    },
    tone: {
      label: "Ton",
      hint: "Choisissez la personnalité de la réécriture."
    },
    audience: {
      label: "Audience",
      hint:
        "À qui s’adresse le texte réécrit. Cela aide à rendre le résultat plus pertinent et plus humain.",
      placeholder: "Audience"
    },
    humanizationStrength: {
      label: "Niveau d’humanisation",
      hint:
        "Léger garde une structure proche. Équilibré améliore la fluidité. Fort réécrit plus librement tout en gardant le message principal."
    },
    preserveOriginalMeaning: {
      label: "Conserver le sens original"
    },
    styleNotes: {
      label: "Notes de style",
      hint:
        "Ajoutez des indications comme des phrases plus courtes, un ton plus direct, moins formel, plus d’émotion ou moins de jargon.",
      placeholder: "Notes de style ou consignes de réécriture"
    }
  },

  sections: {
    rewriteSettings: "Paramètres de réécriture"
  },

  platformOptions: {
    General: "Général",
    Instagram: "Instagram",
    TikTok: "TikTok",
    X: "X",
    LinkedIn: "LinkedIn",
    YouTube: "YouTube",
    WhatsApp: "WhatsApp"
  },

  toneOptions: {
    Natural: "Naturel",
    Conversational: "Conversationnel",
    Bold: "Audacieux",
    Professional: "Professionnel",
    Warm: "Chaleureux",
    Confident: "Assuré",
    Persuasive: "Persuasif",
    "Story-driven": "Axé sur l’histoire"
  },

  strengthOptions: {
    Light: "Léger",
    Balanced: "Équilibré",
    Strong: "Fort"
  },

  actions: {
    humanizing: "Humanisation...",
    humanizeText: "Humaniser le texte",
    clearResults: "Effacer les résultats",
    resetForm: "Réinitialiser le formulaire",
    copied: "Copié",
    copy: "Copier"
  },

  labels: {
    alternative: "Alternative {{number}}",
    whyItFeelsMoreHuman: "Pourquoi cela semble plus humain",
    score: "Score"
  },

  resultsIntro:
    "Voici des options de réécriture humanisée. Choisissez celle qui paraît la plus naturelle, la plus crédible et la plus proche de votre voix souhaitée.",

  errors: {
    originalTextRequired: "Veuillez d’abord saisir le texte original.",
    generateFailed: "Échec de l’humanisation du texte."
  }
},

brandVoiceTrainerTool: {
  badge: "Entraîneur de voix de marque",
  title: "Créez une voix de marque plus cohérente",
  description:
    "Transformez la description de votre marque, vos préférences de ton et votre exemple d’écriture en un profil de voix exploitable avec des règles de message plus claires et une meilleure cohérence.",

  fields: {
    topic: {
      label: "Sujet",
      placeholder: "ex. outils pour créateurs, soins de la peau, éducation business",
      hint: "Le sujet partagé aide à garder votre flux Studio cohérent entre les outils."
    },
    platform: {
      label: "Plateforme",
      hint: "La plateforme peut influencer le style de voix et les recommandations de formulation."
    },
    outputType: {
      label: "Type de sortie",
      hint: "Choisissez si vous voulez un profil de voix, des règles ou un exemple de sortie."
    },
    targetAudience: {
      label: "Public cible",
      placeholder: "ex. fondateurs, femmes 20-35 ans, créateurs nigérians",
      hint: "Le contexte du public aide à définir comment la voix doit sonner."
    },
    preferredToneWords: {
      label: "Mots de ton préférés",
      placeholder: "ex. audacieux, humain, net, chaleureux",
      hint: "Ajoutez des mots qui décrivent ce que votre marque doit dégager."
    },
    avoidWords: {
      label: "Mots / styles à éviter",
      placeholder: "ex. robotique, trop formel, trop argotique, trop agressif",
      hint: "Optionnel. Aide Hookora à éviter le mauvais ton."
    },
    goal: {
      label: "Objectif",
      placeholder: "ex. paraître plus premium, plus humain, plus cohérent",
      hint: "Optionnel. Donne une direction plus claire à l’outil."
    },
    brandDescription: {
      label: "Description de la marque",
      placeholder:
        "Décrivez ce que fait votre marque, ce qu’elle représente et comment elle doit être perçue...",
      hint: "C’est le champ le plus important. Soyez aussi clair que possible."
    },
    sampleWriting: {
      label: "Exemple d’écriture",
      placeholder:
        "Collez un exemple de la manière dont votre marque écrit actuellement, si vous en avez un...",
      hint:
        "Optionnel, mais très utile si vous voulez que le résultat reflète votre style existant."
    }
  },

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    xTwitter: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "Général"
  },

  outputTypeOptions: {
    voiceProfile: "Profil de voix",
    rewriteSample: "Exemple de réécriture",
    brandGuidelines: "Lignes directrices de marque",
    dosAndDonts: "À faire et à éviter"
  },

  actions: {
    generating: "Génération...",
    generate: "Créer la voix de marque",
    clearResults: "Effacer les résultats",
    reset: "Réinitialiser",
    copied: "Copié",
    copy: "Copier"
  },

  sections: {
    brandVoiceProfile: "Profil de voix de marque",
    summary: "Résumé",
    voicePillars: "Piliers de la voix",
    dos: "À faire",
    donts: "À éviter",
    sampleRewrite: "Exemple de réécriture",
    notes: "Notes",
    rawResult: "Résultat brut"
  },

  resultTitle: "Résultat de la voix de marque",

  tip:
    "Astuce : la voix de marque devient meilleure lorsque votre description inclut le public, les valeurs et ce que vous voulez que les gens ressentent après avoir lu votre contenu.",

  messages: {
    copyFailed:
      "Impossible de copier le résultat de la voix de marque pour le moment. Veuillez réessayer.",
    brandDescriptionRequired:
      "Veuillez décrire la marque avant de générer un profil de voix.",
    generateFailed:
      "Une erreur s’est produite lors de la génération du résultat de la voix de marque."
  }
},

captionTool: {
  hero: {
    badge: "Outil d’écriture sociale",
    title: "Légendes",
    description:
      "Utilisez cet outil pour générer des légendes prêtes à publier pour du contenu social. Ajoutez la plateforme, le ton, l’orientation du CTA et un contexte supplémentaire afin que Hookora puisse créer des options de légendes plus fortes et plus pertinentes pour votre publication ou votre vidéo."
  },

  fields: {
    topic: {
      label: "Sujet",
      hint:
        "Le sujet principal de la légende. Soyez clair sur ce dont parle la publication ou la vidéo.",
      placeholder: "De quoi parle la légende ?"
    },
    platform: {
      label: "Plateforme",
      hint:
        "Choisissez où la légende sera publiée. La plateforme influence le ton, le rythme et le style du CTA."
    },
    tone: {
      label: "Ton",
      hint:
        "Comment la légende უნდა sonner. Exemple : engageant, audacieux, ludique, éducatif, premium, direct.",
      placeholder: "Ton"
    },
    audience: {
      label: "Audience",
      hint:
        "À qui s’adresse la légende. Exemple : marques de skincare, créateurs, fondateurs de startup, étudiants nigérians.",
      placeholder: "Audience"
    },
    goal: {
      label: "Objectif",
      hint:
        "Ce que vous voulez que la légende accomplisse. Exemple : plus d’enregistrements, de commentaires, de clics, d’inscriptions ou de partages.",
      placeholder: "Objectif"
    },
    offer: {
      label: "Offre",
      hint:
        "Le produit, service, proposition de valeur, ressource gratuite ou message derrière le contenu.",
      placeholder: "Offre ou produit"
    },
    painPoint: {
      label: "Point de douleur",
      hint: "Le problème ou la frustration de votre audience.",
      placeholder: "Point de douleur"
    },
    desiredOutcome: {
      label: "Résultat souhaité",
      hint:
        "Le résultat que votre audience veut. Exemple : plus d’engagement, plus de prospects, un contenu plus fort, de meilleures ventes.",
      placeholder: "Résultat souhaité"
    },
    angle: {
      label: "Angle",
      hint:
        "La perspective ou le cadrage. Exemple : adapté aux débutants, urgent, narratif, démystification, premium.",
      placeholder: "Angle"
    },
    callToAction: {
      label: "CTA",
      hint:
        "L’action que vous voulez que les gens fassent. Exemple : commenter, partager, enregistrer, suivre, cliquer sur le lien.",
      placeholder: "CTA"
    },
    keywords: {
      label: "Mots-clés",
      hint:
        "Des mots-clés importants à inclure naturellement. Séparez-les par des virgules.",
      placeholder: "Mots-clés"
    },
    brandVoice: {
      label: "Voix de marque",
      hint:
        "La manière dont la légende doit se sentir. Exemple : directe, spirituelle, experte, ludique, premium.",
      placeholder: "Voix de marque"
    },
    avoidPhrases: {
      label: "Phrases à éviter",
      hint:
        "Mots répétés, clichés ou formulations que vous ne voulez pas dans la légende.",
      placeholder: "Phrases à éviter"
    },
    extraContext: {
      label: "Contexte supplémentaire",
      hint:
        "Ajoutez des exemples, détails produit, notes de campagne, preuves ou contexte supplémentaire pour améliorer le résultat.",
      placeholder:
        "Contexte supplémentaire, note de campagne, preuve ou angle créateur"
    }
  },

  contextSectionTitle: "Contexte de la légende",

  actions: {
    generating: "Génération...",
    generate: "Générer des légendes",
    clearResults: "Effacer les résultats",
    resetForm: "Réinitialiser le formulaire",
    copied: "Copié",
    copy: "Copier"
  },

  resultsIntro:
    "Voici des options de légendes prêtes à publier. Choisissez celle qui correspond le mieux à votre plateforme, à votre ton et à votre objectif de conversion.",

  labels: {
    score: "Score"
  },

  errors: {
    topicRequired: "Veuillez d’abord entrer un sujet.",
    generateFailed: "Échec de la génération des légendes."
  },

  fallbackUntitledTopic: "Sujet sans titre"
},

carouselGeneratorTool: {
  title: "Générateur de carrousels",
  description:
    "Créez des concepts de carrousel structurés avec une slide d’ouverture forte, une progression claire et un CTA de clôture.",

  fields: {
    topic: {
      label: "Sujet",
      placeholder: "ex. Pourquoi les publications sur les réseaux performent mal",
      hint:
        "Utilisez la leçon principale, le framework ou le problème que le carrousel doit couvrir."
    },
    platform: {
      label: "Plateforme",
      hint:
        "Choisissez la plateforme où le carrousel sera utilisé."
    },
    tone: {
      label: "Ton"
    },
    style: {
      label: "Style"
    },
    language: {
      label: "Langue"
    },
    audience: {
      label: "Audience",
      placeholder: "ex. Coachs et créateurs"
    },
    goal: {
      label: "Objectif",
      placeholder: "ex. Plus d’enregistrements et de partages"
    },
    offer: {
      label: "Offre",
      placeholder: "ex. Audit ou guide gratuit"
    },
    painPoint: {
      label: "Point de douleur",
      placeholder: "ex. Hooks faibles et faible engagement"
    },
    desiredOutcome: {
      label: "Résultat souhaité",
      placeholder: "ex. Message plus clair et publications plus fortes"
    },
    angle: {
      label: "Angle",
      placeholder: "ex. Les erreurs que la plupart des gens font"
    },
    callToAction: {
      label: "Appel à l’action",
      placeholder: "ex. Enregistre ceci et abonne-toi"
    },
    brandVoice: {
      label: "Voix de marque",
      placeholder: "ex. Intelligent, clair, pratique"
    },
    keywords: {
      label: "Mots-clés",
      placeholder: "ex. stratégie de contenu, croissance sur les réseaux sociaux"
    },
    avoidPhrases: {
      label: "Phrases à éviter",
      placeholder: "ex. révolutionnaire, astuce secrète"
    },
    extraContext: {
      label: "Contexte supplémentaire",
      placeholder:
        "Note de campagne facultative, idée de structure, direction des slides ou contexte d’audience..."
    }


  },

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "Général"
  },

  toneOptions: {
    educational: "Éducatif",
    bold: "Audacieux",
    conversational: "Conversationnel",
    professional: "Professionnel",
    persuasive: "Persuasif",
    warm: "Chaleureux",
    confident: "Confiant"
  },

  styleOptions: {
    educationalBreakdown: "Décomposition éducative",
    mistakeSeries: "Série d’erreurs",
    frameworkSeries: "Série de frameworks"
  },

  languageOptions: {
    english: "Anglais",
    spanish: "Espagnol",
    french: "Français",
    portuguese: "Portugais"
  },

  actions: {
    generating: "Génération...",
    generate: "Générer des slides de carrousel",
    reset: "Réinitialiser",
    copied: "Copié !",
    copy: "Copier"
  },

  results: {
    title: "Concepts de carrousel",
    description:
      "Chaque concept comprend une slide d’accroche, une progression principale, une slide de clôture et une légende.",
    empty: "Aucun concept de carrousel pour l’instant. Générez un lot pour les voir ici."
  },

  resultLabels: {
    carousel: "Carrousel",
    hookSlide: "Slide d’accroche",
    slide: "Slide",
    closingSlide: "Slide de clôture",
    caption: "Légende",
    score: "Score"
  },

  messages: {
    topicRequired: "Veuillez d’abord entrer un sujet.",
    generateFailed: "Échec de la génération des slides du carrousel.",
    copyFailed: "Échec de la copie du carrousel."
  }
},

commentToContentTool: {
  badge: "Commentaire en contenu",
  title: "Transformez les commentaires de votre audience en contenu utile",
  description:
    "Transformez un commentaire, une question ou une réaction de votre audience en réponse, hook, légende, angle de script ou nouvelle idée de contenu réellement exploitable.",

  fields: {
    topic: {
      label: "Sujet",
      placeholder: "ex. hooks, fitness, croissance business",
      hint:
        "Le sujet partagé aide à relier ce résultat au reste de votre workflow Studio."
    },
    platform: {
      label: "Plateforme",
      hint:
        "Le contexte de la plateforme aide Hookora à formater correctement la sortie."
    },
    outputType: {
      label: "Type de sortie",
      hint:
        "Choisissez si vous voulez une réponse, une idée de contenu, une légende ou une sortie de type script."
    },
    tone: {
      label: "Ton",
      hint:
        "Le ton détermine la manière dont le résultat final sera perçu."
    },
    targetAudience: {
      label: "Audience cible",
      placeholder: "ex. créateurs débutants, fondateurs, clients beauté",
      hint:
        "Le contexte d’audience est facultatif, mais peut améliorer la pertinence."
    },
    goal: {
      label: "Objectif",
      placeholder: "ex. augmenter l’engagement, répondre clairement, en faire un post",
      hint:
        "Facultatif. Utilisez-le si vous voulez orienter le résultat vers un objectif précis."
    },
    comment: {
      label: "Commentaire",
      placeholder:
        "Collez ici le commentaire, le DM, la réponse ou la question de l’audience...",
      hint:
        "Cela peut être une question, une objection, un compliment, une critique ou une réaction intéressante."
    },
    extraContext: {
      label: "Contexte supplémentaire",
      placeholder:
        "Ajoutez du contexte sur la publication d’origine, votre marque, votre produit ou ce que vous voulez que le commentaire devienne...",
      hint:
        "Facultatif, mais utile si vous voulez un contenu plus personnalisé."
    }
  },

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "Général"
  },

  outputTypeOptions: {
    reply: "Réponse",
    hook: "Hook",
    caption: "Légende",
    shortScript: "Script court",
    contentIdea: "Idée de contenu",
    carouselAngle: "Angle de carrousel"
  },

  toneOptions: {
    natural: "Naturel",
    friendly: "Amical",
    bold: "Audacieux",
    educational: "Éducatif",
    sales: "Vente",
    professional: "Professionnel"
  },

  actions: {
    generating: "Génération...",
    generate: "Générer à partir du commentaire",
    clearResults: "Effacer les résultats",
    reset: "Réinitialiser",
    copy: "Copier",
    copied: "Copié"
  },

  results: {
    title: "Résultat généré"
  },

  resultLabels: {
    output: "Résultat",
    angle: "Angle",
    summary: "Résumé",
    ideas: "Idées",
    notes: "Notes",
    rawResult: "Résultat brut"
  },

  tip:
    "Astuce : les bons commentaires d’audience révèlent souvent des angles cachés, des objections et des sujets qui méritent d’être transformés en posts.",

  messages: {
    commentRequired: "Veuillez coller le commentaire avant de générer le contenu.",
    generateFailed:
      "Un problème est survenu lors de la transformation du commentaire en contenu.",
    copyFailed:
      "Impossible de copier le résultat généré pour le moment. Veuillez réessayer."
  }
},

contentDifferentiationTool: {
  badge: "Différenciation de contenu",
  title: "Trouvez un angle plus fort dans des espaces de contenu saturés",
  description:
    "Analysez votre niche, votre audience et le style de vos concurrents pour révéler un meilleur positionnement, une singularité plus claire et des angles de contenu qui se démarquent.",

  fields: {
    topic: {
      label: "Sujet",
      placeholder: "ex. finance personnelle, fitness, outils IA",
      hint:
        "Le sujet partagé aide cet outil à rester aligné avec le reste de votre workflow Studio."
    },
    platform: {
      label: "Plateforme",
      hint:
        "Le contexte de la plateforme aide Hookora à adapter les idées de différenciation au bon format."
    },
    contentType: {
      label: "Type de contenu",
      hint:
        "Choisissez le type de contenu pour lequel vous voulez un meilleur positionnement."
    },
    targetAudience: {
      label: "Audience cible",
      placeholder: "ex. créateurs débutants, fondateurs de startup, étudiants",
      hint:
        "Le contexte d’audience aide à révéler de meilleurs angles et des manques dans le marché."
    },
    goal: {
      label: "Objectif",
      placeholder: "ex. se démarquer, paraître plus pertinent, obtenir plus de sauvegardes",
      hint:
        "Facultatif. Utilisez-le si vous voulez orienter le résultat vers un objectif clair."
    },
    brandContext: {
      label: "Contexte de marque",
      placeholder:
        "Décrivez votre marque, votre offre, votre perspective, vos forces et ce qui vous rend différent...",
      hint:
        "Cela donne à Hookora le contexte nécessaire pour trouver un positionnement plus fort."
    },
    competitorStyle: {
      label: "Style concurrentiel / tendance du marché",
      placeholder:
        "Décrivez comment les autres dans votre niche parlent, vendent ou créent généralement du contenu...",
      hint:
        "Facultatif, mais très utile si vous voulez que Hookora trouve un point de différence plus clair."
    },
    currentContent: {
      label: "Contenu ou message actuel",
      placeholder:
        "Collez votre hook, légende, script ou message actuel si vous voulez que Hookora l’améliore...",
      hint:
        "Facultatif. Utilisez-le si vous voulez des suggestions de différenciation plus concrètes liées à un contenu existant."
    }
  },

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "Général"
  },

  contentTypeOptions: {
    hooks: "Hooks",
    captions: "Légendes",
    scripts: "Scripts",
    carousels: "Carrousels",
    ads: "Publicités",
    generalContent: "Contenu général"
  },

  actions: {
    generating: "Génération...",
    generate: "Différencier le contenu",
    clearResults: "Effacer les résultats",
    reset: "Réinitialiser",
    copy: "Copier",
    copied: "Copié"
  },

  results: {
    title: "Résultat de différenciation"
  },

  resultLabels: {
    differentiationAngle: "Angle de différenciation",
    summary: "Résumé",
    marketGaps: "Manques du marché",
    uniqueStrengths: "Forces uniques",
    differentiationIdeas: "Idées de différenciation",
    avoid: "À éviter",
    sampleOutput: "Exemple de résultat",
    rawResult: "Résultat brut"
  },

  tip:
    "Astuce : plus vous expliquez clairement votre marché et la manière dont les autres s’expriment, mieux Hookora pourra vous aider à vous démarquer.",

  messages: {
    topicOrContextRequired:
      "Veuillez saisir un sujet ou un contexte de marque avant de générer des idées de différenciation.",
    generateFailed:
      "Un problème est survenu lors de la génération du résultat de différenciation.",
    copyFailed:
      "Impossible de copier le résultat de différenciation pour le moment. Veuillez réessayer."
  }
},

contentRepurposerTool: {
  title: "Réutilisateur de contenu",
  description:
    "Transformez un seul contenu source en plusieurs versions prêtes pour différentes plateformes, que vous pouvez publier, tester et enregistrer.",

  fields: {
    topic: {
      label: "Sujet",
      placeholder: "ex. Personal branding pour créateurs",
      hint:
        "Partagé entre les outils Studio pour garder la même idée de travail."
    },
    platform: {
      label: "Plateforme",
      placeholder: "ex. LinkedIn",
      hint:
        "Utilisez la plateforme principale de destination pour ce lot de contenus réutilisés."
    },
    sourceText: {
      label: "Texte source",
      placeholder:
        "Collez ici le post, la légende, le thread, le script, l’e-mail ou le brouillon original...",
      hint:
        "Collez le contenu original que vous voulez transformer en différentes versions prêtes pour chaque plateforme."
    },
    audience: {
      label: "Audience",
      placeholder: "ex. propriétaires de petites entreprises"
    },
    goal: {
      label: "Objectif",
      placeholder: "ex. plus d’engagement et de sauvegardes"
    },
    tone: {
      label: "Ton"
    },
    language: {
      label: "Langue"
    },
    brandVoice: {
      label: "Voix de marque",
      placeholder: "ex. claire, percutante, fiable"
    },
    callToAction: {
      label: "Appel à l’action",
      placeholder: "ex. Commentez ou enregistrez ce post"
    },
    keywords: {
      label: "Mots-clés",
      placeholder: "ex. croissance sur les réseaux sociaux, stratégie de contenu"
    },
    avoidPhrases: {
      label: "Phrases à éviter",
      placeholder: "ex. révolutionnaire, hack secret"
    },
    extraContext: {
      label: "Contexte supplémentaire",
      placeholder:
        "Instruction supplémentaire facultative, contexte de campagne, détails de l’offre, points de douleur du public ou note de plateforme..."
    }
  },

  toneOptions: {
    natural: "Naturel",
    conversational: "Conversationnel",
    bold: "Audacieux",
    professional: "Professionnel",
    warm: "Chaleureux",
    confident: "Confiant",
    persuasive: "Persuasif"
  },

  languageOptions: {
    en: "Anglais",
    es: "Espagnol",
    fr: "Français",
    pt: "Portugais"
  },

  actions: {
    generating: "Réutilisation...",
    generate: "Générer des contenus réutilisés",
    reset: "Réinitialiser",
    copy: "Copier",
    copied: "Copié !"
  },

  results: {
    title: "Contenus réutilisés",
    description:
      "Vos variantes générées apparaissent ici. Copiez les plus fortes et enregistrez les meilleures versions dans votre bibliothèque.",
    empty:
      "Aucun résultat pour le moment. Générez un lot pour voir ici votre contenu réutilisé.",
    outputFallback: "Résultat {{number}}",
    score: "Score : {{score}}",
    whyItWorks: "Pourquoi ça fonctionne :"
  },

  messages: {
    sourceRequired: "Veuillez d’abord coller ou saisir votre texte source.",
    generateFailed: "Impossible de générer les contenus réutilisés.",
    copyFailed: "Impossible de copier le résultat."
  }
},

ctaTool: {
  hero: {
    badge: "Outil de rédaction de conversion",
    title: "Générateur de CTA",
    descriptionStart: "Utilisez cet outil pour générer des",
    descriptionStrong: "appels à l’action plus forts",
    descriptionEnd:
      "qui paraissent plus clairs, plus persuasifs et plus naturels pour votre contenu, votre offre et votre plateforme."
  },
  fields: {
    topic: {
      label: "Sujet",
      hint:
        "Le sujet principal du contenu ou de l’offre. Soyez clair sur ce à quoi le CTA doit se rattacher.",
      placeholder: "Sur quoi porte le CTA ?"
    },
    platform: {
      label: "Plateforme",
      hint:
        "Choisissez où le CTA sera utilisé. La plateforme influence le style, l’urgence et la formulation."
    },
    audience: {
      label: "Audience",
      hint:
        "À qui s’adresse le CTA. Exemple : créateurs, fondateurs, étudiants, entrepreneurs.",
      placeholder: "Audience"
    },
    goal: {
      label: "Objectif",
      hint:
        "Ce que vous voulez que les gens fassent ou le résultat que le CTA doit soutenir.",
      placeholder: "Objectif"
    },
    offer: {
      label: "Offre",
      hint:
        "Le produit, le service, la valeur, l’aimant à prospects ou la promesse derrière le CTA.",
      placeholder: "Offre ou produit"
    },
    painPoint: {
      label: "Point de douleur",
      hint:
        "La frustration, la difficulté ou le problème que votre audience rencontre.",
      placeholder: "Point de douleur"
    },
    desiredOutcome: {
      label: "Résultat souhaité",
      hint:
        "Le résultat que votre audience veut obtenir. Exemple : plus de croissance, plus de ventes, moins de stress.",
      placeholder: "Résultat souhaité"
    },
    brandVoice: {
      label: "Voix de marque",
      hint:
        "La façon dont le CTA doit sonner. Exemple : audacieux, chaleureux, direct, premium, ludique.",
      placeholder: "Voix de marque"
    },
    ctaDirection: {
      label: "Direction du CTA",
      hint:
        "Le type d’action que vous voulez encourager. Exemple : s’abonner, commenter, acheter, enregistrer, cliquer.",
      placeholder: "Direction du CTA"
    },
    avoidPhrases: {
      label: "Phrases à éviter",
      hint:
        "Les mots ou expressions clichées que vous ne voulez pas voir dans le CTA.",
      placeholder: "Phrases à éviter"
    },
    extraContext: {
      label: "Contexte supplémentaire",
      hint:
        "Ajoutez des notes de campagne, un sentiment d’urgence, le moment, le comportement de l’audience ou d’autres détails utiles.",
      placeholder: "Contexte supplémentaire"
    }
  },
  context: {
    title: "Contexte du CTA"
  },
  actions: {
    generating: "Génération...",
    generate: "Générer des CTA",
    clearResults: "Effacer les résultats",
    resetForm: "Réinitialiser le formulaire",
    copied: "Copié",
    copy: "Copier"
  },
  resultsIntro: {
    start: "Voici des options de CTA conçues pour être plus",
    strong: "actionnables et convaincantes",
    end: "pour votre audience et votre plateforme."
  },
  output: {
    score: "Score",
    whyItWorks: "Pourquoi ça fonctionne",
    untitledTopic: "Sujet sans titre"
  },
  errors: {
    topicRequired: "Veuillez d’abord entrer un sujet.",
    generateFailed: "Échec de la génération des CTA."
  }
},

descriptionTool: {
  hero: {
    badge: "Outil de réécriture",
    title: "Descriptions",
    description:
      "Utilisez cet outil pour générer des réécritures de description plus fortes pour votre contenu. Vous pouvez coller une description existante pour l’améliorer, ou générer une nouvelle direction à partir de votre sujet et de votre contexte."
  },
  fields: {
    topic: {
      label: "Sujet",
      hint:
        "Le sujet principal de la description. Soyez clair sur ce que le contenu ou l’élément représente.",
      placeholder: "De quoi parle la description ?"
    },
    platform: {
      label: "Plateforme",
      hint:
        "Choisissez la plateforme où cette description sera utilisée."
    },
    originalDescription: {
      label: "Description originale",
      hint:
        "Collez ici la description actuelle si vous voulez l’améliorer au lieu de générer une nouvelle version à partir du contexte seul.",
      placeholder: "Collez la description actuelle si vous voulez la réécrire."
    },
    audience: {
      label: "Audience",
      hint:
        "À qui cette description s’adresse. Exemple : étudiants, créateurs, propriétaires de marque, fondateurs de startup.",
      placeholder: "Audience"
    },
    goal: {
      label: "Objectif",
      hint:
        "Ce que la description doit aider à obtenir. Exemple : conversions, clarté, temps de visionnage, clics, inscriptions.",
      placeholder: "Objectif"
    },
    offer: {
      label: "Offre",
      hint:
        "Le produit, cadeau, service, ressource ou promesse derrière le contenu.",
      placeholder: "Offre ou produit"
    },
    painPoint: {
      label: "Point de douleur",
      hint:
        "Le défi ou la frustration auxquels votre audience fait face.",
      placeholder: "Point de douleur"
    },
    desiredOutcome: {
      label: "Résultat souhaité",
      hint:
        "Le résultat que votre audience veut obtenir. Exemple : plus de croissance, un meilleur contenu, des résultats plus rapides, moins de stress.",
      placeholder: "Résultat souhaité"
    },
    angle: {
      label: "Angle",
      hint:
        "Le positionnement ou l’approche. Exemple : éducatif, premium, urgent, orienté débutant, réponse directe.",
      placeholder: "Angle"
    },
    cta: {
      label: "CTA",
      hint:
        "L’action que la description doit encourager. Exemple : s’abonner, regarder, commenter, enregistrer, visiter le lien.",
      placeholder: "CTA"
    },
    keywords: {
      label: "Mots-clés",
      hint:
        "Mots de recherche ou SEO importants à inclure naturellement. Séparez-les par des virgules.",
      placeholder: "Mots-clés"
    },
    brandVoice: {
      label: "Voix de marque",
      hint:
        "Le ton que la description doit avoir. Exemple : audacieux, expert, ludique, premium, direct.",
      placeholder: "Voix de marque"
    },
    avoidPhrases: {
      label: "Phrases à éviter",
      hint:
        "Les mots ou formulations que vous voulez éviter de répéter.",
      placeholder: "Phrases à éviter"
    },
    extraContext: {
      label: "Contexte supplémentaire",
      hint:
        "Ajoutez des preuves, des détails utiles, l’émotion visée, des notes de campagne ou tout autre contexte pour améliorer la réécriture.",
      placeholder: "Contexte supplémentaire, preuve unique, notes produit ou détail utile"
    }
  },
  contextTitle: "Contexte de réécriture",
  actions: {
    generating: "Génération...",
    generate: "Générer des réécritures",
    clearResults: "Effacer les résultats",
    resetForm: "Réinitialiser le formulaire",
    copied: "Copié",
    copy: "Copier"
  },
  messages: {
    topicRequired: "Veuillez d’abord entrer un sujet.",
    generateFailed: "Échec de la réécriture de la description.",
    ready:
      "Voici des réécritures de description prêtes à l’emploi. Choisissez celle qui correspond le mieux à votre plateforme, à votre objectif de clarté et à votre direction de conversion."
  },
  result: {
    score: "Score"
  }
},

hashtagTool: {
  hero: {
    badge: "Outil de découverte",
    title: "Hashtags",
    description:
      "Utilisez cet outil pour créer des ensembles stratégiques de hashtags selon différents objectifs de contenu. Ajoutez la plateforme, l’audience, le type de contenu et du contexte supplémentaire pour que Hookora puisse proposer de meilleurs mélanges de hashtags pour la découverte, la pertinence et l’engagement."
  },
  fields: {
    topic: {
      label: "Sujet",
      hint:
        "Le sujet, la niche ou la campagne que les ensembles de hashtags doivent soutenir.",
      placeholder: "À quoi sert cet ensemble de hashtags ?"
    },
    platform: {
      label: "Plateforme",
      hint:
        "Choisissez la plateforme, car le comportement des hashtags varie selon la plateforme."
    },
    contentType: {
      label: "Type de contenu",
      hint:
        "Le type de publication ou de contenu. Exemple : tutoriel, démo produit, publication éducative, histoire personnelle, carrousel.",
      placeholder: "Type de contenu"
    },
    audience: {
      label: "Audience",
      hint:
        "Le public que vous voulez attirer avec ces hashtags. Exemple : amateurs de beauté, fondateurs de startups, créateurs étudiants.",
      placeholder: "Audience"
    },
    goal: {
      label: "Objectif",
      hint:
        "Ce que vous voulez que l’ensemble de hashtags aide à atteindre. Exemple : découverte, pertinence, engagement, portée de niche.",
      placeholder: "Objectif"
    },
    keywords: {
      label: "Mots-clés",
      hint:
        "Les mots-clés importants du sujet à refléter dans les hashtags. Séparez-les par des virgules.",
      placeholder: "Mots-clés"
    },
    brandVoice: {
      label: "Voix de marque",
      hint:
        "L’ambiance de la marque ou du contenu. Cela aide à garder les ensembles de hashtags alignés sur le style du contenu.",
      placeholder: "Voix de marque"
    },
    extraContext: {
      label: "Contexte supplémentaire",
      hint:
        "Ajoutez des détails de sous-thème, le contexte de campagne, des précisions sur l’audience ou des notes de niche pour améliorer les ensembles.",
      placeholder: "Contexte supplémentaire, niche, sous-thème ou notes de campagne"
    }
  },
  contextTitle: "Contexte des hashtags",
  errors: {
    enterTopic: "Veuillez d’abord saisir un sujet.",
    generateFailed: "Échec de la génération des hashtags."
  },
  actions: {
    generating: "Génération...",
    generate: "Générer des ensembles de hashtags",
    clearResults: "Effacer les résultats",
    resetForm: "Réinitialiser le formulaire",
    copied: "Copié",
    copy: "Copier"
  },
  resultsHelper:
    "Voici des ensembles de hashtags prêts à l’emploi. Choisissez celui qui correspond le mieux à votre objectif de contenu et au comportement de la plateforme.",
  scoreLabel: "Score",
  assetTitlePrefix: "Ensemble de hashtags",
  untitledTopic: "Sujet sans titre"
},

hookAngleTool: {
  hero: {
    badge: "Outil de stratégie d’angle",
    title: "Angles de Hook",
    description: {
      before: "Utilisez cet outil pour générer",
      strong: "plusieurs angles de hook stratégiques",
      after:
        "à partir d’un seul sujet afin d’aborder la même idée de manière plus fraîche et plus efficace selon différents publics et objectifs."
    }
  },
  fields: {
    topic: {
      label: "Sujet",
      hint: "L’idée principale autour de laquelle Hookora doit créer des angles de hook.",
      placeholder: "Pour quel sujet voulez-vous des angles de hook ?"
    },
    platform: {
      label: "Plateforme",
      hint: "Choisissez la plateforme où le hook sera utilisé."
    },
    audience: {
      label: "Audience",
      hint:
        "À qui le hook doit parler. Exemple : créateurs, fondateurs, étudiants, chefs d’entreprise.",
      placeholder: "Audience"
    },
    goal: {
      label: "Objectif",
      hint:
        "Ce que le hook doit aider à obtenir. Exemple : clics, rétention, curiosité, commentaires, conversions.",
      placeholder: "Objectif"
    },
    offer: {
      label: "Offre",
      hint:
        "Le produit, service, bénéfice ou transformation derrière le contenu.",
      placeholder: "Offre ou message"
    },
    painPoint: {
      label: "Point de douleur",
      hint: "Le problème, la frustration ou le défi que votre audience rencontre.",
      placeholder: "Point de douleur"
    },
    desiredOutcome: {
      label: "Résultat souhaité",
      hint: "Le résultat que votre audience veut obtenir après avoir résolu le problème.",
      placeholder: "Résultat souhaité"
    },
    brandVoice: {
      label: "Voix de marque",
      hint:
        "Le ton que le hook doit avoir. Exemple : audacieux, spirituel, premium, direct, expert.",
      placeholder: "Voix de marque"
    },
    avoidPhrases: {
      label: "Phrases à éviter",
      hint: "Mots ou tournures que vous ne voulez pas voir répétés dans les angles.",
      placeholder: "Phrases à éviter"
    },
    extraContext: {
      label: "Contexte supplémentaire",
      hint:
        "Ajoutez des notes produit, un contexte de campagne, des détails sur l’audience, des preuves ou toute autre indication utile.",
      placeholder: "Contexte supplémentaire, preuve ou notes de campagne"
    }
  },
  contextTitle: "Contexte des Angles de Hook",
  errors: {
    missingTopic: "Veuillez d’abord saisir un sujet.",
    generateFailed: "Échec de la génération des angles de hook."
  },
  actions: {
    generate: "Générer des Angles de Hook",
    generating: "Génération...",
    copy: "Copier",
    copied: "Copié"
  },
  results: {
    intro: {
      before: "Voici des",
      strong: "directions stratégiques d’angles de hook",
      after:
        "que vous pouvez utiliser pour aborder le même sujet sous différents points de vue."
    },
    score: "Score",
    angleType: "Type d’Angle",
    bestUseCase: "Meilleur Cas d’Utilisation",
    sampleHook: "Exemple de Hook"
  }
},

hookScoreAnalyzerTool: {
  badge: "Analyseur de score de hook",
  title: "Analyseur de Score de Hook",
  description:
    "Analysez la force de votre hook avant de publier. Obtenez un score, une répartition, des points forts, des points faibles, des suggestions d’amélioration et de meilleures directions de hook.",
  fields: {
    topic: {
      label: "Sujet",
      placeholder: "ex. fitness, croissance d’entreprise, conseils skincare",
      hint: "Optionnel, mais utile pour une analyse plus précise selon l’angle de votre contenu."
    },
    platform: {
      label: "Plateforme",
      hint: "Le contexte de la plateforme change la façon dont les hooks sont évalués pour la clarté, le rythme et l’attention."
    },
    goal: {
      label: "Objectif",
      placeholder: "ex. obtenir plus de clics, augmenter le watch time, générer des commentaires",
      hint: "Ce que ce hook est censé accomplir."
    },
    targetAudience: {
      label: "Audience Cible",
      placeholder: "ex. débutants, fondateurs, étudiants, créateurs",
      hint: "Le public que le hook doit attirer."
    },
    hook: {
      label: "Hook",
      placeholder: "Collez le hook que vous voulez que Hookora analyse...",
      hint: "Collez la phrase d’ouverture exacte ou le hook que vous voulez noter."
    }
  },
  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "Général"
  },
  actions: {
    analyze: "Analyser le Hook",
    analyzing: "Analyse en cours...",
    clearResults: "Effacer les Résultats",
    reset: "Réinitialiser",
    copy: "Copier",
    copied: "Copié"
  },
  messages: {
    hookRequired: "Veuillez coller un hook à analyser.",
    analyzeFailed: "Un problème est survenu pendant l’analyse de ce hook.",
    copyFailed: "Impossible de copier l’analyse pour le moment. Veuillez réessayer."
  },
  tip:
    "Astuce : incluez le hook réel que vous voulez publier, pas seulement le sujet. Plus le hook est précis, meilleur sera le retour sur le score.",
  exportLabels: {
    overallScore: "Score Global",
    summary: "Résumé",
    breakdown: "Répartition",
    strengths: "Points Forts",
    weaknesses: "Points Faibles",
    suggestions: "Suggestions",
    improvedHooks: "Hooks Améliorés"
  },
  results: {
    title: "Analyse du Score de Hook",
    summary: "Résumé",
    breakdown: "Répartition du Score",
    whatWorks: "Ce qui Fonctionne",
    weakPoints: "Points Faibles",
    howToImprove: "Comment Améliorer",
    improvedHooks: "Hooks Améliorés",
    rawResult: "Résultat Brut",
    scoreLabels: {
      strong: "Hook Fort",
      decent: "Hook Correct",
      weak: "Hook Faible"
    }
  }
},

nigerianAudienceRewriteTool: {
  badge: "Outil de réécriture localisée",
  title: "Réécriture pour un public nigérian",
  subtitle:
    "Adaptez un contenu existant pour qu’il paraisse plus naturel, plus pertinent et plus aligné culturellement pour un public nigérian, sans perdre en clarté ni en intention.",

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "Général"
  },

  toneOptions: {
    natural: "Naturel",
    casual: "Décontracté",
    confident: "Assuré",
    sales: "Vente",
    storytelling: "Narratif",
    professional: "Professionnel"
  },

  formatOptions: {
    caption: "Légende",
    hook: "Accroche",
    shortScript: "Script court",
    adCopy: "Texte publicitaire",
    carouselCopy: "Texte de carrousel",
    general: "Réécriture générale"
  },

  fields: {
    topic: {
      label: "Sujet",
      placeholder: "De quoi parle ce contenu ?",
      hint: "Le sujet principal ou le message derrière la réécriture."
    },
    platform: {
      label: "Plateforme",
      hint: "Choisissez où la version réécrite sera utilisée."
    },
    tone: {
      label: "Ton",
      hint: "Choisissez le ton que doit avoir la version localisée."
    },
    contentFormat: {
      label: "Format du contenu",
      hint: "Choisissez le type de contenu que vous réécrivez."
    },
    goal: {
      label: "Objectif",
      placeholder: "Quel résultat cette réécriture doit-elle produire ?",
      hint: "Le résultat souhaité, comme la clarté, la conversion, l’engagement ou la proximité."
    },
    brandContext: {
      label: "Contexte de marque",
      placeholder: "Ajoutez la voix de marque, le positionnement, des indices sur l’audience ou un contexte important",
      hint: "Contexte facultatif pour rendre la réécriture plus fidèle à la marque et au public."
    },
    originalContent: {
      label: "Contenu original",
      placeholder: "Collez le contenu original que vous souhaitez réécrire pour un public nigérian",
      hint: "Collez le texte exact que Hookora doit adapter."
    }
  },

  buttons: {
    rewrite: "Réécrire pour un public nigérian",
    rewriting: "Réécriture...",
    clearResults: "Effacer les résultats",
    reset: "Réinitialiser",
    copy: "Copier",
    copied: "Copié"
  },

  messages: {
    missingOriginalText: "Veuillez d’abord saisir le contenu original.",
    copyError: "Échec de la copie du résultat.",
    genericError: "Nous n’avons pas pu réécrire ce contenu pour le moment."
  },

  tip:
    "Utilisez cet outil lorsque vous voulez qu’un contenu paraisse plus familier, plus naturel et plus pertinent localement pour des lecteurs ou spectateurs nigérians.",

  result: {
    title: "Résultat de la réécriture",
    assetTitle: "Réécriture pour un public nigérian",
    sections: {
      rewrittenOutput: "Contenu réécrit",
      summary: "Résumé",
      adaptationNotes: "Notes d’adaptation",
      alternativeVersions: "Versions alternatives",
      suggestions: "Suggestions",
      rawResult: "Résultat brut"
    }
  },

  exportLabels: {
    rewrittenOutput: "Contenu réécrit",
    summary: "Résumé",
    adaptationNotes: "Notes d’adaptation",
    alternativeVersions: "Versions alternatives",
    suggestions: "Suggestions"
  }
},

offerPositioningTool: {
  title: "Positionnement de l’offre",
  description:
    "Utilisez cet outil pour affiner la manière dont votre offre est présentée, expliquée et différenciée afin qu’elle paraisse plus claire, plus pertinente et plus convaincante pour le bon public.",

  fields: {
    topic: {
      label: "Sujet",
      placeholder: "De quoi parle ce positionnement ?",
      hint: "Le sujet principal, la niche ou l’orientation du positionnement de l’offre."
    },
    platform: {
      label: "Plateforme",
      placeholder: "Où ce positionnement sera-t-il utilisé ?",
      hint: "Choisissez où ce message apparaîtra, comme sur un site web, une page de vente, un e-mail ou du contenu social."
    },
    tone: {
      label: "Ton"
    },
    language: {
      label: "Langue"
    },
    offer: {
      label: "Offre",
      placeholder: "Décrivez l’offre, le produit ou le service",
      hint: "Expliquez ce que vous vendez, proposez ou aidez les gens à obtenir."
    },
    audience: {
      label: "Audience",
      placeholder: "À qui cela s’adresse-t-il ?"
    },
    goal: {
      label: "Objectif",
      placeholder: "Que doit accomplir ce positionnement ?"
    },
    painPoint: {
      label: "Point de douleur",
      placeholder: "Quel problème le public rencontre-t-il ?"
    },
    desiredOutcome: {
      label: "Résultat souhaité",
      placeholder: "Quel résultat le public veut-il obtenir ?"
    },
    angle: {
      label: "Angle",
      placeholder: "Quel angle ce positionnement doit-il adopter ?"
    },
    callToAction: {
      label: "Appel à l’action",
      placeholder: "Quelle action les gens doivent-ils entreprendre ?"
    },
    brandVoice: {
      label: "Voix de marque",
      placeholder: "Quel ton la marque doit-elle avoir ?"
    },
    keywords: {
      label: "Mots-clés",
      placeholder: "Mots-clés à inclure"
    },
    avoidPhrases: {
      label: "Phrases à éviter",
      placeholder: "Mots ou expressions à éviter"
    },
    extraContext: {
      label: "Contexte supplémentaire",
      placeholder: "Ajoutez du contexte, des preuves, des détails sur l’audience ou des notes de positionnement"
    }
  },

  toneOptions: {
    confident: "Confiant",
    professional: "Professionnel",
    bold: "Audacieux",
    conversational: "Conversationnel",
    warm: "Chaleureux",
    persuasive: "Persuasif",
    direct: "Direct"
  },

  languageOptions: {
    en: "Anglais",
    es: "Espagnol",
    fr: "Français",
    pt: "Portugais"
  },

  actions: {
    generate: "Générer le positionnement",
    generating: "Génération...",
    reset: "Réinitialiser",
    copy: "Copier",
    copied: "Copié"
  },

  messages: {
    topicOrOfferRequired: "Veuillez d’abord saisir un sujet ou décrire l’offre.",
    generateFailed: "Échec de la génération du positionnement de l’offre.",
    copyFailed: "Échec de la copie du contenu de positionnement."
  },

  results: {
    title: "Résultats du positionnement",
    description:
      "Examinez différentes directions de positionnement, propositions de valeur, cadrages d’audience et exemples de messages pour votre offre.",
    empty: "Aucun résultat de positionnement pour le moment. Générez un positionnement de l’offre pour voir les résultats ici.",
    fallbackTitle: "Option de positionnement {{number}}",
    score: "Score : {{score}}",
    positioningAngle: "Angle de positionnement",
    valueProposition: "Proposition de valeur",
    whoItsFor: "Pour qui c’est",
    problemFrame: "Cadre du problème",
    differentiator: "Élément différenciateur",
    messageExample: "Exemple de message"
  }
},
retentionIntroTool: {
  hero: {
    badge: "Intro de rétention",
    title: "Générez des premières secondes plus fortes",
    description:
      "Créez des intros conçues pour capter l’attention rapidement grâce à un hook fort, un effet d’aperçu, un thème plus profond et une raison claire de continuer à regarder."
  },

  fields: {
    topic: {
      label: "Sujet",
      placeholder: "ex. pourquoi la plupart des créateurs restent bloqués",
      hint: "Cet outil fonctionne mieux quand le sujet est précis et bien défini."
    },
    platform: {
      label: "Plateforme",
      hint: "Le contexte de la plateforme aide à façonner le rythme et la structure de l’intro."
    },
    contentType: {
      label: "Type de contenu",
      hint: "Choisissez le type de contenu pour lequel vous voulez créer l’ouverture."
    },
    tone: {
      label: "Ton",
      hint: "Le ton influence l’impact émotionnel et la voix de l’intro."
    },
    targetAudience: {
      label: "Audience cible",
      placeholder: "ex. créateurs débutants, fondateurs, étudiants",
      hint: "Le contexte d’audience est optionnel, mais il peut rendre l’intro plus ciblée."
    },
    goal: {
      label: "Objectif",
      placeholder: "ex. augmenter le watch time, améliorer la rétention, susciter la curiosité",
      hint: "Optionnel. Utile si vous voulez optimiser l’intro pour un résultat précis."
    },
    extraContext: {
      label: "Contexte supplémentaire",
      placeholder:
        "Décrivez l’idée du contenu, l’angle narratif, la leçon, le produit ou la direction émotionnelle...",
      hint: "Ajoutez du contexte si vous voulez que l’ouverture soit plus adaptée et plus précise."
    }
  },

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "Général"
  },

  contentTypeOptions: {
    short_video: "Vidéo courte",
    long_video: "Vidéo longue",
    ad: "Publicité",
    ugc: "UGC",
    educational: "Éducatif",
    storytelling: "Storytelling"
  },

  toneOptions: {
    bold: "Audacieux",
    natural: "Naturel",
    dramatic: "Dramatique",
    confident: "Confiant",
    curious: "Curieux",
    urgent: "Urgent"
  },

  actions: {
    generateIntro: "Générer l’intro",
    generating: "Génération...",
    clearResults: "Effacer les résultats",
    reset: "Réinitialiser",
    copy: "Copier",
    copied: "Copié"
  },

  messages: {
    topicRequired: "Veuillez saisir un sujet avant de générer une intro de rétention.",
    generateFailed: "Un problème est survenu lors de la génération de l’intro de rétention.",
    copyFailed: "Impossible de copier l’intro pour le moment. Veuillez réessayer."
  },

  tip:
    "Astuce : une bonne intro de rétention crée rapidement de la tension, montre tôt la valeur, et donne au spectateur une raison de ne pas faire défiler.",

  output: {
    resultTitle: "Résultat de l’intro de rétention",
    retentionIntro: "Intro de rétention",
    previewOpeningVisual: "Aperçu / Visuel d’ouverture",
    valuePromise: "Promesse de valeur",
    deeperTheme: "Thème plus profond",
    notes: "Notes",
    alternativeVersions: "Versions alternatives",
    rawResult: "Résultat brut"
  }
},

scriptTool: {
  hero: {
    badge: "Générateur de scripts",
    title: "Créez des scripts courts plus rapidement",
    description: {
      before: "Créez des ",
      strong: "scripts prêts à enregistrer",
      after:
        " avec de meilleurs hooks, une structure plus claire et une direction CTA adaptée à votre sujet, votre audience et votre plateforme."
    }
  },

  fields: {
    topic: {
      label: "Sujet",
      hint: "Le sujet du script. Soyez précis pour obtenir un meilleur résultat.",
      placeholder: "De quoi le script doit-il parler ?"
    },
    platform: {
      label: "Plateforme",
      hint: "Choisissez où le script sera utilisé afin que le rythme et la structure correspondent au format."
    },
    style: {
      label: "Style du script",
      hint: "Choisissez le format de script que Hookora doit générer."
    },
    audience: {
      label: "Audience",
      hint: "À qui s’adresse le script.",
      placeholder: "Audience"
    },
    goal: {
      label: "Objectif",
      hint: "Ce que le script doit aider à accomplir.",
      placeholder: "Objectif"
    },
    offer: {
      label: "Offre",
      hint: "Le produit, service, bénéfice ou message derrière le script.",
      placeholder: "Offre ou produit"
    },
    painPoint: {
      label: "Point de douleur",
      hint: "Le problème ou la frustration que rencontre l’audience.",
      placeholder: "Point de douleur"
    },
    desiredOutcome: {
      label: "Résultat souhaité",
      hint: "Le résultat que l’audience veut obtenir.",
      placeholder: "Résultat souhaité"
    },
    angle: {
      label: "Angle",
      hint: "La perspective ou l’approche que vous voulez donner au script.",
      placeholder: "Angle"
    },
    callToAction: {
      label: "CTA",
      hint: "L’action que vous voulez que le spectateur réalise.",
      placeholder: "CTA"
    },
    keywords: {
      label: "Mots-clés",
      hint: "Mots ou expressions importants à intégrer naturellement dans le script.",
      placeholder: "Mots-clés"
    },
    brandVoice: {
      label: "Voix de marque",
      hint: "Le ton global du script.",
      placeholder: "Voix de marque"
    },
    extraContext: {
      label: "Contexte supplémentaire",
      hint: "Ajoutez des détails, des preuves, des infos produit ou des notes créateur pour améliorer le script.",
      placeholder: "Contexte supplémentaire"
    }
  },

  styleOptions: {
    "direct-to-camera": "Face caméra",
    "story-led": "Axé sur l’histoire",
    "problem-solution": "Problème-Solution",
    "myth-busting": "Démystification"
  },

  context: {
    title: "Contexte du script"
  },

  actions: {
    generate: "Générer des scripts",
    generating: "Génération...",
    clear: "Effacer les résultats",
    reset: "Réinitialiser le formulaire",
    copy: "Copier",
    copied: "Copié"
  },

  errors: {
    topicRequired: "Veuillez d’abord saisir un sujet.",
    generateFailed: "Échec de la génération des scripts."
  },

  output: {
    helper: {
      before: "Voici des ",
      strong: "options de scripts prêtes à l’emploi",
      after: ". Choisissez celle qui correspond le mieux à votre angle, votre plateforme et votre objectif de conversion."
    },
    score: "Score",
    hookLabel: "Hook",
    bodyLabel: "Corps",
    ctaLabel: "CTA",
    untitledTopic: "Sujet sans titre"
  }
},

thumbnailTool: {
  hero: {
    badge: "Outil d’optimisation du CTR",
    title: "Texte de miniature",
    description:
      "Utilisez cet outil pour analyser le texte actuel de votre miniature et générer des alternatives plus fortes. Ajoutez le sujet et le contexte de positionnement pour que Hookora puisse suggérer des mots plus percutants et plus cliquables.",
  },
  fields: {
    topic: {
      label: "Sujet",
      hint:
        "Contexte facultatif du sujet pour l’analyseur de miniature. Cela aide les suggestions à être plus pertinentes par rapport au contenu.",
      placeholder: "Sujet facultatif pour l’analyse de la miniature",
    },
    thumbnailText: {
      label: "Texte de la miniature",
      hint:
        "Les mots exacts de la miniature que vous souhaitez analyser. Gardez-les courts et percutants pour de meilleurs résultats.",
      placeholder: "Entrez le texte actuel de la miniature",
    },
    audience: {
      label: "Audience",
      hint:
        "Le public que la miniature essaie d’attirer. Exemple : créateurs, étudiants, fondateurs, passionnés de fitness.",
      placeholder: "Audience",
    },
    angle: {
      label: "Angle",
      hint:
        "Le positionnement ou le cadrage émotionnel. Exemple : choquant, premium, direct, urgent, adapté aux débutants.",
      placeholder: "Angle",
    },
    desiredOutcome: {
      label: "Résultat souhaité",
      hint:
        "Le résultat que la miniature doit aider à générer. Exemple : clics, curiosité, durée de visionnage, conversions.",
      placeholder: "Résultat souhaité",
    },
    avoidPhrases: {
      label: "Phrases à éviter",
      hint:
        "Les mots ou expressions que vous ne voulez pas voir répétés dans les suggestions.",
      placeholder: "Phrases à éviter",
    },
    extraContext: {
      label: "Contexte supplémentaire",
      hint:
        "Ajoutez l’émotion visée, des notes de positionnement, le style des concurrents ou tout autre contexte pour améliorer les suggestions.",
      placeholder: "Contexte supplémentaire, cible émotionnelle ou note de positionnement",
    },
  },
  analysisContext: {
    title: "Contexte d’analyse",
  },
  actions: {
    analyzing: "Analyse en cours...",
    analyze: "Analyser le texte de la miniature",
    clearResults: "Effacer les résultats",
    resetForm: "Réinitialiser le formulaire",
    copied: "Copié",
    copy: "Copier",
  },
  analysis: {
    title: "Analyse du texte actuel",
  },
  labels: {
    score: "Score",
    words: "Mots",
    characters: "Caractères",
    untitledTopic: "Sujet sans titre",
  },
  suggestionsIntro:
    "Voici des alternatives suggérées pour le texte de la miniature. Choisissez celle qui semble la plus claire, la plus percutante et la plus cliquable pour votre audience.",
  errors: {
    enterTextOrTopic: "Saisissez d’abord un texte de miniature ou un sujet.",
    analyzeFailed: "Échec de l’analyse du texte de la miniature.",
  },
},

titleTool: {
  hero: {
    badge: "Outil axé sur le clic",
    title: "Titres",
    description:
      "Utilisez cet outil pour générer des options de titres cliquables pour des vidéos et des contenus. Ajoutez l’intention de recherche, l’angle et le contexte afin que Hookora puisse proposer des variantes de titres plus fortes, plus claires et plus susceptibles d’attirer l’attention.",
  },
  fields: {
    topic: {
      label: "Sujet",
      hint:
        "Le sujet principal de la vidéo ou du contenu. Plus votre sujet est clair, meilleures seront les variations de titre.",
      placeholder: "Pour quelle vidéo ou quel sujet ces titres sont-ils destinés ?",
    },
    audience: {
      label: "Audience",
      hint:
        "À qui le contenu s’adresse. Exemple : étudiants, créateurs, fondateurs e-commerce, freelances nigérians.",
      placeholder: "Audience",
    },
    goal: {
      label: "Objectif",
      hint:
        "Ce que le titre doit optimiser. Exemple : clics, curiosité, trafic de recherche, autorité, conversions.",
      placeholder: "Objectif",
    },
    angle: {
      label: "Angle",
      hint:
        "La manière de présenter le titre. Exemple : basé sur une erreur, adapté aux débutants, contrariant, axé sur les résultats.",
      placeholder: "Angle",
    },
    keywords: {
      label: "Mots-clés",
      hint:
        "Les termes de recherche importants à intégrer naturellement dans les titres. Séparez-les par des virgules.",
      placeholder: "Mots-clés (séparés par des virgules)",
    },
    brandVoice: {
      label: "Voix de marque",
      hint:
        "Le ton que le titre doit dégager. Exemple : percutant, premium, direct, ludique, audacieux.",
      placeholder: "Voix de marque",
    },
    avoidPhrases: {
      label: "Phrases à éviter",
      hint:
        "Les mots ou formulations répétitives que vous ne voulez pas voir apparaître dans les titres.",
      placeholder: "Phrases à éviter",
    },
    extraContext: {
      label: "Contexte supplémentaire",
      hint:
        "Ajoutez des preuves, des résultats précis, l’émotion visée ou des notes de positionnement pour améliorer la qualité des titres.",
      placeholder:
        "Contexte supplémentaire, preuve unique, résultat, exemple ou note de positionnement",
    },
  },
  contextSectionTitle: "Contexte du titre",
  actions: {
    generating: "Génération...",
    generate: "Générer des titres",
    clearResults: "Effacer les résultats",
    resetForm: "Réinitialiser le formulaire",
    copied: "Copié",
    copy: "Copier",
  },
  resultsIntro:
    "Voici des options de titres prêtes à être testées. Choisissez la plus forte selon son pouvoir d’attraction, sa clarté et son adéquation avec votre audience.",
  labels: {
    score: "Score",
  },
  fallbackUntitledTopic: "Sujet sans titre",
  errors: {
    topicRequired: "Veuillez d’abord entrer un sujet.",
    generateFailed: "Échec de la génération des titres.",
  },
},

trendToContentTool: {
  badge: "Outil tendance vers contenu",
  title: "Tendance vers contenu",
  description:
    "Transformez les tendances, conversations ou évolutions du marché en directions de contenu, idées, hooks, légendes et angles exploitables pour votre marque.",
  fields: {
    topic: {
      label: "Sujet",
      placeholder: "Sujet facultatif pour guider le résultat",
      hint:
        "Ajoutez votre sujet principal si vous voulez que les idées issues de la tendance restent liées à un thème ou une niche précise.",
    },
    platform: {
      label: "Plateforme",
      hint:
        "Choisissez la plateforme où vous voulez transformer cette tendance en contenu.",
    },
    outputType: {
      label: "Type de sortie",
      hint:
        "Sélectionnez le type de résultat que vous voulez que Hookora génère à partir de la tendance.",
    },
    goal: {
      label: "Objectif",
      placeholder: "Que doit permettre ce contenu ?",
      hint:
        "Exemple : portée, engagement, autorité, notoriété, clics ou conversions.",
    },
    audience: {
      label: "Audience",
      placeholder: "À qui ce contenu s’adresse-t-il ?",
      hint:
        "Décrivez l’audience à laquelle le contenu généré doit parler.",
    },
    trend: {
      label: "Tendance",
      placeholder: "Décrivez la tendance, le schéma, la conversation ou l’évolution",
      hint:
        "Collez une tendance, une observation, un schéma viral, une évolution du marché ou un comportement d’audience que vous voulez transformer en contenu.",
    },
    brandContext: {
      label: "Contexte de marque",
      placeholder: "Ajoutez le contexte de marque, d’offre ou de positionnement",
      hint:
        "Expliquez l’angle de votre marque, votre offre, votre positionnement ou votre style de contenu afin que les idées soient mieux alignées.",
    },
  },
  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "Général",
  },
  outputTypeOptions: {
    contentIdeas: "Idées de contenu",
    hooks: "Hooks",
    captions: "Légendes",
    scriptAngles: "Angles de script",
    carouselIdeas: "Idées de carrousel",
    videoConcepts: "Concepts vidéo",
  },
  actions: {
    generating: "Génération...",
    generate: "Générer",
    clearResults: "Effacer les résultats",
    reset: "Réinitialiser",
    copied: "Copié",
    copy: "Copier",
  },
  tip:
    "Astuce : plus la tendance et le contexte de marque sont précis, plus le résultat sera exploitable et différencié.",
  messages: {
    trendRequired: "Veuillez d’abord entrer une tendance.",
    generateFailed: "Échec de la génération de contenu à partir de la tendance.",
    copyFailed: "Échec de la copie du résultat.",
  },
  results: {
    title: "Résultats",
    summary: "Résumé",
    trendInsight: "Insight sur la tendance",
    generatedIdeas: "Idées générées",
    hookOptions: "Options de hooks",
    captionOptions: "Options de légendes",
    angles: "Angles",
    recommendations: "Recommandations",
    rawResult: "Résultat brut",
  },
  exportLabels: {
    summary: "Résumé",
    trendInsight: "Insight sur la tendance",
    generatedIdeas: "Idées générées",
    hookOptions: "Options de hooks",
    captionOptions: "Options de légendes",
    angles: "Angles",
    recommendations: "Recommandations",
  },
},

viralRewriterTool: {
  title: "Réécriture virale",
  description:
    "Réécrivez un contenu existant en versions plus fortes et plus prêtes à devenir virales, tout en conservant le message central.",
  fields: {
    topic: {
      label: "Sujet",
      placeholder: "De quoi parle cette réécriture ?",
      hint:
        "Optionnel. Ajoutez un sujet si vous voulez que la réécriture reste ancrée dans un thème précis."
    },
    platform: {
      label: "Plateforme",
      placeholder: "Où cela sera-t-il publié ?"
    },
    tone: {
      label: "Ton"
    },
    language: {
      label: "Langue"
    },
    sourceText: {
      label: "Texte source",
      placeholder: "Collez le contenu que vous voulez réécrire",
      hint:
        "Collez le texte original, la légende, le script ou la publication que vous voulez que Hookora réécrive."
    },
    audience: {
      label: "Audience",
      placeholder: "À qui cela s’adresse-t-il ?"
    },
    goal: {
      label: "Objectif",
      placeholder: "Que doit accomplir cette réécriture ?"
    },
    brandVoice: {
      label: "Voix de marque",
      placeholder: "Quel ton cela doit-il avoir ?"
    },
    callToAction: {
      label: "Appel à l’action",
      placeholder: "Quelle action cela doit-il encourager ?"
    },
    keywords: {
      label: "Mots-clés",
      placeholder: "Mots ou expressions importants à inclure"
    },
    avoidPhrases: {
      label: "Phrases à éviter",
      placeholder: "Mots ou expressions à éviter"
    },
    extraContext: {
      label: "Contexte supplémentaire",
      placeholder: "Y a-t-il autre chose que Hookora devrait savoir ?"
    }
  },
  toneOptions: {
    bold: "Audacieux",
    conversational: "Conversationnel",
    confident: "Confiant",
    persuasive: "Persuasif",
    professional: "Professionnel",
    warm: "Chaleureux",
    natural: "Naturel"
  },
  languageOptions: {
    en: "Anglais",
    es: "Espagnol",
    fr: "Français",
    pt: "Portugais"
  },
  actions: {
    generating: "Génération...",
    generate: "Générer des réécritures",
    reset: "Réinitialiser",
    copied: "Copié",
    copy: "Copier"
  },
  messages: {
    sourceRequired: "Veuillez d’abord coller le contenu à réécrire.",
    generateFailed: "Échec de la génération des réécritures virales.",
    copyFailed: "Impossible de copier la réécriture pour le moment."
  },
  results: {
    title: "Résultats de réécriture",
    description:
      "Choisissez la version qui vous paraît la plus forte, la plus nette et la plus capable de retenir l’attention.",
    empty: "Vos réécritures prêtes à devenir virales apparaîtront ici.",
    rewriteFallback: "Réécriture {{number}}",
    score: "Score : {{score}}",
    whyItWorks: "Pourquoi cela fonctionne :"
  }
},
},

  about: {
    seoTitle: "À propos de Hookora | Plateforme de workflow IA pour créateurs",
    seoDescription:
      "Découvrez ce que fait Hookora, pourquoi il existe et comment il aide les créateurs à construire de meilleurs hooks, titres, légendes, scripts et un meilleur workflow de contenu.",
    heroBadge: "À propos de Hookora",
    heroTitle: "Hookora aide les créateurs à passer de l’idée à un meilleur contenu plus vite",
    heroDescription:
      "Hookora est une plateforme de workflow IA conçue pour les créateurs qui veulent une manière plus propre, plus rapide et plus sûre de générer hooks, titres, légendes, hashtags, descriptions, scripts et texte de miniature.",
    createFreeAccount: "Créer un compte gratuit",
    exploreFeatures: "Explorer les fonctionnalités",
    storyBadge: "Pourquoi Hookora existe",
    storyTitle: "La création de contenu devrait sembler plus claire, pas plus lourde",
    storyParagraph1:
      "Beaucoup de créateurs savent ce qu’ils veulent dire, mais restent ralentis par les mots. Le hook semble faible. Le titre ne fonctionne pas. La légende paraît plate. Le script prend trop de temps. Cette friction s’accumule et rend la publication plus difficile qu’elle ne devrait l’être.",
    storyParagraph2:
      "Hookora a été créé pour réduire cette friction. Au lieu d’obliger les créateurs à passer entre des outils dispersés, des notes aléatoires et des workflows désordonnés, Hookora réunit le processus dans un système plus propre qui aide à transformer les idées en meilleurs résultats plus vite.",
    storyParagraph3:
      "Qu’une personne crée depuis Lagos, Londres ou Los Angeles, le problème est souvent le même : la création de contenu demande trop d’énergie mentale pour bien le packager. Hookora aide les créateurs à se sentir plus soutenus, plus réguliers et mieux en contrôle de leur workflow.",
    whyTitle: "Ce qui rend Hookora utile",
    whySubtitle:
      "Hookora n’est pas conçu pour faire sentir les créateurs remplacés. Il est conçu pour qu’ils se sentent plus affûtés, plus rapides et mieux soutenus.",
    ctaBadge: "Commencez avec Hookora",
    ctaTitle: "Construisez votre workflow de contenu avec plus de clarté et moins de friction",
    ctaDescription:
      "Si vous voulez une manière plus propre de générer hooks, titres, légendes, scripts et plus encore, Hookora vous offre un point de départ pratique.",
    viewPricing: "Voir les tarifs",
    guidesSectionTitle: "En savoir plus sur Hookora et comment il aide les créateurs",
    guidesSectionDescription:
      "Ces guides rapides expliquent ce que fait Hookora, qui il aide et comment il rend l’écriture de hooks et le workflow de contenu plus clairs et plus rapides.",
    schemaDescription:
      "Découvrez ce qu’est Hookora, qui il aide et comment Hookora accompagne les créateurs avec un workflow de contenu plus fluide.",
    guides: {
      whatIsHookora: {
        description:
          "Découvrez ce qu’est Hookora, comment il fonctionne et pourquoi il aide les créateurs à passer d’une idée à un contenu plus fort plus rapidement."
      },
      nigerianCreators: {
        description:
          "Découvrez comment Hookora aide les créateurs nigérians à produire de meilleurs hooks, titres, légendes, scripts et un meilleur packaging de contenu."
      },
      writeHooksFaster: {
        description:
          "Explorez comment Hookora aide les créateurs à écrire des hooks plus vite avec des angles plus clairs et des premières lignes plus fortes."
      }
    },
    cards: {
      builtForCreators: "Conçu pour les créateurs qui veulent moins de friction",
      keepsOutputMoving: "Pensé pour garder la production de contenu en mouvement",
      oneWorkflow:
        "Conçu pour relier hooks, titres, légendes et scripts dans un seul workflow",
      globalCreators: "Utile pour les créateurs au Nigeria et au-delà",
      connectedWorkflow: {
        title: "Un workflow connecté",
        text:
          "Hooks, titres, légendes, hashtags, scripts, descriptions et texte de miniature fonctionnent mieux lorsqu’ils sont créés dans un système connecté."
      },
      momentum: {
        title: "Un meilleur élan créatif",
        text:
          "Hookora aide les créateurs à passer moins de temps bloqués sur les mots et plus de temps à avancer vers un contenu prêt à publier."
      },
      packaging: {
        title: "Un meilleur packaging de contenu",
        text:
          "Un bon contenu ne dépend pas seulement des idées. Il dépend aussi de la manière dont ces idées sont présentées, cadrées et rendues plus faciles à cliquer ou à regarder."
      },
      growingCreators: {
        title: "Utile pour les créateurs en croissance",
        text:
          "Hookora est conçu pour être utile que vous débutiez ou que vous créiez déjà régulièrement et que vous ayez besoin d’un workflow plus fluide."
      }
    }
  },

  home: {
    featuredGuidesBadge: "Guides Hookora à la une",
    learnSectionTitle: "Découvrez comment Hookora aide les créateurs à avancer plus vite",
    learnSectionDescription:
      "Explorez des guides rapides sur ce qu’est Hookora, qui il aide et comment il rend l’écriture de hooks, l’idéation de contenu, les réécritures, le packaging et le workflow créateur plus clairs et plus rapides.",
    guides: {
      whatIsHookora: {
        title: "Qu’est-ce que Hookora ?",
        description:
          "Découvrez ce qu’est Hookora, comment il aide les créateurs à passer d’une idée à un contenu plus fort plus rapidement, et pourquoi il va maintenant au-delà de la simple génération de hooks."
      },
      nigerianCreators: {
        title: "Hookora pour les créateurs nigérians",
        description:
          "Découvrez comment Hookora aide les créateurs nigérians à produire de meilleurs hooks, titres, légendes, scripts, réécritures localisées et un meilleur packaging de contenu."
      },
      writeHooksFaster: {
        title: "Comment Hookora aide les créateurs à écrire des hooks plus vite",
        description:
          "Explorez comment Hookora aide les créateurs à écrire des hooks plus vite, à trouver de meilleurs angles, à améliorer la rétention et à transformer plus d’idées en contenu prêt à publier."
      }
    }
  },

  homeHero: {
    badge: "Plateforme de workflow IA pour créateurs",
    title: "Créez un contenu plus fort plus vite avec le Studio à 22 outils de Hookora",
    description:
      "Commencez gratuitement avec les outils d’écriture de base, puis débloquez des outils premium de workflow et des outils stratégiques avancés à mesure que vous progressez. Hookora aide les créateurs à générer des hooks, titres, légendes, hashtags, descriptions, scripts, réécritures, intros de rétention, systèmes de voix et bien plus depuis un workflow plus propre.",
    trustPoints: {
      tools: "22 outils Studio dans un seul workflow",
      free: "Commencez gratuitement avec les outils de base",
      upgrade: "Passez à un niveau supérieur pour les outils premium et stratégiques"
    },
    ideaLabel: "Idée de contenu",
    ideaText: "« Pourquoi la plupart des créateurs perdent l’attention dans les 3 premières secondes »",
    metrics: {
      tools: "Outils",
      workflow: "Workflow"
    },
    footerScale: "Conçu pour évoluer"
  },

  homeFeatures: {
    title: "Pourquoi les créateurs utilisent Hookora",
    subtitle:
      "Un workflow plus propre pour les hooks, titres, légendes, scripts et un meilleur packaging du contenu.",
    cards: {
      createFaster: {
        title: "Créez plus vite",
        text:
          "Passez d’une idée brute à un contenu utile plus rapidement sans rester bloqué sur les mots."
      },
      keepMessageSharp: {
        title: "Gardez un message plus clair",
        text:
          "Générez des hooks, titres, légendes et scripts plus clairs et plus intentionnels."
      },
      upgradeOnlyWhenNeeded: {
        title: "Passez à un niveau supérieur seulement quand nécessaire",
        text:
          "Commencez avec Free, puis débloquez les outils Creator et Pro à mesure que votre workflow grandit."
      }
    },
    studioToolsBadge: "Aperçu des outils Studio",
    workflowTitle: "Un workflow, plusieurs outils de contenu",
    workflowDescription:
      "Hookora relie votre workflow d’écriture pour vous permettre de passer des hooks à de meilleurs titres, légendes, scripts, réécritures et plus encore sans jongler entre des outils désordonnés.",
    toolCards: {
      free: "Outils gratuits pour les hooks, titres, légendes, hashtags, descriptions et scripts.",
      creatorOne: "Outils Creator pour les angles de hook, les CTAs et un meilleur packaging du contenu.",
      creatorTwo: "Support de rétention, réécritures et amélioration du texte de miniature.",
      creatorThree: "Réutilisation, copy publicitaire, rédaction de carrousels et positionnement d’offre.",
      creatorFour: "Analyse du score du hook, réécritures nigérianes et idées de contenu issues des commentaires.",
      proOne: "Outils Pro pour la conversion de tendances et une stratégie créative plus poussée.",
      proTwo: "Systèmes de voix de marque pour rendre le contenu plus cohérent.",
      proThree: "Outils de différenciation du contenu pour aider les créateurs à se démarquer plus clairement."
    }
  },

  homeCta: {
    badge: "Commencez avec plus de clarté",
    title: "Donnez à votre contenu une première impression plus forte",
    description:
      "Hookora vous aide à créer avec plus de confiance, plus de rapidité et plus de régularité. Commencez gratuitement et construisez un workflow plus propre pour les hooks, légendes, scripts, réécritures, rétention et un packaging de contenu plus fort."
  },

  features: {
    planBasedStudioAccess: "Accès Studio selon le plan",
    planSectionTitle: "22 outils Studio sur Free, Creator et Pro",
    planSectionDescription:
      "Hookora est désormais structuré pour offrir une progression claire : outils de base sur Free, outils premium de workflow sur Creator et outils stratégiques avancés sur Pro.",
    guidesSectionTitle: "En savoir plus sur le fonctionnement de Hookora",
    guidesSectionDescription:
      "Explorez des guides rapides sur ce qu’est Hookora, qui il aide et comment il améliore l’écriture de hooks, l’idéation, les réécritures, le packaging et le workflow créateur.",
    guides: {
      whatIsHookora: {
        title: "Qu’est-ce que Hookora ?",
        description:
          "Découvrez ce qu’est Hookora, comment il fonctionne et pourquoi il aide les créateurs à passer plus vite d’une idée à un contenu plus fort grâce à un workflow Studio de 22 outils."
      },
      nigerianCreators: {
        title: "Hookora pour les créateurs nigérians",
        description:
          "Voyez comment Hookora aide les créateurs nigérians à produire de meilleurs hooks, titres, légendes, scripts, réécritures localisées et un meilleur packaging de contenu."
      },
      writeHooksFaster: {
        title: "Comment Hookora aide les créateurs à écrire des hooks plus vite",
        description:
          "Découvrez comment Hookora aide les créateurs à écrire des hooks plus vite avec des angles plus clairs, de meilleures premières lignes, un meilleur soutien à la rétention et des outils plus flexibles."
      }
    },
    planGroups: {
      free: {
        title: "Plan Free",
        subtitle: "Commencez avec le workflow d’écriture de base",
        description:
          "Les utilisateurs Free peuvent accéder aux outils essentiels de Studio pour l’écriture de contenu et l’idéation au quotidien."
      },
      creator: {
        title: "Plan Creator",
        subtitle: "Débloquez les outils premium de workflow",
        description:
          "Creator débloque la couche premium intermédiaire de Hookora pour le packaging, les réécritures, la rétention, la réutilisation et une exécution plus forte du contenu."
      },
      pro: {
        title: "Plan Pro",
        subtitle: "Débloquez les outils stratégiques avancés",
        description:
          "Pro est conçu pour un travail créatif stratégique plus profond, afin de transformer de meilleures idées en meilleurs systèmes de contenu."
      }
    }
  },

  featuresHero: {
    badge: "Accès Studio selon le plan",
    title: "22 outils Studio pour le workflow créateur moderne",
    subtitle:
      "Hookora propose désormais une progression plus claire avec des outils de base sur Free, des outils premium de workflow sur Creator et des outils stratégiques avancés sur Pro.",
    startFree: "Commencer gratuitement",
    viewPricing: "Voir les tarifs",
    cards: {
      free: {
        label: "FREE",
        text: "Hooks, Titres, Légendes, Hashtags, Descriptions et Scripts."
      },
      creator: {
        label: "CREATOR",
        text:
          "Des outils premium de workflow pour le packaging, les réécritures, la rétention, les CTAs, les carrousels, le repurposing et plus encore."
      },
      pro: {
        label: "PRO",
        text:
          "Des outils stratégiques avancés pour la conversion des tendances, l’entraînement de la voix de marque et la différenciation du contenu."
      },
      oneStudio: {
        label: "Un Studio",
        text:
          "Créez hooks, titres, légendes, scripts, réécritures et outils avancés dans un seul espace."
      }
    },
    footerStats: {
      tools: "22 outils Studio",
      tieredAccess: "Accès par niveaux",
      cleanerWorkflow: "Workflow plus propre"
    }
  },

  featuresGrid: {
    title: "Ce que Hookora vous aide à créer",
    subtitle:
      "Un workflow complet pour les créateurs avec des outils gratuits, premium et stratégiques avancés.",
    items: {
      workflow22: {
        title: "Workflow à 22 outils",
        text:
          "Passez entre hooks, titres, légendes, descriptions, scripts, réécritures et outils stratégiques depuis un espace connecté."
      },
      freeCoreWriting: {
        title: "Outils gratuits de base",
        text:
          "Commencez avec les outils essentiels pour hooks, titres, légendes, hashtags, descriptions et scripts."
      },
      creatorPremium: {
        title: "Workflow premium Creator",
        text:
          "Débloquez un meilleur packaging, des réécritures, du repurposing, le support CTA, le support thumbnail, le positionnement d’offre et l’aide à la rétention."
      },
      proStrategic: {
        title: "Outils stratégiques Pro",
        text:
          "Utilisez des outils avancés pour transformer les tendances en contenu, entraîner la voix de marque et différencier votre contenu."
      },
      hooksAngles: {
        title: "Hooks et angles de hook",
        text:
          "Générez de meilleures ouvertures et explorez plusieurs directions à partir d’un même sujet."
      },
      titlesCaptionsHashtags: {
        title: "Titres, légendes et hashtags",
        text:
          "Écrivez des éléments de packaging plus forts qui rendent votre contenu plus clair, plus cliquable et plus facile à publier."
      },
      descriptionsScriptsCtas: {
        title: "Descriptions, scripts et CTAs",
        text:
          "Créez un meilleur support long format, des scripts courts et des appels à l’action plus clairs à partir d’une même idée centrale."
      },
      thumbnailHookScore: {
        title: "Texte de miniature et score de hook",
        text:
          "Améliorez la première impression et analysez la force du hook avant de publier."
      },
      humanizerRewriteRepurpose: {
        title: "Humaniseur, réécritures et réutilisation",
        text:
          "Rendez votre texte plus naturel, reformulez des idées et transformez une seule bonne entrée en plusieurs contenus."
      },
      retentionVoiceDifferentiation: {
        title: "Rétention, voix de marque et différenciation",
        text:
          "Affinez la manière dont votre contenu sonne, retient l’attention et se démarque des textes génériques pour créateurs."
      },
      trendCommentToContent: {
        title: "Tendances et contenu issu des commentaires",
        text:
          "Transformez les tendances et les signaux de l’audience en idées de contenu, angles et plans d’exécution plus forts."
      },
      librarySavedAssets: {
        title: "Bibliothèque et éléments enregistrés",
        text:
          "Conservez vos meilleurs hooks et sorties Studio pour que votre workflow devienne plus rapide avec le temps."
      }
    }
  },

  featuresWorkflow: {
    badge: "Le ressenti du workflow",
    title: "Conçu pour réduire la friction et garder votre élan créatif vivant",
    description:
      "Hookora est le plus utile lorsqu’il rend la création de contenu plus simple, plus claire et plus répétable. C’est le workflow qui lui donne cette sensation.",
    seePlans: "Voir les plans",
    steps: {
      start: {
        title: "Commencez par un sujet",
        text:
          "Ajoutez votre idée de contenu brute, angle, offre ou concept approximatif sans avoir besoin qu’il soit parfait."
      },
      generate: {
        title: "Générez de meilleures options",
        text:
          "Utilisez Hookora pour explorer hooks, angles de hook, titres, légendes, scripts, réécritures et plus encore à partir de cette même idée centrale."
      },
      refine: {
        title: "Affinez ce qui se démarque",
        text:
          "Améliorez la rétention, la voix, le positionnement et la différenciation pour que votre contenu paraisse plus fort avant publication."
      },
      save: {
        title: "Enregistrez et avancez plus vite la prochaine fois",
        text:
          "Conservez les sorties utiles dans votre bibliothèque afin de pouvoir les réutiliser, les affiner et créer avec plus de régularité."
      }
    }
  },

  blog: {
    seoTitle: "Blog Hookora | Guides créateurs, conseils de workflow et pages d’outils",
    seoDescription:
      "Explorez les articles du blog Hookora et les guides publics pour créateurs sur les hooks, titres, légendes, scripts, outils de contenu et un workflow créateur plus fluide.",
    title: "Blog Hookora",
    subtitle:
      "Des guides créateurs, des idées de workflow et du contenu de découverte qui montrent comment Hookora aide à créer un contenu plus fort.",
    heroBadge: "Blog Hookora",
    heroTitle: "Guides créateurs, conseils de workflow et contenu de découverte",
    heroDescription:
      "Explorez des guides pratiques conçus pour aider les créateurs à écrire de meilleurs hooks, légendes, titres et packagings avec un workflow plus fluide.",
    startFree: "Commencer gratuitement",
    exploreFeatures: "Explorer les fonctionnalités",
    readGuide: "Lire le guide",
    relatedReading: "Lectures liées",
    categories: {
      all: "Tous",
      hooks: "Hooks",
      contentTools: "Outils de contenu",
      captions: "Légendes",
      hookWriting: "Écriture de hooks"
    },

    posts: {
      whatIsHookora: {
        badge: "Hookora",
        title: "Qu’est-ce que Hookora ?",
        description:
          "Découvrez ce qu’est Hookora, comment il fonctionne et pourquoi Hookora aide les créateurs à générer plus vite des hooks, titres, légendes, hashtags, scripts, descriptions et textes de miniature."
      },
      nigerianCreators: {
        badge: "Hookora",
        title: "Hookora pour les créateurs nigérians",
        description:
          "Découvrez comment Hookora aide les créateurs nigérians à écrire plus vite de meilleurs hooks, titres, légendes, scripts et packagings de contenu avec un workflow plus fluide."
      },
      writeHooksFaster: {
        badge: "Hookora",
        title: "Comment Hookora aide les créateurs à écrire des hooks plus vite",
        description:
          "Découvrez comment Hookora aide les créateurs à écrire des hooks plus vite avec des angles plus clairs, de meilleures premières lignes et un workflow de contenu plus fluide."
      },
      aiToolsNigeria: {
        badge: "Workflow créateur",
        title: "Outils IA pour les créateurs nigérians",
        description:
          "Découvrez des outils IA pour les créateurs nigérians qui veulent un workflow de contenu plus rapide, de meilleurs hooks, de meilleures légendes, des titres plus percutants et une production plus régulière."
      },
      bestHookGeneratorNigeria: {
        badge: "Hooks",
        title: "Meilleur générateur de hooks pour les créateurs nigérians",
        description:
          "Découvrez comment les créateurs nigérians peuvent utiliser Hookora pour créer de meilleurs hooks, de meilleures premières lignes et de meilleures premières impressions pour les vidéos et les contenus sociaux."
      },
      contentToolsNigeria: {
        badge: "Outils de contenu",
        title: "Outils de contenu pour les créateurs au Nigeria",
        description:
          "Explorez des outils de contenu pour les créateurs au Nigeria qui veulent un workflow plus clair pour les hooks, légendes, titres, scripts et le packaging de contenu."
      },
      bestCaptionGeneratorNigeria: {
        badge: "Légendes",
        title: "Meilleur générateur de légendes pour les créateurs nigérians",
        description:
          "Découvrez comment Hookora aide les créateurs nigérians à écrire des légendes plus claires et plus rapides pour Instagram, TikTok, Shorts et un workflow créateur plus large."
      },
      betterHooksNigeria: {
        badge: "Écriture de hooks",
        title: "Comment les créateurs nigérians peuvent écrire de meilleurs hooks",
        description:
          "Découvrez des façons pratiques pour les créateurs nigérians d’écrire de meilleurs hooks pour Reels, Shorts, TikTok et les contenus sociaux avec des premières lignes plus fortes."
      }
    },

    articles: {
      whatIsHookora: {
        seoTitle: "Qu’est-ce que Hookora ? | Blog Hookora",
        seoDescription:
          "Découvrez ce qu’est Hookora, comment il fonctionne et pourquoi Hookora aide les créateurs à générer plus vite des hooks, titres, légendes, hashtags, scripts, descriptions et textes de miniature.",
        schemaDescription:
          "Découvrez ce qu’est Hookora, comment il fonctionne et pourquoi il aide les créateurs à passer d’une idée à un contenu plus fort plus rapidement.",
        title: "Qu’est-ce que Hookora ?",
        heroDescription:
          "Hookora est une plateforme de workflow IA pour les créateurs qui aide à transformer des idées brutes en hooks, titres, légendes, hashtags, descriptions, scripts et textes de miniature plus solides dans un système de contenu plus propre.",
        paragraphs: {
          0: "Hookora est conçu pour les créateurs qui savent que la qualité du contenu ne dépend pas seulement de bonnes idées. Elle dépend aussi de la manière dont ces idées sont emballées. Un hook plus fort, un titre plus clair, une meilleure légende et un script plus net peuvent changer la manière dont le contenu est perçu avant même que quelqu’un ne le regarde ou ne le lise en entier."
        },
        sections: {
          whatHookoraHelps: {
            heading: "Ce que Hookora aide les créateurs à faire",
            paragraph1:
              "Hookora aide les créateurs à avancer plus vite lorsque les mots ralentissent tout. Au lieu de rester bloqués au début du processus, ils peuvent générer de meilleures options pour les parties du contenu qui créent habituellement de la friction. Cela inclut les hooks d’ouverture, les titres vidéo, les légendes sociales, les groupes de hashtags, les descriptions réécrites, les scripts courts et les directions de texte pour les miniatures.",
            paragraph2:
              "L’objectif n’est pas de remplacer la créativité. L’objectif est de la soutenir. Hookora est conçu pour aider les créateurs à se sentir plus confiants lorsqu’ils transforment une idée en quelque chose de publiable."
          },
          whyHookoraExists: {
            heading: "Pourquoi Hookora existe",
            paragraph1:
              "Beaucoup de créateurs perdent du temps aux mêmes endroits. L’idée est là, mais le hook paraît faible. Le titre ne semble pas assez cliquable. La légende semble plate. Le script prend trop de temps à structurer. Ces petits retards créent avec le temps beaucoup de friction créative.",
            paragraph2:
              "Hookora existe pour réduire cette friction. Au lieu d’obliger les créateurs à utiliser des outils éparpillés et des workflows déconnectés, Hookora rassemble le processus de contenu dans un seul endroit qui paraît plus clair et plus simple à utiliser."
          },
          whoHookoraIsFor: {
            heading: "À qui Hookora s’adresse",
            paragraph1:
              "Hookora s’adresse aux créateurs, aux marques personnelles, aux marketeurs et aux utilisateurs des réseaux sociaux qui veulent un packaging de contenu plus fort sans perdre trop de temps sur la formulation. Il est utile pour les créateurs qui réalisent des vidéos YouTube, des Shorts, des publications Instagram, des Reels, des TikToks et d’autres formes de contenu social.",
            paragraph2:
              "Il est également utile pour les créateurs en croissance dans des endroits comme le Nigeria, où davantage de personnes construisent des marques en ligne, des entreprises portées par l’audience et des revenus créatifs, et ont besoin d’outils qui les aident à avancer plus vite."
          },
          whatMakesHookoraDifferent: {
            heading: "Ce qui rend Hookora différent"
          },
          inSimpleTerms: {
            heading: "En termes simples",
            paragraph:
              "Hookora est une plateforme de workflow de contenu qui aide les créateurs à passer d’une idée à un résultat plus fort plus rapidement. Si quelqu’un veut de meilleurs hooks, titres, légendes, scripts et packaging sans rendre le processus plus lourd, Hookora est conçu pour cela."
          }
        },
        list: {
          0: "Il connecte plusieurs tâches de contenu dans un seul workflow au lieu de traiter chaque tâche comme un outil aléatoire séparé.",
          1: "Il aide les créateurs à conserver leur élan lorsqu’ils restent bloqués sur les mots.",
          2: "Il se concentre sur un résultat pratique pour les créateurs, pas seulement sur une génération de texte IA générique.",
          3: "Il est conçu pour rendre la création de contenu plus fluide, plus claire et plus répétable."
        },
        ctaTitle: "Essayez Hookora et développez votre contenu plus vite avec un workflow plus fluide",
        ctaDescription:
          "Si vous voulez une manière plus propre de créer des hooks, titres, légendes, scripts et plus encore, Hookora vous offre un point de départ pratique."
      },

      contentToolsNigeria: {
  seoTitle: "Outils de contenu pour les créateurs au Nigeria | Hookora",
  seoDescription:
    "Découvrez des outils de contenu pour les créateurs au Nigeria qui veulent un workflow plus rapide, de meilleurs hooks, de meilleures légendes, des titres plus nets et une création de contenu plus simple.",
  title: "Outils de contenu pour les créateurs au Nigeria",
  heroBadge: "Guide Hookora • Outils de contenu pour les créateurs au Nigeria",
  heroTitle:
    "Des outils de contenu pour les créateurs au Nigeria qui veulent un workflow plus rapide et mieux organisé",
  heroDescription:
    "Les créateurs au Nigeria doivent souvent tout faire eux-mêmes, des idées de contenu jusqu’aux légendes, titres, hooks, scripts et à la publication. Hookora aide à simplifier ce processus en rendant la partie écriture et packaging plus rapide, plus claire et plus facile à répéter.",
  highlights: {
    0: "Création de contenu plus rapide",
    1: "Workflow plus clair",
    2: "Packaging de contenu plus fort",
    3: "Conçu pour les créateurs modernes"
  },
  previewTitle:
    "Ce que des outils de contenu devraient aider les créateurs à faire",
  previewItems: {
    0: {
      label: "Organiser",
      text: "Rassembler hooks, légendes, scripts et titres dans un workflow plus propre."
    },
    1: {
      label: "Accélérer",
      text: "Réduire le temps nécessaire pour passer d’une idée à un contenu exploitable."
    },
    2: {
      label: "Améliorer le résultat",
      text: "Rendre le contenu plus net et plus facile à publier régulièrement."
    }
  },
  benefitsEyebrow: "Pourquoi les créateurs les utilisent",
  benefitsTitle:
    "Les outils de contenu devraient rendre la création plus légère, pas plus dispersée",
  benefitsDescription:
    "Les meilleurs outils pour créateurs n’ajoutent pas seulement plus de fonctionnalités. Ils retirent de la friction, rendent le résultat plus facile à façonner et aident les créateurs à avancer plus vite sans perdre en clarté.",
  benefits: {
    0: {
      title: "Réduire la pression de tout faire manuellement",
      text: "Beaucoup de créateurs gèrent seuls l’idéation, l’écriture, le packaging et la publication. Hookora aide à alléger une partie de cette charge en rendant l’écriture de contenu plus rapide."
    },
    1: {
      title: "Soutenir hooks, légendes, titres et scripts dans un seul flux",
      text: "Au lieu de jongler entre des outils déconnectés, les créateurs peuvent construire de meilleurs résultats à partir d’un workflow plus fluide."
    },
    2: {
      title: "Aider les créateurs à rester plus réguliers",
      text: "La régularité devient plus facile quand le système derrière votre contenu est plus simple et plus facile à répéter."
    },
    3: {
      title: "Utile pour différents types de créateurs",
      text: "Que vous créiez des vidéos éducatives, des skits, des formats face caméra, du lifestyle ou du contenu de marque, un meilleur soutien du workflow fait une vraie différence."
    },
    4: {
      title: "Rendre le packaging de contenu plus fort",
      text: "Il ne s’agit pas seulement de l’idée de contenu. Le packaging compte aussi. Hookora aide sur la couche d’écriture derrière ce packaging."
    },
    5: {
      title: "Donner plus d’élan aux créateurs solo",
      text: "Un workflow de contenu plus fluide peut aider un créateur solo à avancer plus vite et à créer avec plus de confiance au fil du temps."
    }
  },
  faqTitle: "FAQ sur les outils de contenu pour les créateurs au Nigeria",
  faqIntro:
    "Voici quelques questions fréquentes sur les outils de contenu pour les créateurs au Nigeria et la place de Hookora dans ce workflow.",
  faqs: {
    0: {
      question: "Que sont les outils de contenu pour les créateurs au Nigeria ?",
      answer:
        "Les outils de contenu pour les créateurs au Nigeria sont des plateformes qui aident à rendre la création de contenu plus simple, plus rapide et plus organisée. Ils peuvent aider avec les hooks, titres, légendes, scripts, hashtags et le packaging de contenu."
    },
    1: {
      question:
        "Pourquoi les créateurs au Nigeria ont-ils besoin d’outils de workflow de contenu ?",
      answer:
        "Beaucoup de créateurs produisent du contenu régulièrement sans grandes équipes ni beaucoup de temps supplémentaire. Les outils de workflow aident à réduire le stress, à accélérer l’écriture et à rendre la production plus répétable."
    },
    2: {
      question: "Quels types de créateurs peuvent utiliser Hookora ?",
      answer:
        "Hookora est utile pour les YouTubers, créateurs TikTok, créateurs Instagram, créateurs de skits, créateurs lifestyle, éducateurs, marques personnelles, marketeurs et community managers."
    },
    3: {
      question: "Hookora remplace-t-il les outils de montage ?",
      answer:
        "Non. Hookora se concentre sur l’écriture et le packaging du contenu. Il aide avec les hooks, titres, légendes, scripts, hashtags, descriptions et textes de miniature."
    },
    4: {
      question: "Hookora peut-il aider les créateurs qui publient souvent ?",
      answer:
        "Oui. Hookora est particulièrement utile pour les créateurs qui ont besoin d’une manière plus fluide et plus rapide de continuer à produire régulièrement."
    },
    5: {
      question:
        "Hookora est-il utile pour les créateurs débutants au Nigeria ?",
      answer:
        "Oui. Il est utile pour les débutants comme pour les créateurs actifs parce qu’il aide à enlever la pression de la page blanche et soutient un workflow de contenu plus clair."
    }
  },
  ctaTitle:
    "Utilisez Hookora comme partie de votre workflow de contenu au Nigeria",
  ctaDescription:
    "Si vous voulez une manière plus claire de créer des hooks, légendes, titres, scripts, hashtags et packagings de contenu, Hookora vous offre un point de départ pratique.",
  cta: {
    badge: "Commencez gratuitement avec Hookora",
    title:
      "Utilisez Hookora comme partie de votre workflow de contenu au Nigeria",
    description:
      "Si vous voulez une manière plus claire de créer des hooks, légendes, titres, scripts, hashtags et packagings de contenu, Hookora vous offre un point de départ pratique.",
    primaryLabel: "Créer un compte gratuit",
    secondaryLabel: "Voir les fonctionnalités"
  }
},

      nigerianCreators: {
        seoTitle: "Hookora pour les créateurs nigérians | Blog Hookora",
        seoDescription:
          "Découvrez comment Hookora aide les créateurs nigérians à écrire plus vite de meilleurs hooks, titres, légendes, scripts et packagings de contenu avec un workflow plus fluide.",
        schemaDescription:
          "Découvrez comment Hookora aide les créateurs nigérians à passer d’une idée à un packaging de contenu plus fort plus rapidement.",
        title: "Hookora pour les créateurs nigérians",
        heroDescription:
          "Hookora aide les créateurs nigérians à transformer des idées de contenu brutes en hooks, titres, légendes, scripts, hashtags, descriptions et textes de miniature plus solides sans rendre le workflow plus lourd.",
        sections: {
          intro1:
            "De plus en plus de créateurs au Nigeria construisent des audiences, des marques personnelles, des entreprises et des pages média en ligne. Mais une chose continue d’en ralentir beaucoup : transformer de bonnes idées en contenu assez fort pour être publié. Le message peut être clair dans votre tête, mais le hook est faible, la légende paraît plate ou le titre manque de force.",
          intro2:
            "C’est là que Hookora intervient. Hookora est conçu pour rendre la création de contenu plus claire et plus rapide, surtout pour les créateurs qui ont besoin d’aide avec les mots qui façonnent la première impression.",
          heading1: "Pourquoi Hookora compte pour les créateurs nigérians",
          paragraph1:
            "Les créateurs nigérians travaillent sur plusieurs formats en même temps. Certains réalisent des YouTube Shorts, d’autres développent des pages Instagram, certains publient sur TikTok, et d’autres font grandir leur marque personnelle avec du contenu éducatif ou de divertissement. Dans tous ces cas, le packaging du contenu compte.",
          paragraph2:
            "Une première ligne plus forte peut aider un spectateur à s’arrêter de scroller. Un titre plus clair peut rendre une idée de vidéo plus facile à comprendre. Une meilleure légende peut rendre une publication plus intentionnelle. Un script court mieux structuré peut rendre l’enregistrement plus simple. Hookora aide les créateurs à franchir ces étapes avec moins de friction.",
          heading2: "Ce que Hookora aide à faire",
          list: {
            0: "Générer des hooks plus forts pour les vidéos sociales et le contenu créateur",
            1: "Améliorer les idées de titres YouTube pour qu’elles soient plus claires et plus cliquables",
            2: "Écrire plus vite des légendes pour Instagram, TikTok et Shorts",
            3: "Créer des groupes de hashtags plus pertinents pour le contenu",
            4: "Réécrire des descriptions pour rendre le message plus propre",
            5: "Structurer des scripts courts avec un meilleur hook et un meilleur flux",
            6: "Améliorer le texte de miniature pour une première impression plus forte"
          },
          heading3: "Un workflow plus propre pour les créateurs qui publient souvent",
          paragraph3:
            "L’une des parties les plus difficiles de la création de contenu n’est pas toujours l’idée en elle-même. C’est parfois la répétition. Quand vous publiez souvent, la pression de devoir continuer à trouver de meilleurs hooks, de meilleures légendes et de meilleurs titres peut devenir fatigante. Hookora réduit cette pression en vous aidant à passer plus vite d’une idée brute à un résultat utilisable.",
          paragraph4:
            "C’est particulièrement utile pour les créateurs nigérians qui essaient de rester réguliers tout en jonglant avec le contenu, le travail, l’école, une activité ou d’autres responsabilités. Un workflow plus fluide peut rendre la régularité plus réaliste.",
          heading4: "Hookora n’est pas réservé à un seul type de créateur",
          paragraph5:
            "Hookora peut aider les créateurs de divertissement, d’éducation, de business, les marques personnelles, les marketeurs et les community managers. Que votre contenu soit amusant, informatif, persuasif ou communautaire, la manière dont vous emballez l’idée compte toujours. Hookora aide à renforcer cette couche.",
          heading5: "En termes simples",
          paragraph6:
            "Hookora aide les créateurs nigérians à créer avec plus de vitesse, plus de confiance et moins de friction. Si vous avez déjà des idées mais avez besoin d’aide pour les transformer en hooks, titres, légendes et scripts plus forts, Hookora est conçu pour soutenir ce processus."
        },
        relatedLinks: {
          first: "Qu’est-ce que Hookora ?",
          second: "Comment Hookora aide les créateurs à écrire des hooks plus vite"
        },
        cta: {
          badge: "Commencez gratuitement avec Hookora",
          title: "Utilisez Hookora pour créer un contenu plus fort plus vite",
          description:
            "Si vous voulez un workflow plus propre pour les hooks, titres, légendes, scripts et plus encore, Hookora vous offre un point de départ pratique.",
          primaryLabel: "Créer un compte gratuit",
          secondaryLabel: "Explorer les fonctionnalités"
        }
      },

      writeHooksFaster: {
        seoTitle: "Comment Hookora aide les créateurs à écrire des hooks plus vite | Blog Hookora",
        seoDescription:
          "Découvrez comment Hookora aide les créateurs à écrire des hooks plus vite avec des angles plus clairs, de meilleures premières lignes et un workflow de contenu plus fluide.",
        schemaDescription:
          "Découvrez comment Hookora aide les créateurs à écrire des hooks plus vite avec une meilleure structure et de meilleures options d’ouverture.",
        title: "Comment Hookora aide les créateurs à écrire des hooks plus vite",
        heroDescription:
          "Hookora aide les créateurs à passer plus vite d’idées brutes à de meilleures premières lignes, afin que le début du processus de contenu soit moins frustrant et plus utile.",
        paragraphs: {
          0: "Écrire un hook semble simple jusqu’au moment où il faut le faire chaque jour. Beaucoup de créateurs connaissent leur sujet, connaissent leur message et savent même la valeur qu’ils veulent transmettre, mais restent bloqués au début. La première ligne ne semble pas assez forte. L’ouverture paraît trop banale. Le hook ressemble à quelque chose que les gens feraient défiler.",
          1: "Hookora aide à réduire ce délai. Au lieu d’obliger les créateurs à rester devant un écran vide à essayer d’inventer l’ouverture parfaite depuis zéro, Hookora aide à générer plus vite des directions plus fortes."
        },
        list: {
          0: "Il aide à transformer des idées brutes en angles d’ouverture plus clairs.",
          1: "Il donne aux créateurs des points de départ plus forts au lieu de les forcer à commencer de zéro.",
          2: "Il réduit l’hésitation lorsque plusieurs directions de hooks sont possibles.",
          3: "Il aide les créateurs à tester différentes approches autour d’un même sujet.",
          4: "Il rend plus facile la continuité de la création sans rester bloqué trop longtemps sur la première ligne."
        },
        sections: {
          whyHooksSlowCreators: {
            heading: "Pourquoi les hooks ralentissent les créateurs",
            paragraph1:
              "Les hooks portent beaucoup de pression. Ils façonnent la première impression. Ils influencent le fait que quelqu’un s’arrête de scroller, clique ou continue de regarder. À cause de cela, les créateurs les suranalysent souvent. Une simple idée de vidéo peut prendre trop de temps à démarrer parce que la première ligne semble encore faible.",
            paragraph2:
              "Ce retard affecte le reste du workflow. Quand le hook n’est pas clair, le script paraît plus lent. Le titre devient plus difficile à façonner. La légende est moins précise. Hookora aide en résolvant cette première partie plus vite."
          },
          howHookoraMakesItFaster: {
            heading: "Comment Hookora accélère l’écriture des hooks"
          },
          fromIdeaToFirstLine: {
            heading: "De l’idée à une première ligne plus forte",
            paragraph1:
              "Un créateur peut savoir qu’il veut parler d’erreurs d’argent, de conseils de contenu, de relations, de leçons business ou de commentaires sociaux, mais la meilleure première ligne n’est pas toujours évidente. Hookora aide à transformer ce sujet en directions de hooks plus fortes qui semblent plus structurées, plus guidées par la curiosité ou plus directes, selon ce dont l’idée a besoin.",
            paragraph2:
              "Cette rapidité compte parce que la création de contenu fonctionne mieux lorsque l’élan n’est pas interrompu en permanence. Plus vite un créateur peut passer de l’idée à un hook utilisable, plus il devient facile de construire le reste du contenu."
          },
          usefulForAllCreators: {
            heading: "Utile pour les créateurs short-form et long-form",
            paragraph:
              "Hookora est particulièrement utile pour les créateurs short-form parce que les hooks comptent énormément dans les Reels, Shorts et vidéos TikTok. Mais il aide aussi les créateurs long-form, car de meilleurs angles d’ouverture peuvent améliorer les titres, les intros et la présentation globale d’une vidéo ou d’une publication."
          },
          whatThisMeans: {
            heading: "Ce que cela signifie dans la pratique",
            paragraph:
              "Au lieu de passer trop de temps à essayer de formuler une seule première ligne, les créateurs peuvent utiliser Hookora pour générer de meilleurs points de départ, choisir un angle plus fort et avancer plus vite dans le reste du workflow. Cela signifie moins de friction au départ et plus d’espace pour se concentrer sur la qualité du contenu complet."
          },
          inSimpleTerms: {
            heading: "En termes simples",
            paragraph:
              "Hookora aide les créateurs à écrire des hooks plus vite en rendant la partie d’ouverture du processus de contenu plus claire, moins stressante et plus facile à traverser. Si écrire les premières lignes est l’endroit où votre processus ralentit le plus souvent, Hookora est conçu pour cela."
          }
        },
        relatedLinks: {
          first: "Qu’est-ce que Hookora ?",
          second: "Hookora pour les créateurs nigérians"
        },
        ctaTitle: "Utilisez Hookora pour créer des hooks plus forts avec moins de friction",
        ctaDescription:
          "Si vous voulez une manière plus rapide de façonner de meilleures premières lignes, de meilleurs angles et un workflow de contenu plus fluide, Hookora vous offre un point de départ pratique.",
        cta: {
          badge: "Commencez gratuitement avec Hookora",
          title: "Utilisez Hookora pour créer des hooks plus forts avec moins de friction",
          description:
            "Si vous voulez une manière plus rapide de façonner de meilleures premières lignes, de meilleurs angles et un workflow de contenu plus fluide, Hookora vous offre un point de départ pratique.",
          primaryLabel: "Créer un compte gratuit",
          secondaryLabel: "Explorer les fonctionnalités"
        }
      },

      aiToolsNigeria: {
        seoTitle: "Outils IA pour les créateurs nigérians | Hookora",
        seoDescription:
          "Découvrez des outils IA pour les créateurs nigérians qui veulent un workflow de contenu plus rapide, de meilleurs hooks, de meilleures légendes, des titres plus percutants et une production plus régulière.",
        title: "Outils IA pour les créateurs nigérians",
        heroBadge: "Guide Hookora • Outils IA pour les créateurs nigérians",
        heroTitle: "Des outils IA pour les créateurs nigérians qui veulent un workflow de contenu plus rapide et plus clair",
        heroDescription:
          "Les créateurs nigérians évoluent dans un espace rapide et compétitif où l’attention bouge vite et où la régularité compte. Hookora aide à réduire la friction d’écriture derrière les hooks, titres, légendes, scripts, hashtags et le packaging de contenu afin que les créateurs avancent plus vite avec plus de confiance.",
        highlights: {
          0: "Workflow de contenu plus rapide",
          1: "Meilleurs hooks et titres",
          2: "Moins de friction d’écriture",
          3: "Conçu pour les créateurs modernes"
        },
        previewTitle: "Ce que les outils IA aident les créateurs nigérians à faire",
        previewItems: {
          0: {
            label: "Vitesse",
            text: "Passez d’une idée brute à un résultat utilisable plus vite sans rester bloqué sur les mots."
          },
          1: {
            label: "Régularité",
            text: "Continuez à publier avec plus de structure même quand la créativité semble ralentie."
          },
          2: {
            label: "Workflow",
            text: "Reliez hooks, légendes, scripts et titres dans un système plus fluide."
          }
        },
        eyebrow: "Pourquoi c’est important",
        benefitsTitle:
          "Les créateurs au Nigeria ont besoin d’outils qui rendent la création de contenu plus légère, plus nette et plus facile à répéter",
        benefitsDescription:
          "Beaucoup de créateurs ne manquent pas d’idées. Ils souffrent surtout du fait que transformer des idées en résultats propres prend trop de temps. Hookora aide à combler plus vite cet écart.",
        benefits: {
          0: {
            title: "Réduire la friction d’écriture dans tout le workflow",
            text: "Beaucoup de créateurs savent ce qu’ils veulent dire mais perdent encore du temps à le transformer en contenu exploitable. Hookora aide à rendre ce processus plus simple."
          },
          1: {
            title: "Créer plus vite de meilleures premières impressions",
            text: "Les hooks, titres et textes de miniature influencent le fait que les gens s’arrêtent ou non. Hookora aide à renforcer ces premiers points de contact."
          },
          2: {
            title: "Soutenir une création de contenu plus régulière",
            text: "La régularité devient plus simple lorsque votre workflow semble répétable. Hookora aide les créateurs à produire plus vite sans rendre le résultat aléatoire."
          },
          3: {
            title: "Utile pour les créateurs short-form et social-first",
            text: "Que vous créiez des skits, des vidéos éducatives, du commentaire, du lifestyle ou du contenu de marque personnelle, Hookora aide sur la couche d’écriture derrière le packaging."
          },
          4: {
            title: "Aider les créateurs solo à avancer comme une petite équipe",
            text: "Un seul créateur doit souvent penser aux hooks, titres, légendes, scripts et à la publication en même temps. Hookora aide à alléger cette charge."
          },
          5: {
            title: "Conçu pour les créateurs qui veulent un résultat plus clair",
            text: "L’objectif n’est pas seulement d’avoir plus de contenu. L’objectif est d’avoir un contenu plus clair, plus fort et plus facile à publier."
          }
        },
        faqTitle: "FAQ sur les outils IA pour les créateurs nigérians",
        faqIntro:
          "Voici quelques questions fréquentes sur les outils IA pour les créateurs nigérians et la place de Hookora.",
        faqs: {
          0: {
            question: "Que sont les outils IA pour les créateurs nigérians ?",
            answer:
              "Les outils IA pour les créateurs nigérians sont des plateformes qui aident à accélérer le travail de contenu comme les hooks, légendes, scripts, titres et la planification. Ils sont utiles pour les créateurs qui veulent rester réguliers sans tout faire manuellement."
          },
          1: {
            question: "Pourquoi les créateurs nigérians ont-ils besoin d’outils de workflow IA ?",
            answer:
              "Beaucoup de créateurs au Nigeria produisent du contenu avec peu de temps, peu de soutien et une forte pression pour rester visibles en ligne. Les outils de workflow IA aident à réduire la friction d’écriture et rendent la création plus rapide et plus répétable."
          },
          2: {
            question: "Hookora est-il réservé aux créateurs nigérians ?",
            answer:
              "Non. Hookora est conçu pour les créateurs en général, mais il est aussi utile pour les créateurs nigérians qui veulent un workflow plus fluide pour les hooks, titres, légendes, hashtags, scripts et textes de miniature."
          },
          3: {
            question: "Quels types de créateurs peuvent utiliser Hookora ?",
            answer:
              "Hookora est utile pour les YouTubers, créateurs TikTok, créateurs Instagram, créateurs de skits, marques personnelles, marketeurs, community managers et créateurs de petites entreprises."
          },
          4: {
            question: "Hookora aide-t-il à plus que les hooks ?",
            answer:
              "Oui. Hookora inclut des outils pour les hooks viraux, les titres YouTube, les légendes, les hashtags, la réécriture de descriptions, les scripts courts et l’analyse de texte de miniature."
          },
          5: {
            question: "Les débutants peuvent-ils utiliser Hookora ?",
            answer:
              "Oui. Hookora est conçu pour être utile que vous débutiez ou que vous créiez déjà du contenu régulièrement."
          }
        },
        ctaTitle:
          "Utilisez Hookora pour construire un workflow de contenu plus rapide en tant que créateur nigérian",
        ctaDescription:
          "Si vous voulez une manière plus fluide de créer des hooks, titres, légendes, scripts, hashtags et packagings de contenu, Hookora vous offre un point de départ pratique.",
        cta: {
          badge: "Commencez gratuitement avec Hookora",
          title:
            "Utilisez Hookora pour construire un workflow de contenu plus rapide en tant que créateur nigérian",
          description:
            "Si vous voulez une manière plus fluide de créer des hooks, titres, légendes, scripts, hashtags et packagings de contenu, Hookora vous offre un point de départ pratique.",
          primaryLabel: "Créer un compte gratuit",
          secondaryLabel: "Voir les fonctionnalités"
        }
      },

      
        bestCaptionNigeria: {
  seoTitle: "Meilleur générateur de légendes pour les créateurs nigérians | Hookora",
  seoDescription:
    "Vous cherchez le meilleur générateur de légendes pour les créateurs nigérians ? Utilisez Hookora pour écrire des légendes plus claires, plus fortes et plus rapides pour le contenu social et le workflow créateur.",
  title: "Meilleur générateur de légendes pour les créateurs nigérians",
  heroBadge: "Guide Hookora • Meilleur générateur de légendes pour les créateurs nigérians",
  heroTitle:
    "Le meilleur générateur de légendes pour les créateurs nigérians qui veulent des légendes sociales plus rapides et plus claires",
  heroDescription:
    "Écrire une légende à chaque publication peut ralentir votre workflow, surtout lorsque vous connaissez déjà l’idée du contenu mais ne savez pas comment bien la formuler. Hookora aide les créateurs nigérians à générer plus vite des directions de légendes plus claires pour que la publication sociale semble plus fluide et plus régulière.",
  highlights: {
    0: "Écrire des légendes plus vite",
    1: "Améliorer la clarté du message",
    2: "Soutenir le workflow social",
    3: "Conçu pour les créateurs"
  },
  secondaryCta: "Explorer l’outil de légendes",
  previewTitle: "Ce qu’un générateur de légendes devrait vous aider à faire",
  previewItems: {
    0: {
      label: "Vitesse",
      text: "Passer d’une idée à une légende exploitable plus vite sans rester bloqué sur les mots."
    },
    1: {
      label: "Message",
      text: "Façonner des légendes plus claires, plus intentionnelles et plus simples à publier."
    },
    2: {
      label: "Régularité",
      text: "Continuer à publier avec un workflow de légendes plus fluide sur différentes plateformes."
    }
  },
  benefitsEyebrow: "Pourquoi les créateurs l’utilisent",
  benefitsTitle:
    "Le meilleur générateur de légendes pour les créateurs nigérians doit aider le contenu à sembler plus complet sans rendre les légendes génériques",
  benefitsDescription:
    "Les légendes font partie de la manière dont votre audience comprend ce que vous voulez dire. Hookora aide les créateurs à façonner cette couche écrite plus vite afin que le packaging du contenu paraisse plus clair et plus simple à gérer.",
  benefits: {
    0: {
      title: "Écrire plus vite des légendes à partir d’une seule idée de contenu",
      text: "Vous n’avez pas besoin de tout réécrire depuis zéro à chaque publication. Hookora aide les créateurs à passer plus vite d’une pensée brute à une légende exploitable."
    },
    1: {
      title: "Améliorer la clarté dans les publications sociales",
      text: "Une légende fonctionne mieux lorsque le message est simple à comprendre. Hookora aide à soutenir cette clarté sans rendre l’écriture rigide."
    },
    2: {
      title: "Soutenir différentes plateformes de créateurs",
      text: "Que vous publiiez sur Instagram, TikTok, YouTube Shorts ou d’autres plateformes, un meilleur soutien des légendes rend le workflow plus fluide."
    },
    3: {
      title: "Réduire la friction répétitive de publication",
      text: "Écrire des légendes peut devenir répétitif quand on crée souvent. Hookora aide à rendre cette partie du workflow plus facile."
    },
    4: {
      title: "Relier les légendes au reste de votre système de contenu",
      text: "Les légendes fonctionnent mieux lorsqu’elles correspondent au hook, au titre, au script et au message. Hookora soutient ce workflow créateur plus large."
    },
    5: {
      title: "Utile pour les débutants et les créateurs actifs",
      text: "Que vous appreniez encore à mieux packager votre contenu ou publiiez déjà régulièrement, un meilleur soutien des légendes vous aide à créer avec plus de confiance."
    }
  },
  faqTitle: "FAQ sur le meilleur générateur de légendes pour les créateurs nigérians",
  faqIntro:
    "Voici quelques questions fréquentes sur les générateurs de légendes pour les créateurs nigérians et comment Hookora aide.",
  faqs: {
    0: {
      question: "Quel est le meilleur générateur de légendes pour les créateurs nigérians ?",
      answer:
        "Le meilleur générateur de légendes pour les créateurs nigérians est celui qui aide à transformer rapidement des idées de contenu en légendes plus claires et plus exploitables. Hookora aide les créateurs à écrire des légendes plus vite tout en soutenant aussi les hooks, titres, scripts, hashtags et le packaging de contenu."
    },
    1: {
      question: "Pourquoi les légendes comptent-elles pour les créateurs nigérians ?",
      answer:
        "Les légendes aident à expliquer votre message, à ajouter du contexte et à rendre le contenu plus complet. De meilleures légendes peuvent aider les créateurs à communiquer plus clairement et à publier avec plus de confiance."
    },
    2: {
      question: "Hookora peut-il aider avec les légendes Instagram et TikTok ?",
      answer:
        "Oui. Hookora est utile pour les créateurs qui réalisent du contenu pour Instagram, TikTok, YouTube Shorts et d’autres plateformes sociales où les légendes soutiennent toute la publication."
    },
    3: {
      question: "Hookora génère-t-il seulement des légendes ?",
      answer:
        "Non. Hookora inclut aussi des outils pour les hooks viraux, les titres YouTube, les hashtags, la réécriture de descriptions, les scripts courts et l’analyse de texte de miniature."
    },
    4: {
      question: "Les créateurs débutants peuvent-ils utiliser Hookora pour les légendes ?",
      answer:
        "Oui. Hookora est utile que vous débutiez ou publiiez déjà régulièrement. Il aide à réduire la friction d’écriture et rend la création de légendes plus simple."
    },
    5: {
      question: "Hookora peut-il m’aider à écrire plus vite des légendes à partir d’une idée ?",
      answer:
        "Oui. L’un des principaux avantages est d’utiliser un sujet ou un angle de contenu pour générer plus vite des directions de légendes plus claires."
    }
  },
  ctaTitle: "Utilisez Hookora pour écrire de meilleures légendes en tant que créateur nigérian",
  ctaDescription:
    "Si vous voulez écrire des légendes plus vite, mieux soutenir votre message et avoir un workflow de contenu plus fluide, Hookora vous offre un point de départ pratique.",
  ctaSecondaryLabel: "Essayer le générateur de légendes"
},

      bestCaptionGeneratorNigeria: {
        seoTitle: "Meilleur générateur de légendes pour les créateurs nigérians | Hookora",
        seoDescription:
          "Vous cherchez le meilleur générateur de légendes pour les créateurs nigérians ? Utilisez Hookora pour écrire des légendes plus claires, plus fortes et plus rapides pour le contenu social et le workflow créateur.",
        title: "Meilleur générateur de légendes pour les créateurs nigérians",
        heroBadge: "Guide Hookora • Meilleur générateur de légendes pour les créateurs nigérians",
        heroTitle: "Le meilleur générateur de légendes pour les créateurs nigérians qui veulent des légendes sociales plus rapides et plus claires",
        heroDescription:
          "Écrire une légende à chaque publication peut ralentir votre workflow, surtout lorsque vous connaissez déjà l’idée du contenu mais ne savez pas comment bien la formuler. Hookora aide les créateurs nigérians à générer plus vite des directions de légendes plus claires pour que la publication sociale semble plus fluide et plus régulière.",
        highlights: {
          0: "Écrire des légendes plus vite",
          1: "Améliorer la clarté du message",
          2: "Soutenir le workflow social",
          3: "Conçu pour les créateurs"
        },
        primaryCtaLabel: "Commencer gratuitement",
        secondaryCtaLabel: "Explorer l’outil de légendes",
        previewTitle: "Ce qu’un générateur de légendes devrait vous aider à faire",
        previewItems: {
          0: {
            label: "Vitesse",
            text: "Passer d’une idée à une légende exploitable plus vite sans rester bloqué sur les mots."
          },
          1: {
            label: "Message",
            text: "Façonner des légendes plus claires, plus intentionnelles et plus simples à publier."
          },
          2: {
            label: "Régularité",
            text: "Continuer à publier avec un workflow de légendes plus fluide sur différentes plateformes."
          }
        },
        eyebrow: "Pourquoi les créateurs l’utilisent",
        benefitsTitle:
          "Le meilleur générateur de légendes pour les créateurs nigérians doit aider le contenu à sembler plus complet sans rendre les légendes génériques",
        benefitsDescription:
          "Les légendes font partie de la manière dont votre audience comprend ce que vous voulez dire. Hookora aide les créateurs à façonner cette couche écrite plus vite afin que le packaging du contenu paraisse plus clair et plus simple à gérer.",
        benefits: {
          0: {
            title: "Écrire plus vite des légendes à partir d’une seule idée de contenu",
            text: "Vous n’avez pas besoin de tout réécrire depuis zéro à chaque publication. Hookora aide les créateurs à passer plus vite d’une pensée brute à une légende exploitable."
          },
          1: {
            title: "Améliorer la clarté dans les publications sociales",
            text: "Une légende fonctionne mieux lorsque le message est simple à comprendre. Hookora aide à soutenir cette clarté sans rendre l’écriture rigide."
          },
          2: {
            title: "Soutenir différentes plateformes de créateurs",
            text: "Que vous publiiez sur Instagram, TikTok, YouTube Shorts ou d’autres plateformes, un meilleur soutien des légendes rend le workflow plus fluide."
          },
          3: {
            title: "Réduire la friction répétitive de publication",
            text: "Écrire des légendes peut devenir répétitif quand on crée souvent. Hookora aide à rendre cette partie du workflow plus facile."
          },
          4: {
            title: "Relier les légendes au reste de votre système de contenu",
            text: "Les légendes fonctionnent mieux lorsqu’elles correspondent au hook, au titre, au script et au message. Hookora soutient ce workflow créateur plus large."
          },
          5: {
            title: "Utile pour les débutants et les créateurs actifs",
            text: "Que vous appreniez encore à mieux packager votre contenu ou publiiez déjà régulièrement, un meilleur soutien des légendes vous aide à créer avec plus de confiance."
          }
        },
        faqTitle: "FAQ sur le meilleur générateur de légendes pour les créateurs nigérians",
        faqIntro:
          "Voici quelques questions fréquentes sur les générateurs de légendes pour les créateurs nigérians et comment Hookora aide.",
        faqs: {
          0: {
            question: "Quel est le meilleur générateur de légendes pour les créateurs nigérians ?",
            answer:
              "Le meilleur générateur de légendes pour les créateurs nigérians est celui qui aide à transformer rapidement des idées de contenu en légendes plus claires et plus exploitables. Hookora aide les créateurs à écrire des légendes plus vite tout en soutenant aussi les hooks, titres, scripts, hashtags et le packaging de contenu."
          },
          1: {
            question: "Pourquoi les légendes comptent-elles pour les créateurs nigérians ?",
            answer:
              "Les légendes aident à expliquer votre message, à ajouter du contexte et à rendre le contenu plus complet. De meilleures légendes peuvent aider les créateurs à communiquer plus clairement et à publier avec plus de confiance."
          },
          2: {
            question: "Hookora peut-il aider avec les légendes Instagram et TikTok ?",
            answer:
              "Oui. Hookora est utile pour les créateurs qui réalisent du contenu pour Instagram, TikTok, YouTube Shorts et d’autres plateformes sociales où les légendes soutiennent toute la publication."
          },
          3: {
            question: "Hookora génère-t-il seulement des légendes ?",
            answer:
              "Non. Hookora inclut aussi des outils pour les hooks viraux, les titres YouTube, les hashtags, la réécriture de descriptions, les scripts courts et l’analyse de texte de miniature."
          },
          4: {
            question: "Les créateurs débutants peuvent-ils utiliser Hookora pour les légendes ?",
            answer:
              "Oui. Hookora est utile que vous débutiez ou publiiez déjà régulièrement. Il aide à réduire la friction d’écriture et rend la création de légendes plus simple."
          },
          5: {
            question: "Hookora peut-il m’aider à écrire plus vite des légendes à partir d’une idée ?",
            answer:
              "Oui. L’un des principaux avantages est d’utiliser un sujet ou un angle de contenu pour générer plus vite des directions de légendes plus claires."
          }
        },
        ctaTitle: "Utilisez Hookora pour écrire de meilleures légendes en tant que créateur nigérian",
        ctaDescription:
          "Si vous voulez écrire des légendes plus vite, mieux soutenir votre message et avoir un workflow de contenu plus fluide, Hookora vous offre un point de départ pratique.",
        cta: {
          badge: "Commencez gratuitement avec Hookora",
          title: "Utilisez Hookora pour écrire de meilleures légendes en tant que créateur nigérian",
          description:
            "Si vous voulez écrire des légendes plus vite, mieux soutenir votre message et avoir un workflow de contenu plus fluide, Hookora vous offre un point de départ pratique.",
          primaryLabel: "Créer un compte gratuit",
          secondaryLabel: "Essayer le générateur de légendes"
        }
      },

     betterHooksNigeria: {
  seoTitle:
    "Comment les créateurs nigérians peuvent écrire de meilleurs hooks | Hookora",
  seoDescription:
    "Découvrez comment les créateurs nigérians peuvent écrire de meilleurs hooks pour Reels, Shorts, TikTok et le contenu social avec des premières lignes plus claires et une attention plus forte.",
  title: "Comment les créateurs nigérians peuvent écrire de meilleurs hooks",
  heroBadge:
    "Guide Hookora • Meilleurs hooks pour les créateurs nigérians",
  heroTitle:
    "Comment les créateurs nigérians peuvent écrire de meilleurs hooks pour capter davantage l’attention",
  heroDescription:
    "Beaucoup de créateurs ont déjà de bonnes idées, mais des ouvertures faibles rendent leur contenu plus facile à ignorer. Hookora aide les créateurs nigérians à construire de meilleurs hooks en rendant les premières lignes plus claires, plus nettes et plus faciles à explorer à partir d’un seul sujet.",
  highlights: {
    0: "Des premières lignes plus fortes",
    1: "Une création de hooks plus rapide",
    2: "Un meilleur packaging short-form",
    3: "Pensé pour le workflow créateur"
  },
  secondaryCta: "Essayer le générateur de hooks",
  previewTitle:
    "Ce que de meilleurs hooks aident les créateurs à accomplir",
  previewItems: {
    0: {
      label: "Arrêter le scroll",
      text: "Faites en sorte que les gens vous remarquent plus vite avant qu’ils ne passent à autre chose."
    },
    1: {
      label: "Clarifier",
      text: "Transformez des angles de contenu bruts en ouvertures plus faciles à comprendre."
    },
    2: {
      label: "Renforcer",
      text: "Donnez à votre contenu une meilleure première impression avant même que le reste du message ne se déroule."
    }
  },
  benefitsEyebrow: "Comment améliorer ses hooks",
  benefitsTitle:
    "Écrire de meilleurs hooks commence par rendre votre première ligne plus claire, plus percutante et plus digne d’attention",
  benefitsDescription:
    "La plupart des hooks faibles n’échouent pas parce que le sujet est mauvais. Ils échouent parce que l’ouverture paraît trop plate, trop vague ou trop facile à ignorer. Hookora aide les créateurs à améliorer ce premier moment.",
  benefits: {
    0: {
      title: "Commencez par l’angle le plus fort de votre idée",
      text: "Un hook plus fort commence avec la partie la plus intéressante, surprenante, utile ou émotionnellement marquante de votre contenu."
    },
    1: {
      title: "Évitez les ouvertures faibles et lentes",
      text: "Si votre première ligne paraît trop générique ou trop prudente, les gens auront plus tendance à continuer de défiler. De meilleurs hooks vont plus vite à l’essentiel."
    },
    2: {
      title: "Explorez plusieurs directions de hook",
      text: "Beaucoup de créateurs s’arrêtent à la première version d’un hook. Un contenu plus fort naît souvent lorsque plusieurs directions sont testées avant d’en choisir une."
    },
    3: {
      title: "Adaptez le hook à la plateforme",
      text: "Le contenu short-form exige souvent un langage qui capte l’attention plus rapidement. De meilleurs hooks aident les créateurs à adapter leur message à la manière dont les gens consomment le contenu."
    },
    4: {
      title: "Reliez les hooks au reste du contenu",
      text: "Un bon hook fonctionne encore mieux lorsqu’il s’accorde avec le titre, la légende, le script et la miniature. Hookora aide les créateurs à façonner ce packaging complet."
    },
    5: {
      title: "Utilisez des outils qui réduisent la pression de la page blanche",
      text: "Quand les créateurs ne savent pas comment commencer, le processus ralentit. Hookora aide à réduire cette friction et rend l’écriture de meilleurs hooks plus facile à répéter."
    }
  },
  faqTitle:
    "FAQ sur la manière dont les créateurs nigérians peuvent écrire de meilleurs hooks",
  faqIntro:
    "Voici quelques questions fréquentes sur la manière d’écrire de meilleurs hooks en tant que créateur nigérian et sur la façon dont Hookora aide.",
  faqs: {
    0: {
      question:
        "Pourquoi les hooks comptent-ils autant pour les créateurs nigérians ?",
      answer:
        "Les hooks comptent parce qu’ils façonnent la première impression. Sur les plateformes rapides, la première ligne décide souvent si les gens continuent de regarder, de lire ou quittent."
    },
    1: {
      question: "Qu’est-ce qui rend un hook plus fort ?",
      answer:
        "Un hook plus fort est généralement plus clair, plus adapté à l’attention et plus intéressant sur le plan émotionnel. Il donne aux gens une raison de rester plus longtemps avec le contenu."
    },
    2: {
      question:
        "Hookora peut-il aider les créateurs nigérians à écrire de meilleurs hooks plus vite ?",
      answer:
        "Oui. Hookora aide les créateurs à générer des directions de hooks plus nettes à partir d’un seul sujet afin qu’ils passent moins de temps à deviner et plus de temps à créer."
    },
    3: {
      question:
        "Hookora est-il seulement utile pour les créateurs de short-form ?",
      answer:
        "Non. Hookora est particulièrement utile pour le short-form, mais il aide aussi avec les légendes, titres, scripts, hashtags, descriptions et textes de miniature dans des workflows créatifs plus larges."
    },
    4: {
      question:
        "Les débutants peuvent-ils utiliser Hookora pour améliorer leurs hooks ?",
      answer:
        "Oui. Hookora est utile pour les débutants parce qu’il réduit la pression de la page blanche et rend plus facile l’exploration de directions d’ouverture plus fortes."
    },
    5: {
      question: "Un seul sujet peut-il produire plusieurs hooks ?",
      answer:
        "Oui. Une idée de contenu peut souvent être formulée de plusieurs façons. Explorer plusieurs angles de hooks est l’un des meilleurs moyens d’améliorer le packaging du contenu."
    }
  },
  ctaTitle:
    "Utilisez Hookora pour écrire de meilleurs hooks avec moins de friction",
  ctaDescription:
    "Si vous voulez des premières lignes plus fortes, des directions de hooks plus claires et une manière plus fluide de packager votre contenu, Hookora vous offre un point de départ pratique.",
  ctaSecondaryLabel: "Explorer le générateur de hooks"
},
    }
  },

  plans: {
  free: "Free",
  creator: "Creator",
  pro: "Pro",
  freeBadge: "Free",
  creatorBadge: "✔ Creator",
  proBadge: "✔ Pro"
},

  publicPricingHero: {
  badge: "Tarifs mensuels ou annuels pour les créateurs",
  title: "Choisissez le plan Hookora qui correspond à votre rythme créatif",
  description:
    "Commencez gratuitement, prenez de l’élan et passez à un niveau supérieur lorsque vous avez besoin de plus de générations, de plus d’éléments enregistrés et d’un workflow plus fort pour une production régulière. Choisissez la facturation mensuelle ou économisez 17 % avec l’annuelle.",
  startFree: "Commencer gratuitement",
  exploreFeatures: "Explorer les fonctionnalités",
  summaryTitle: "Choisissez le plan Hookora adapté à votre workflow",
  summaryDescription:
    "Commencez avec Free, puis passez à Creator ou Pro lorsque vous avez besoin de plus de volume, de plus d’outils premium et d’un meilleur support de workflow."
},

  pricingFaq: {
  title: "FAQ sur les tarifs",
  subtitle:
    "Quelques réponses rapides aux questions courantes sur les tarifs de Hookora.",
  items: {
    freePlan: {
      question: "Puis-je commencer avec le plan Free ?",
      answer:
        "Oui. Le plan Free est conçu pour vous aider à essayer Hookora, explorer le workflow et voir comment les outils s’intègrent à votre processus de contenu avant de passer à un niveau supérieur."
    },
    moveToCreator: {
      question: "Quand devrais-je passer à Creator ?",
      answer:
        "Creator est idéal lorsque vous publiez plus régulièrement et que vous voulez plus de générations, des hooks enregistrés illimités et un meilleur support de workflow."
    },
    whoIsProFor: {
      question: "À qui s’adresse Pro ?",
      answer:
        "Pro est idéal pour les créateurs sérieux et les utilisateurs avancés qui veulent des limites plus élevées, plus de flexibilité et une valeur de workflow plus profonde à mesure que Hookora continue de grandir."
    },
    upgradeLater: {
      question: "Puis-je passer à un niveau supérieur plus tard ?",
      answer:
        "Oui. Vous pouvez commencer gratuitement et évoluer lorsque votre workflow de contenu a besoin de plus d’espace."
    },
    moreThanOneTool: {
      question: "Hookora inclut-il plus d’un outil ?",
      answer:
        "Oui. Hookora inclut les hooks, l’optimisation de titres, les légendes, les hashtags, la réécriture de descriptions, les scripts courts, l’analyse de texte de miniature et plus encore."
    },
    beginnerCreators: {
      question: "Hookora est-il aussi conçu pour les créateurs débutants ?",
      answer:
        "Oui. Hookora est conçu pour être utile, que vous débutiez ou que vous créiez déjà régulièrement."
    }
  }
},

  pricingCta: {
  badge: "Commencez gratuitement avec Hookora",
  title:
    "Choisissez un plan Hookora et construisez votre workflow de contenu avec plus de clarté",
  description:
    "Commencez avec le plan Free, passez à un niveau supérieur lorsque vous avez besoin de plus d’espace créatif et utilisez Hookora pour rendre la création de contenu plus rapide, plus nette et plus cohérente.",
  startFree: "Créer un compte gratuit",
  exploreFeatures: "Explorer les fonctionnalités"
},

publicPricing: {
  title: "Choisissez le plan Hookora adapté à votre workflow",
  subtitle:
    "Commencez avec Free, puis passez à Creator ou Pro lorsque vous avez besoin de plus de volume, de plus d’outils premium et d’un meilleur support de workflow."
},

  pricing: {
  heading: "Des plans conçus pour les créateurs",
  seoTitle: "Tarifs Hookora | Plans Free, Creator et Pro",
  seoDescription:
    "Consultez les tarifs Hookora pour les plans Free, Creator et Pro. Commencez gratuitement, puis passez à un niveau supérieur pour obtenir plus de générations, plus d’éléments enregistrés et un meilleur support du workflow créateur.",
  softwareSchemaDescription:
    "Hookora est une plateforme de workflow IA pour créateurs avec des outils pour les hooks, titres, légendes, hashtags, descriptions, scripts et textes de miniature.",
  billing: "Facturation",
  billingPageIntro:
    "Consultez votre plan actuel, votre statut de facturation, votre intervalle de facturation, vos Reward Credits, votre Wallet Balance et vos contrôles d’abonnement.",
  save17: "Économisez 17 %",
  twoMonthsFree: "2 mois gratuits",
  inactive: "Inactif",
  monthly: "Mensuel",
  yearly: "Annuel",
  periodMonth: "/mois",
  periodYear: "/an",
  approx: "Env.",
  billedMonthly: "Facturé mensuellement",
  choose: "Choisir",
  currentPlan: "Plan actuel",
  switchToFree: "Passer à Free",
  updating: "Mise à jour...",
  redirecting: "Redirection...",
  mostPopular: "Le plus populaire",
  startFree: "Commencer gratuitement",
  chooseCreator: "Choisir Creator",
  choosePro: "Choisir Pro",
  startWithCoreWorkflow: "Commencez avec le workflow de base",
  freeDescription:
    "Idéal pour essayer Hookora et se familiariser avec le workflow principal avant de débloquer plus de possibilités.",
  creatorDescription:
    "Idéal pour les créateurs solo qui veulent des outils premium pour le packaging, les réécritures, la rétention et une meilleure qualité de contenu.",
  proDescription:
    "Idéal pour les créateurs sérieux et les petites équipes qui veulent des outils stratégiques plus poussés, plus de volume et un meilleur contrôle créatif.",
  coreWritingTools: "Outils d’écriture de base",
  premiumWorkflowTools: "Outils premium de workflow",
  advancedStrategicTools: "Outils stratégiques avancés",
  heroDescription:
    "Choisissez le plan Hookora qui correspond à votre workflow, à votre niveau d’utilisation et à vos objectifs de créateur.",
  currentUsage: "Utilisation actuelle",
  plan: "Plan",
  billingStatus: "Statut de facturation",
  billingInterval: "Intervalle de facturation",
  monthlyGenerations: "Générations mensuelles",
  remainingThisMonth: "Restant ce mois-ci",
  savedHooks: "Hooks enregistrés",
  unlimited: "Illimité",
  resetIn: "Réinitialisation dans",
  days: "jours",
  billingPeriodEnd: "Fin de la période de facturation",
  creatorRewards: "Hookora Creator Rewards",
  creatorRewardsDescription:
    "Partagez Hookora, gagnez des Reward Credits et continuez à faire avancer votre workflow créatif.",
  rewardCreditsBalance: "Solde de Reward Credits",
  shareYourReferral: "Partagez votre parrainage",
  referralCode: "Code de parrainage",
  referralLink: "Lien de parrainage",
  referralRewardNote:
    "Les Reward Credits sont appliqués avant le Wallet Balance, et le Wallet Balance est appliqué avant le paiement par carte au moment du checkout.",
  rewardsSummary: "Résumé des récompenses",
  rewardCredits: "Reward Credits",
  walletBalance: "Wallet Balance",
  totalReferrals: "Parrainages totaux",
  verifiedReferrals: "Parrainages vérifiés",
  paidReferrals: "Parrainages payants",
  fundWalletBalance: "Alimenter le Wallet Balance",
  fundWalletBalanceDescription:
    "Ajoutez de l’argent à votre Wallet Balance via Paystack et utilisez-le pour vos paiements futurs.",
  fundWalletDescription:
    "Ajoutez de l’argent à votre Wallet Balance pour les paiements d’abonnement actuels ou futurs.",
  enterAmount: "Saisir un montant",
  enterWalletFundingAmount: "Saisissez un montant pour alimenter le portefeuille",
  fundWallet: "Alimenter le portefeuille",
  walletTransactionHistory: "Historique des transactions du portefeuille",
  noWalletTransactions: "Aucune transaction de portefeuille pour le moment.",
  walletFunding: "Alimentation du portefeuille",
  appliedToBilling: "Appliqué à la facturation",
  transaction: "Transaction",
  checkoutOrderPrefix: "Lors du checkout, Hookora applique",
  then: "puis",
  cardPayment: "le paiement par carte",
  checkoutOrderSuffix: "pour le montant restant.",
  whatEachPlanUnlocks: "Ce que chaque plan débloque",
  planUnlockFreeText:
    "Des outils d’écriture de base pour la création de contenu au quotidien.",
  planUnlockCreatorText:
    "Des outils premium de workflow pour un meilleur packaging, de meilleures réécritures, une meilleure rétention et une meilleure exécution.",
  planUnlockProText:
    "Des outils stratégiques avancés pour des systèmes créateurs plus poussés et une différenciation plus claire.",
  proPlanAccess: "Accès au plan Pro",
  proPlanAccessDescription:
    "Vous avez actuellement accès aux outils stratégiques avancés de Hookora.",
  creatorPlanAccess: "Accès au plan Creator",
  creatorPlanAccessDescription:
    "Vous avez actuellement accès aux outils premium de workflow de Hookora.",
  freePlanAccess: "Accès au plan Free",
  freePlanAccessDescription:
    "Vous utilisez actuellement les outils gratuits de base de Hookora.",
  subscriptionStatus: "Statut de l’abonnement",
  subscriptionStatusDescription:
    "Vous n’avez actuellement aucun abonnement payant récurrent actif à gérer ici.",
  cancelSubscription: "Annuler l’abonnement",
  cancelSubscriptionDescription:
    "Vous pouvez annuler votre abonnement actuel à tout moment. Votre accès restera actif jusqu’à la fin de la période de facturation en cours.",
  cancelling: "Annulation...",
  cancellationFooter:
    "Après l’annulation, votre plan restera actif jusqu’à la fin de la période de facturation en cours.",
  billingPreview: "Aperçu de la facturation",
  planAmount: "Montant du plan",
  billingCycle: "Cycle de facturation",
  rewardCreditsApplied: "Reward Credits appliqués",
  walletBalanceApplied: "Wallet Balance appliqué",
  cardDueNow: "Paiement par carte dû maintenant",
  secureRecurringCheckoutPrefix: "Checkout récurrent sécurisé pour la facturation",
  secureRecurringCheckoutSuffix: ".",
  billingAndSubscriptions: "Facturation et abonnements",
  billingAndSubscriptionsDescription:
    "Choisissez votre plan, gérez vos mises à niveau et gardez vos informations de facturation alignées avec votre utilisation de Hookora.",
  waitlistTitle: "Soyez averti lorsque la facturation sera disponible",
  waitlistDescription:
    "Rejoignez la liste d’attente pour les plans payants et l’accès anticipé à la facturation. Cela nous aide à savoir quels utilisateurs veulent Creator ou Pro en priorité.",
  waitlistSuccess:
    "Vous avez bien été ajouté à la liste d’attente pour la facturation.",
  waitlistError:
    "Impossible de rejoindre la liste d’attente pour la facturation.",
  emailLabel: "E-mail",
  emailPlaceholder: "vous@exemple.com",
  interestedInLabel: "Intéressé par",
  bothPlans: "Les deux",
  notesLabel: "Notes",
  notesPlaceholder:
    "Dites-nous ce que vous aimeriez le plus dans les plans payants...",
  submitting: "Envoi...",
  joinBillingWaitlist: "Rejoindre la liste d’attente facturation",
  freePlan: "Plan Free",
  moveToCreator: "Passer à Creator",
  whoIsProFor: "À qui s’adresse Pro ?",
  upgradeLater: "Puis-je passer à un niveau supérieur plus tard ?",
  moreThanOneTool: "Hookora inclut-il plus d’un outil ?",
  beginnerCreators:
    "Hookora est-il aussi conçu pour les créateurs débutants ?",
  features: {
    free: {
      generations: "Générations mensuelles incluses",
      savedHooks: "Hooks enregistrés inclus",
      coreToolsOnly: "Outils de base uniquement",
      coreToolsList:
        "Hooks, titres, légendes, hashtags, descriptions et scripts",
      simpleWorkflow: "Workflow simple pour la création au quotidien",
      oneUser: "Accès pour un seul utilisateur"
    },
    creator: {
      generations: "Plus de générations mensuelles",
      savedHooks: "Plus de hooks enregistrés",
      everythingInFree: "Tout ce qui est dans Free",
      premiumTools: "Outils premium de workflow",
      workflowSupport:
        "Meilleur support pour le packaging, les réécritures et la rétention",
      analytics: "Meilleur support du workflow créateur",
      fullCreatorWorkflowSupport:
        "Support plus solide pour le packaging, les réécritures, la rétention et l’exécution"
    },
    pro: {
      generations: "Le plus grand nombre de générations mensuelles",
      everythingInCreator: "Tout ce qui est dans Creator",
      advancedTools: "Outils stratégiques avancés",
      strategySupport: "Support stratégique créateur plus poussé",
      futurePresets:
        "Accès prioritaire à une extension plus poussée du workflow",
      prioritySupport: "Support prioritaire"
    }
  },
  messages: {
    couldNotLoadBillingDetails:
      "Impossible de charger vos informations de facturation pour le moment.",
    confirmCancelSubscription:
      "Êtes-vous sûr de vouloir annuler votre abonnement ?",
    subscriptionCancelledSuccessfully:
      "L’abonnement a bien été annulé.",
    couldNotCancelSubscription:
      "Impossible d’annuler l’abonnement.",
    invalidWalletFundingAmount:
      "Veuillez saisir un montant valide pour alimenter le portefeuille.",
    walletFundingUrlNotReturned:
      "L’URL d’alimentation du portefeuille n’a pas été renvoyée.",
    couldNotStartWalletFunding:
      "Impossible de démarrer l’alimentation du portefeuille.",
    paymentSubmittedPaystack:
      "Votre paiement a bien été envoyé. Votre plan devrait être mis à jour bientôt.",
    paymentReceived:
      "Paiement reçu. Votre plan devrait être mis à jour bientôt.",
    walletSubmittedPaystack:
      "Le paiement pour alimenter le portefeuille a bien été envoyé.",
    walletReceived:
      "L’alimentation du portefeuille a bien été reçue.",
    checkoutCancelled: "Le checkout a été annulé.",
    loginToManageBilling:
      "Connectez-vous pour gérer votre facturation et vos récompenses.",
    failedToLoadBilling:
      "Impossible de charger les informations de facturation.",
    failedToCopy: "Impossible de copier.",
    alreadyOnFree: "Vous êtes déjà sur le plan Free.",
    changedToFree: "Le plan a bien été changé vers Free.",
    failedToChangePlan: "Impossible de changer de plan.",
    alreadyOnPlan: "Vous êtes déjà sur le plan {{plan}} {{cycle}}.",
    checkoutUrlMissing: "L’URL du checkout est manquante.",
    failedToStartCheckout: "Impossible de démarrer le checkout.",
    invalidWalletAmount:
      "Veuillez saisir un montant valide pour alimenter le portefeuille.",
    walletUrlMissing:
      "L’URL d’alimentation du portefeuille est manquante.",
    failedToStartWalletFunding:
      "Impossible de démarrer l’alimentation du portefeuille."
  }
},



  footer: {
    description:
      "Hookora est une plateforme de workflow social media alimentée par l’IA pour générer hooks, titres, légendes, scripts, hashtags et plus encore.",
    refundPolicy: "Politique de remboursement",
    privacyPolicy: "Politique de confidentialité",
    terms: "Conditions",
    contactSupport: "Contact et support",
    copyright: "© {{year}} Hookora. Tous droits réservés."
  },

  tools: {
  hooks: "Hooks",
  hookAngles: "Angles de Hook",
  titles: "Titres",
  captions: "Légendes",
  hashtags: "Hashtags",
  descriptions: "Descriptions",
  scripts: "Scripts",
  ctas: "CTAs",
  thumbnailAnalyzer: "Analyseur de miniature",
  aiHumanizer: "Humaniseur IA",
  repurposer: "Réutilisation",
  adCopy: "Texte publicitaire",
  carousels: "Carrousels",
  viralRewrite: "Réécriture virale",
  offerPositioning: "Positionnement d’offre",
  hookScore: "Score du hook",
  trendConverter: "Convertisseur de tendances",
  nigerianRewrite: "Réécriture nigériane",
  retentionIntro: "Intro de rétention",
  brandVoiceTrainer: "Entraîneur de voix de marque",
  contentDifferentiation: "Différenciation du contenu",
  commentIdeas: "Idées issues des commentaires",
  viralHookGenerator: "Générateur de hooks viraux",
  youtubeTitleOptimizer: "Optimiseur de titres YouTube",
  captionGenerator: "Générateur de légendes",
  hashtagGenerator: "Générateur de hashtags",
  descriptionRewriter: "Réécriture de descriptions",
  shortScriptGenerator: "Générateur de scripts courts",
  thumbnailTextAnalyzer: "Analyseur de texte de miniature",
  hookAngleGenerator: "Générateur d’angles de hook",
  ctaGenerator: "Générateur de CTA",
  contentRepurposer: "Réutilisateur de contenu",
  adCopyGenerator: "Générateur de texte publicitaire",
  carouselGenerator: "Générateur de carrousels",
  viralRewriter: "Réécriveur viral",
  trendToContent: "Tendance vers contenu",
  nigerianAudienceRewrite: "Réécriture pour audience nigériane",
  retentionIntroGenerator: "Générateur d’intro de rétention",
  commentToContent: "Commentaire vers contenu"
},
});

const pt = deepMerge(en, {
  common: {
  login: "Entrar",
  register: "Registrar-se",
  logout: "Sair",
  dashboard: "Painel",
  studio: "Studio",
  library: "Biblioteca",
  billing: "Faturamento",
  home: "Início",
  features: "Recursos",
  pricing: "Preços",
  about: "Sobre",
  blog: "Blog",
  settings: "Configurações da conta",
  waitlistAdmin: "Admin da lista de espera",
  fullscreenEnter: "Entrar em tela cheia",
  fullscreenExit: "Sair da tela cheia",
  light: "Claro",
  dark: "Escuro",
  language: "Idioma",
  theme: "Tema",
  current: "Atual",
  tools: "Ferramentas",
  unlockAllStudioTools: "Desbloquear todas as ferramentas do Studio",
  readGuide: "Ler guia",
  loading: "Carregando",
  copied: "Copiado",
  copyCode: "Copiar código",
  copyLink: "Copiar link"
},

  languages: {
    en: "Inglês",
    es: "Espanhol",
    fr: "Francês",
    pt: "Português"
  },

  navbar: {
    hi: "Oi",
    publicToolPages: "Páginas públicas de ferramentas",
    moreToolsInStudio: "Mais ferramentas disponíveis dentro do Studio",
    loggedIn: "Conectado",
    openSettingsMenu: "Abrir menu de configurações",
    toggleNavigationMenu: "Abrir menu de navegação",
    display: "Tela"
  },


  loginPage: {
  seoTitle: "Entrar | Hookora",
  seoDescription:
    "Entre na Hookora para acessar seu painel, ferramentas do Studio, biblioteca salva e controles de faturamento.",
  badge: "🚀 Bem-vindo de volta",
  title: "Entre na Hookora",
  description:
    "Acesse seu painel, gerador, biblioteca de hooks e ferramentas de preços.",
  emailPlaceholder: "E-mail",
  passwordPlaceholder: "Senha",
  showPassword: "Mostrar senha",
  hidePassword: "Ocultar senha",
  loginButton: "Entrar",
  loggingIn: "Entrando...",
  forgotPassword: "Esqueceu a senha?",
  noAccount: "Ainda não tem uma conta?",
  registerLink: "Registrar-se",
  errors: {
    default: "Falha no login."
  }
},

registerPage: {
  seoTitle: "Registro | Hookora",
  seoDescription:
    "Crie sua conta na Hookora e comece a gerar hooks, títulos, legendas, roteiros e muito mais em um só fluxo.",
  badge: "✨ Crie sua conta",
  title: "Crie sua conta na Hookora",
  subtitle:
    "Comece a gerar, salvar e gerenciar hooks em um só lugar.",
  referralBanner:
    "Use um código de indicação para ajudar alguém a ganhar <strong>Reward Credits</strong>. Indicadores ganham ₦300 após o cadastro e a verificação do e-mail, além de ₦700 extras depois de um upgrade pago.",
  referralDetected:
    "Código de indicação detectado e aplicado ao seu cadastro.",
  fields: {
    username: {
      label: "Nome de usuário",
      placeholder: "Escolha um nome de usuário"
    },
    email: {
      label: "E-mail",
      placeholder: "Digite seu e-mail"
    },
    password: {
      label: "Senha",
      placeholder: "Crie uma senha"
    },
    referralCode: {
      label: "Código de indicação",
      optional: "(opcional)",
      placeholder: "Digite o código de indicação",
      help:
        "Cadastrar-se por indicação ajuda quem indicou você a ganhar Reward Credits depois que seu e-mail for verificado."
    }
  },
  passwordHints: {
    title: "A senha deve incluir:",
    minLength: "Pelo menos 6 caracteres",
    hasLetter: "Pelo menos 1 letra",
    hasNumberOrSymbol: "Pelo menos 1 número ou símbolo"
  },
  actions: {
    register: "Registrar",
    creatingAccount: "Criando conta...",
    resendVerificationEmail: "Reenviar e-mail de verificação",
    sending: "Enviando...",
    goToLogin: "Ir para login",
    login: "Entrar"
  },
  messages: {
    accountCreated:
      "Conta criada com sucesso. Verifique seu e-mail antes de entrar.",
    verifyInbox:
      "Verifique sua caixa de entrada para encontrar o e-mail de verificação. Depois de verificar, você poderá entrar e sua elegibilidade de indicação será contabilizada corretamente.",
    enterEmailForResend:
      "Digite primeiro seu e-mail para que possamos reenviar o link de verificação.",
    verificationSent:
      "E-mail de verificação enviado com sucesso.",
    resendFailed:
      "Falha ao reenviar o e-mail de verificação. Tente novamente.",
    registrationFailed: "Falha no registro.",
    referralNote: "Nota de indicação:"
  },
  links: {
    alreadyHaveAccount: "Já tem uma conta?"
  },
  aria: {
    showPassword: "Mostrar senha",
    hidePassword: "Ocultar senha"
  }
},

plans: {
  free: "Free",
  creator: "Creator",
  pro: "Pro",
  freeBadge: "Free",
  creatorBadge: "Creator",
  proBadge: "Pro"
},

dashboard: {
  title: "Painel",
  subtitle:
    "Acompanhe o desempenho dos seus hooks, a atividade do Studio, as tendências e o conteúdo com melhor desempenho.",

  loading: "Carregando painel...",

  errors: {
    notLoggedIn: "Você não está conectado. Faça login primeiro.",
    loadFailed: "Falha ao carregar o painel.",
    copyFailed: "Falha ao copiar. Tente novamente.",
    copyFailedConsole: "Falha ao copiar:",
    copyRewardFailedConsole: "Falha ao copiar texto de recompensa:",
    toggleFavoriteConsole: "Falha ao alterar favorito:",
    favoriteUpdateFailed: "Falha ao atualizar o status de favorito.",
    deleteHookConsole: "Falha ao excluir hook:",
    deleteHookFailed: "Falha ao excluir hook.",
    deleteStudioAssetConsole: "Falha ao excluir ativo do Studio:",
    deleteStudioAssetFailed: "Falha ao excluir ativo do Studio.",
    regenerateConsole: "Falha ao regenerar hook:",
    regenerateFailed: "Falha ao regenerar hook."
  },

  confirm: {
    deleteHook: "Tem certeza de que deseja excluir este hook?",
    deleteStudioAsset: "Tem certeza de que deseja excluir este ativo salvo do Studio?"
  },

  actions: {
  copied: "Copiado",
  copyHook: "Copiar hook",
  copyAsset: "Copiar ativo",
  copyCode: "Copiar código",
  copyLink: "Copiar link",
  regenerate: "Regenerar",
  favorite: "Favorito",
  unfavorite: "Remover dos favoritos",
  delete: "Excluir",
  working: "Processando...",
  saving: "Salvando...",
  dismiss: "Fechar",
  seeProPlan: "Ver plano Pro",
  upgradePlan: "Fazer upgrade",
  goToStudio: "Ir para o Studio",
  openBilling: "Abrir faturamento",
  viewSignupFlow: "Ver fluxo de cadastro",
  openMilestoneWatch: "Abrir Milestone Watch"
},

  labels: {
    best: "Melhor",
    favorite: "Favorito",
    score: "Pontuação",
    toolType: "Tipo de ferramenta",
    plan: "Plano",
    pro: "PRO",
    proInsights: "PRO INSIGHTS",
    saved: "salvos",
    runs: "execuções",
    hooks: "hooks",
    success: "Sucesso",
    failed: "Falhas",
    avgTime: "Tempo médio"
  },

  plan: {
    free: "Free",
    freeLowercase: "free",
    creator: "✔ Creator",
    pro: "✔ Pro"
  },

  upgradePrompt: {
    message:
      "{{featureName}} está disponível no plano Pro. Faça upgrade para desbloquear este recurso avançado de análises.",
    clickToUnlock: "Clique para desbloquear com Pro",
    clickForPro: "Clique para acessar análises Pro"
  },

  filters: {
    allPlatforms: "Todos",
    allTools: "Todos",
    platform: "Plataforma",
    studioTool: "Ferramenta do Studio",
    allStudioTools: "Todas as ferramentas do Studio",
    sortBy: "Ordenar por",
    score: "Pontuação",
    topic: "Tópico",
    searchTopic: "Buscar tópico",
    searchPlaceholder: "Digite para buscar..."
  },

  metrics: {
    totalHooks: "Total de hooks",
    studioAssets: "Ativos do Studio",
    totalSavedItems: "Total de itens salvos",
    studioRuns: "Execuções do Studio",
    actualTrackedToolRuns: "Execuções reais de ferramentas rastreadas",
    averageScore: "Pontuação média",
    favoriteHooks: "Hooks favoritos",
    topHookScore: "Maior pontuação de hook",
    successRate: "Taxa de sucesso",
    studioToolRunSuccess: "Sucesso nas execuções das ferramentas do Studio"
  },

  rewards: {
    title: "Hookora Creator Rewards",
    subtitle:
      "Acompanhe seus Reward Credits e o crescimento das suas indicações em um só lugar.",
    rewardCredits: "Reward Credits",
    quickShare: "Compartilhamento rápido de indicação",
    referralCode: "Código de indicação",
    referralLink: "Link de indicação",
    snapshot: "Resumo das recompensas",
    walletBalance: "Saldo da carteira",
    totalReferrals: "Indicações totais",
    verifiedReferrals: "Indicações verificadas",
    paidReferrals: "Indicações pagas"
  },

  founder: {
    title: "Visão geral do fundador",
    subtitle: "Resumo do crescimento geral e da conversão.",
    totalUsers: "Total de usuários",
    activeUsers: "Usuários ativos",
    paidUsers: "Usuários pagantes",
    freeUsers: "Usuários Free",
    creatorUsers: "Usuários Creator",
    proUsers: "Usuários Pro",
    newUsers7d: "Novos usuários (7d)",
    newUsers30d: "Novos usuários (30d)",
    signupToPaid: "Cadastro → Pago",
    weeklyFocus: "Foco semanal do fundador",
    recentSignups: "Cadastros recentes",
    noSignupData: "Ainda não há dados de cadastro."
  },

  usage: {
    title: "Uso do plano",
    monthlyGenerationsUsed: "Gerações mensais usadas",
    remainingThisMonth: "Restante neste mês",
    savedHooks: "Hooks salvos"
  },

  studioSnapshot: {
    title: "Resumo das ferramentas do Studio",
    subtitle:
      "Inclui Hook Angles, CTA Generator, AI Humanizer e outras ferramentas do Studio"
  },

  charts: {
    platformDistribution: "Distribuição por plataforma",
    studioToolUsage: "Uso das ferramentas do Studio",
    studioRunTrend: "Tendência de execuções do Studio",
    studioUsageSummary: "Resumo de uso do Studio"
  },

  summary: {
    mostUsedTool: "Ferramenta mais usada",
    successfulRuns: "Execuções bem-sucedidas",
    failedRuns: "Execuções com falha",
    averageGenerationTime: "Tempo médio de geração"
  },

  pro: {
    averageScoreInsights: "Insights de pontuação média",
    topHookScoreAnalytics: "Análises da maior pontuação de hook",
    dailyAverageScoreTrend: "Tendência diária de pontuação média",
    dailyAverageScoreTrendDescription:
      "Acompanhe o movimento da pontuação ao longo do tempo com visibilidade histórica e tendências mais ricas.",
    bestTopicsByAverageScore: "Melhores tópicos por pontuação média",
    bestTopicsByAverageScoreDescription:
      "Descubra seus tópicos com melhor desempenho com insights mais profundos por tópico."
  },

  sections: {
    favoriteHooks: "Hooks favoritos",
    strongestHookPerTopic: "Hook mais forte por tópico",
    topHooks: "Melhores hooks",
    recentHooks: "Hooks recentes",
    recentStudioAssets: "Ativos recentes do Studio",
    combinedRecentActivity: "Atividade recente combinada"
  },

  empty: {
    noPlatformAnalytics: "Ainda não há análises por plataforma.",
    noStudioActivity: "Ainda não há atividade no Studio.",
    noStudioRunHistory:
      "Ainda não há histórico de execuções do Studio. Use as ferramentas do Studio para preencher este gráfico.",
    noTrendData:
      "Ainda não há dados de tendência. Gere hooks ao longo do tempo para preencher este gráfico.",
    noTopicAnalytics: "Ainda não há análises por tópico.",
    noContentTitle: "Ainda não há conteúdo gerado",
    noContentDescription:
      "Comece a usar as ferramentas do Studio para gerar hooks, títulos, legendas e roteiros. Suas análises pessoais aparecerão aqui à medida que você criar e salvar conteúdo.",
    noFavoriteHooks: "Ainda não há hooks favoritos.",
    noStudioAssetsSaved: "Ainda não há ativos do Studio salvos.",
    noRecentActivity: "Ainda não há atividade recente."
  },

  tools: {
    hooks: {
      label: "Gerador de Hooks Virais",
      badge: "HOOKS"
    },
    youtubeTitleOptimizer: {
      label: "Otimizador de Títulos do YouTube",
      badge: "TITLE"
    },
    captionGenerator: {
      label: "Gerador de Legendas",
      badge: "CAPTION"
    },
    hashtagGenerator: {
      label: "Gerador de Hashtags",
      badge: "HASHTAG"
    },
    descriptionRewriter: {
      label: "Reescritor de Descrições",
      badge: "DESCRIPTION"
    },
    shortScriptGenerator: {
      label: "Gerador de Roteiros Curtos",
      badge: "SCRIPT"
    },
    thumbnailTextAnalyzer: {
      label: "Analisador de Texto para Thumbnail",
      badge: "THUMBNAIL"
    },
    hookAngles: {
      label: "Ângulos de Hook",
      badge: "ANGLES"
    },
    ctaGenerator: {
      label: "Gerador de CTA",
      badge: "CTA"
    },
    humanizer: {
      label: "Humanizador de IA",
      badge: "HUMANIZER"
    },
    other: {
      label: "Outro",
      badge: "OTHER"
    }
  },

  welcome: {
  creator: "Criador",
  time: {
    morning: "Bom dia",
    afternoon: "Boa tarde",
    evening: "Boa noite",
    night: "Boa noite"
  },
  messages: {
    one: "Bem-vindo de volta à Hookora. Vamos criar algo forte hoje.",
    two: "Seu painel está pronto. Vamos criar conteúdos mais afiados.",
    three: "Bom te ver de novo. Seu espaço criativo está esperando.",
    four: "Vamos transformar as ideias de hoje em hooks e conteúdos mais fortes.",
    five: "A Hookora está pronta quando você estiver."
  }
},
},

libraryPage: {
  title: "Biblioteca",
  subtitle: "Navegue pelos hooks salvos e pelos itens do Studio salvos no seu fluxo de conteúdo.",

  currentPlanLabel: "Seu plano atual:",

  planLabels: {
    free: "Grátis",
    creator: "✔ Creator",
    pro: "✔ Pro"
  },

  planBox: {
    line1: "Os hooks e os itens do Studio agora ficam armazenados aqui.",
    line2: {
      before: "A exportação CSV e as ações em massa estão disponíveis nos planos",
      creator: "Creator",
      middle: "e",
      pro: "Pro"
    },
    line3: {
      before: "A inteligência avançada da biblioteca e os futuros workspaces estão planejados para",
      pro: "Pro"
    }
  },

  badges: {
    creatorPlus: "CREATOR+",
    proInsights: "INSIGHTS PRO",
    locked: "Bloqueado"
  },

  tools: {
    hooks: { label: "Gerador de Hooks Virais", badge: "HOOKS" },
    youtubeTitleOptimizer: { label: "Otimizador de Títulos do YouTube", badge: "TÍTULO" },
    captionGenerator: { label: "Gerador de Legendas", badge: "LEGENDA" },
    hashtagGenerator: { label: "Gerador de Hashtags", badge: "HASHTAG" },
    descriptionRewriter: { label: "Reescritor de Descrição", badge: "DESCRIÇÃO" },
    shortScriptGenerator: { label: "Gerador de Roteiro Curto", badge: "ROTEIRO" },
    thumbnailTextAnalyzer: { label: "Analisador de Texto de Thumbnail", badge: "THUMBNAIL" },
    hookAngles: { label: "Ângulos de Hook", badge: "ÂNGULOS" },
    ctaGenerator: { label: "Gerador de CTA", badge: "CTA" },
    humanizer: { label: "Humanizador com IA", badge: "HUMANIZADOR" },
    other: { label: "Outro", badge: "OUTRO" }
  },

  stats: {
    savedHooks: "Hooks salvos",
    savedStudioAssets: "Itens salvos do Studio",
    favoriteHooks: "Hooks favoritos",
    averageHookScore: "Pontuação média dos hooks"
  },

  toolBreakdown: {
    title: "Resumo das ferramentas do Studio",
    savedItemsCount: "{{count}} item salvo do Studio",
    savedItemsCount_plural: "{{count}} itens salvos do Studio"
  },

  filters: {
    search: "Pesquisar",
    searchPlaceholder: "Pesquisar por tópico, conteúdo ou ferramenta...",
    platform: "Plataforma",
    allPlatforms: "Todas",
    studioTool: "Ferramenta do Studio",
    allStudioTools: "Todas as ferramentas do Studio",
    sortBy: "Ordenar por",
    favoritesOnly: "Somente favoritos"
  },

  sort: {
    newest: "Mais recentes",
    oldest: "Mais antigos",
    highestScore: "Maior pontuação",
    lowestScore: "Menor pontuação",
    topic: "Tópico"
  },

  actions: {
    upgradeToCreator: "Fazer upgrade para Creator",
    unselectVisibleHooks: "Desmarcar hooks visíveis",
    selectVisibleHooks: "Selecionar hooks visíveis",
    clearSelection: "Limpar seleção",
    copiedAll: "Tudo copiado!",
    copyVisibleHooks: "Copiar hooks visíveis",
    exportHooksTxt: "Exportar hooks em TXT",
    exportHooksCsv: "Exportar hooks em CSV",
    favoriteSelected: "Favoritar selecionados",
    unfavoriteSelected: "Remover dos favoritos os selecionados",
    deleteSelected: "Excluir selecionados",
    copied: "Copiado!",
    copy: "Copiar",
    copyHook: "Copiar hook",
    regenerate: "Regenerar",
    favorite: "Favoritar",
    unfavorite: "Remover dos favoritos",
    delete: "Excluir",
    rename: "Renomear",
    deleteFolder: "Excluir pasta",
    save: "Salvar",
    saving: "Salvando...",
    cancel: "Cancelar",
    deleting: "Excluindo..."
  },

  states: {
    loading: "Carregando biblioteca de hooks...",
    working: "Processando...",
    noFoldersMatch: "Nenhuma pasta corresponde aos filtros atuais."
  },

  labels: {
    untitled: "Sem título",
    score: "Pontuação",
    selectedHooksCount: "{{count}} hooks selecionados",
    toolType: "Tipo de ferramenta",
    noDate: "Sem data",
    favorite: "Favorito"
  },

  export: {
    topic: "Tópico",
    platform: "Plataforma",
    score: "Pontuação",
    favorite: "Favorito",
    created: "Criado em",
    hook: "Hook",
    yes: "Sim",
    no: "Não"
  },

  features: {
    bulkSelection: "Seleção em massa",
    csvExport: "Exportação CSV",
    bulkDelete: "Exclusão em massa",
    bulkFavorite: "Favoritar em massa",
    bulkUnfavorite: "Remover dos favoritos em massa"
  },

  notices: {
    freeLock: {
      before: "As ações em massa e a exportação CSV estão bloqueadas no plano Grátis.",
      upgradeLink: "Faça upgrade para Creator"
    },
    proPreview: {
      before: "Prévia Pro: inteligência futura da biblioteca, presets salvos e workspaces estarão disponíveis no plano",
      pro: "Pro"
    }
  },

  folders: {
    title: "Pastas de tópicos",
    folderNamePlaceholder: "Digite o nome da pasta",
    itemsCount: "{{count}} item",
    itemsCount_plural: "{{count}} itens",
    hooksCount: "{{count}} hook",
    hooksCount_plural: "{{count}} hooks",
    assetsCount: "{{count}} item",
    assetsCount_plural: "{{count}} itens"
  },

  sections: {
    studioAssets: "Itens do Studio",
    hooks: "Hooks"
  },

  confirmations: {
    deleteHook: "Tem certeza de que deseja excluir este hook?",
    deleteAsset: "Tem certeza de que deseja excluir este item salvo?",
    deleteSelectedHooks: "Tem certeza de que deseja excluir {{count}} hook(s) selecionado(s)?",
    deleteFolder: "Tem certeza de que deseja excluir esta pasta e todo o seu conteúdo?\n\nPasta: {{folderName}}\nItens: {{itemCount}}"
  },

  messages: {
    notLoggedIn: "Você não está conectado. Faça login primeiro.",
    loadFailed: "Falha ao carregar a biblioteca de hooks.",
    upgradeRequired: "{{featureName}} está disponível nos planos Creator e Pro. Faça upgrade para desbloquear este recurso.",
    favoriteUpdateFailed: "Falha ao atualizar o status de favorito.",
    deleteHookFailed: "Falha ao excluir hook.",
    deleteAssetFailed: "Falha ao excluir item salvo.",
    deleteFolderFailed: "Falha ao excluir pasta.",
    regenerateFailed: "Falha ao regenerar hook.",
    folderNameRequired: "O nome da pasta não pode estar vazio.",
    renameFolderFailed: "Falha ao renomear pasta.",
    copyVisibleFailed: "Falha ao copiar hooks visíveis.",
    csvExportFailed: "Falha na exportação CSV.",
    bulkDeleteFailed: "Falha ao excluir hooks selecionados.",
    bulkUpdateFailed: "Falha ao atualizar hooks selecionados."
  }
},

hookTool: {
  heroBadge: "Ferramenta focada em resultado",
  title: "Hooks",
  description:
    "Use esta ferramenta quando quiser <strong>linhas de abertura prontas para usar</strong>. Comece com o seu tema, escolha um estilo de hook e a Hookora vai gerar hooks diretos para legendas, vídeos, carrosséis, títulos, intros e aberturas de posts.",

  familyFallback: "Hook",

  templates: {
    curiosity: {
      label: "Curiosidade",
      description: "Cria intriga e faz as pessoas quererem a próxima linha."
    },
    authority: {
      label: "Autoridade",
      description: "Passa confiança, conhecimento e liderança."
    },
    problem: {
      label: "Problema",
      description: "Aponta dores, travas ou erros rapidamente."
    },
    benefit: {
      label: "Benefício",
      description: "Começa com resultado, ganho ou transformação."
    },
    controversy: {
      label: "Controvérsia",
      description: "Usa tensão, contraste ou discordância forte."
    },
    story: {
      label: "História",
      description: "Soa mais humano, pessoal e emocional."
    }
  },

  fields: {
    topic: "Tema",
    hookStyle: "Estilo de Hook",
    platform: "Plataforma",
    audience: "Público",
    goal: "Objetivo",
    offer: "Oferta",
    painPoint: "Dor",
    desiredOutcome: "Resultado Desejado",
    angle: "Ângulo",
    ctaDirection: "Direção do CTA",
    keywords: "Palavras-chave",
    brandVoice: "Voz da Marca",
    avoidPhrases: "Evitar Frases",
    extraContext: "Contexto Extra"
  },

  hints: {
    topic:
      "A ideia específica de conteúdo para a qual você quer hooks. Temas claros produzem hooks mais fortes e mais úteis.",
    hookStyle:
      "Escolha o tipo de hook que a Hookora deve priorizar: curiosidade, autoridade, problema, benefício, controvérsia ou história.",
    platform:
      "Escolha onde o hook será usado. A plataforma afeta ritmo, impacto e forma de prender atenção.",
    audience:
      "Para quem o hook está tentando chamar atenção. Ex.: fundadores, criadores, coaches, donos de pequenos negócios, estudantes.",
    goal:
      "O que o hook deve ajudar a alcançar. Ex.: mais cliques, melhor retenção, comentários mais fortes, mais cadastros.",
    offer:
      "O produto, serviço, lição ou valor por trás do hook.",
    painPoint:
      "A frustração, desafio ou erro exato que o hook deve tocar.",
    desiredOutcome:
      "O resultado que seu público quer. Ex.: mais alcance, mais vendas, melhor posicionamento, conteúdo mais forte.",
    angle:
      "Direção opcional para guiar o hook. Ex.: contrária, verdade escondida, focada em erro, premium, emocional.",
    ctaDirection:
      "Ação opcional para a qual o conteúdo deve conduzir. Ex.: comentar, salvar, clicar, enviar DM, inscrever-se.",
    keywords:
      "Palavras ou frases importantes que devem influenciar o hook.",
    brandVoice:
      "Como o hook deve soar. Ex.: forte, premium, acolhedor, afiado, divertido, direto.",
    avoidPhrases:
      "Palavras, clichês ou expressões muito usadas que você não quer que a Hookora use.",
    extraContext:
      "Adicione provas, notas de campanha, objeções, exemplos, detalhes de posicionamento ou contexto extra para melhorar os hooks finais."
  },

  placeholders: {
    topic: "Sobre o que você quer que o hook seja?",
    audience: "Público",
    goal: "Objetivo",
    offer: "Oferta ou produto",
    painPoint: "Dor",
    desiredOutcome: "Resultado desejado",
    angle: "Ângulo",
    ctaDirection: "Direção do CTA",
    keywords: "Palavras-chave",
    brandVoice: "Voz da marca",
    avoidPhrases: "Evitar frases",
    extraContext:
      "Contexto extra, nota de posicionamento, prova, exemplos ou detalhes da campanha"
  },

  contextSectionTitle: "Contexto do Hook",

  actions: {
    generating: "Gerando...",
    generate: "Gerar Hooks",
    clearResults: "Limpar Resultados",
    copied: "Copiado",
    copyHook: "Copiar Hook"
  },

  messages: {
    topicRequired: "Por favor, insira um tema primeiro.",
    generateFailed: "Falha ao gerar hooks.",
    readyToUse:
      "Estes são <strong>hooks prontos para usar</strong>. Escolha o mais forte e use-o diretamente como sua linha de abertura, início de título, intro de vídeo, abertura de legenda ou primeiro slide do carrossel.",
    score: "Pontuação"
  }
},


studioLayout: {
  headerChip: "Espaço criativo",
  description:
    "Crie hooks, ângulos de hook, títulos, legendas, hashtags, descrições, scripts, CTAs, texto para thumbnail, conteúdo reaproveitado, copy para anúncios, copy para carrossel, reescritas virais, posicionamento de oferta, análise de pontuação de hooks, conversão de tendências, reescritas para público nigeriano, introduções de retenção, sistemas de voz de marca, ideias de diferenciação e conteúdo orientado por comentários em um único espaço de trabalho mais polido.",
},


studioSidebar: {
  title: "Ferramentas do Studio",
  subtitle:
    "Escolha uma ferramenta com base no tipo de conteúdo que você quer criar.",
  lockedSuffix: "bloqueado",

  toolDescriptions: {
    hooks: "Gere linhas de abertura diretas que você possa usar rapidamente.",
    hookAngles:
      "Explore várias direções antes de escolher seu hook mais forte.",
    titles:
      "Crie títulos mais fortes para vídeos, posts e empacotamento de conteúdo.",
    captions:
      "Escreva legendas mais claras que apoiem sua mensagem e sua plataforma.",
    hashtags:
      "Gere hashtags relevantes para apoiar descoberta e alcance.",
    descriptions:
      "Reescreva ou melhore descrições para mais clareza e melhor fluxo.",
    scripts:
      "Crie roteiros curtos com estrutura e entrega melhores.",
    ctas:
      "Crie chamadas para ação que pareçam mais claras e intencionais.",
    thumbnail:
      "Melhore o texto da thumbnail para uma primeira impressão mais forte.",
    humanizer:
      "Faça seu texto soar mais natural, humano e fácil de ler.",
    repurpose:
      "Transforme uma ideia em várias saídas de conteúdo utilizáveis.",
    adCopy:
      "Crie textos promocionais mais precisos para ofertas e campanhas.",
    carousels:
      "Transforme ideias em conteúdo de carrossel estruturado mais rápido.",
    viralRewrite:
      "Reescreva textos fracos para algo mais forte e mais capaz de parar o scroll.",
    positioning:
      "Deixe mais claro como sua oferta deve ser apresentada para parecer mais forte.",
    hookScore:
      "Analise o quão forte seu hook parece antes de publicar.",
    trendToContent:
      "Transforme tendências em ângulos e ideias de conteúdo utilizáveis.",
    nigerianRewrite:
      "Reescreva conteúdo para soar mais natural para um público nigeriano.",
    retentionIntro:
      "Crie intros mais fortes pensadas para prender atenção mais cedo.",
    brandVoiceTrainer:
      "Treine saídas para combinarem melhor com sua voz e tom.",
    contentDifferentiation:
      "Faça seu conteúdo parecer mais distinto e menos genérico.",
    commentToContent:
      "Transforme comentários da audiência em direções de conteúdo mais fortes."
  }
},
studioTools: {
  adCopyTool: {
    title: "Gerador de copy para anúncios",
    description:
      "Crie texto principal, títulos, descrições e CTAs prontos para anúncios da sua oferta ou campanha.",

    fields: {
      topic: {
        label: "Tópico",
        placeholder: "ex. Ferramenta de conteúdo para redes sociais de criadores",
        help:
          "Use o produto, oferta ou foco da campanha em torno do qual você quer criar os anúncios."
      },
      platform: {
        label: "Plataforma",
        placeholder: "ex. Facebook",
        help: "Escolha primeiro a plataforma onde o anúncio vai rodar."
      },
      tone: {
        label: "Tom"
      },
      language: {
        label: "Idioma"
      },
      audience: {
        label: "Público",
        placeholder: "ex. Pequenos empresários"
      },
      goal: {
        label: "Objetivo",
        placeholder: "ex. Gerar inscrições"
      },
      offer: {
        label: "Oferta",
        placeholder: "ex. Teste grátis para novos usuários"
      },
      painPoint: {
        label: "Dor",
        placeholder: "ex. Copy fraca e baixo engajamento"
      },
      desiredOutcome: {
        label: "Resultado desejado",
        placeholder: "ex. Criação de conteúdo mais rápida e melhores conversões"
      },
      angle: {
        label: "Ângulo",
        placeholder: "ex. Melhor copy sem perder tempo"
      },
      callToAction: {
        label: "Chamada para ação",
        placeholder: "ex. Comece seu teste grátis"
      },
      brandVoice: {
        label: "Voz da marca",
        placeholder: "ex. Clara, afiada, moderna"
      },
      keywords: {
        label: "Palavras-chave",
        placeholder: "ex. copywriting com IA, anúncios para redes sociais"
      },
      avoidPhrases: {
        label: "Frases a evitar",
        placeholder: "ex. divisor de águas, hack secreto"
      },
      extraContext: {
        label: "Contexto extra",
        placeholder:
          "Nota opcional da campanha, contexto de preço, objeções do público, mensagem da landing page ou direção extra..."
      }
    },

    toneOptions: {
      Persuasive: "Persuasivo",
      Bold: "Ousado",
      Professional: "Profissional",
      Confident: "Confiante",
      Warm: "Acolhedor",
      Conversational: "Conversacional",
      Direct: "Direto"
    },

    languageOptions: {
      english: "Inglês",
      spanish: "Espanhol",
      french: "Francês",
      portuguese: "Português"
    },

    actions: {
      generate: "Gerar copy de anúncio",
      generating: "Gerando...",
      reset: "Redefinir",
      copy: "Copiar",
      copied: "Copiado!"
    },

    results: {
      title: "Variações de anúncios",
      description:
        "Revise cada variação, copie a mais forte e salve os melhores anúncios na sua biblioteca.",
      empty:
        "Nenhum copy de anúncio ainda. Gere um lote para ver suas variações aqui.",
      variantFallback: "Variação de anúncio",
      score: "Pontuação"
    },

    output: {
      primaryText: "Texto principal",
      headline: "Título",
      description: "Descrição",
      cta: "CTA"
    },

    messages: {
      topicRequired: "Digite um tópico primeiro.",
      generateFailed: "Falha ao gerar o copy do anúncio.",
      copyFailed: "Falha ao copiar o conteúdo."
    }
  },

  aiHumanizerTool: {
  hero: {
    badge: "Ferramenta de refinamento de reescrita",
    title: "Humanizador de IA",
    description:
      "Use esta ferramenta para transformar um texto rígido, robótico ou com excesso de cara de IA em uma escrita mais natural, convincente e humana. Ajuste o tom, a intensidade e as configurações de reescrita para obter uma versão certa para a sua plataforma."
  },

  fields: {
    originalText: {
      label: "Texto original",
      hint:
        "Cole o texto escrito por IA ou muito engessado que você quer deixar mais humano, natural e convincente.",
      placeholder: "Cole o texto original que você quer humanizar"
    },
    platform: {
      label: "Plataforma",
      hint:
        "Escolha onde a reescrita será usada. Isso afeta ritmo, fraseado e estilo."
    },
    tone: {
      label: "Tom",
      hint: "Escolha a personalidade da reescrita."
    },
    audience: {
      label: "Público",
      hint:
        "Para quem é o texto reescrito. Isso ajuda a deixar o resultado mais relevante e humano.",
      placeholder: "Público"
    },
    humanizationStrength: {
      label: "Intensidade da humanização",
      hint:
        "Leve mantém a estrutura mais próxima. Equilibrado melhora o fluxo. Forte reescreve com mais liberdade sem perder a mensagem principal."
    },
    preserveOriginalMeaning: {
      label: "Preservar o significado original"
    },
    styleNotes: {
      label: "Notas de estilo",
      hint:
        "Adicione orientações como frases mais curtas, tom mais direto, menos formalidade, mais emoção ou menos jargão.",
      placeholder: "Notas de estilo ou orientações de reescrita"
    }
  },

  sections: {
    rewriteSettings: "Configurações de reescrita"
  },

  platformOptions: {
    General: "Geral",
    Instagram: "Instagram",
    TikTok: "TikTok",
    X: "X",
    LinkedIn: "LinkedIn",
    YouTube: "YouTube",
    WhatsApp: "WhatsApp"
  },

  toneOptions: {
    Natural: "Natural",
    Conversational: "Conversacional",
    Bold: "Marcante",
    Professional: "Profissional",
    Warm: "Acolhedor",
    Confident: "Confiante",
    Persuasive: "Persuasivo",
    "Story-driven": "Guiado por narrativa"
  },

  strengthOptions: {
    Light: "Leve",
    Balanced: "Equilibrado",
    Strong: "Forte"
  },

  actions: {
    humanizing: "Humanizando...",
    humanizeText: "Humanizar texto",
    clearResults: "Limpar resultados",
    resetForm: "Redefinir formulário",
    copied: "Copiado",
    copy: "Copiar"
  },

  labels: {
    alternative: "Alternativa {{number}}",
    whyItFeelsMoreHuman: "Por que parece mais humano",
    score: "Pontuação"
  },

  resultsIntro:
    "Estas são opções de reescrita humanizada. Escolha a que parecer mais natural, mais convincente e mais próxima da voz que você quer transmitir.",

  errors: {
    originalTextRequired: "Digite primeiro o texto original.",
    generateFailed: "Falha ao humanizar o texto."
  }
},

brandVoiceTrainerTool: {
  badge: "Treinador de voz da marca",
  title: "Treine uma voz de marca mais consistente",
  description:
    "Transforme a descrição da sua marca, suas preferências de tom e seu texto de exemplo em um perfil de voz utilizável, com regras de mensagem mais claras e maior consistência.",

  fields: {
    topic: {
      label: "Tópico",
      placeholder: "ex. ferramentas para criadores, skincare, educação de negócios",
      hint: "O tópico compartilhado ajuda a manter seu fluxo do Studio alinhado entre as ferramentas."
    },
    platform: {
      label: "Plataforma",
      hint: "A plataforma pode influenciar o estilo de voz e as recomendações de entrega."
    },
    outputType: {
      label: "Tipo de saída",
      hint: "Escolha se você quer um perfil de voz, regras ou um exemplo de saída."
    },
    targetAudience: {
      label: "Público-alvo",
      placeholder: "ex. fundadores, mulheres de 20-35 anos, criadores nigerianos",
      hint: "O contexto do público ajuda a moldar como a voz deve soar."
    },
    preferredToneWords: {
      label: "Palavras de tom preferidas",
      placeholder: "ex. ousado, humano, direto, acolhedor",
      hint: "Adicione palavras que descrevem como sua marca deve soar."
    },
    avoidWords: {
      label: "Palavras / estilos a evitar",
      placeholder: "ex. robótico, formal demais, gírias demais, agressivo",
      hint: "Opcional. Ajuda a Hookora a evitar o tom errado."
    },
    goal: {
      label: "Objetivo",
      placeholder: "ex. soar mais premium, mais humano, mais consistente",
      hint: "Opcional. Dá uma direção mais clara para a ferramenta."
    },
    brandDescription: {
      label: "Descrição da marca",
      placeholder:
        "Descreva o que sua marca faz, o que ela representa e como deve ser percebida...",
      hint: "Este é o campo mais importante. Seja o mais claro possível."
    },
    sampleWriting: {
      label: "Texto de exemplo",
      placeholder:
        "Cole um exemplo de como sua marca escreve atualmente, se tiver um...",
      hint:
        "Opcional, mas muito útil quando você quer que o resultado reflita seu estilo atual."
    }
  },

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    xTwitter: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "Geral"
  },

  outputTypeOptions: {
    voiceProfile: "Perfil de voz",
    rewriteSample: "Exemplo de reescrita",
    brandGuidelines: "Diretrizes da marca",
    dosAndDonts: "Faça e não faça"
  },

  actions: {
    generating: "Gerando...",
    generate: "Treinar voz da marca",
    clearResults: "Limpar resultados",
    reset: "Redefinir",
    copied: "Copiado",
    copy: "Copiar"
  },

  sections: {
    brandVoiceProfile: "Perfil de voz da marca",
    summary: "Resumo",
    voicePillars: "Pilares da voz",
    dos: "Faça",
    donts: "Evite",
    sampleRewrite: "Exemplo de reescrita",
    notes: "Notas",
    rawResult: "Resultado bruto"
  },

  resultTitle: "Resultado da voz da marca",

  tip:
    "Dica: a voz da marca fica melhor quando sua descrição inclui público, valores e como você quer que as pessoas se sintam depois de ler seu conteúdo.",

  messages: {
    copyFailed:
      "Não foi possível copiar o resultado da voz da marca agora. Tente novamente.",
    brandDescriptionRequired:
      "Descreva a marca antes de gerar um perfil de voz.",
    generateFailed:
      "Algo deu errado ao gerar o resultado da voz da marca."
  }
},

captionTool: {
  hero: {
    badge: "Ferramenta de escrita social",
    title: "Legendas",
    description:
      "Use esta ferramenta para gerar legendas prontas para publicar em conteúdo social. Adicione plataforma, tom, direção de CTA e contexto extra para que a Hookora possa criar opções de legenda mais fortes e mais relevantes para o seu post ou vídeo."
  },

  fields: {
    topic: {
      label: "Tópico",
      hint:
        "O assunto principal da legenda. Seja claro sobre o que é o post ou o vídeo.",
      placeholder: "Sobre o que é a legenda?"
    },
    platform: {
      label: "Plataforma",
      hint:
        "Escolha onde a legenda será publicada. A plataforma afeta o tom, o ritmo e o estilo do CTA."
    },
    tone: {
      label: "Tom",
      hint:
        "Como a legenda deve soar. Exemplo: envolvente, ousado, divertido, educativo, premium, direto.",
      placeholder: "Tom"
    },
    audience: {
      label: "Público",
      hint:
        "Para quem é a legenda. Exemplo: marcas de skincare, criadores, fundadores de startups, estudantes nigerianos.",
      placeholder: "Público"
    },
    goal: {
      label: "Objetivo",
      hint:
        "O que você quer que a legenda alcance. Exemplo: mais salvamentos, comentários, cliques, inscrições ou compartilhamentos.",
      placeholder: "Objetivo"
    },
    offer: {
      label: "Oferta",
      hint:
        "O produto, serviço, proposta de valor, brinde ou mensagem por trás do conteúdo.",
      placeholder: "Oferta ou produto"
    },
    painPoint: {
      label: "Dor",
      hint: "O problema ou frustração que seu público está enfrentando.",
      placeholder: "Dor"
    },
    desiredOutcome: {
      label: "Resultado desejado",
      hint:
        "O resultado que seu público quer. Exemplo: mais engajamento, mais leads, conteúdo mais forte, melhores vendas.",
      placeholder: "Resultado desejado"
    },
    angle: {
      label: "Ângulo",
      hint:
        "A perspectiva ou enquadramento. Exemplo: para iniciantes, urgente, em formato de história, quebra de mito, premium.",
      placeholder: "Ângulo"
    },
    callToAction: {
      label: "CTA",
      hint:
        "A ação que você quer que as pessoas façam. Exemplo: comentar, compartilhar, salvar, seguir, clicar no link.",
      placeholder: "CTA"
    },
    keywords: {
      label: "Palavras-chave",
      hint:
        "Palavras importantes de busca ou tema para incluir naturalmente. Separe com vírgulas.",
      placeholder: "Palavras-chave"
    },
    brandVoice: {
      label: "Voz da marca",
      hint:
        "Como a legenda deve parecer. Exemplo: direta, espirituosa, especialista, divertida, premium.",
      placeholder: "Voz da marca"
    },
    avoidPhrases: {
      label: "Frases a evitar",
      hint:
        "Palavras repetidas, clichês ou frases que você não quer na legenda.",
      placeholder: "Frases a evitar"
    },
    extraContext: {
      label: "Contexto extra",
      hint:
        "Adicione exemplos, detalhes do produto, notas de campanha, provas ou contexto extra para melhorar o resultado.",
      placeholder:
        "Contexto extra, nota de campanha, prova ou ângulo do criador"
    }
  },

  contextSectionTitle: "Contexto da legenda",

  actions: {
    generating: "Gerando...",
    generate: "Gerar legendas",
    clearResults: "Limpar resultados",
    resetForm: "Redefinir formulário",
    copied: "Copiado",
    copy: "Copiar"
  },

  resultsIntro:
    "Estas são opções de legendas prontas para publicar. Escolha a que melhor combina com sua plataforma, tom e objetivo de conversão.",

  labels: {
    score: "Pontuação"
  },

  errors: {
    topicRequired: "Por favor, insira um tópico primeiro.",
    generateFailed: "Falha ao gerar legendas."
  },

  fallbackUntitledTopic: "Tópico sem título"
},

carouselGeneratorTool: {
  title: "Gerador de Carrossel",
  description:
    "Crie conceitos de carrossel estruturados com um slide de abertura forte, progressão clara e um CTA de encerramento.",

  fields: {
    topic: {
      label: "Tópico",
      placeholder: "ex. Por que posts em redes sociais têm baixo desempenho",
      hint:
        "Use a lição principal, o framework ou o problema que o carrossel deve abordar."
    },
    platform: {
      label: "Plataforma",
      hint:
        "Escolha a plataforma onde o carrossel será usado."
    },
    tone: {
      label: "Tom"
    },
    style: {
      label: "Estilo"
    },
    language: {
      label: "Idioma"
    },
    audience: {
      label: "Público",
      placeholder: "ex. Coaches e criadores"
    },
    goal: {
      label: "Objetivo",
      placeholder: "ex. Mais salvamentos e compartilhamentos"
    },
    offer: {
      label: "Oferta",
      placeholder: "ex. Auditoria ou guia gratuito"
    },
    painPoint: {
      label: "Dor",
      placeholder: "ex. Hooks fracos e baixo engajamento"
    },
    desiredOutcome: {
      label: "Resultado desejado",
      placeholder: "ex. Mensagem mais clara e posts mais fortes"
    },
    angle: {
      label: "Ângulo",
      placeholder: "ex. Erros que a maioria das pessoas comete"
    },
    callToAction: {
      label: "Chamada para ação",
      placeholder: "ex. Salve isto e siga"
    },
    brandVoice: {
      label: "Voz da marca",
      placeholder: "ex. Inteligente, clara, prática"
    },
    keywords: {
      label: "Palavras-chave",
      placeholder: "ex. estratégia de conteúdo, crescimento nas redes sociais"
    },
    avoidPhrases: {
      label: "Frases a evitar",
      placeholder: "ex. divisor de águas, segredo hack"
    },
    extraContext: {
      label: "Contexto extra",
      placeholder:
        "Nota opcional de campanha, ideia de estrutura, direção dos slides ou contexto de público..."
    }
  },

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "Geral"
  },

  toneOptions: {
    educational: "Educacional",
    bold: "Ousado",
    conversational: "Conversacional",
    professional: "Profissional",
    persuasive: "Persuasivo",
    warm: "Acolhedor",
    confident: "Confiante"
  },

  styleOptions: {
    educationalBreakdown: "Análise educacional",
    mistakeSeries: "Série de erros",
    frameworkSeries: "Série de frameworks"
  },

  languageOptions: {
    english: "Inglês",
    spanish: "Espanhol",
    french: "Francês",
    portuguese: "Português"
  },

  actions: {
    generating: "Gerando...",
    generate: "Gerar slides de carrossel",
    reset: "Redefinir",
    copied: "Copiado!",
    copy: "Copiar"
  },

  results: {
    title: "Conceitos de Carrossel",
    description:
      "Cada conceito inclui um slide de gancho, fluxo principal, slide de encerramento e legenda.",
    empty: "Ainda não há conceitos de carrossel. Gere um lote para vê-los aqui."
  },

  resultLabels: {
    carousel: "Carrossel",
    hookSlide: "Slide de gancho",
    slide: "Slide",
    closingSlide: "Slide de encerramento",
    caption: "Legenda",
    score: "Pontuação"
  },

  messages: {
    topicRequired: "Por favor, insira um tópico primeiro.",
    generateFailed: "Falha ao gerar os slides do carrossel.",
    copyFailed: "Falha ao copiar o carrossel."
  }
},

commentToContentTool: {
  badge: "Comentário para Conteúdo",
  title: "Transforme comentários da audiência em conteúdo útil",
  description:
    "Transforme um comentário, pergunta ou reação da audiência em resposta, hook, legenda, ângulo de roteiro ou nova ideia de conteúdo que você realmente possa usar.",

  fields: {
    topic: {
      label: "Tópico",
      placeholder: "ex. hooks, fitness, crescimento de negócios",
      hint:
        "O tópico compartilhado ajuda a conectar este resultado ao restante do seu fluxo no Studio."
    },
    platform: {
      label: "Plataforma",
      hint:
        "O contexto da plataforma ajuda a Hookora a moldar melhor a saída."
    },
    outputType: {
      label: "Tipo de saída",
      hint:
        "Escolha se você quer uma resposta, ideia de conteúdo, legenda ou saída em formato de roteiro."
    },
    tone: {
      label: "Tom",
      hint:
        "O tom define como o resultado final vai soar."
    },
    targetAudience: {
      label: "Público-alvo",
      placeholder: "ex. criadores iniciantes, fundadores, clientes de beleza",
      hint:
        "Contexto de público é opcional, mas pode melhorar a relevância."
    },
    goal: {
      label: "Objetivo",
      placeholder: "ex. aumentar engajamento, responder com clareza, virar um post",
      hint:
        "Opcional. Use isto quando quiser direcionar a saída para um resultado específico."
    },
    comment: {
      label: "Comentário",
      placeholder:
        "Cole aqui o comentário da audiência, DM, resposta ou pergunta...",
      hint:
        "Pode ser uma pergunta, objeção, elogio, crítica ou reação interessante."
    },
    extraContext: {
      label: "Contexto extra",
      placeholder:
        "Adicione qualquer contexto sobre o post original, sua marca, seu produto ou no que você quer transformar o comentário...",
      hint:
        "Opcional, mas útil quando você quer um conteúdo mais personalizado."
    }
  },

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "Geral"
  },

  outputTypeOptions: {
    reply: "Resposta",
    hook: "Hook",
    caption: "Legenda",
    shortScript: "Roteiro curto",
    contentIdea: "Ideia de conteúdo",
    carouselAngle: "Ângulo de carrossel"
  },

  toneOptions: {
    natural: "Natural",
    friendly: "Amigável",
    bold: "Ousado",
    educational: "Educacional",
    sales: "Vendas",
    professional: "Profissional"
  },

  actions: {
    generating: "Gerando...",
    generate: "Gerar a partir do comentário",
    clearResults: "Limpar resultados",
    reset: "Redefinir",
    copy: "Copiar",
    copied: "Copiado"
  },

  results: {
    title: "Resultado gerado"
  },

  resultLabels: {
    output: "Saída",
    angle: "Ângulo",
    summary: "Resumo",
    ideas: "Ideias",
    notes: "Notas",
    rawResult: "Resultado bruto"
  },

  tip:
    "Dica: comentários fortes da audiência geralmente revelam ângulos escondidos, objeções e temas que valem virar posts.",

  messages: {
    commentRequired: "Cole o comentário antes de gerar o conteúdo.",
    generateFailed:
      "Algo deu errado ao transformar o comentário em conteúdo.",
    copyFailed:
      "Não foi possível copiar o resultado gerado agora. Tente novamente."
  }
},

contentDifferentiationTool: {
  badge: "Diferenciação de Conteúdo",
  title: "Encontre um ângulo mais forte em espaços de conteúdo concorridos",
  description:
    "Analise seu nicho, público e estilo dos concorrentes para descobrir um posicionamento mais forte, uma singularidade mais clara e ângulos de conteúdo que se destacam.",

  fields: {
    topic: {
      label: "Tópico",
      placeholder: "ex. finanças pessoais, fitness, ferramentas de IA",
      hint:
        "O tópico compartilhado ajuda esta ferramenta a se manter alinhada com o restante do seu fluxo no Studio."
    },
    platform: {
      label: "Plataforma",
      hint:
        "O contexto da plataforma ajuda a Hookora a adaptar as ideias de diferenciação para o formato certo."
    },
    contentType: {
      label: "Tipo de conteúdo",
      hint:
        "Escolha o tipo de conteúdo para o qual você quer um posicionamento mais forte."
    },
    targetAudience: {
      label: "Público-alvo",
      placeholder: "ex. criadores iniciantes, fundadores de startup, estudantes",
      hint:
        "O contexto do público ajuda a revelar ângulos melhores e lacunas no mercado."
    },
    goal: {
      label: "Objetivo",
      placeholder: "ex. se destacar, soar mais inteligente, conseguir mais salvamentos",
      hint:
        "Opcional. Use isto quando quiser focar a saída em um resultado claro."
    },
    brandContext: {
      label: "Contexto da marca",
      placeholder:
        "Descreva sua marca, oferta, perspectiva, pontos fortes e o que torna você diferente...",
      hint:
        "Isso dá à Hookora o contexto necessário para encontrar um posicionamento mais forte."
    },
    competitorStyle: {
      label: "Estilo dos concorrentes / padrão do mercado",
      placeholder:
        "Descreva como os outros no seu nicho normalmente falam, vendem ou criam conteúdo...",
      hint:
        "Opcional, mas muito útil quando você quer que a Hookora encontre um ponto de diferença mais claro."
    },
    currentContent: {
      label: "Conteúdo ou mensagem atual",
      placeholder:
        "Cole seu hook, legenda, roteiro ou mensagem atual se quiser que a Hookora melhore...",
      hint:
        "Opcional. Use isto se quiser sugestões de diferenciação mais práticas ligadas ao conteúdo existente."
    }
  },

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "Geral"
  },

  contentTypeOptions: {
    hooks: "Hooks",
    captions: "Legendas",
    scripts: "Roteiros",
    carousels: "Carrosséis",
    ads: "Anúncios",
    generalContent: "Conteúdo geral"
  },

  actions: {
    generating: "Gerando...",
    generate: "Diferenciar conteúdo",
    clearResults: "Limpar resultados",
    reset: "Redefinir",
    copy: "Copiar",
    copied: "Copiado"
  },

  results: {
    title: "Resultado da diferenciação"
  },

  resultLabels: {
    differentiationAngle: "Ângulo de diferenciação",
    summary: "Resumo",
    marketGaps: "Lacunas de mercado",
    uniqueStrengths: "Forças únicas",
    differentiationIdeas: "Ideias de diferenciação",
    avoid: "Evitar",
    sampleOutput: "Exemplo de saída",
    rawResult: "Resultado bruto"
  },

  tip:
    "Dica: quanto mais claramente você explicar seu mercado e como os outros soam, melhor a Hookora poderá ajudar você a se destacar.",

  messages: {
    topicOrContextRequired:
      "Digite um tópico ou contexto de marca antes de gerar ideias de diferenciação.",
    generateFailed:
      "Algo deu errado ao gerar a saída de diferenciação.",
    copyFailed:
      "Não foi possível copiar o resultado de diferenciação agora. Tente novamente."
  }
},

contentRepurposerTool: {
  title: "Reaproveitador de Conteúdo",
  description:
    "Transforme uma única peça de conteúdo em várias versões prontas para diferentes plataformas, para publicar, testar e salvar.",

  fields: {
    topic: {
      label: "Tópico",
      placeholder: "ex. marca pessoal para criadores",
      hint:
        "Compartilhado entre as ferramentas do Studio para manter a mesma ideia de trabalho."
    },
    platform: {
      label: "Plataforma",
      placeholder: "ex. LinkedIn",
      hint:
        "Use a principal plataforma de destino para este lote de reaproveitamento."
    },
    sourceText: {
      label: "Texto de origem",
      placeholder:
        "Cole aqui o post, legenda, thread, roteiro, e-mail ou rascunho original...",
      hint:
        "Cole a peça original que você quer transformar em diferentes versões prontas para cada plataforma."
    },
    audience: {
      label: "Público",
      placeholder: "ex. donos de pequenos negócios"
    },
    goal: {
      label: "Objetivo",
      placeholder: "ex. mais engajamento e mais salvamentos"
    },
    tone: {
      label: "Tom"
    },
    language: {
      label: "Idioma"
    },
    brandVoice: {
      label: "Voz da marca",
      placeholder: "ex. clara, forte, confiável"
    },
    callToAction: {
      label: "Chamada para ação",
      placeholder: "ex. Comente ou salve este post"
    },
    keywords: {
      label: "Palavras-chave",
      placeholder: "ex. crescimento nas redes sociais, estratégia de conteúdo"
    },
    avoidPhrases: {
      label: "Frases para evitar",
      placeholder: "ex. divisor de águas, hack secreto"
    },
    extraContext: {
      label: "Contexto extra",
      placeholder:
        "Instrução extra opcional, contexto de campanha, detalhes da oferta, dores do público ou nota sobre a plataforma..."
    }
  },

  toneOptions: {
    natural: "Natural",
    conversational: "Conversacional",
    bold: "Ousado",
    professional: "Profissional",
    warm: "Acolhedor",
    confident: "Confiante",
    persuasive: "Persuasivo"
  },

  languageOptions: {
    en: "Inglês",
    es: "Espanhol",
    fr: "Francês",
    pt: "Português"
  },

  actions: {
    generating: "Reaproveitando...",
    generate: "Gerar saídas reaproveitadas",
    reset: "Redefinir",
    copy: "Copiar",
    copied: "Copiado!"
  },

  results: {
    title: "Saídas reaproveitadas",
    description:
      "Suas variações geradas aparecem aqui. Copie as melhores e salve as versões mais fortes na sua biblioteca.",
    empty:
      "Ainda não há saídas. Gere um lote para ver aqui seu conteúdo reaproveitado.",
    outputFallback: "Saída {{number}}",
    score: "Pontuação: {{score}}",
    whyItWorks: "Por que funciona:"
  },

  messages: {
    sourceRequired: "Cole ou digite primeiro o seu texto de origem.",
    generateFailed: "Falha ao gerar saídas reaproveitadas.",
    copyFailed: "Falha ao copiar a saída."
  }
},

ctaTool: {
  hero: {
    badge: "Ferramenta de escrita para conversão",
    title: "Gerador de CTA",
    descriptionStart: "Use esta ferramenta para gerar",
    descriptionStrong: "chamadas para ação mais fortes",
    descriptionEnd:
      "que pareçam mais claras, mais persuasivas e mais naturais para o seu conteúdo, a sua oferta e a sua plataforma."
  },
  fields: {
    topic: {
      label: "Tópico",
      hint:
        "O assunto principal do conteúdo ou da oferta. Seja claro sobre ao que o CTA deve se conectar.",
      placeholder: "Sobre o que é o CTA?"
    },
    platform: {
      label: "Plataforma",
      hint:
        "Escolha onde o CTA será usado. A plataforma afeta o estilo, a urgência e a forma de escrever."
    },
    audience: {
      label: "Público",
      hint:
        "Para quem o CTA é. Exemplo: criadores, fundadores, estudantes, donos de negócios.",
      placeholder: "Público"
    },
    goal: {
      label: "Objetivo",
      hint:
        "O que você quer que as pessoas façam ou qual resultado o CTA deve apoiar.",
      placeholder: "Objetivo"
    },
    offer: {
      label: "Oferta",
      hint:
        "O produto, serviço, valor, isca digital ou promessa por trás do CTA.",
      placeholder: "Oferta ou produto"
    },
    painPoint: {
      label: "Dor",
      hint:
        "A frustração, dificuldade ou problema que o seu público está enfrentando.",
      placeholder: "Dor"
    },
    desiredOutcome: {
      label: "Resultado desejado",
      hint:
        "O resultado que o seu público quer alcançar. Exemplo: mais crescimento, mais vendas, menos estresse.",
      placeholder: "Resultado desejado"
    },
    brandVoice: {
      label: "Voz da marca",
      hint:
        "Como o CTA deve soar. Exemplo: ousado, acolhedor, direto, premium, leve.",
      placeholder: "Voz da marca"
    },
    ctaDirection: {
      label: "Direção do CTA",
      hint:
        "O tipo de ação que você quer incentivar. Exemplo: inscrever-se, comentar, comprar, salvar, clicar.",
      placeholder: "Direção do CTA"
    },
    avoidPhrases: {
      label: "Frases para evitar",
      hint:
        "Palavras ou frases clichês que você não quer incluir no CTA.",
      placeholder: "Frases para evitar"
    },
    extraContext: {
      label: "Contexto extra",
      hint:
        "Adicione notas de campanha, urgência, timing, comportamento do público ou outros detalhes úteis.",
      placeholder: "Contexto extra"
    }
  },
  context: {
    title: "Contexto do CTA"
  },
  actions: {
    generating: "Gerando...",
    generate: "Gerar CTAs",
    clearResults: "Limpar resultados",
    resetForm: "Redefinir formulário",
    copied: "Copiado",
    copy: "Copiar"
  },
  resultsIntro: {
    start: "Estas são opções de CTA criadas para parecerem mais",
    strong: "acionáveis e convincentes",
    end: "para o seu público e a sua plataforma."
  },
  output: {
    score: "Pontuação",
    whyItWorks: "Por que funciona",
    untitledTopic: "Tópico sem título"
  },
  errors: {
    topicRequired: "Por favor, insira um tópico primeiro.",
    generateFailed: "Falha ao gerar CTAs."
  }
},

descriptionTool: {
  hero: {
    badge: "Ferramenta de reescrita",
    title: "Descrições",
    description:
      "Use esta ferramenta para gerar reescritas de descrição mais fortes para o seu conteúdo. Você pode colar uma descrição existente para melhorá-la ou gerar uma nova direção a partir do seu tópico e contexto."
  },
  fields: {
    topic: {
      label: "Tópico",
      hint:
        "O assunto principal da descrição. Seja claro sobre o que o conteúdo ou ativo representa.",
      placeholder: "Sobre o que é a descrição?"
    },
    platform: {
      label: "Plataforma",
      hint:
        "Escolha a plataforma onde esta descrição será usada."
    },
    originalDescription: {
      label: "Descrição original",
      hint:
        "Cole aqui a descrição atual se quiser melhorá-la em vez de gerar uma nova versão apenas com base no contexto.",
      placeholder: "Cole a descrição atual se quiser reescrevê-la."
    },
    audience: {
      label: "Público",
      hint:
        "Para quem esta descrição é destinada. Exemplo: estudantes, criadores, donos de marca, fundadores de startup.",
      placeholder: "Público"
    },
    goal: {
      label: "Objetivo",
      hint:
        "No que a descrição deve ajudar. Exemplo: conversões, clareza, tempo de visualização, cliques, inscrições.",
      placeholder: "Objetivo"
    },
    offer: {
      label: "Oferta",
      hint:
        "O produto, brinde, serviço, recurso ou promessa por trás do conteúdo.",
      placeholder: "Oferta ou produto"
    },
    painPoint: {
      label: "Dor",
      hint:
        "O desafio ou a frustração que o seu público está enfrentando.",
      placeholder: "Dor"
    },
    desiredOutcome: {
      label: "Resultado desejado",
      hint:
        "O resultado que o seu público quer alcançar. Exemplo: mais crescimento, conteúdo melhor, resultados mais rápidos, menos estresse.",
      placeholder: "Resultado desejado"
    },
    angle: {
      label: "Ângulo",
      hint:
        "O posicionamento ou enquadramento. Exemplo: educativo, premium, urgente, para iniciantes, resposta direta.",
      placeholder: "Ângulo"
    },
    cta: {
      label: "CTA",
      hint:
        "A ação que a descrição deve incentivar. Exemplo: inscrever-se, assistir, comentar, salvar, visitar o link.",
      placeholder: "CTA"
    },
    keywords: {
      label: "Palavras-chave",
      hint:
        "Palavras importantes de busca ou SEO para incluir naturalmente. Separe com vírgulas.",
      placeholder: "Palavras-chave"
    },
    brandVoice: {
      label: "Voz da marca",
      hint:
        "Como a descrição deve soar. Exemplo: ousada, especialista, divertida, premium, direta.",
      placeholder: "Voz da marca"
    },
    avoidPhrases: {
      label: "Frases para evitar",
      hint:
        "Palavras ou padrões que você quer que o gerador evite repetir.",
      placeholder: "Frases para evitar"
    },
    extraContext: {
      label: "Contexto extra",
      hint:
        "Adicione provas, detalhes de apoio, emoção desejada, notas de campanha ou qualquer outro contexto para melhorar a reescrita.",
      placeholder: "Contexto extra, prova única, notas do produto ou detalhe de apoio"
    }
  },
  contextTitle: "Contexto da reescrita",
  actions: {
    generating: "Gerando...",
    generate: "Gerar reescritas",
    clearResults: "Limpar resultados",
    resetForm: "Redefinir formulário",
    copied: "Copiado",
    copy: "Copiar"
  },
  messages: {
    topicRequired: "Por favor, insira um tópico primeiro.",
    generateFailed: "Falha ao reescrever a descrição.",
    ready:
      "Estas são reescritas de descrição prontas para uso. Escolha a que melhor combina com a sua plataforma, objetivo de clareza e direção de conversão."
  },
  result: {
    score: "Pontuação"
  }
},

hashtagTool: {
  hero: {
    badge: "Ferramenta de descoberta",
    title: "Hashtags",
    description:
      "Use esta ferramenta para criar conjuntos estratégicos de hashtags para diferentes objetivos de conteúdo. Adicione plataforma, público, tipo de conteúdo e contexto extra para que o Hookora possa retornar combinações melhores de hashtags para descoberta, relevância e engajamento."
  },
  fields: {
    topic: {
      label: "Tópico",
      hint:
        "O assunto, nicho ou campanha que os conjuntos de hashtags devem apoiar.",
      placeholder: "Para que serve este conjunto de hashtags?"
    },
    platform: {
      label: "Plataforma",
      hint:
        "Escolha a plataforma, porque o comportamento das hashtags muda de uma plataforma para outra."
    },
    contentType: {
      label: "Tipo de conteúdo",
      hint:
        "O tipo de post ou conteúdo. Exemplo: tutorial, demonstração de produto, post educativo, história pessoal, carrossel.",
      placeholder: "Tipo de conteúdo"
    },
    audience: {
      label: "Público",
      hint:
        "Quem você quer atrair com estas hashtags. Exemplo: amantes de beleza, fundadores de startups, criadores estudantes.",
      placeholder: "Público"
    },
    goal: {
      label: "Objetivo",
      hint:
        "No que você quer que o conjunto de hashtags ajude. Exemplo: descoberta, relevância, engajamento, alcance de nicho.",
      placeholder: "Objetivo"
    },
    keywords: {
      label: "Palavras-chave",
      hint:
        "Palavras importantes do tópico para refletir nas hashtags. Separe com vírgulas.",
      placeholder: "Palavras-chave"
    },
    brandVoice: {
      label: "Voz da marca",
      hint:
        "A vibe da marca ou do conteúdo. Isso ajuda a manter os conjuntos de hashtags alinhados com o estilo do conteúdo.",
      placeholder: "Voz da marca"
    },
    extraContext: {
      label: "Contexto extra",
      hint:
        "Adicione detalhes do subtópico, contexto de campanha, especificidades do público ou notas de nicho para melhorar os conjuntos.",
      placeholder: "Contexto extra, nicho, subtópico ou notas de campanha"
    }
  },
  contextTitle: "Contexto das hashtags",
  errors: {
    enterTopic: "Por favor, insira um tópico primeiro.",
    generateFailed: "Falha ao gerar hashtags."
  },
  actions: {
    generating: "Gerando...",
    generate: "Gerar conjuntos de hashtags",
    clearResults: "Limpar resultados",
    resetForm: "Redefinir formulário",
    copied: "Copiado",
    copy: "Copiar"
  },
  resultsHelper:
    "Estes são conjuntos de hashtags prontos para uso. Escolha o que melhor combina com o objetivo do seu conteúdo e com o comportamento da plataforma.",
  scoreLabel: "Pontuação",
  assetTitlePrefix: "Conjunto de hashtags",
  untitledTopic: "Tópico sem título"
},

hookAngleTool: {
  hero: {
    badge: "Ferramenta de estratégia de ângulo",
    title: "Ângulos de Hook",
    description: {
      before: "Use esta ferramenta para gerar",
      strong: "múltiplos ângulos estratégicos de hook",
      after:
        "a partir de um único tema, para abordar a mesma ideia de formas mais novas e mais eficazes para diferentes públicos e objetivos."
    }
  },
  fields: {
    topic: {
      label: "Tema",
      hint: "A ideia principal em torno da qual a Hookora deve criar ângulos de hook.",
      placeholder: "Para que tema você quer ângulos de hook?"
    },
    platform: {
      label: "Plataforma",
      hint: "Escolha a plataforma onde o hook será usado."
    },
    audience: {
      label: "Público",
      hint:
        "Com quem o hook deve falar. Exemplo: criadores, fundadores, estudantes, donos de negócio.",
      placeholder: "Público"
    },
    goal: {
      label: "Objetivo",
      hint:
        "O que o hook deve ajudar a alcançar. Exemplo: cliques, retenção, curiosidade, comentários, conversões.",
      placeholder: "Objetivo"
    },
    offer: {
      label: "Oferta",
      hint:
        "O produto, serviço, valor ou transformação por trás do conteúdo.",
      placeholder: "Oferta ou mensagem"
    },
    painPoint: {
      label: "Dor",
      hint: "O problema, frustração ou desafio que o seu público enfrenta.",
      placeholder: "Dor"
    },
    desiredOutcome: {
      label: "Resultado desejado",
      hint: "O resultado que o seu público quer alcançar depois de resolver o problema.",
      placeholder: "Resultado desejado"
    },
    brandVoice: {
      label: "Voz da marca",
      hint:
        "Como o hook deve soar. Exemplo: ousado, inteligente, premium, direto, especialista.",
      placeholder: "Voz da marca"
    },
    avoidPhrases: {
      label: "Frases a evitar",
      hint: "Palavras ou padrões que você não quer ver repetidos nos ângulos.",
      placeholder: "Frases a evitar"
    },
    extraContext: {
      label: "Contexto extra",
      hint:
        "Adicione notas do produto, contexto de campanha, detalhes do público, provas ou qualquer direção extra.",
      placeholder: "Contexto extra, prova ou notas da campanha"
    }
  },
  contextTitle: "Contexto do Ângulo de Hook",
  errors: {
    missingTopic: "Por favor, insira primeiro um tema.",
    generateFailed: "Falha ao gerar ângulos de hook."
  },
  actions: {
    generate: "Gerar Ângulos de Hook",
    generating: "Gerando...",
    copy: "Copiar",
    copied: "Copiado"
  },
  results: {
    intro: {
      before: "Estas são",
      strong: "direções estratégicas de ângulos de hook",
      after:
        "que você pode usar para abordar o mesmo tema a partir de diferentes perspectivas."
    },
    score: "Pontuação",
    angleType: "Tipo de Ângulo",
    bestUseCase: "Melhor Caso de Uso",
    sampleHook: "Exemplo de Hook"
  }
},

hookScoreAnalyzerTool: {
  badge: "Analisador de hooks",
  title: "Analisador de Pontuação de Hook",
  description:
    "Analise a força do seu hook, entenda o que está funcionando, identifique pontos fracos e receba sugestões mais fortes para melhorar clareza, curiosidade e potencial de retenção.",

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "Geral"
  },

  fields: {
    topic: {
      label: "Tópico",
      placeholder: "Ex.: produtividade, finanças, crescimento no Instagram",
      hint: "Opcional, mas útil. Dá mais contexto para avaliar se o hook combina com o tema."
    },
    platform: {
      label: "Plataforma",
      hint: "Escolha onde o hook será usado para uma análise mais precisa."
    },
    goal: {
      label: "Objetivo",
      placeholder: "Ex.: gerar curiosidade, aumentar retenção, conseguir cliques",
      hint: "Explique o que esse hook deve alcançar."
    },
    targetAudience: {
      label: "Público-alvo",
      placeholder: "Ex.: criadores, donos de negócio, estudantes",
      hint: "Defina para quem o hook é direcionado."
    },
    hook: {
      label: "Hook",
      placeholder: "Cole o hook que você quer analisar...",
      hint: "Cole a frase de abertura, primeira linha ou hook completo para pontuar."
    }
  },

  actions: {
    analyze: "Analisar Hook",
    analyzing: "Analisando...",
    clearResults: "Limpar Resultados",
    reset: "Redefinir",
    copy: "Copiar",
    copied: "Copiado"
  },

  messages: {
    hookRequired: "Por favor, insira um hook para analisar.",
    analyzeFailed: "Não foi possível analisar este hook agora.",
    copyFailed: "Não foi possível copiar o resultado agora."
  },

  tip:
    "Dica: inclua o objetivo e o público-alvo para receber uma análise mais útil e alinhada ao contexto.",

  exportLabels: {
    overallScore: "Pontuação geral",
    summary: "Resumo",
    breakdown: "Detalhamento",
    strengths: "Pontos fortes",
    weaknesses: "Pontos fracos",
    suggestions: "Sugestões",
    improvedHooks: "Hooks melhorados"
  },

  results: {
    title: "Resultado da Análise",
    summary: "Resumo",
    breakdown: "Detalhamento da Pontuação",
    whatWorks: "O que está funcionando",
    weakPoints: "Pontos fracos",
    howToImprove: "Como melhorar",
    improvedHooks: "Hooks melhorados",
    rawResult: "Resultado bruto",
    scoreLabels: {
      strong: "Forte",
      decent: "Bom",
      weak: "Fraco"
    }
  }
},

nigerianAudienceRewriteTool: {
  badge: "Ferramenta de reescrita localizada",
  title: "Reescrita para o público nigeriano",
  subtitle:
    "Adapte um conteúdo existente para que soe mais natural, mais relevante e mais alinhado culturalmente para um público nigeriano, sem perder clareza nem intenção.",

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "Geral"
  },

  toneOptions: {
    natural: "Natural",
    casual: "Casual",
    confident: "Confiante",
    sales: "Vendas",
    storytelling: "Narrativo",
    professional: "Profissional"
  },

  formatOptions: {
    caption: "Legenda",
    hook: "Gancho",
    shortScript: "Roteiro curto",
    adCopy: "Texto publicitário",
    carouselCopy: "Texto para carrossel",
    general: "Reescrita geral"
  },

  fields: {
    topic: {
      label: "Tópico",
      placeholder: "Sobre o que é este conteúdo?",
      hint: "O tópico principal ou a mensagem por trás da reescrita."
    },
    platform: {
      label: "Plataforma",
      hint: "Escolha onde a versão reescrita será usada."
    },
    tone: {
      label: "Tom",
      hint: "Escolha como a versão localizada deve soar."
    },
    contentFormat: {
      label: "Formato do conteúdo",
      hint: "Escolha o tipo de conteúdo que você está reescrevendo."
    },
    goal: {
      label: "Objetivo",
      placeholder: "O que esta reescrita deve ajudar a alcançar?",
      hint: "O resultado desejado com a reescrita, como clareza, conversão, engajamento ou identificação."
    },
    brandContext: {
      label: "Contexto da marca",
      placeholder: "Adicione voz da marca, posicionamento, pistas sobre o público ou contexto importante",
      hint: "Contexto opcional para fazer a reescrita soar mais alinhada à marca e ao público."
    },
    originalContent: {
      label: "Conteúdo original",
      placeholder: "Cole o conteúdo original que você quer reescrever para um público nigeriano",
      hint: "Cole o texto exato que o Hookora deve adaptar."
    }
  },

  buttons: {
    rewrite: "Reescrever para o público nigeriano",
    rewriting: "Reescrevendo...",
    clearResults: "Limpar resultados",
    reset: "Redefinir",
    copy: "Copiar",
    copied: "Copiado"
  },

  messages: {
    missingOriginalText: "Digite primeiro o conteúdo original.",
    copyError: "Falha ao copiar o resultado.",
    genericError: "Não foi possível reescrever este conteúdo agora."
  },

  tip:
    "Use esta ferramenta quando quiser que o conteúdo pareça mais familiar, mais natural e mais relevante localmente para leitores ou espectadores nigerianos.",

  result: {
    title: "Resultado da reescrita",
    assetTitle: "Reescrita para o público nigeriano",
    sections: {
      rewrittenOutput: "Conteúdo reescrito",
      summary: "Resumo",
      adaptationNotes: "Notas de adaptação",
      alternativeVersions: "Versões alternativas",
      suggestions: "Sugestões",
      rawResult: "Resultado bruto"
    }
  },

  exportLabels: {
    rewrittenOutput: "Conteúdo reescrito",
    summary: "Resumo",
    adaptationNotes: "Notas de adaptação",
    alternativeVersions: "Versões alternativas",
    suggestions: "Sugestões"
  }
},

offerPositioningTool: {
  title: "Posicionamento da Oferta",
  description:
    "Use esta ferramenta para refinar como a sua oferta é apresentada, explicada e diferenciada, para que pareça mais clara, mais relevante e mais convincente para o público certo.",

  fields: {
    topic: {
      label: "Tópico",
      placeholder: "Sobre o que é este posicionamento?",
      hint: "O tópico principal, nicho ou foco do posicionamento da oferta."
    },
    platform: {
      label: "Plataforma",
      placeholder: "Onde este posicionamento será usado?",
      hint: "Escolha onde essa mensagem aparecerá, como site, página de vendas, e-mail ou conteúdo social."
    },
    tone: {
      label: "Tom"
    },
    language: {
      label: "Idioma"
    },
    offer: {
      label: "Oferta",
      placeholder: "Descreva a oferta, produto ou serviço",
      hint: "Explique o que você vende, entrega ou ajuda as pessoas a alcançar."
    },
    audience: {
      label: "Público",
      placeholder: "Para quem é isso?"
    },
    goal: {
      label: "Objetivo",
      placeholder: "O que este posicionamento deve alcançar?"
    },
    painPoint: {
      label: "Dor",
      placeholder: "Qual problema o público enfrenta?"
    },
    desiredOutcome: {
      label: "Resultado desejado",
      placeholder: "Que resultado o público quer alcançar?"
    },
    angle: {
      label: "Ângulo",
      placeholder: "Que ângulo este posicionamento deve adotar?"
    },
    callToAction: {
      label: "Chamada para ação",
      placeholder: "Que ação as pessoas devem tomar?"
    },
    brandVoice: {
      label: "Voz da marca",
      placeholder: "Como a marca deve soar?"
    },
    keywords: {
      label: "Palavras-chave",
      placeholder: "Palavras-chave para incluir"
    },
    avoidPhrases: {
      label: "Frases a evitar",
      placeholder: "Palavras ou frases a evitar"
    },
    extraContext: {
      label: "Contexto extra",
      placeholder: "Adicione contexto extra, provas, detalhes do público ou notas de posicionamento"
    }
  },

  toneOptions: {
    confident: "Confiante",
    professional: "Profissional",
    bold: "Ousado",
    conversational: "Conversacional",
    warm: "Caloroso",
    persuasive: "Persuasivo",
    direct: "Direto"
  },

  languageOptions: {
    en: "Inglês",
    es: "Espanhol",
    fr: "Francês",
    pt: "Português"
  },

  actions: {
    generate: "Gerar posicionamento",
    generating: "Gerando...",
    reset: "Redefinir",
    copy: "Copiar",
    copied: "Copiado"
  },

  messages: {
    topicOrOfferRequired: "Primeiro, insira um tópico ou descreva a oferta.",
    generateFailed: "Falha ao gerar o posicionamento da oferta.",
    copyFailed: "Falha ao copiar a saída do posicionamento."
  },

  results: {
    title: "Resultados de posicionamento",
    description:
      "Revise diferentes direções de posicionamento, propostas de valor, enquadramentos de público e exemplos de mensagem para a sua oferta.",
    empty: "Ainda não há resultados de posicionamento. Gere um posicionamento de oferta para ver os resultados aqui.",
    fallbackTitle: "Opção de posicionamento {{number}}",
    score: "Pontuação: {{score}}",
    positioningAngle: "Ângulo de posicionamento",
    valueProposition: "Proposta de valor",
    whoItsFor: "Para quem é",
    problemFrame: "Enquadramento do problema",
    differentiator: "Diferencial",
    messageExample: "Exemplo de mensagem"
  }
},

retentionIntroTool: {
  hero: {
    badge: "Intro de retenção",
    title: "Gere segundos iniciais mais fortes",
    description:
      "Crie intros feitas para prender a atenção logo no começo com um hook forte, sensação de prévia, tema mais profundo e um motivo claro para continuar assistindo."
  },

  fields: {
    topic: {
      label: "Tópico",
      placeholder: "ex.: por que a maioria dos criadores fica presa",
      hint: "Esta ferramenta funciona melhor quando o tópico é claro e específico."
    },
    platform: {
      label: "Plataforma",
      hint: "O contexto da plataforma ajuda a moldar o ritmo e a estrutura da intro."
    },
    contentType: {
      label: "Tipo de conteúdo",
      hint: "Escolha o tipo de conteúdo para o qual você quer criar a abertura."
    },
    tone: {
      label: "Tom",
      hint: "O tom afeta o impacto emocional e a voz da intro."
    },
    targetAudience: {
      label: "Público-alvo",
      placeholder: "ex.: criadores iniciantes, fundadores, estudantes",
      hint: "O contexto do público é opcional, mas pode deixar a intro mais direcionada."
    },
    goal: {
      label: "Objetivo",
      placeholder: "ex.: aumentar watch time, melhorar retenção, gerar curiosidade",
      hint: "Opcional. Útil quando você quer otimizar a intro para um resultado específico."
    },
    extraContext: {
      label: "Contexto extra",
      placeholder:
        "Descreva a ideia do conteúdo, o ângulo da história, a lição, o produto ou a direção emocional...",
      hint: "Adicione contexto quando quiser que a abertura fique mais personalizada e precisa."
    }
  },

  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "Geral"
  },

  contentTypeOptions: {
    short_video: "Vídeo curto",
    long_video: "Vídeo longo",
    ad: "Anúncio",
    ugc: "UGC",
    educational: "Educativo",
    storytelling: "Storytelling"
  },

  toneOptions: {
    bold: "Ousado",
    natural: "Natural",
    dramatic: "Dramático",
    confident: "Confiante",
    curious: "Curioso",
    urgent: "Urgente"
  },

  actions: {
    generateIntro: "Gerar intro",
    generating: "Gerando...",
    clearResults: "Limpar resultados",
    reset: "Redefinir",
    copy: "Copiar",
    copied: "Copiado"
  },

  messages: {
    topicRequired: "Digite um tópico antes de gerar uma intro de retenção.",
    generateFailed: "Algo deu errado ao gerar a intro de retenção.",
    copyFailed: "Não foi possível copiar a intro agora. Tente novamente."
  },

  tip:
    "Dica: boas intros de retenção geralmente criam tensão rapidamente, mostram valor cedo e fazem o espectador sentir que não deve sair do vídeo.",

  output: {
    resultTitle: "Resultado da intro de retenção",
    retentionIntro: "Intro de retenção",
    previewOpeningVisual: "Prévia / Visual de abertura",
    valuePromise: "Promessa de valor",
    deeperTheme: "Tema mais profundo",
    notes: "Notas",
    alternativeVersions: "Versões alternativas",
    rawResult: "Resultado bruto"
  }
},

scriptTool: {
  hero: {
    badge: "Construtor de scripts",
    title: "Crie scripts curtos mais rápido",
    description: {
      before: "Crie ",
      strong: "scripts prontos para gravar",
      after:
        " com hooks mais fortes, estrutura mais clara e direção de CTA adaptada ao seu tema, público e plataforma."
    }
  },

  fields: {
    topic: {
      label: "Tópico",
      hint: "Sobre o que o script deve falar. Seja específico para obter um resultado melhor.",
      placeholder: "Sobre o que o script deve falar?"
    },
    platform: {
      label: "Plataforma",
      hint: "Escolha onde o script será usado para que o ritmo e a estrutura combinem com o formato."
    },
    style: {
      label: "Estilo do script",
      hint: "Escolha o formato de script que o Hookora deve gerar."
    },
    audience: {
      label: "Público",
      hint: "Para quem o script é.",
      placeholder: "Público"
    },
    goal: {
      label: "Objetivo",
      hint: "O que o script deve ajudar a alcançar.",
      placeholder: "Objetivo"
    },
    offer: {
      label: "Oferta",
      hint: "O produto, serviço, valor ou mensagem por trás do script.",
      placeholder: "Oferta ou produto"
    },
    painPoint: {
      label: "Dor",
      hint: "O problema ou frustração que o público está enfrentando.",
      placeholder: "Dor"
    },
    desiredOutcome: {
      label: "Resultado desejado",
      hint: "O resultado que o público quer alcançar.",
      placeholder: "Resultado desejado"
    },
    angle: {
      label: "Ângulo",
      hint: "A perspectiva ou abordagem que você quer que o script siga.",
      placeholder: "Ângulo"
    },
    callToAction: {
      label: "CTA",
      hint: "A ação que você quer que o público realize.",
      placeholder: "CTA"
    },
    keywords: {
      label: "Palavras-chave",
      hint: "Palavras ou frases importantes para incluir naturalmente no script.",
      placeholder: "Palavras-chave"
    },
    brandVoice: {
      label: "Voz da marca",
      hint: "Como o script deve soar.",
      placeholder: "Voz da marca"
    },
    extraContext: {
      label: "Contexto extra",
      hint: "Adicione detalhes, provas, informações do produto ou notas do criador para melhorar o script.",
      placeholder: "Contexto extra"
    }
  },

  styleOptions: {
    "direct-to-camera": "Direto para a câmera",
    "story-led": "Guiado por história",
    "problem-solution": "Problema-Solução",
    "myth-busting": "Quebrando mitos"
  },

  context: {
    title: "Contexto do script"
  },

  actions: {
    generate: "Gerar scripts",
    generating: "Gerando...",
    clear: "Limpar resultados",
    reset: "Redefinir formulário",
    copy: "Copiar",
    copied: "Copiado"
  },

  errors: {
    topicRequired: "Digite um tópico primeiro.",
    generateFailed: "Falha ao gerar os scripts."
  },

  output: {
    helper: {
      before: "Estas são ",
      strong: "opções de script prontas para uso",
      after: ". Escolha a que melhor combina com seu ângulo de conteúdo, plataforma e objetivo de conversão."
    },
    score: "Pontuação",
    hookLabel: "Hook",
    bodyLabel: "Corpo",
    ctaLabel: "CTA",
    untitledTopic: "Tópico sem título"
  }
},

thumbnailTool: {
  hero: {
    badge: "Ferramenta de otimização de CTR",
    title: "Texto da thumbnail",
    description:
      "Use esta ferramenta para analisar o texto atual da thumbnail e gerar alternativas mais fortes. Adicione o tópico e o contexto de posicionamento para que a Hookora possa sugerir palavras mais curtas, mais fortes e mais clicáveis.",
  },
  fields: {
    topic: {
      label: "Tópico",
      hint:
        "Contexto opcional do tópico para o analisador de thumbnail. Isso ajuda as sugestões a ficarem mais relevantes para o conteúdo.",
      placeholder: "Tópico opcional para a análise da thumbnail",
    },
    thumbnailText: {
      label: "Texto da thumbnail",
      hint:
        "As palavras exatas da thumbnail que você quer analisar. Mantenha curto e impactante para obter melhores resultados.",
      placeholder: "Digite o texto atual da thumbnail",
    },
    audience: {
      label: "Público",
      hint:
        "Quem a thumbnail está tentando atrair. Exemplo: criadores, estudantes, fundadores, fãs de fitness.",
      placeholder: "Público",
    },
    angle: {
      label: "Ângulo",
      hint:
        "O posicionamento ou enquadramento emocional. Exemplo: chocante, premium, direto, urgente, para iniciantes.",
      placeholder: "Ângulo",
    },
    desiredOutcome: {
      label: "Resultado desejado",
      hint:
        "Qual resultado a thumbnail deve ajudar a gerar. Exemplo: cliques, curiosidade, tempo de visualização, conversões.",
      placeholder: "Resultado desejado",
    },
    avoidPhrases: {
      label: "Frases para evitar",
      hint:
        "Palavras ou expressões que você não quer que o analisador repita nas sugestões.",
      placeholder: "Frases para evitar",
    },
    extraContext: {
      label: "Contexto extra",
      hint:
        "Adicione emoção desejada, notas de posicionamento, estilo de concorrentes ou contexto extra para melhorar as sugestões.",
      placeholder: "Contexto extra, alvo emocional ou nota de posicionamento",
    },
  },
  analysisContext: {
    title: "Contexto da análise",
  },
  actions: {
    analyzing: "Analisando...",
    analyze: "Analisar texto da thumbnail",
    clearResults: "Limpar resultados",
    resetForm: "Redefinir formulário",
    copied: "Copiado",
    copy: "Copiar",
  },
  analysis: {
    title: "Análise do texto atual",
  },
  labels: {
    score: "Pontuação",
    words: "Palavras",
    characters: "Caracteres",
    untitledTopic: "Tópico sem título",
  },
  suggestionsIntro:
    "Estas são sugestões alternativas de texto para thumbnail. Escolha a opção que parecer mais clara, mais forte e mais clicável para o seu público.",
  errors: {
    enterTextOrTopic: "Digite primeiro um texto de thumbnail ou um tópico.",
    analyzeFailed: "Falha ao analisar o texto da thumbnail.",
  },
},

titleTool: {
  hero: {
    badge: "Ferramenta focada em cliques",
    title: "Títulos",
    description:
      "Use esta ferramenta para gerar opções de títulos clicáveis para vídeos e conteúdos. Adicione intenção de busca, ângulo e contexto para que a Hookora possa criar variações de títulos mais fortes, mais claras e com maior chance de chamar atenção.",
  },
  fields: {
    topic: {
      label: "Tópico",
      hint:
        "O assunto principal do vídeo ou da peça de conteúdo. Quanto mais claro for o seu tópico, melhores serão as variações de título.",
      placeholder: "Para qual vídeo ou tópico esses títulos são?",
    },
    audience: {
      label: "Público",
      hint:
        "Para quem o conteúdo é feito. Exemplo: estudantes, criadores, fundadores de ecommerce, freelancers nigerianos.",
      placeholder: "Público",
    },
    goal: {
      label: "Objetivo",
      hint:
        "O que o título deve otimizar. Exemplo: cliques, curiosidade, tráfego de busca, autoridade, conversões.",
      placeholder: "Objetivo",
    },
    angle: {
      label: "Ângulo",
      hint:
        "O enquadramento do título. Exemplo: baseado em erro, amigável para iniciantes, contrarian, focado em resultados.",
      placeholder: "Ângulo",
    },
    keywords: {
      label: "Palavras-chave",
      hint:
        "Termos de busca importantes para incluir naturalmente nos títulos. Separe com vírgulas.",
      placeholder: "Palavras-chave (separadas por vírgula)",
    },
    brandVoice: {
      label: "Voz da marca",
      hint:
        "Como o título deve soar. Exemplo: afiado, premium, direto, divertido, ousado.",
      placeholder: "Voz da marca",
    },
    avoidPhrases: {
      label: "Frases para evitar",
      hint:
        "Palavras ou padrões repetitivos que você não quer que o gerador de títulos use.",
      placeholder: "Frases para evitar",
    },
    extraContext: {
      label: "Contexto extra",
      hint:
        "Adicione provas, resultados específicos, emoção desejada ou notas extras de posicionamento para melhorar a qualidade dos títulos.",
      placeholder:
        "Contexto extra, prova única, resultado, exemplo ou nota de posicionamento",
    },
  },
  contextSectionTitle: "Contexto do título",
  actions: {
    generating: "Gerando...",
    generate: "Gerar títulos",
    clearResults: "Limpar resultados",
    resetForm: "Redefinir formulário",
    copied: "Copiado",
    copy: "Copiar",
  },
  resultsIntro:
    "Estas são opções de títulos prontas para teste. Escolha a mais forte com base no apelo de clique, clareza e adequação ao seu público.",
  labels: {
    score: "Pontuação",
  },
  fallbackUntitledTopic: "Tópico sem título",
  errors: {
    topicRequired: "Digite um tópico primeiro.",
    generateFailed: "Falha ao gerar títulos.",
  },
},

trendToContentTool: {
  badge: "Ferramenta de tendência para conteúdo",
  title: "Tendência para Conteúdo",
  description:
    "Transforme tendências, conversas ou mudanças de mercado em direções de conteúdo, ideias, hooks, legendas e ângulos utilizáveis para a sua marca.",
  fields: {
    topic: {
      label: "Tópico",
      placeholder: "Tópico opcional para orientar a saída",
      hint:
        "Adicione o seu tópico principal se quiser que as ideias da tendência continuem ligadas a um assunto ou nicho específico.",
    },
    platform: {
      label: "Plataforma",
      hint:
        "Escolha a plataforma onde você quer transformar essa tendência em conteúdo.",
    },
    outputType: {
      label: "Tipo de saída",
      hint:
        "Selecione o tipo de resultado que você quer que a Hookora gere a partir da tendência.",
    },
    goal: {
      label: "Objetivo",
      placeholder: "O que esse conteúdo deve ajudar você a alcançar?",
      hint:
        "Exemplo: alcance, engajamento, autoridade, awareness, cliques ou conversões.",
    },
    audience: {
      label: "Público",
      placeholder: "Para quem é esse conteúdo?",
      hint:
        "Descreva o público com o qual o conteúdo gerado deve falar.",
    },
    trend: {
      label: "Tendência",
      placeholder: "Descreva a tendência, padrão, conversa ou mudança",
      hint:
        "Cole uma tendência, observação, padrão viral, mudança de mercado ou comportamento de público que você quer transformar em conteúdo.",
    },
    brandContext: {
      label: "Contexto da marca",
      placeholder: "Adicione contexto da marca, da oferta ou notas de posicionamento",
      hint:
        "Explique o ângulo da sua marca, oferta, posicionamento ou estilo de conteúdo para que as ideias fiquem mais alinhadas.",
    },
  },
  platformOptions: {
    instagram: "Instagram",
    tiktok: "TikTok",
    youtube: "YouTube",
    x: "X / Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    general: "Geral",
  },
  outputTypeOptions: {
    contentIdeas: "Ideias de conteúdo",
    hooks: "Hooks",
    captions: "Legendas",
    scriptAngles: "Ângulos de roteiro",
    carouselIdeas: "Ideias de carrossel",
    videoConcepts: "Conceitos de vídeo",
  },
  actions: {
    generating: "Gerando...",
    generate: "Gerar",
    clearResults: "Limpar resultados",
    reset: "Redefinir",
    copied: "Copiado",
    copy: "Copiar",
  },
  tip:
    "Dica: quanto mais específica for a tendência e o contexto da marca, mais útil e diferenciado será o resultado.",
  messages: {
    trendRequired: "Digite uma tendência primeiro.",
    generateFailed: "Falha ao gerar conteúdo baseado na tendência.",
    copyFailed: "Falha ao copiar o resultado.",
  },
  results: {
    title: "Resultados",
    summary: "Resumo",
    trendInsight: "Insight da tendência",
    generatedIdeas: "Ideias geradas",
    hookOptions: "Opções de hooks",
    captionOptions: "Opções de legendas",
    angles: "Ângulos",
    recommendations: "Recomendações",
    rawResult: "Resultado bruto",
  },
  exportLabels: {
    summary: "Resumo",
    trendInsight: "Insight da tendência",
    generatedIdeas: "Ideias geradas",
    hookOptions: "Opções de hooks",
    captionOptions: "Opções de legendas",
    angles: "Ângulos",
    recommendations: "Recomendações",
  },
},

viralRewriterTool: {
  title: "Reescritor Viral",
  description:
    "Reescreva conteúdos existentes em versões mais fortes e mais virais, mantendo a mensagem principal intacta.",
  fields: {
    topic: {
      label: "Tópico",
      placeholder: "Sobre o que é esta reescrita?",
      hint:
        "Opcional. Adicione um tópico se quiser que a reescrita continue focada em um assunto específico."
    },
    platform: {
      label: "Plataforma",
      placeholder: "Onde isso será publicado?"
    },
    tone: {
      label: "Tom"
    },
    language: {
      label: "Idioma"
    },
    sourceText: {
      label: "Texto de origem",
      placeholder: "Cole o conteúdo que deseja reescrever",
      hint:
        "Cole o texto original, legenda, roteiro ou publicação que o Hookora deve reescrever."
    },
    audience: {
      label: "Público",
      placeholder: "Para quem é isso?"
    },
    goal: {
      label: "Objetivo",
      placeholder: "O que esta reescrita deve alcançar?"
    },
    brandVoice: {
      label: "Voz da marca",
      placeholder: "Como deve soar?"
    },
    callToAction: {
      label: "Chamada para ação",
      placeholder: "Que ação ela deve incentivar?"
    },
    keywords: {
      label: "Palavras-chave",
      placeholder: "Palavras ou frases importantes para incluir"
    },
    avoidPhrases: {
      label: "Frases a evitar",
      placeholder: "Palavras ou frases a evitar"
    },
    extraContext: {
      label: "Contexto extra",
      placeholder: "Mais alguma coisa que o Hookora deve saber?"
    }
  },
  toneOptions: {
    bold: "Ousado",
    conversational: "Conversacional",
    confident: "Confiante",
    persuasive: "Persuasivo",
    professional: "Profissional",
    warm: "Acolhedor",
    natural: "Natural"
  },
  languageOptions: {
    en: "Inglês",
    es: "Espanhol",
    fr: "Francês",
    pt: "Português"
  },
  actions: {
    generating: "Gerando...",
    generate: "Gerar reescritas",
    reset: "Redefinir",
    copied: "Copiado",
    copy: "Copiar"
  },
  messages: {
    sourceRequired: "Cole primeiro um conteúdo para reescrever.",
    generateFailed: "Falha ao gerar reescritas virais.",
    copyFailed: "Não foi possível copiar a reescrita agora."
  },
  results: {
    title: "Resultados da reescrita",
    description:
      "Escolha a versão que parecer mais forte, mais afiada e mais capaz de prender atenção.",
    empty: "Suas reescritas prontas para viralizar aparecerão aqui.",
    rewriteFallback: "Reescrita {{number}}",
    score: "Pontuação: {{score}}",
    whyItWorks: "Por que funciona:"
  }
},
},
  about: {
    seoTitle: "Sobre a Hookora | Plataforma de fluxo de trabalho com IA para criadores",
    seoDescription:
      "Aprenda o que a Hookora faz, por que ela existe e como ajuda criadores a construir hooks, títulos, legendas, roteiros e fluxos de conteúdo melhores.",
    heroBadge: "Sobre a Hookora",
    heroTitle: "A Hookora ajuda criadores a sair da ideia para um conteúdo melhor mais rápido",
    heroDescription:
      "A Hookora é uma plataforma de fluxo de trabalho com IA feita para criadores que querem uma forma mais limpa, mais rápida e mais confiante de gerar hooks, títulos, legendas, hashtags, descrições, roteiros e texto de thumbnail.",
    createFreeAccount: "Criar conta grátis",
    exploreFeatures: "Explorar recursos",
    storyBadge: "Por que a Hookora existe",
    storyTitle: "Criar conteúdo deveria parecer mais claro, não mais pesado",
    storyParagraph1:
      "Muitos criadores sabem o que querem dizer, mas ainda ficam travados nas palavras. O hook parece fraco. O título não encaixa. A legenda fica sem força. O roteiro demora demais. Essa fricção se acumula e torna a publicação mais difícil do que deveria.",
    storyParagraph2:
      "A Hookora foi criada para reduzir essa fricção. Em vez de forçar criadores a pularem entre ferramentas soltas, anotações aleatórias e fluxos bagunçados, a Hookora reúne o processo em um sistema mais limpo que ajuda ideias a virarem saídas mais fortes mais rápido.",
    storyParagraph3:
      "Se alguém cria de Lagos, Londres ou Los Angeles, o problema costuma ser o mesmo: conteúdo exige energia mental demais para ser bem empacotado. A Hookora ajuda criadores a se sentirem mais apoiados, mais consistentes e mais no controle do próprio fluxo.",
    whyTitle: "O que torna a Hookora útil",
    whySubtitle:
      "A Hookora não foi feita para fazer criadores se sentirem substituídos. Foi feita para fazer criadores se sentirem mais afiados, mais rápidos e mais apoiados.",
    ctaBadge: "Comece com a Hookora",
    ctaTitle: "Construa seu fluxo de conteúdo com mais clareza e menos fricção",
    ctaDescription:
      "Se você quer uma forma mais limpa de gerar hooks, títulos, legendas, roteiros e mais, a Hookora oferece um ponto de partida prático.",
    viewPricing: "Ver preços",
    guidesSectionTitle: "Saiba mais sobre a Hookora e como ela ajuda criadores",
    guidesSectionDescription:
      "Esses guias rápidos explicam o que a Hookora faz, quem ela ajuda e como torna a escrita de hooks e o fluxo de conteúdo mais claros e rápidos.",
    schemaDescription:
      "Aprenda o que a Hookora é, quem ela ajuda e como a Hookora apoia criadores com um fluxo de conteúdo mais suave.",
    guides: {
      whatIsHookora: {
        description:
          "Aprenda o que é a Hookora, como ela funciona e por que ajuda criadores a saírem de uma ideia para um conteúdo mais forte mais rápido."
      },
      nigerianCreators: {
        description:
          "Veja como a Hookora ajuda criadores nigerianos a construir hooks, títulos, legendas, roteiros e um empacotamento de conteúdo melhor."
      },
      writeHooksFaster: {
        description:
          "Explore como a Hookora ajuda criadores a escrever hooks mais rápido com ângulos mais claros e primeiras linhas mais fortes."
      }
    },
    cards: {
      builtForCreators: "Feita para criadores que querem menos fricção",
      keepsOutputMoving: "Desenhada para manter a produção em movimento",
      oneWorkflow:
        "Criada para conectar hooks, títulos, legendas e roteiros em um só fluxo",
      globalCreators: "Útil para criadores na Nigéria e além",
      connectedWorkflow: {
        title: "Um fluxo conectado",
        text:
          "Hooks, títulos, legendas, hashtags, roteiros, descrições e texto de thumbnail funcionam melhor quando são criados em um sistema conectado."
      },
      momentum: {
        title: "Mais impulso criativo",
        text:
          "A Hookora ajuda criadores a passarem menos tempo travados nas palavras e mais tempo avançando para conteúdo pronto para publicar."
      },
      packaging: {
        title: "Melhor empacotamento de conteúdo",
        text:
          "Conteúdo forte não depende só de ideias. Também depende de como essas ideias são apresentadas, enquadradas e tornadas mais fáceis de clicar ou assistir."
      },
      growingCreators: {
        title: "Útil para criadores em crescimento",
        text:
          "A Hookora foi desenhada para ajudar tanto quem está começando quanto quem já cria com consistência e precisa de um fluxo mais suave."
      }
    },
  },

  home: {
    featuredGuidesBadge: "Guias em destaque da Hookora",
    learnSectionTitle: "Aprenda como a Hookora ajuda criadores a avançar mais rápido",
    learnSectionDescription:
      "Explore guias rápidos sobre o que é a Hookora, quem ela ajuda e como torna escrita de hooks, ideação de conteúdo, reescritas, empacotamento e fluxo criativo mais claros e rápidos.",
    guides: {
      whatIsHookora: {
        title: "O que é Hookora?",
        description:
          "Aprenda o que é a Hookora, como ela ajuda criadores a sair de uma ideia para um conteúdo mais forte mais rápido e por que agora vai além da geração básica de hooks."
      },
      nigerianCreators: {
        title: "Hookora para criadores nigerianos",
        description:
          "Veja como a Hookora ajuda criadores nigerianos a construir hooks, títulos, legendas, roteiros, reescritas localizadas e um empacotamento de conteúdo melhor."
      },
      writeHooksFaster: {
        title: "Como a Hookora ajuda criadores a escrever hooks mais rápido",
        description:
          "Explore como a Hookora ajuda criadores a escrever hooks mais rápido, encontrar ângulos mais fortes, melhorar retenção e transformar mais ideias em conteúdo pronto para publicar."
      }
    }
  },

  homeHero: {
    badge: "Plataforma de fluxo de IA para criadores",
    title: "Crie conteúdo mais forte mais rápido com o Studio de 22 ferramentas da Hookora",
    description:
      "Comece grátis com ferramentas principais de escrita, depois desbloqueie ferramentas premium de fluxo e ferramentas estratégicas avançadas conforme você cresce. A Hookora ajuda criadores a gerar hooks, títulos, legendas, hashtags, descrições, roteiros, reescritas, intros de retenção, sistemas de voz e mais a partir de um fluxo mais limpo.",
    trustPoints: {
      tools: "22 ferramentas do Studio em um único fluxo",
      free: "Comece grátis com ferramentas principais",
      upgrade: "Faça upgrade para ferramentas premium e estratégicas"
    },
    ideaLabel: "Ideia de conteúdo",
    ideaText: "“Por que a maioria dos criadores perde atenção nos primeiros 3 segundos”",
    metrics: {
      tools: "Ferramentas",
      workflow: "Fluxo"
    },
    footerScale: "Feito para escalar"
  },

  homeFeatures: {
    title: "Por que os criadores usam a Hookora",
    subtitle:
      "Um fluxo mais limpo para hooks, títulos, legendas, roteiros e um empacotamento de conteúdo melhor.",
    cards: {
      createFaster: {
        title: "Crie mais rápido",
        text:
          "Passe de uma ideia inicial para conteúdo útil mais rápido sem ficar travado nas palavras."
      },
      keepMessageSharp: {
        title: "Mantenha sua mensagem mais clara",
        text:
          "Gere hooks, títulos, legendas e roteiros mais claros e mais intencionais."
      },
      upgradeOnlyWhenNeeded: {
        title: "Faça upgrade só quando precisar",
        text:
          "Comece com Free e desbloqueie ferramentas Creator e Pro conforme seu fluxo crescer."
      }
    },
    studioToolsBadge: "Visão geral das ferramentas do Studio",
    workflowTitle: "Um fluxo, várias ferramentas de conteúdo",
    workflowDescription:
      "A Hookora conecta seu fluxo de escrita para que você possa sair de hooks para títulos melhores, legendas, roteiros, reescritas e mais sem alternar entre ferramentas bagunçadas.",
    toolCards: {
      free: "Ferramentas gratuitas para hooks, títulos, legendas, hashtags, descrições e roteiros.",
      creatorOne: "Ferramentas Creator para ângulos de hook, CTAs e empacotamento de conteúdo mais forte.",
      creatorTwo: "Suporte de retenção, reescritas e melhoria do texto de thumbnail.",
      creatorThree: "Reaproveitamento, texto publicitário, escrita de carrosséis e posicionamento de oferta.",
      creatorFour: "Análise de pontuação do hook, reescritas nigerianas e ideias de conteúdo a partir de comentários.",
      proOne: "Ferramentas Pro para conversão de tendências e estratégia criativa mais profunda.",
      proTwo: "Sistemas de voz de marca para deixar o conteúdo mais consistente.",
      proThree: "Ferramentas de diferenciação de conteúdo para ajudar criadores a se destacarem com mais clareza."
    }
  },

  homeCta: {
    badge: "Comece com clareza",
    title: "Dê ao seu conteúdo uma primeira impressão mais forte",
    description:
      "A Hookora ajuda você a criar com mais confiança, mais velocidade e mais consistência. Comece grátis e construa um fluxo mais limpo para hooks, legendas, roteiros, reescritas, retenção e um empacotamento de conteúdo mais forte."
  },

  features: {
  seoTitle:
    "Recursos da Hookora | 22 ferramentas de IA para Hooks, Títulos, Legendas, Roteiros e mais",
  seoDescription:
    "Explore os recursos da Hookora para criadores, incluindo 22 ferramentas do Studio para hooks, ângulos de hook, títulos, legendas, hashtags, descrições, roteiros, CTAs, texto para thumbnail, reescritas, retenção, localização, tendências, voz de marca e mais.",
  softwareSchemaDescription:
    "A Hookora é uma plataforma de fluxo de trabalho com IA para criadores com 22 ferramentas do Studio para hooks, ângulos de hook, títulos, legendas, hashtags, descrições, roteiros, CTAs, texto para thumbnail, reescritas, localização, intros de retenção, conversão de tendências, treinamento de voz de marca, diferenciação e mais.",
  planBasedStudioAccess: "Acesso ao Studio por plano",
  planSectionTitle: "22 ferramentas do Studio em Free, Creator e Pro",
  planSectionDescription:
    "A Hookora agora está estruturada para dar aos usuários um caminho claro de upgrade: ferramentas principais no Free, ferramentas premium de fluxo no Creator e ferramentas estratégicas avançadas no Pro.",
  guidesSectionTitle: "Saiba mais sobre como a Hookora funciona",
  guidesSectionDescription:
    "Explore guias rápidos sobre o que é a Hookora, quem ela ajuda e como melhora a escrita de hooks, ideação, reescritas, empacotamento e fluxo de trabalho do criador.",
  guides: {
    whatIsHookora: {
      title: "O que é Hookora?",
      description:
        "Aprenda o que é a Hookora, como funciona e por que ajuda criadores a ir de uma ideia para um conteúdo melhor mais rápido com um fluxo Studio de 22 ferramentas."
    },
    nigerianCreators: {
      title: "Hookora para criadores nigerianos",
      description:
        "Veja como a Hookora ajuda criadores nigerianos a criar hooks, títulos, legendas, roteiros, reescritas localizadas e melhor empacotamento de conteúdo."
    },
    writeHooksFaster: {
      title: "Como a Hookora ajuda a escrever hooks mais rápido",
      description:
        "Explore como a Hookora ajuda criadores a escrever hooks mais rápido com ângulos mais claros, primeiras linhas mais fortes, melhor suporte de retenção e ferramentas mais flexíveis."
    }
  },
  planGroups: {
    free: {
      title: "Plano Free",
      subtitle: "Comece com o fluxo principal de escrita",
      description:
        "Usuários Free podem acessar as ferramentas essenciais do Studio para escrita de conteúdo e desenvolvimento de ideias no dia a dia."
    },
    creator: {
      title: "Plano Creator",
      subtitle: "Desbloqueie ferramentas premium de fluxo",
      description:
        "Creator desbloqueia a camada premium intermediária da Hookora para empacotamento, reescritas, retenção, reaproveitamento e execução de conteúdo mais forte."
    },
    pro: {
      title: "Plano Pro",
      subtitle: "Desbloqueie ferramentas estratégicas avançadas",
      description:
        "Pro foi criado para um trabalho criativo estratégico mais profundo, ajudando usuários a transformar melhores ideias em sistemas de conteúdo mais fortes."
    }
  }
},

  featuresHero: {
    badge: "Acesso ao Studio por plano",
    title: "22 ferramentas do Studio para o fluxo moderno do criador",
    subtitle:
      "A Hookora agora oferece um caminho de upgrade mais claro com ferramentas principais no Free, ferramentas premium de fluxo no Creator e ferramentas estratégicas avançadas no Pro.",
    startFree: "Comece grátis",
    viewPricing: "Ver preços",
    cards: {
      free: {
        label: "FREE",
        text: "Hooks, Títulos, Legendas, Hashtags, Descrições e Roteiros."
      },
      creator: {
        label: "CREATOR",
        text:
          "Ferramentas premium de fluxo para empacotamento, reescritas, retenção, CTAs, carrosséis, reaproveitamento e muito mais."
      },
      pro: {
        label: "PRO",
        text:
          "Ferramentas estratégicas avançadas para conversão de tendências, treinamento de voz da marca e diferenciação de conteúdo."
      },
      oneStudio: {
        label: "Um Studio",
        text:
          "Crie hooks, títulos, legendas, roteiros, reescritas e ferramentas avançadas em um só lugar."
      }
    },
    footerStats: {
      tools: "22 ferramentas do Studio",
      tieredAccess: "Acesso por níveis",
      cleanerWorkflow: "Fluxo mais limpo"
    }
  },

  featuresGrid: {
    title: "O que a Hookora ajuda você a criar",
    subtitle:
      "Um fluxo completo para criadores com ferramentas gratuitas, premium e estratégicas avançadas.",
    items: {
      workflow22: {
        title: "Fluxo com 22 ferramentas",
        text:
          "Mude entre hooks, títulos, legendas, descrições, roteiros, reescritas e ferramentas estratégicas em um espaço conectado."
      },
      freeCoreWriting: {
        title: "Ferramentas principais gratuitas",
        text:
          "Comece com as ferramentas essenciais para hooks, títulos, legendas, hashtags, descrições e roteiros."
      },
      creatorPremium: {
        title: "Fluxo premium Creator",
        text:
          "Desbloqueie melhor empacotamento, reescritas, reaproveitamento, suporte de CTA, suporte de thumbnail, posicionamento de oferta e ajuda de retenção."
      },
      proStrategic: {
        title: "Ferramentas estratégicas Pro",
        text:
          "Use ferramentas avançadas para transformar tendências em conteúdo, treinar a voz da marca e diferenciar seu conteúdo."
      },
      hooksAngles: {
        title: "Hooks e ângulos de hook",
        text:
          "Gere aberturas melhores e explore várias direções a partir de um único tópico."
      },
      titlesCaptionsHashtags: {
        title: "Títulos, legendas e hashtags",
        text:
          "Escreva elementos de empacotamento mais fortes que ajudem seu conteúdo a ficar mais claro, mais clicável e mais fácil de publicar."
      },
      descriptionsScriptsCtas: {
        title: "Descrições, roteiros e CTAs",
        text:
          "Crie melhor suporte de conteúdo longo, roteiros curtos e chamadas para ação mais claras a partir da mesma ideia central."
      },
      thumbnailHookScore: {
        title: "Texto de thumbnail e pontuação de hook",
        text:
          "Melhore a primeira impressão e analise a força do hook antes de publicar."
      },
      humanizerRewriteRepurpose: {
        title: "Humanizador, reescritas e reaproveitamento",
        text:
          "Faça seu texto soar mais natural, reformule ideias e transforme uma boa entrada em múltiplas saídas de conteúdo."
      },
      retentionVoiceDifferentiation: {
        title: "Retenção, voz de marca e diferenciação",
        text:
          "Refine como seu conteúdo soa, prende atenção e se destaca de textos genéricos para criadores."
      },
      trendCommentToContent: {
        title: "Tendências e conteúdo vindo de comentários",
        text:
          "Transforme tendências e sinais da audiência em ideias de conteúdo, ângulos e caminhos de execução mais fortes."
      },
      librarySavedAssets: {
        title: "Biblioteca e ativos salvos",
        text:
          "Guarde seus melhores hooks e saídas do Studio para que seu fluxo fique mais rápido com o tempo."
      }
    }
  },

  featuresWorkflow: {
    badge: "Como o fluxo se sente",
    title: "Feito para reduzir fricção e manter vivo o impulso do seu conteúdo",
    description:
      "A Hookora é mais útil quando faz a criação de conteúdo parecer mais fácil, mais clara e mais repetível. Esse é o fluxo que dá essa sensação.",
    seePlans: "Ver planos",
    steps: {
      start: {
        title: "Comece com um tópico",
        text:
          "Coloque sua ideia bruta de conteúdo, ângulo, oferta ou conceito inicial sem precisar estar perfeito."
      },
      generate: {
        title: "Gere opções mais fortes",
        text:
          "Use a Hookora para explorar hooks, ângulos de hook, títulos, legendas, roteiros, reescritas e mais a partir dessa mesma ideia central."
      },
      refine: {
        title: "Refine o que mais se destaca",
        text:
          "Melhore retenção, voz, posicionamento e diferenciação para que seu conteúdo pareça mais afiado antes de publicar."
      },
      save: {
        title: "Salve e construa mais rápido na próxima vez",
        text:
          "Mantenha saídas úteis na sua biblioteca para poder reaproveitar, refinar e criar com mais consistência."
      }
    }
  },

  publicPricingHero: {
    badge: "Preços mensais ou anuais para criadores",
    title: "Escolha o plano da Hookora que combina com sua velocidade criativa",
    description:
      "Comece grátis, ganhe impulso e faça upgrade quando precisar de mais gerações, mais ativos salvos e um fluxo mais forte para uma produção consistente de conteúdo. Escolha cobrança mensal ou economize 17% com a anual.",
    startFree: "Comece grátis",
    exploreFeatures: "Explorar recursos",
    summaryTitle: "Escolha o plano da Hookora que combina com seu fluxo",
    summaryDescription:
      "Comece com Free, depois passe para Creator ou Pro quando precisar de mais produção, mais ferramentas premium e suporte de fluxo mais forte."
  },

  pricingFaq: {
    title: "FAQ de preços",
    subtitle: "Algumas respostas rápidas para perguntas comuns sobre os preços da Hookora.",
    items: {
      freePlan: {
        question: "Posso começar com o plano Free?",
        answer:
          "Sim. O plano Free foi feito para ajudar você a testar a Hookora, explorar o fluxo e ver como as ferramentas se encaixam no seu processo de conteúdo antes de fazer upgrade."
      },
      moveToCreator: {
        question: "Quando devo passar para Creator?",
        answer:
          "Creator é melhor quando você já publica com mais consistência e quer mais gerações, hooks salvos ilimitados e suporte de fluxo mais forte."
      },
      whoIsProFor: {
        question: "Para quem é o Pro?",
        answer:
          "Pro é ideal para criadores sérios e usuários avançados que querem limites maiores, mais flexibilidade e mais valor de fluxo à medida que a Hookora continua crescendo."
      },
      upgradeLater: {
        question: "Posso fazer upgrade depois?",
        answer:
          "Sim. Você pode começar grátis e fazer upgrade quando seu fluxo de conteúdo precisar de mais espaço."
      },
      moreThanOneTool: {
        question: "A Hookora inclui mais de uma ferramenta?",
        answer:
          "Sim. A Hookora inclui hooks, otimização de títulos, legendas, hashtags, reescrita de descrições, roteiros curtos, análise de texto para thumbnails e mais."
      },
      beginnerCreators: {
        question: "A Hookora também foi feita para criadores iniciantes?",
        answer:
          "Sim. A Hookora foi desenhada para ser útil tanto se você está começando quanto se já cria conteúdo com frequência."
      }
    }
  },

  pricingCta: {
    badge: "Comece grátis com a Hookora",
    title: "Escolha um plano da Hookora e construa seu fluxo de conteúdo com mais clareza",
    description:
      "Comece com o plano Free, faça upgrade quando precisar de mais espaço criativo e use a Hookora para tornar a criação de conteúdo mais rápida, mais afiada e mais consistente.",
    startFree: "Criar conta grátis",
    exploreFeatures: "Explorar recursos"
  },

  pricing: {
  heading: "Planos feitos para criadores",
  heroDescription:
    "Escolha o plano Hookora que combina com seu fluxo de trabalho, nível de uso e objetivos como criador.",
  billing: "Faturamento",
  billingPageIntro:
    "Revise seu plano atual, status de cobrança, intervalo de cobrança, Créditos de Recompensa, Saldo da Carteira e controles da assinatura.",
  save17: "Economize 17%",
  twoMonthsFree: "2 meses grátis",
  inactive: "Inativo",
  monthly: "Mensal",
  yearly: "Anual",
  periodMonth: "/mês",
  periodYear: "/ano",
  approx: "≈",
  billedMonthly: "Cobrado mensalmente",
  choose: "Escolher",
  currentPlan: "Plano atual",
  switchToFree: "Mudar para Free",
  updating: "Atualizando...",
  redirecting: "Redirecionando...",
  mostPopular: "Mais popular",
  startFree: "Comece grátis",
  chooseCreator: "Escolher Creator",
  choosePro: "Escolher Pro",
  startWithCoreWorkflow: "Comece com o fluxo principal",
  freeDescription:
    "Ideal para testar a Hookora e se familiarizar com o fluxo principal antes de desbloquear mais poder.",
  creatorDescription:
    "Ideal para criadores individuais que querem ferramentas premium para melhorar empacotamento, reescritas, retenção e qualidade do conteúdo.",
  proDescription:
    "Ideal para criadores sérios e pequenas equipes que querem ferramentas estratégicas mais profundas, mais volume e controle criativo mais forte.",
  coreWritingTools: "Ferramentas principais de escrita",
  premiumWorkflowTools: "Ferramentas premium de fluxo",
  advancedStrategicTools: "Ferramentas estratégicas avançadas",

  plan: "Plano",
  billingStatus: "Status da cobrança",
  billingInterval: "Intervalo de cobrança",
  monthlyGenerations: "Gerações mensais",
  remainingThisMonth: "Restante neste mês",
  savedHooks: "Hooks salvos",
  unlimited: "Ilimitado",
  resetIn: "Reinicia em",
  days: "dias",
  billingPeriodEnd: "Fim do período de cobrança",
  currentUsage: "Uso atual",

  creatorRewards: "Hookora Creator Rewards",
  creatorRewardsDescription:
    "Compartilhe a Hookora, ganhe Reward Credits e continue fortalecendo seu fluxo como criador.",
  rewardCreditsBalance: "Saldo de Reward Credits",
  shareYourReferral: "Compartilhe sua indicação",
  referralCode: "Código de indicação",
  referralLink: "Link de indicação",
  referralRewardNote:
    "Os Reward Credits são aplicados antes do Saldo da Carteira, e o Saldo da Carteira é aplicado antes do pagamento com cartão no checkout.",
  rewardsSummary: "Resumo das recompensas",
  rewardCredits: "Reward Credits",
  walletBalance: "Saldo da Carteira",
  totalReferrals: "Indicações totais",
  verifiedReferrals: "Indicações verificadas",
  paidReferrals: "Indicações pagas",

  fundWalletBalance: "Adicionar saldo à carteira",
  fundWalletDescription:
    "Adicione dinheiro à sua carteira para que cobranças futuras possam usar seu Saldo da Carteira antes de cobrar do seu cartão.",
  enterWalletFundingAmount: "Digite o valor para adicionar à carteira",
  fundWallet: "Adicionar à carteira",
  walletTransactionHistory: "Histórico de transações da carteira",
  noWalletTransactions: "Ainda não há transações na carteira.",
  walletFunding: "Adição à carteira",
  appliedToBilling: "Aplicado à cobrança",
  transaction: "Transação",

  checkoutOrderPrefix: "No checkout, a Hookora aplica",
  then: "depois",
  cardPayment: "Pagamento com cartão",
  checkoutOrderSuffix: "para cobrir o valor restante.",

  billingPreview: "Prévia da cobrança",
  planAmount: "Valor do plano",
  billingCycle: "Ciclo de cobrança",
  rewardCreditsApplied: "Reward Credits aplicados",
  walletBalanceApplied: "Saldo da Carteira aplicado",
  cardDueNow: "Valor no cartão agora",

  secureRecurringCheckoutPrefix: "Checkout recorrente seguro para cobrança",
  secureRecurringCheckoutSuffix: ".",

  billingAndSubscriptions: "Cobrança e assinaturas",
  billingAndSubscriptionsDescription:
    "Escolha seu plano, gerencie upgrades e mantenha seus dados de cobrança alinhados ao seu uso da Hookora.",

    waitlistTitle: "Receba aviso quando a cobrança entrar no ar",
waitlistDescription:
  "Entre na lista de espera para planos pagos e acesso antecipado à cobrança. Isso nos ajuda a saber quais usuários querem Creator ou Pro primeiro.",
emailLabel: "E-mail",
emailPlaceholder: "voce@exemplo.com",
interestedInLabel: "Interesse em",
bothPlans: "Ambos",
notesLabel: "Notas",
notesPlaceholder: "Conte para nós o que você mais gostaria de ver nos planos pagos...",
submitting: "Enviando...",
joinBillingWaitlist: "Entrar na lista de espera da cobrança",
waitlistSuccess: "Você entrou na lista de espera da cobrança com sucesso.",
waitlistError: "Falha ao entrar na lista de espera da cobrança.",

  features: {
    free: {
      generations: "20 gerações por mês",
      savedHooks: "10 hooks salvos",
      coreToolsOnly: "Somente ferramentas principais do Studio",
      coreToolsList:
        "Hooks, Títulos, Legendas, Hashtags, Descrições e Roteiros",
      simpleWorkflow: "Fluxo de copy simples",
      oneUser: "1 usuário"
    },
    creator: {
      generations: "300 gerações por mês",
      savedHooks: "Hooks salvos ilimitados",
      everythingInFree: "Tudo no Free",
      premiumTools: "Ferramentas premium de fluxo em todo o Studio",
      workflowSupport:
        "Reescritas mais fortes, melhor empacotamento, melhor retenção e melhor suporte ao fluxo do criador",
      analytics: "Painel completo de análises",
      fullCreatorWorkflowSupport: "Suporte completo ao fluxo do criador"
    },
    pro: {
      generations: "1.500 gerações por mês",
      everythingInCreator: "Tudo no Creator",
      advancedTools: "Ferramentas estratégicas avançadas do Studio",
      strategySupport:
        "Suporte para conversão de tendências, treinamento de voz da marca e diferenciação",
      futurePresets: "Futuros presets e workspaces",
      prioritySupport: "Suporte prioritário"
    }
  },

  messages: {
    paymentSubmittedPaystack:
      "Seu pagamento foi enviado com sucesso. Seu plano deve ser atualizado em instantes.",
    paymentReceived:
      "Pagamento recebido. Seu plano deve ser atualizado em instantes.",
    walletSubmittedPaystack:
      "Seu pagamento para adicionar saldo à carteira foi enviado com sucesso.",
    walletReceived:
      "Saldo da carteira recebido com sucesso.",
    checkoutCancelled: "O checkout foi cancelado.",
    loginToManageBilling:
      "Faça login para gerenciar seu faturamento e suas recompensas.",
    failedToLoadBilling:
      "Falha ao carregar os detalhes de faturamento.",
    failedToCopy: "Falha ao copiar.",
    alreadyOnFree: "Você já está no plano Free.",
    changedToFree: "Plano alterado para Free com sucesso.",
    failedToChangePlan: "Falha ao alterar o plano.",
    alreadyOnPlan: "Você já está neste plano.",
    checkoutUrlMissing: "O link do checkout está ausente.",
    failedToStartCheckout: "Falha ao iniciar o checkout.",
    invalidWalletAmount: "Digite um valor válido para adicionar à carteira.",
    walletUrlMissing: "O link para adicionar saldo à carteira está ausente.",
    failedToStartWalletFunding:
      "Falha ao iniciar a adição de saldo à carteira."
  }
},

  blog: {
    seoTitle: "Blog da Hookora | Guias para criadores, dicas de fluxo e páginas de ferramentas",
    seoDescription:
      "Explore posts do blog da Hookora e guias públicos para criadores sobre hooks, títulos, legendas, roteiros, ferramentas de conteúdo e um fluxo criativo mais suave.",
    title: "Blog da Hookora",
    subtitle:
      "Guias para criadores, ideias de fluxo e conteúdo de descoberta que mostram como a Hookora ajuda na criação de conteúdo mais forte.",
    heroBadge: "Blog da Hookora",
    heroTitle: "Guias para criadores, dicas de fluxo e conteúdo de descoberta",
    heroDescription:
      "Explore guias práticos criados para ajudar criadores a escrever hooks, legendas, títulos e empacotamento melhores com um fluxo mais suave.",
    startFree: "Comece grátis",
    exploreFeatures: "Explorar recursos",
    readGuide: "Ler guia",
    relatedReading: "Leitura relacionada",
    categories: {
      all: "Tudo",
      hooks: "Hooks",
      contentTools: "Ferramentas de conteúdo",
      captions: "Legendas",
      hookWriting: "Escrita de hooks"
    },

    posts: {
      whatIsHookora: {
        badge: "Hookora",
        title: "O que é Hookora?",
        description:
          "Aprenda o que é a Hookora, como ela funciona e por que a Hookora ajuda criadores a gerar hooks, títulos, legendas, hashtags, roteiros, descrições e texto de thumbnail mais rápido."
      },
      nigerianCreators: {
        badge: "Hookora",
        title: "Hookora para criadores nigerianos",
        description:
          "Aprenda como a Hookora ajuda criadores nigerianos a escrever hooks, títulos, legendas, roteiros e empacotamento de conteúdo mais fortes e mais rápidos com um fluxo mais suave."
      },
      writeHooksFaster: {
        badge: "Hookora",
        title: "Como a Hookora ajuda criadores a escrever hooks mais rápido",
        description:
          "Aprenda como a Hookora ajuda criadores a escrever hooks mais rápido com ângulos mais claros, primeiras linhas mais fortes e um fluxo de conteúdo mais suave."
      },
      aiToolsNigeria: {
        badge: "Fluxo do Criador",
        title: "Ferramentas de IA para criadores nigerianos",
        description:
          "Descubra ferramentas de IA para criadores nigerianos que querem um fluxo de conteúdo mais rápido, hooks melhores, legendas melhores, títulos mais fortes e produção mais consistente."
      },
      bestHookGeneratorNigeria: {
        badge: "Hooks",
        title: "Melhor gerador de hooks para criadores nigerianos",
        description:
          "Veja como criadores nigerianos podem usar a Hookora para criar hooks, primeiras linhas e primeiras impressões mais fortes para vídeos e conteúdo social."
      },
      contentToolsNigeria: {
        badge: "Ferramentas de Conteúdo",
        title: "Ferramentas de conteúdo para criadores na Nigéria",
        description:
          "Explore ferramentas de conteúdo para criadores na Nigéria que querem um fluxo mais claro para hooks, legendas, títulos, roteiros e empacotamento de conteúdo."
      },
      bestCaptionGeneratorNigeria: {
        badge: "Legendas",
        title: "Melhor gerador de legendas para criadores nigerianos",
        description:
          "Aprenda como a Hookora ajuda criadores nigerianos a escrever legendas mais claras e mais rápidas para Instagram, TikTok, Shorts e um fluxo criativo mais amplo."
      },
      betterHooksNigeria: {
        badge: "Escrita de Hooks",
        title: "Como criadores nigerianos podem escrever hooks melhores",
        description:
          "Aprenda maneiras práticas de criadores nigerianos escreverem hooks melhores para Reels, Shorts, TikTok e conteúdo social com primeiras linhas mais fortes."
      }
    },

    articles: {
      whatIsHookora: {
        seoTitle: "O que é Hookora? | Blog da Hookora",
        seoDescription:
          "Aprenda o que é a Hookora, como ela funciona e por que a Hookora ajuda criadores a gerar hooks, títulos, legendas, hashtags, roteiros, descrições e texto de thumbnail mais rápido.",
        schemaDescription:
          "Aprenda o que é a Hookora, como ela funciona e por que ajuda criadores a sair de uma ideia para um conteúdo mais forte mais rápido.",
        title: "O que é Hookora?",
        heroDescription:
          "A Hookora é uma plataforma de fluxo de trabalho com IA para criadores que ajuda a transformar ideias brutas em hooks, títulos, legendas, hashtags, descrições, roteiros e texto de thumbnail mais fortes dentro de um sistema de conteúdo mais limpo.",
        paragraphs: {
          0: "A Hookora foi criada para criadores que entendem que qualidade de conteúdo não é só ter boas ideias. Também é sobre empacotar essas ideias bem. Um hook mais forte, um título mais claro, uma legenda melhor e um roteiro mais afiado podem mudar como o conteúdo é percebido antes mesmo de alguém assistir ou ler tudo."
        },
        sections: {
          whatHookoraHelps: {
            heading: "O que a Hookora ajuda criadores a fazer",
            paragraph1:
              "A Hookora ajuda criadores a se moverem mais rápido quando as palavras tornam tudo mais lento. Em vez de ficar travado no começo do processo, o criador pode gerar opções melhores para as partes do conteúdo que normalmente criam fricção. Isso inclui hooks de abertura, títulos de vídeo, legendas para redes sociais, grupos de hashtags, descrições reescritas, roteiros curtos e direções para texto de thumbnail.",
            paragraph2:
              "O objetivo não é substituir a criatividade. O objetivo é apoiá-la. A Hookora foi desenhada para ajudar criadores a se sentirem mais confiantes quando estão transformando uma ideia em algo pronto para publicar."
          },
          whyHookoraExists: {
            heading: "Por que a Hookora existe",
            paragraph1:
              "Muitos criadores perdem tempo nos mesmos pontos. A ideia existe, mas o hook parece fraco. O título não parece clicável o bastante. A legenda parece sem força. O roteiro demora demais para ganhar estrutura. Esses pequenos atrasos criam muita fricção criativa com o tempo.",
            paragraph2:
              "A Hookora existe para reduzir essa fricção. Em vez de forçar criadores a usarem ferramentas espalhadas e fluxos desconectados, a Hookora reúne o processo de conteúdo em um só lugar que parece mais claro e mais fácil de usar."
          },
          whoHookoraIsFor: {
            heading: "Para quem é a Hookora",
            paragraph1:
              "A Hookora é para criadores, marcas pessoais, profissionais de marketing e usuários de redes sociais que querem um empacotamento de conteúdo mais forte sem perder tempo demais com formulação. Ela é útil para criadores que fazem vídeos para YouTube, Shorts, posts no Instagram, Reels, TikToks e outras formas de conteúdo social.",
            paragraph2:
              "Ela também é útil para criadores em crescimento em lugares como a Nigéria, onde mais pessoas estão construindo marcas online, negócios guiados por audiência e fontes de renda criativa, e precisam de ferramentas que ajudem a avançar mais rápido."
          },
          whatMakesHookoraDifferent: {
            heading: "O que torna a Hookora diferente"
          },
          inSimpleTerms: {
            heading: "Em termos simples",
            paragraph:
              "A Hookora é uma plataforma de fluxo de conteúdo que ajuda criadores a ir da ideia para uma saída mais forte mais rápido. Se alguém quer hooks, títulos, legendas, roteiros e empacotamento melhores sem tornar o processo mais pesado, a Hookora foi feita para isso."
          }
        },
        list: {
          0: "Ela conecta várias tarefas de conteúdo dentro de um só fluxo em vez de tratar cada tarefa como uma ferramenta aleatória separada.",
          1: "Ela ajuda criadores a manterem o impulso quando ficam travados nas palavras.",
          2: "Ela foca em saída prática para criadores, não apenas em geração genérica de texto por IA.",
          3: "Ela foi desenhada para tornar a criação de conteúdo mais suave, mais clara e mais repetível."
        },
        ctaTitle: "Experimente a Hookora e construa seu conteúdo mais rápido com um fluxo mais suave",
        ctaDescription:
          "Se você quer uma forma mais limpa de criar hooks, títulos, legendas, roteiros e mais, a Hookora oferece um ponto de partida prático."
      },

      nigerianCreators: {
        seoTitle: "Hookora para criadores nigerianos | Blog da Hookora",
        seoDescription:
          "Aprenda como a Hookora ajuda criadores nigerianos a escrever hooks, títulos, legendas, roteiros e empacotamento de conteúdo mais fortes e mais rápidos com um fluxo mais suave.",
        schemaDescription:
          "Aprenda como a Hookora ajuda criadores nigerianos a sair de uma ideia para um empacotamento de conteúdo mais forte mais rápido.",
        title: "Hookora para criadores nigerianos",
        heroDescription:
          "A Hookora ajuda criadores nigerianos a transformar ideias de conteúdo brutas em hooks, títulos, legendas, roteiros, hashtags, descrições e textos de thumbnail mais fortes sem fazer o fluxo parecer mais pesado.",
        sections: {
          intro1:
            "Mais criadores na Nigéria estão construindo audiências, marcas pessoais, negócios e páginas de mídia online. Mas uma coisa ainda desacelera muitos deles: transformar boas ideias em conteúdo que pareça forte o bastante para publicar. A mensagem pode estar clara na sua cabeça, mas o hook é fraco, a legenda parece sem força ou o título não parece afiado o bastante.",
          intro2:
            "É aí que a Hookora entra. A Hookora foi feita para tornar a criação de conteúdo mais clara e mais rápida, especialmente para criadores que precisam de ajuda com as palavras que moldam a primeira impressão.",
          heading1: "Por que a Hookora importa para criadores nigerianos",
          paragraph1:
            "Criadores nigerianos trabalham em muitos formatos ao mesmo tempo. Alguns fazem YouTube Shorts, outros estão construindo páginas no Instagram, alguns postam no TikTok, e outros estão fazendo crescer marcas pessoais com conteúdo educativo ou de entretenimento. Em todos esses casos, o empacotamento do conteúdo importa.",
          paragraph2:
            "Uma primeira linha mais forte pode ajudar um espectador a parar de rolar. Um título mais claro pode tornar uma ideia de vídeo mais fácil de entender. Uma legenda melhor pode fazer um post parecer mais intencional. Um roteiro curto mais estruturado pode tornar a gravação mais fácil. A Hookora ajuda criadores a passarem por essas etapas com menos fricção.",
          heading2: "Com o que a Hookora ajuda",
          list: {
            0: "Gerar hooks mais fortes para vídeos sociais e conteúdo de criador",
            1: "Melhorar ideias de títulos para YouTube para que pareçam mais claros e mais clicáveis",
            2: "Escrever legendas mais rápido para Instagram, TikTok e Shorts",
            3: "Criar grupos de hashtags que pareçam mais relevantes para o conteúdo",
            4: "Reescrever descrições para que a mensagem fique mais limpa",
            5: "Estruturar roteiros curtos com hook e fluxo melhores",
            6: "Melhorar texto de thumbnail para uma primeira impressão mais forte"
          },
          heading3: "Um fluxo mais limpo para criadores que postam com frequência",
          paragraph3:
            "Uma das partes mais difíceis da criação de conteúdo nem sempre é a ideia em si. Às vezes é a repetição. Quando você posta com frequência, a pressão para continuar encontrando hooks, legendas e títulos melhores pode ficar cansativa. A Hookora reduz essa pressão ajudando você a passar mais rápido de uma ideia bruta para uma saída utilizável.",
          paragraph4:
            "Isso é especialmente útil para criadores nigerianos que tentam manter consistência enquanto equilibram conteúdo com trabalho, escola, negócio ou outras responsabilidades. Um fluxo mais suave pode fazer a consistência parecer mais realista.",
          heading4: "A Hookora não é só para um tipo de criador",
          paragraph5:
            "A Hookora pode ajudar criadores de entretenimento, educação, negócios, marcas pessoais, profissionais de marketing e social media managers. Seja o seu conteúdo divertido, informativo, persuasivo ou voltado para comunidade, a forma como você empacota a ideia ainda importa. A Hookora ajuda a fortalecer essa camada.",
          heading5: "Em termos simples",
          paragraph6:
            "A Hookora ajuda criadores nigerianos a criarem com mais velocidade, mais confiança e menos fricção. Se você já tem ideias, mas precisa de ajuda para transformá-las em hooks, títulos, legendas e roteiros mais fortes, a Hookora foi feita para apoiar esse processo."
        },
        cta: {
          badge: "Comece grátis com a Hookora",
          title: "Use a Hookora para construir conteúdo mais forte mais rápido",
          description:
            "Se você quer um fluxo mais limpo para hooks, títulos, legendas, roteiros e mais, a Hookora oferece um ponto de partida prático.",
          primaryLabel: "Criar conta grátis",
          secondaryLabel: "Explorar recursos"
        }
      },

      writeHooksFaster: {
        seoTitle: "Como a Hookora ajuda criadores a escrever hooks mais rápido | Blog da Hookora",
        seoDescription:
          "Aprenda como a Hookora ajuda criadores a escrever hooks mais rápido com ângulos mais claros, primeiras linhas mais fortes e um fluxo de conteúdo mais suave.",
        schemaDescription:
          "Aprenda como a Hookora ajuda criadores a escrever hooks mais rápido com estrutura melhor e opções de abertura mais fortes.",
        title: "Como a Hookora ajuda criadores a escrever hooks mais rápido",
        heroDescription:
          "A Hookora ajuda criadores a irem de ideias brutas para primeiras linhas mais fortes mais rápido, para que o começo do processo de conteúdo pareça menos frustrante e mais útil.",
        paragraphs: {
          0: "Escrever um hook parece simples até você realmente tentar fazer isso todos os dias. Muitos criadores conhecem o tema, conhecem a mensagem e até sabem o valor que querem entregar, mas ainda ficam travados no começo. A primeira linha não parece forte o suficiente. A abertura parece simples demais. O hook soa como algo que as pessoas deixariam passar.",
          1: "A Hookora ajuda a reduzir esse atraso. Em vez de forçar criadores a ficarem diante de uma tela em branco tentando inventar a abertura perfeita do zero, a Hookora ajuda a gerar direções mais fortes mais rápido."
        },
        list: {
          0: "Ela ajuda a transformar ideias brutas em ângulos de abertura mais claros.",
          1: "Ela dá aos criadores pontos de partida mais fortes em vez de obrigá-los a começar do zero.",
          2: "Ela reduz a hesitação quando várias direções de hook são possíveis.",
          3: "Ela ajuda criadores a testarem abordagens diferentes em torno do mesmo tema.",
          4: "Ela torna mais fácil continuar criando sem ficar travado por muito tempo na primeira linha."
        },
        sections: {
          whyHooksSlowCreators: {
            heading: "Por que hooks desaceleram criadores",
            paragraph1:
              "Hooks carregam muita pressão. Eles moldam a primeira impressão. Influenciam se alguém para de rolar, clica ou continua assistindo. Por causa disso, criadores costumam pensar demais neles. Uma ideia simples de vídeo pode demorar demais para começar porque a linha de abertura ainda parece fraca.",
            paragraph2:
              "Esse atraso afeta o resto do fluxo. Quando o hook está pouco claro, o roteiro parece mais lento. O título fica mais difícil de moldar. A legenda fica menos focada. A Hookora ajuda resolvendo essa primeira parte mais rápido."
          },
          howHookoraMakesItFaster: {
            heading: "Como a Hookora torna a escrita de hooks mais rápida"
          },
          fromIdeaToFirstLine: {
            heading: "Da ideia para uma primeira linha mais forte",
            paragraph1:
              "Um criador pode saber que quer falar sobre erros com dinheiro, dicas de conteúdo, relacionamentos, lições de negócios ou comentários sociais, mas a melhor primeira linha nem sempre é óbvia. A Hookora ajuda a moldar esse tema em direções de hook mais fortes que parecem mais estruturadas, mais guiadas por curiosidade ou mais diretas, dependendo do que a ideia precisa.",
            paragraph2:
              "Essa velocidade importa porque a criação de conteúdo funciona melhor quando o impulso não é interrompido o tempo todo. Quanto mais rápido um criador consegue passar da ideia para um hook utilizável, mais fácil fica construir o resto do conteúdo."
          },
          usefulForAllCreators: {
            heading: "Útil para criadores de short-form e long-form",
            paragraph:
              "A Hookora é especialmente útil para criadores de short-form porque hooks importam muito em Reels, Shorts e vídeos do TikTok. Mas ela também ajuda criadores de long-form, porque ângulos de abertura melhores podem melhorar títulos, introduções e a apresentação geral de um vídeo ou post."
          },
          whatThisMeans: {
            heading: "O que isso significa na prática",
            paragraph:
              "Em vez de gastar tempo demais tentando formular uma única primeira linha, criadores podem usar a Hookora para gerar melhores pontos de partida, escolher um ângulo mais forte e entrar no resto do fluxo mais rápido. Isso significa menos fricção no início e mais espaço para focar na qualidade do conteúdo completo."
          },
          inSimpleTerms: {
            heading: "Em termos simples",
            paragraph:
              "A Hookora ajuda criadores a escrever hooks mais rápido tornando a parte de abertura da criação de conteúdo mais clara, menos estressante e mais fácil de atravessar. Se escrever primeiras linhas é a parte em que seu processo costuma desacelerar, a Hookora foi feita para isso."
          }
        },
        relatedLinks: {
          first: "O que é Hookora?",
          second: "Hookora para criadores nigerianos"
        },
        ctaTitle: "Use a Hookora para criar hooks mais fortes com menos fricção",
        ctaDescription:
          "Se você quer uma forma mais rápida de moldar primeiras linhas melhores, ângulos mais fortes e um fluxo de conteúdo mais suave, a Hookora oferece um ponto de partida prático.",
        cta: {
          badge: "Comece grátis com a Hookora",
          title: "Use a Hookora para criar hooks mais fortes com menos fricção",
          description:
            "Se você quer uma forma mais rápida de moldar primeiras linhas melhores, ângulos mais fortes e um fluxo de conteúdo mais suave, a Hookora oferece um ponto de partida prático.",
          primaryLabel: "Criar conta grátis",
          secondaryLabel: "Explorar recursos"
        }
      },

      aiToolsNigeria: {
        seoTitle: "Ferramentas de IA para criadores nigerianos | Hookora",
        seoDescription:
          "Descubra ferramentas de IA para criadores nigerianos que querem um fluxo de conteúdo mais rápido, hooks melhores, legendas melhores, títulos mais fortes e produção de conteúdo mais consistente.",
        title: "Ferramentas de IA para criadores nigerianos",
        heroBadge: "Guia Hookora • Ferramentas de IA para criadores nigerianos",
        heroTitle: "Ferramentas de IA para criadores nigerianos que querem um fluxo de conteúdo mais rápido e mais claro",
        heroDescription:
          "Criadores nigerianos estão construindo em um espaço rápido e competitivo onde a atenção se move depressa e a consistência importa. A Hookora ajuda a reduzir a fricção de escrita por trás de hooks, títulos, legendas, roteiros, hashtags e empacotamento de conteúdo para que criadores avancem mais rápido com mais confiança.",
        highlights: {
          0: "Fluxo de conteúdo mais rápido",
          1: "Hooks e títulos melhores",
          2: "Menos fricção de escrita",
          3: "Feita para criadores modernos"
        },
        previewTitle: "O que ferramentas de IA ajudam criadores nigerianos a fazer",
        previewItems: {
          0: {
            label: "Velocidade",
            text: "Passe de uma ideia bruta para uma saída utilizável mais rápido sem travar nas palavras."
          },
          1: {
            label: "Consistência",
            text: "Continue postando com mais estrutura mesmo quando a criatividade parecer lenta."
          },
          2: {
            label: "Fluxo",
            text: "Conecte hooks, legendas, roteiros e títulos dentro de um sistema mais suave."
          }
        },
        eyebrow: "Por que isso importa",
        benefitsTitle:
          "Criadores na Nigéria precisam de ferramentas que tornem a criação de conteúdo mais leve, mais afiada e mais fácil de repetir",
        benefitsDescription:
          "Muitos criadores não estão sofrendo por falta de ideias. Eles estão sofrendo porque transformar ideias em saídas polidas leva tempo demais. A Hookora ajuda criadores a fechar essa lacuna mais rápido.",
        benefits: {
          0: {
            title: "Reduza a fricção de escrita em todo o seu fluxo",
            text: "Muitos criadores sabem o que querem dizer, mas ainda perdem tempo moldando isso em conteúdo utilizável. A Hookora ajuda a tornar esse processo mais fácil."
          },
          1: {
            title: "Crie primeiras impressões mais fortes mais rápido",
            text: "Hooks, títulos e texto de thumbnail afetam se as pessoas param e prestam atenção. A Hookora ajuda a fortalecer esses primeiros momentos de contato."
          },
          2: {
            title: "Apoie uma criação de conteúdo mais consistente",
            text: "Consistência fica mais fácil quando seu fluxo parece repetível. A Hookora ajuda criadores a produzirem mais rápido sem fazer a saída parecer aleatória."
          },
          3: {
            title: "Útil para criadores de short-form e social-first",
            text: "Seja criando skits, vídeos educativos, comentários, lifestyle ou posts de marca pessoal, a Hookora ajuda na camada de escrita por trás do empacotamento."
          },
          4: {
            title: "Ajude criadores solo a se moverem como uma pequena equipe",
            text: "Um único criador muitas vezes precisa pensar em hooks, títulos, legendas, roteiros e publicação ao mesmo tempo. A Hookora ajuda a simplificar essa carga."
          },
          5: {
            title: "Feita para criadores que querem uma saída mais clara",
            text: "O objetivo não é apenas mais conteúdo. O objetivo é conteúdo que pareça mais claro, mais forte e mais fácil de publicar."
          }
        },
        faqTitle: "FAQ sobre ferramentas de IA para criadores nigerianos",
        faqIntro:
          "Aqui estão algumas perguntas comuns sobre ferramentas de IA para criadores nigerianos e onde a Hookora se encaixa.",
        faqs: {
          0: {
            question: "O que são ferramentas de IA para criadores nigerianos?",
            answer:
              "Ferramentas de IA para criadores nigerianos são plataformas que ajudam a acelerar trabalhos de conteúdo como hooks, legendas, roteiros, títulos e planejamento. Elas são úteis para criadores que querem manter consistência sem fazer tudo manualmente."
          },
          1: {
            question: "Por que criadores nigerianos precisam de ferramentas de fluxo com IA?",
            answer:
              "Muitos criadores na Nigéria estão construindo conteúdo com tempo limitado, apoio limitado e alta pressão para permanecer visíveis online. Ferramentas de fluxo com IA ajudam a reduzir a fricção de escrita e tornam a criação mais rápida e mais repetível."
          },
          2: {
            question: "A Hookora é só para criadores nigerianos?",
            answer:
              "Não. A Hookora foi feita para criadores de forma ampla, mas também é útil para criadores nigerianos que querem um fluxo mais suave para hooks, títulos, legendas, hashtags, roteiros e texto de thumbnail."
          },
          3: {
            question: "Que tipo de criadores podem usar a Hookora?",
            answer:
              "A Hookora é útil para YouTubers, criadores do TikTok, criadores do Instagram, criadores de skits, marcas pessoais, profissionais de marketing, social media managers e criadores de pequenos negócios."
          },
          4: {
            question: "A Hookora ajuda com mais do que hooks?",
            answer:
              "Sim. A Hookora inclui ferramentas para hooks virais, títulos para YouTube, legendas, hashtags, reescrita de descrições, roteiros curtos e análise de texto de thumbnail."
          },
          5: {
            question: "Iniciantes podem usar a Hookora?",
            answer:
              "Sim. A Hookora foi desenhada para ser útil tanto se você está começando quanto se já cria conteúdo com consistência."
          }
        },
        cta: {
  badge: "Comece grátis com a Hookora",
  title: "Use a Hookora para construir um fluxo de conteúdo mais rápido como criador nigeriano",
  description:
    "Se você quer uma forma mais suave de criar hooks, títulos, legendas, roteiros, hashtags e empacotamento de conteúdo, a Hookora oferece um ponto de partida prático.",
  primaryLabel: "Criar conta grátis",
  secondaryLabel: "Ver recursos"
},
        ctaTitle: "Use a Hookora para construir um fluxo de conteúdo mais rápido como criador nigeriano",
        ctaDescription:
          "Se você quer uma forma mais suave de criar hooks, títulos, legendas, roteiros, hashtags e empacotamento de conteúdo, a Hookora oferece um ponto de partida prático."
      },

      bestHookGeneratorNigeria: {
        seoTitle: "Melhor gerador de hooks para criadores nigerianos | Hookora",
        seoDescription:
          "Está procurando o melhor gerador de hooks para criadores nigerianos? Use a Hookora para criar hooks, primeiras linhas e ideias de conteúdo mais chamativas mais rápido.",
        title: "Melhor gerador de hooks para criadores nigerianos",
        heroBadge: "Guia Hookora • Melhor gerador de hooks para criadores nigerianos",
        heroTitle: "O melhor gerador de hooks para criadores nigerianos que querem atenção mais forte desde a primeira linha",
        heroDescription:
          "Um hook forte pode mudar como as pessoas reagem ao seu conteúdo desde o primeiro segundo. A Hookora ajuda criadores nigerianos a gerarem ideias de hooks mais afiadas mais rápido para criarem aberturas mais fortes para vídeos, legendas e conteúdo social sem perder tempo olhando para uma página em branco.",
        highlights: [
          "Primeiras linhas mais fortes",
          "Ideação de hooks mais rápida",
          "Melhor atenção ao conteúdo",
          "Feita para o fluxo do criador"
        ],
        primaryCtaLabel: "Comece grátis",
        secondaryCtaLabel: "Testar recursos de hooks",
        previewTitle: "O que um gerador de hooks deve ajudar você a fazer",
        previewItems: {
          attention: {
            label: "Atenção",
            text: "Crie primeiras linhas que façam as pessoas parar mais rápido e prestar atenção à sua ideia."
          },
          clarity: {
            label: "Clareza",
            text: "Transforme ângulos de conteúdo brutos em direções de hook mais afiadas e mais fáceis de usar."
          },
          options: {
            label: "Opções",
            text: "Explore múltiplos ângulos de hook a partir de um tema em vez de ficar travado em uma abertura fraca."
          }
        },
        benefitsEyebrow: "Por que criadores usam isso",
        benefitsTitle:
          "O melhor gerador de hooks para criadores nigerianos deve ajudar o conteúdo a parecer mais forte antes mesmo de a audiência ver a mensagem completa",
        benefitsDescription:
          "Hooks não são só um detalhe de escrita. Eles moldam a primeira impressão, a curiosidade e se as pessoas continuam assistindo. A Hookora ajuda criadores a chegarem a primeiras linhas mais fortes mais rápido e com menos fricção.",
        benefits: {
          one: {
            title: "Comece o conteúdo mais rápido a partir de uma ideia bruta",
            text: "Você não precisa de um conceito perfeito antes de começar. A Hookora ajuda a transformar pensamentos brutos em direções de abertura mais fortes rapidamente."
          },
          two: {
            title: "Crie múltiplos ângulos de hook a partir de um tema",
            text: "Uma única ideia pode ser enquadrada de maneiras diferentes. A Hookora ajuda criadores a explorarem essas opções sem desperdiçar tempo demais reescrevendo tudo manualmente."
          },
          three: {
            title: "Torne o empacotamento short-form mais forte",
            text: "Para criadores que fazem Reels, Shorts, vídeos do TikTok e legendas curtas, uma abertura forte importa ainda mais porque a atenção se move rápido."
          },
          four: {
            title: "Reduza a hesitação criativa",
            text: "Muitos criadores adiam a postagem porque estão travados em como começar. A Hookora ajuda a remover parte dessa hesitação e mantém o fluxo em movimento."
          },
          five: {
            title: "Apoie um empacotamento de conteúdo mais amplo",
            text: "Um hook funciona melhor quando se conecta com título, legenda, roteiro e thumbnail. A Hookora apoia esse fluxo criativo mais completo."
          },
          six: {
            title: "Útil para criadores construindo consistência",
            text: "Seja você alguém que já posta com frequência ou está tentando se tornar mais consistente, um suporte melhor para hooks torna a publicação mais fácil."
          }
        },
        faqTitle: "FAQ sobre o melhor gerador de hooks para criadores nigerianos",
        faqIntro:
          "Aqui estão algumas perguntas comuns sobre geradores de hooks para criadores nigerianos e como a Hookora ajuda.",
        faqs: {
          one: {
            question: "Qual é o melhor gerador de hooks para criadores nigerianos?",
            answer:
              "O melhor gerador de hooks para criadores nigerianos é aquele que ajuda a transformar ideias brutas em primeiras linhas mais fortes de forma rápida e clara. A Hookora foi desenhada para ajudar criadores a construírem hooks, títulos, legendas e roteiros mais fortes dentro de um único fluxo."
          },
          two: {
            question: "Por que hooks importam tanto para criadores nigerianos?",
            answer:
              "Hooks importam porque a primeira linha muitas vezes decide se alguém continua assistindo, lendo ou rolando. Em plataformas rápidas, hooks mais fortes ajudam criadores a conseguirem atenção mais rápido."
          },
          three: {
            question: "A Hookora pode ajudar com Reels, Shorts e TikTok?",
            answer:
              "Sim. A Hookora é útil para criadores de short-form que querem aberturas mais fortes para YouTube Shorts, Instagram Reels, vídeos do TikTok, legendas e conteúdo social."
          },
          four: {
            question: "A Hookora é apenas um gerador de hooks?",
            answer:
              "Não. A Hookora também inclui ferramentas para títulos do YouTube, legendas, hashtags, reescrita de descrições, roteiros curtos e análise de texto de thumbnail."
          },
          five: {
            question: "Criadores iniciantes na Nigéria podem usar a Hookora?",
            answer:
              "Sim. A Hookora é útil para iniciantes e também para criadores ativos porque ajuda a reduzir a pressão da página em branco."
          },
          six: {
            question: "A Hookora me ajuda a criar múltiplos ângulos de hook?",
            answer:
              "Sim. Um dos principais benefícios é explorar múltiplas direções de hook a partir de um tema para que você possa escolher uma formulação mais forte sem pensar demais."
          }
        },
        cta: {
          badge: "Comece grátis com a Hookora",
          title: "Use a Hookora como seu gerador de hooks para aberturas de conteúdo mais fortes",
          description:
            "Se você quer hooks melhores, primeiras impressões melhores e um fluxo criativo mais suave, a Hookora oferece um ponto de partida prático.",
          primaryLabel: "Criar conta grátis",
          secondaryLabel: "Explorar gerador de hooks"
        }
      },

      contentToolsNigeria: {
        seoTitle: "Ferramentas de conteúdo para criadores na Nigéria | Hookora",
        seoDescription:
          "Descubra ferramentas de conteúdo para criadores na Nigéria que querem um fluxo mais rápido, hooks melhores, legendas melhores, títulos mais fortes e criação de conteúdo mais fácil.",
        title: "Ferramentas de conteúdo para criadores na Nigéria",
        heroBadge: "Guia Hookora • Ferramentas de conteúdo para criadores na Nigéria",
        heroTitle: "Ferramentas de conteúdo para criadores na Nigéria que querem um fluxo mais rápido e mais organizado",
        heroDescription:
          "Criadores na Nigéria muitas vezes precisam fazer tudo sozinhos, desde ideias de conteúdo até legendas, títulos, hooks, roteiros e publicação. A Hookora ajuda a simplificar esse processo tornando a parte de escrita e empacotamento da criação mais rápida, mais clara e mais fácil de repetir.",
        highlights: {
          0: "Criação de conteúdo mais rápida",
          1: "Fluxo mais claro",
          2: "Empacotamento de conteúdo mais forte",
          3: "Feita para criadores modernos"
        },
        primaryCtaLabel: "Comece grátis",
        secondaryCtaLabel: "Explorar recursos",
        previewTitle: "O que ferramentas de conteúdo devem ajudar criadores a fazer",
        previewItems: {
          0: {
            label: "Organizar",
            text: "Reunir hooks, legendas, roteiros e títulos em um fluxo mais limpo."
          },
          1: {
            label: "Acelerar",
            text: "Reduzir o tempo necessário para passar da ideia para conteúdo utilizável."
          },
          2: {
            label: "Melhorar a saída",
            text: "Fazer o conteúdo parecer mais afiado e mais fácil de publicar com consistência."
          }
        },
        benefitsEyebrow: "Por que criadores usam isso",
        benefitsTitle: "Ferramentas de conteúdo devem fazer a criação parecer mais leve, não mais espalhada",
        benefitsDescription:
          "As melhores ferramentas para criadores não apenas adicionam mais recursos. Elas removem fricção, tornam a saída mais fácil de moldar e ajudam criadores a se moverem mais rápido sem perder clareza.",
        benefits: {
          0: {
            title: "Reduza a pressão de fazer tudo manualmente",
            text: "Muitos criadores lidam sozinhos com ideação, escrita, empacotamento e publicação. A Hookora ajuda a reduzir parte dessa carga tornando a escrita de conteúdo mais rápida."
          },
          1: {
            title: "Apoie hooks, legendas, títulos e roteiros em um só fluxo",
            text: "Em vez de pular entre ferramentas desconectadas, criadores podem construir saídas mais fortes a partir de um fluxo mais suave."
          },
          2: {
            title: "Ajude criadores a manterem mais consistência",
            text: "Consistência fica mais fácil quando o sistema por trás do seu conteúdo é mais simples e mais fácil de repetir."
          },
          3: {
            title: "Útil em diferentes tipos de criador",
            text: "Seja criando vídeos educativos, skits, talking-head, lifestyle ou conteúdo de marca, apoio mais claro de fluxo faz diferença real."
          },
          4: {
            title: "Torne o empacotamento de conteúdo mais forte",
            text: "Não é só sobre a ideia do conteúdo. O empacotamento também importa. A Hookora ajuda na camada de escrita por trás desse empacotamento."
          },
          5: {
            title: "Dê mais impulso a criadores solo",
            text: "Um fluxo de conteúdo mais suave pode ajudar um criador solo a se mover mais rápido e criar com mais confiança ao longo do tempo."
          }
        },
        faqTitle: "FAQ sobre ferramentas de conteúdo para criadores na Nigéria",
        faqIntro:
          "Aqui estão algumas perguntas comuns sobre ferramentas de conteúdo para criadores na Nigéria e como a Hookora se encaixa nesse fluxo.",
        faqs: {
          0: {
            question: "O que são ferramentas de conteúdo para criadores na Nigéria?",
            answer:
              "Ferramentas de conteúdo para criadores na Nigéria são plataformas que ajudam a tornar a criação de conteúdo mais fácil, mais rápida e mais organizada. Elas podem ajudar com hooks, títulos, legendas, roteiros, hashtags e empacotamento de conteúdo."
          },
          1: {
            question: "Por que criadores na Nigéria precisam de ferramentas de fluxo de conteúdo?",
            answer:
              "Muitos criadores estão produzindo conteúdo consistentemente sem grandes equipes ou muito tempo extra. Ferramentas de fluxo ajudam a reduzir o estresse, acelerar a escrita e tornar a produção mais fácil de repetir."
          },
          2: {
            question: "Que tipo de criadores podem usar a Hookora?",
            answer:
              "A Hookora é útil para YouTubers, criadores do TikTok, criadores do Instagram, criadores de skits, criadores de lifestyle, educadores, marcas pessoais, profissionais de marketing e social media managers."
          },
          3: {
            question: "A Hookora substitui ferramentas de edição?",
            answer:
              "Não. A Hookora foca no lado de escrita e empacotamento da criação. Ela ajuda com hooks, títulos, legendas, roteiros, hashtags, descrições e texto de thumbnail."
          },
          4: {
            question: "A Hookora pode ajudar criadores que postam com frequência?",
            answer:
              "Sim. A Hookora é especialmente útil para criadores que precisam de uma forma mais suave e mais rápida de continuar produzindo conteúdo com consistência."
          },
          5: {
            question: "A Hookora é útil para criadores iniciantes na Nigéria?",
            answer:
              "Sim. Ela é útil tanto para iniciantes quanto para criadores ativos porque ajuda a remover a pressão da página em branco e apoia um fluxo de conteúdo mais claro."
          }
        },
        ctaTitle: "Use a Hookora como parte do seu fluxo de conteúdo na Nigéria",
        ctaDescription:
          "Se você quer uma forma mais clara de criar hooks, legendas, títulos, roteiros, hashtags e empacotamento de conteúdo, a Hookora oferece um ponto de partida prático.",
        cta: {
          badge: "Comece grátis com a Hookora",
          title: "Use a Hookora como parte do seu fluxo de conteúdo na Nigéria",
          description:
            "Se você quer uma forma mais clara de criar hooks, legendas, títulos, roteiros, hashtags e empacotamento de conteúdo, a Hookora oferece um ponto de partida prático.",
          primaryLabel: "Criar conta grátis",
          secondaryLabel: "Ver recursos"
        }
      },

      bestCaptionGeneratorNigeria: {
  seoTitle: "Melhor gerador de legendas para criadores nigerianos | Hookora",
  seoDescription:
    "Está procurando o melhor gerador de legendas para criadores nigerianos? Use a Hookora para escrever legendas mais claras, mais fortes e mais rápidas para conteúdo social e fluxo criativo.",
  title: "Melhor gerador de legendas para criadores nigerianos",
  heroBadge: "Guia Hookora • Melhor gerador de legendas para criadores nigerianos",
  heroTitle:
    "O melhor gerador de legendas para criadores nigerianos que querem legendas sociais mais rápidas e mais claras",
  heroDescription:
    "Escrever legendas sempre que você posta pode desacelerar seu fluxo, especialmente quando você já conhece a ideia do conteúdo, mas não sabe como formulá-la bem. A Hookora ajuda criadores nigerianos a gerar direções de legenda mais claras mais rápido para que postar em redes sociais pareça mais suave e mais consistente.",
  highlights: {
    0: "Escreva legendas mais rápido",
    1: "Melhore a clareza da mensagem",
    2: "Apoie o fluxo social",
    3: "Feita para criadores"
  },
  primaryCtaLabel: "Comece grátis",
  secondaryCtaLabel: "Explorar ferramenta de legendas",
  previewTitle: "O que um gerador de legendas deve ajudar você a fazer",
  previewItems: {
    0: {
      label: "Velocidade",
      text: "Passe da ideia para uma legenda utilizável mais rápido sem travar nas palavras."
    },
    1: {
      label: "Mensagem",
      text: "Molde legendas que pareçam mais claras, mais intencionais e mais fáceis de publicar."
    },
    2: {
      label: "Consistência",
      text: "Continue postando com um fluxo de legendas mais suave em diferentes plataformas."
    }
  },
  eyebrow: "Por que criadores usam isso",
  benefitsTitle:
    "O melhor gerador de legendas para criadores nigerianos deve ajudar o conteúdo a parecer mais completo sem fazer as legendas parecerem genéricas",
  benefitsDescription:
    "Legendas fazem parte de como sua audiência entende o que você quer dizer. A Hookora ajuda criadores a moldarem essa camada escrita mais rápido para que o empacotamento do conteúdo pareça mais claro e mais fácil de administrar.",
  benefits: {
    0: {
      title: "Escreva legendas mais rápido a partir de uma ideia de conteúdo",
      text: "Você não precisa continuar reescrevendo tudo do zero toda vez que posta. A Hookora ajuda criadores a passarem de um pensamento bruto para uma legenda utilizável mais rápido."
    },
    1: {
      title: "Melhore a clareza em posts sociais",
      text: "Uma legenda funciona melhor quando a mensagem parece fácil de entender. A Hookora ajuda a apoiar essa clareza sem deixar a escrita rígida."
    },
    2: {
      title: "Apoie diferentes plataformas de criadores",
      text: "Seja você alguém que posta no Instagram, TikTok, YouTube Shorts ou outras plataformas, um suporte melhor para legendas ajuda o fluxo a parecer mais suave."
    },
    3: {
      title: "Reduza a fricção repetitiva de postagem",
      text: "Escrever legendas pode se tornar repetitivo quando você cria com frequência. A Hookora ajuda a tornar essa parte do fluxo mais fácil."
    },
    4: {
      title: "Conecte legendas ao resto do seu sistema de conteúdo",
      text: "Legendas funcionam melhor quando combinam com seu hook, título, roteiro e mensagem. A Hookora apoia esse fluxo criativo mais amplo."
    },
    5: {
      title: "Útil para iniciantes e criadores ativos",
      text: "Seja você alguém que ainda está aprendendo a empacotar conteúdo ou já posta regularmente, um suporte melhor para legendas ajuda você a criar com mais confiança."
    }
  },
  faqTitle: "FAQ sobre o melhor gerador de legendas para criadores nigerianos",
  faqIntro:
    "Aqui estão algumas perguntas comuns sobre geradores de legendas para criadores nigerianos e como a Hookora ajuda.",
  faqs: {
    0: {
      question: "Qual é o melhor gerador de legendas para criadores nigerianos?",
      answer:
        "O melhor gerador de legendas para criadores nigerianos é aquele que ajuda a transformar ideias de conteúdo em legendas mais claras e mais utilizáveis rapidamente. A Hookora ajuda criadores a escrever legendas mais rápido enquanto também apoia hooks, títulos, roteiros, hashtags e empacotamento de conteúdo."
    },
    1: {
      question: "Por que legendas importam para criadores nigerianos?",
      answer:
        "Legendas ajudam a explicar sua mensagem, acrescentam contexto e fazem o conteúdo parecer mais completo. Legendas mais fortes podem ajudar criadores a se comunicarem com mais clareza e postarem com mais confiança."
    },
    2: {
      question: "A Hookora pode ajudar com legendas para Instagram e TikTok?",
      answer:
        "Sim. A Hookora é útil para criadores que fazem conteúdo para Instagram, TikTok, YouTube Shorts e outras plataformas sociais onde as legendas apoiam o post completo."
    },
    3: {
      question: "A Hookora só gera legendas?",
      answer:
        "Não. A Hookora também inclui ferramentas para hooks virais, títulos do YouTube, hashtags, reescrita de descrições, roteiros curtos e análise de texto de thumbnail."
    },
    4: {
      question: "Criadores iniciantes podem usar a Hookora para legendas?",
      answer:
        "Sim. A Hookora é útil tanto se você está começando quanto se já posta com consistência. Ela ajuda a reduzir a fricção da escrita e torna a criação de legendas mais fácil."
    },
    5: {
      question: "A Hookora pode me ajudar a escrever legendas mais rápido a partir de uma ideia?",
      answer:
        "Sim. Um dos principais benefícios é usar um tema ou ângulo de conteúdo para gerar direções de legenda mais claras mais rapidamente."
    }
  },
  ctaTitle:
    "Use a Hookora para escrever legendas mais fortes como criador nigeriano",
  ctaDescription:
    "Se você quer escrever legendas mais rápido, apoiar melhor sua mensagem e ter um fluxo de conteúdo mais suave, a Hookora oferece um ponto de partida prático.",
  cta: {
    badge: "Comece grátis com a Hookora",
    title:
      "Use a Hookora para escrever legendas mais fortes como criador nigeriano",
    description:
      "Se você quer escrever legendas mais rápido, apoiar melhor sua mensagem e ter um fluxo de conteúdo mais suave, a Hookora oferece um ponto de partida prático.",
    primaryLabel: "Criar conta grátis",
    secondaryLabel: "Experimentar gerador de legendas"
  }
},

      betterHooksNigeria: {
  seoTitle:
    "Como criadores nigerianos podem escrever hooks melhores | Hookora",
  seoDescription:
    "Aprenda como criadores nigerianos podem escrever hooks melhores para Reels, Shorts, TikTok e conteúdo social com primeiras linhas mais claras e fluxo de atenção mais forte.",
  title: "Como criadores nigerianos podem escrever hooks melhores",
  heroBadge:
    "Guia Hookora • Hooks melhores para criadores nigerianos",
  heroTitle:
    "Como criadores nigerianos podem escrever hooks melhores para uma atenção mais forte ao conteúdo",
  heroDescription:
    "Muitos criadores já têm boas ideias, mas aberturas fracas tornam o conteúdo mais fácil de ignorar. A Hookora ajuda criadores nigerianos a construírem hooks melhores tornando as primeiras linhas mais claras, mais afiadas e mais fáceis de explorar a partir de um único tema.",
  highlights: {
    0: "Primeiras linhas mais fortes",
    1: "Criação de hooks mais rápida",
    2: "Empacotamento short-form melhor",
    3: "Feita para o fluxo do criador"
  },
  secondaryCta: "Experimentar gerador de hooks",
  previewTitle:
    "O que hooks melhores ajudam criadores a fazer",
  previewItems: {
    0: {
      label: "Parar o scroll",
      text: "Fazer as pessoas prestarem atenção mais rápido antes de passarem para outra coisa."
    },
    1: {
      label: "Clarear",
      text: "Transformar ângulos de conteúdo brutos em aberturas mais fáceis de entender."
    },
    2: {
      label: "Fortalecer",
      text: "Dar ao seu conteúdo uma primeira impressão melhor antes que o resto da mensagem se desenrole."
    }
  },
  benefitsEyebrow: "Como melhorar hooks",
  benefitsTitle:
    "Escrever hooks melhores começa tornando sua primeira linha mais clara, mais afiada e mais digna de atenção",
  benefitsDescription:
    "A maioria dos hooks fracos não falha porque o tema é ruim. Eles falham porque a abertura parece plana demais, vaga demais ou fácil demais de ignorar. A Hookora ajuda criadores a melhorar esse primeiro momento.",
  benefits: {
    0: {
      title: "Comece pelo ângulo mais forte da sua ideia",
      text: "Um hook fica mais forte quando lidera com o que há de mais interessante, surpreendente, útil ou emocionalmente forte no conteúdo."
    },
    1: {
      title: "Evite aberturas fracas e lentas",
      text: "Se sua primeira linha parece genérica demais ou segura demais, as pessoas têm mais chance de continuar rolando. Hooks melhores chegam ao ponto mais rápido."
    },
    2: {
      title: "Explore mais de uma direção de hook",
      text: "Muitos criadores param na primeira versão de um hook. Conteúdo mais forte normalmente vem de testar múltiplas direções de abertura antes de escolher uma."
    },
    3: {
      title: "Adapte o hook à plataforma",
      text: "Conteúdo short-form muitas vezes precisa de linguagem que prenda atenção mais rápido. Hooks melhores ajudam criadores a adaptar sua mensagem a como as pessoas consomem conteúdo."
    },
    4: {
      title: "Conecte hooks ao resto do conteúdo",
      text: "Um bom hook funciona ainda melhor quando combina com o título, a legenda, o roteiro e a thumbnail. A Hookora ajuda criadores a moldarem esse empacotamento completo."
    },
    5: {
      title: "Use ferramentas que reduzam a pressão da página em branco",
      text: "Quando criadores não sabem como começar, o processo desacelera. A Hookora ajuda a reduzir essa fricção e torna mais fácil repetir uma escrita de hooks melhor."
    }
  },
  faqTitle:
    "FAQ sobre como criadores nigerianos podem escrever hooks melhores",
  faqIntro:
    "Aqui estão algumas perguntas comuns sobre escrever hooks melhores como criador nigeriano e como a Hookora ajuda.",
  faqs: {
    0: {
      question: "Por que hooks importam tanto para criadores nigerianos?",
      answer:
        "Hooks importam porque moldam a primeira impressão. Em plataformas rápidas, a linha de abertura muitas vezes decide se as pessoas continuam assistindo, lendo ou rolando embora."
    },
    1: {
      question: "O que torna um hook mais forte?",
      answer:
        "Um hook mais forte geralmente é mais claro, mais favorável à atenção e mais interessante emocionalmente. Ele dá às pessoas um motivo para ficarem com o conteúdo por mais tempo."
    },
    2: {
      question: "A Hookora pode ajudar criadores nigerianos a escrever hooks melhores mais rápido?",
      answer:
        "Sim. A Hookora ajuda criadores a gerar direções de hook mais afiadas a partir de um único tema para que eles passem menos tempo adivinhando e mais tempo criando."
    },
    3: {
      question: "A Hookora é útil apenas para criadores de short-form?",
      answer:
        "Não. A Hookora é especialmente útil para conteúdo short-form, mas também ajuda com legendas, títulos, roteiros, hashtags, descrições e texto de thumbnail em fluxos criativos mais amplos."
    },
    4: {
      question: "Iniciantes podem usar a Hookora para melhorar seus hooks?",
      answer:
        "Sim. A Hookora é útil para iniciantes porque reduz a pressão da página em branco e torna mais fácil explorar direções de abertura mais fortes."
    },
    5: {
      question: "Um único tema pode produzir múltiplos hooks?",
      answer:
        "Sim. Uma ideia de conteúdo muitas vezes pode ser enquadrada de várias formas. Explorar múltiplos ângulos de hook é uma das melhores formas de melhorar o empacotamento do conteúdo."
    }
  },
  ctaTitle: "Use a Hookora para escrever hooks melhores com menos fricção",
  ctaDescription:
    "Se você quer primeiras linhas mais fortes, direções de hook mais claras e uma forma mais suave de empacotar conteúdo, a Hookora oferece um ponto de partida prático.",
  ctaSecondaryLabel: "Explorar gerador de hooks"
     }
    }
  },

  footer: {
    description:
      "A Hookora é uma plataforma de fluxo de trabalho para redes sociais com IA para gerar hooks, títulos, legendas, roteiros, hashtags e mais.",
    refundPolicy: "Política de reembolso",
    privacyPolicy: "Política de privacidade",
    terms: "Termos",
    contactSupport: "Contato e suporte",
    copyright: "© {{year}} Hookora. Todos os direitos reservados."
  },

  tools: {
  hooks: "Hooks",
  hookAngles: "Ângulos de Hook",
  titles: "Títulos",
  captions: "Legendas",
  hashtags: "Hashtags",
  descriptions: "Descrições",
  scripts: "Roteiros",
  ctas: "CTAs",
  thumbnailAnalyzer: "Analisador de Thumbnail",
  aiHumanizer: "Humanizador de IA",
  repurposer: "Reaproveitador",
  adCopy: "Texto de Anúncio",
  carousels: "Carrosséis",
  viralRewrite: "Reescrita Viral",
  offerPositioning: "Posicionamento de Oferta",
  hookScore: "Pontuação do Hook",
  trendConverter: "Conversor de Tendências",
  nigerianRewrite: "Reescrita Nigeriana",
  retentionIntro: "Intro de Retenção",
  brandVoiceTrainer: "Treinador de Voz da Marca",
  contentDifferentiation: "Diferenciação de Conteúdo",
  commentIdeas: "Ideias de Comentários",
  viralHookGenerator: "Gerador de Hooks Virais",
  youtubeTitleOptimizer: "Otimizador de Títulos do YouTube",
  captionGenerator: "Gerador de Legendas",
  hashtagGenerator: "Gerador de Hashtags",
  descriptionRewriter: "Reescritor de Descrições",
  shortScriptGenerator: "Gerador de Roteiros Curtos",
  thumbnailTextAnalyzer: "Analisador de Texto para Thumbnail",
  hookAngleGenerator: "Gerador de Ângulos de Hook",
  ctaGenerator: "Gerador de CTA",
  contentRepurposer: "Reaproveitador de Conteúdo",
  adCopyGenerator: "Gerador de Texto de Anúncio",
  carouselGenerator: "Gerador de Carrosséis",
  viralRewriter: "Reescritor Viral",
  trendToContent: "Tendência para Conteúdo",
  nigerianAudienceRewrite: "Reescrita para Público Nigeriano",
  retentionIntroGenerator: "Gerador de Intro de Retenção",
  commentToContent: "Comentário para Conteúdo"
},
});

export const translations = {
  en,
  es,
  fr,
  pt
};
